import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Checkbox, Dropdown, Input, InputNumber, Modal, Pagination, Radio, Select, Spin, Switch, Table, Tabs, Tag, Tooltip, Typography, Upload, message } from 'antd';
import { CheckCircleFilled, CopyOutlined, PrinterOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PDFDocument } from 'pdf-lib';
import request from '../../../../utils/request';
import { getInventoryUnitDetail } from '../../../../services/inventory';
import './DropshipPackRenderer.css';

type OperationRecord = {
  time: string;
  mark_mode: '波次操作' | '直接标注';
  instructions: Array<{ system_code: string; status: string; error_reason?: string }>;
};

type ScanMatchRecord = {
  time: string;
  scanValue: string;
  matchedInstruction: string;
  matchMethod: string;
};

type ScanRecommendPositionResult = {
  sortingPosition: string;
  sourceTabName: '已有运单' | '仓库提供运单' | '先申请包装数据';
  shippedQty: number;
  pickedQty: number;
  gapQty: number;
  iuCode: string;
};

type Props = {
  step?: any;
  data?: any;
  metrics?: any;
  onChange?: (k: string, v: any) => void;
  onSubmit?: () => Promise<void> | void;
  instructionCodeFromParent?: string;
  canOperate?: boolean;
  taskId?: number | null;
  stageNo?: number | null;
  batchDetail?: any;
  onBatchRefresh?: () => Promise<void>;
};

const DropshipPackRenderer: React.FC<Props> = ({ batchDetail, onBatchRefresh, canOperate = true }) => {
  const readonly = canOperate === false;
  const navigate = useNavigate();
  const items = useMemo(() => (Array.isArray(batchDetail?.items) ? batchDetail.items : []), [batchDetail]);
  const [localWaybillOverrideByInstrId, setLocalWaybillOverrideByInstrId] = useState<Record<number, {
    waybill_carrier?: string;
    waybill_tracking_no?: string;
    attachments_detail?: any[];
    actual_pack_length_cm?: number | null;
    actual_pack_width_cm?: number | null;
    actual_pack_height_cm?: number | null;
    actual_gross_weight_g?: number | null;
    pack_result_status?: string;
  }>>({});
  const itemsPatched = useMemo(() => {
    const keys = Object.keys(localWaybillOverrideByInstrId || {});
    if (keys.length === 0) return items;
    return (items || []).map((r: any) => {
      const id = Number(r?.instruction ?? r?.instruction_id ?? r?.id ?? 0);
      if (!id || !localWaybillOverrideByInstrId[id]) return r;
      const patch = localWaybillOverrideByInstrId[id];
      return {
        ...r,
        waybill_carrier: patch.waybill_carrier != null ? patch.waybill_carrier : r?.waybill_carrier,
        waybill_tracking_no: patch.waybill_tracking_no != null ? patch.waybill_tracking_no : r?.waybill_tracking_no,
        attachments_detail: Array.isArray(patch.attachments_detail) ? patch.attachments_detail : (r?.attachments_detail || []),
        actual_pack_length_cm: patch.actual_pack_length_cm != null ? patch.actual_pack_length_cm : r?.actual_pack_length_cm,
        actual_pack_width_cm: patch.actual_pack_width_cm != null ? patch.actual_pack_width_cm : r?.actual_pack_width_cm,
        actual_pack_height_cm: patch.actual_pack_height_cm != null ? patch.actual_pack_height_cm : r?.actual_pack_height_cm,
        actual_gross_weight_g: patch.actual_gross_weight_g != null ? patch.actual_gross_weight_g : r?.actual_gross_weight_g,
        pack_result_status: patch.pack_result_status != null ? patch.pack_result_status : r?.pack_result_status,
        instruction: (r && typeof r.instruction === 'object')
          ? {
              ...r.instruction,
              waybill_carrier: patch.waybill_carrier != null ? patch.waybill_carrier : r?.instruction?.waybill_carrier,
              waybill_tracking_no: patch.waybill_tracking_no != null ? patch.waybill_tracking_no : r?.instruction?.waybill_tracking_no,
              actual_pack_length_cm: patch.actual_pack_length_cm != null ? patch.actual_pack_length_cm : r?.instruction?.actual_pack_length_cm,
              actual_pack_width_cm: patch.actual_pack_width_cm != null ? patch.actual_pack_width_cm : r?.instruction?.actual_pack_width_cm,
              actual_pack_height_cm: patch.actual_pack_height_cm != null ? patch.actual_pack_height_cm : r?.instruction?.actual_pack_height_cm,
              actual_gross_weight_g: patch.actual_gross_weight_g != null ? patch.actual_gross_weight_g : r?.instruction?.actual_gross_weight_g,
              pack_result_status: patch.pack_result_status != null ? patch.pack_result_status : r?.instruction?.pack_result_status,
            }
          : r?.instruction,
      };
    });
  }, [items, localWaybillOverrideByInstrId]);
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsPageSize, setItemsPageSize] = useState(10);
  const [progressSelectedKeys, setProgressSelectedKeys] = useState<React.Key[]>([]);
  const [batchMarkModalOpen, setBatchMarkModalOpen] = useState(false);
  const [batchMarkStatus, setBatchMarkStatus] = useState<'completed' | 'unoperated' | 'unmarked'>('completed');
  const [batchMarkUnoperatedReason, setBatchMarkUnoperatedReason] = useState('');
  const [filters, setFilters] = useState({
    instructionCode: '',
    done: '',
    waybillStatus: '',
    instructionType: '',
    carrier: '',
    trackingNo: '',
    ocrText: '',
    sortingBarcodeText: '',
    isPackRequired: '' as '' | 'yes' | 'no',
    remarksOnly: false,
    executionTipsOnly: false,
  });
  const [singleSkuPage, setSingleSkuPage] = useState(1);
  const [singleSkuPageSize, setSingleSkuPageSize] = useState(10);
  const [mixedPage, setMixedPage] = useState(1);
  const [mixedPageSize, setMixedPageSize] = useState(10);
  const [opAreaTab, setOpAreaTab] = useState<string>('upload_immediate');
  const [singleSkuIuFilter, setSingleSkuIuFilter] = useState<string>('');
  const [singleSkuInstrCodeFilter, setSingleSkuInstrCodeFilter] = useState<string>('');
  const [singleSkuUnmarkedOnly, setSingleSkuUnmarkedOnly] = useState<boolean>(false);
  const [singleSkuSortStatusFilter, setSingleSkuSortStatusFilter] = useState<string>('');
  const [singleSkuCarrierFilter, setSingleSkuCarrierFilter] = useState<string>('');
  const [mixedIuFilter, setMixedIuFilter] = useState<string>('');
  const [mixedInstrCodeFilter, setMixedInstrCodeFilter] = useState<string>('');
  const [mixedUnmarkedOnly, setMixedUnmarkedOnly] = useState<boolean>(false);
  const [mixedSortStatusFilter, setMixedSortStatusFilter] = useState<string>('');
  const [mixedSelectedInstrKeys, setMixedSelectedInstrKeys] = useState<React.Key[]>([]);
  const [mixedBatchModalOpen, setMixedBatchModalOpen] = useState(false);
  const [mixedBatchSelectedItems, setMixedBatchSelectedItems] = useState<Array<{ instr: any; instrIndex: number }>>([]);
  const [mixedBatchResultFilter, setMixedBatchResultFilter] = useState<'all' | 'done' | 'unoperated' | 'unmarked'>('all');
  const [mixedBatchTrackingFilter, setMixedBatchTrackingFilter] = useState('');
  const [mixedBatchSystemCodeFilter, setMixedBatchSystemCodeFilter] = useState('');
  const [mixedBatchOcrFilter, setMixedBatchOcrFilter] = useState('');
  const [mixedBatchPage, setMixedBatchPage] = useState(1);
  const [mixedBatchPageSize, setMixedBatchPageSize] = useState(10);
  const [mixedBatchTipsOnly, setMixedBatchTipsOnly] = useState(false);
  const [mixedBatchPrintOnly, setMixedBatchPrintOnly] = useState(false);
  const [mixedBatchDoneIds, setMixedBatchDoneIds] = useState<Set<string>>(new Set());
  const [mixedBatchUnoperatedIds, setMixedBatchUnoperatedIds] = useState<Set<string>>(new Set());
  const [mixedBatchUnoperatedReasons, setMixedBatchUnoperatedReasons] = useState<Record<string, string>>({});
  const [mixedBatchUnoperatedReasonModalOpen, setMixedBatchUnoperatedReasonModalOpen] = useState(false);
  const [mixedBatchUnoperatedReasonInstr, setMixedBatchUnoperatedReasonInstr] = useState<any>(null);
  const [mixedBatchUnoperatedReasonInput, setMixedBatchUnoperatedReasonInput] = useState('');
  const [mixedBatchMarkAllUnoperatedModalOpen, setMixedBatchMarkAllUnoperatedModalOpen] = useState(false);
  const [mixedBatchMarkAllUnoperatedReason, setMixedBatchMarkAllUnoperatedReason] = useState('');
  const [mixedBatchMergeLoading, setMixedBatchMergeLoading] = useState(false);
  const [mixedBatchScanAreaVisible, setMixedBatchScanAreaVisible] = useState(false);
  const [mixedBatchScanInputValue, setMixedBatchScanInputValue] = useState('');
  const [mixedBatchScanMatchedCode, setMixedBatchScanMatchedCode] = useState('');
  const [mixedBatchDraftScanMatchRecords, setMixedBatchDraftScanMatchRecords] = useState<ScanMatchRecord[]>([]);
  const [mixedBatchScanRecordPage, setMixedBatchScanRecordPage] = useState(1);
  const [mixedBatchScanRecordPageSize, setMixedBatchScanRecordPageSize] = useState(10);
  const [mixedBatchPackingEstimateByInstrKey, setMixedBatchPackingEstimateByInstrKey] = useState<Record<string, { code: string; name: string } | null>>({});
  const [mixedBatchPackingEstimateLoadingByInstrKey, setMixedBatchPackingEstimateLoadingByInstrKey] = useState<Record<string, boolean>>({});
  const [mixedBatchPackDataModalOpen, setMixedBatchPackDataModalOpen] = useState(false);
  const [mixedBatchPackDataLengthCm, setMixedBatchPackDataLengthCm] = useState<number | null>(null);
  const [mixedBatchPackDataWidthCm, setMixedBatchPackDataWidthCm] = useState<number | null>(null);
  const [mixedBatchPackDataHeightCm, setMixedBatchPackDataHeightCm] = useState<number | null>(null);
  const [mixedBatchPackDataWeightG, setMixedBatchPackDataWeightG] = useState<number | null>(null);
  const [mixedBatchPackDataSaving, setMixedBatchPackDataSaving] = useState(false);
  const [mixedBatchCreateIocLoading, setMixedBatchCreateIocLoading] = useState(false);
  const mixedBatchScanInputRef = useRef<any>(null);
  const [instrModalOpen, setInstrModalOpen] = useState(false);
  const [instrModalList, setInstrModalList] = useState<any[]>([]);
  const [instrModalTitleLeft, setInstrModalTitleLeft] = useState('');
  const [instrModalTitleRight, setInstrModalTitleRight] = useState('');
  const [instrModalOperateContext, setInstrModalOperateContext] = useState<{
    iuCode: string; sortBarcodes: string[]; perOrderQty: number; carrierName: string;
    isNoPack: boolean; methodLabel: string; operateCount: number; packagingKey: string;
  } | null>(null);
  const [instrModalCarrierContext, setInstrModalCarrierContext] = useState<{
    iuCode: string; sortBarcodes: string[]; perOrderQty: number; totalOrders: number; typeLabel: string;
    row: any; carrierKey: string; carrierName: string;
  } | null>(null);
  const [instrModalDoneFilter, setInstrModalDoneFilter] = useState<string>('');
  const [instrModalPage, setInstrModalPage] = useState(1);
  const [instrModalPageSize, setInstrModalPageSize] = useState(10);
  const [scanInputValue, setScanInputValue] = useState<string>('');
  const scanQueryInputRef = useRef<any>(null);
  const [scanResultOpen, setScanResultOpen] = useState(false);
  const [scanMatchedRows, setScanMatchedRows] = useState<any[]>([]);
  const [scanMatchSource, setScanMatchSource] = useState<string | null>(null);
  const [scanResultPage, setScanResultPage] = useState(1);
  const [scanResultPageSize, setScanResultPageSize] = useState(10);
  const [scanSortOnly, setScanSortOnly] = useState(false);
  const [scanInlineRecommendPositionResult, setScanInlineRecommendPositionResult] = useState<ScanRecommendPositionResult | null>(null);
  const [scanInlineOnlySingleHint, setScanInlineOnlySingleHint] = useState<string | null>(null);
  const [scanInlineScanValue, setScanInlineScanValue] = useState<string>('');
  const [scanRecommendPositionResult, setScanRecommendPositionResult] = useState<ScanRecommendPositionResult | null>(null);
  const [scanRecommendPutQty, setScanRecommendPutQty] = useState<number>(1);
  const [sortedPickedQtyMap, setSortedPickedQtyMap] = useState<Record<string, number>>({});
  const [operateConfirmOpen, setOperateConfirmOpen] = useState(false);
  const [operateConfirmContext, setOperateConfirmContext] = useState<{
    row: any;
    carrierKey: string;
    carrierName: string;
    total: number;
    done: number;
    undone: number;
  } | null>(null);
  const [operateConfirmQty, setOperateConfirmQty] = useState<number>(0);
  const [operateConfirmSelectedPackaging, setOperateConfirmSelectedPackaging] = useState<string | null>(null);
  const [operateConfirmPhase, setOperateConfirmPhase] = useState<'idle' | 'batch_active'>('idle');
  const [operateConfirmBatchFetched, setOperateConfirmBatchFetched] = useState(false);
  const [operateConfirmCurrentBatchInstrs, setOperateConfirmCurrentBatchInstrs] = useState<any[]>([]);
  const [operateConfirmMergedMeta, setOperateConfirmMergedMeta] = useState<Record<string, { pageCount: number; selectedInstrs: any[] }>>({});
  const [operateConfirmPackSnapshot, setOperateConfirmPackSnapshot] = useState<{ isNoPack: boolean; noPackCount: number; entries: Array<{ key: string; count: number }> } | null>(null);
  const mergedWaybillBatchInstrsRef = useRef<any[]>([]);
  const [operateConfirmInstrPage, setOperateConfirmInstrPage] = useState(1);
  const [operateConfirmInstrPageSize, setOperateConfirmInstrPageSize] = useState(10);
  const [operateConfirmTipsOnly, setOperateConfirmTipsOnly] = useState(false);
  const [operateConfirmPrintOnly, setOperateConfirmPrintOnly] = useState(false);
  const [operateConfirmWaybillStatusFilter, setOperateConfirmWaybillStatusFilter] = useState<string>('');
  const [operateConfirmTrackingNoFilter, setOperateConfirmTrackingNoFilter] = useState('');
  const [operateConfirmSystemCodeFilter, setOperateConfirmSystemCodeFilter] = useState('');
  const [operateConfirmOcrTextFilter, setOperateConfirmOcrTextFilter] = useState('');
  const [operateConfirmScanAreaVisible, setOperateConfirmScanAreaVisible] = useState(false);
  const [operateConfirmScanInputValue, setOperateConfirmScanInputValue] = useState('');
  const [operateConfirmScanMatchedCode, setOperateConfirmScanMatchedCode] = useState('');
  const [operateConfirmGlobalScanMatchRecords, setOperateConfirmGlobalScanMatchRecords] = useState<ScanMatchRecord[]>([]);
  const [operateConfirmDraftScanMatchRecords, setOperateConfirmDraftScanMatchRecords] = useState<ScanMatchRecord[]>([]);
  const [operateConfirmScanRecordPage, setOperateConfirmScanRecordPage] = useState(1);
  const [operateConfirmScanRecordPageSize, setOperateConfirmScanRecordPageSize] = useState(10);
  const [operateConfirmManualMatchModalOpen, setOperateConfirmManualMatchModalOpen] = useState(false);
  const [operateConfirmManualMatchCandidates, setOperateConfirmManualMatchCandidates] = useState<any[]>([]);
  const [operateConfirmManualMatchSelectedKey, setOperateConfirmManualMatchSelectedKey] = useState('');
  const [operateConfirmManualCodeFilter, setOperateConfirmManualCodeFilter] = useState('');
  const [operateConfirmManualOcrFilter, setOperateConfirmManualOcrFilter] = useState('');
  const [operateConfirmPendingScanValue, setOperateConfirmPendingScanValue] = useState('');
  const [operateConfirmMarkedDoneIds, setOperateConfirmMarkedDoneIds] = useState<Set<string>>(new Set());
  const [operateConfirmMarkedUnoperatedIds, setOperateConfirmMarkedUnoperatedIds] = useState<Set<string>>(new Set());
  const [operateConfirmMarkedUnmarkedIds, setOperateConfirmMarkedUnmarkedIds] = useState<Set<string>>(new Set());
  const [operateConfirmUnoperatedReasons, setOperateConfirmUnoperatedReasons] = useState<Record<string, string>>({});
  const [operateConfirmDraftDoneIds, setOperateConfirmDraftDoneIds] = useState<Set<string>>(new Set());
  const [operateConfirmDraftUnoperatedIds, setOperateConfirmDraftUnoperatedIds] = useState<Set<string>>(new Set());
  const [operateConfirmDraftUnmarkedIds, setOperateConfirmDraftUnmarkedIds] = useState<Set<string>>(new Set());
  const [operateConfirmDraftUnoperatedReasons, setOperateConfirmDraftUnoperatedReasons] = useState<Record<string, string>>({});
  const [operateConfirmUnoperatedReasonModalOpen, setOperateConfirmUnoperatedReasonModalOpen] = useState(false);
  const [operateConfirmUnoperatedReasonInstr, setOperateConfirmUnoperatedReasonInstr] = useState<any>(null);
  const [operateConfirmUnoperatedReasonInput, setOperateConfirmUnoperatedReasonInput] = useState('');
  const [operateConfirmUnoperatedReasonMode, setOperateConfirmUnoperatedReasonMode] = useState<'draft' | 'global'>('draft');
  const [operateConfirmSubmitModalOpen, setOperateConfirmSubmitModalOpen] = useState(false);
  const [operateConfirmMarkAllUnoperatedModalOpen, setOperateConfirmMarkAllUnoperatedModalOpen] = useState(false);
  const [operateConfirmMarkAllUnoperatedReason, setOperateConfirmMarkAllUnoperatedReason] = useState('');
  const [operationRecords, setOperationRecords] = useState<OperationRecord[]>([]);
  const [operationRecordCodeFilter, setOperationRecordCodeFilter] = useState('');
  const [operationRecordStatusFilter, setOperationRecordStatusFilter] = useState('');
  const [operationRecordMarkModeFilter, setOperationRecordMarkModeFilter] = useState('');
  const [scanRecordMatchedInstructionFilter, setScanRecordMatchedInstructionFilter] = useState('');
  const [scanRecordScanValueFilter, setScanRecordScanValueFilter] = useState('');
  const [operationRecordTabKey, setOperationRecordTabKey] = useState<'operation' | 'scan'>('operation');
  const [operationRecordPage, setOperationRecordPage] = useState(1);
  const [operationRecordPageSize, setOperationRecordPageSize] = useState(10);
  const [scanRecordPage, setScanRecordPage] = useState(1);
  const [scanRecordPageSize, setScanRecordPageSize] = useState(10);
  const [operateConfirmSubmitLoading, setOperateConfirmSubmitLoading] = useState(false);
  const [operateConfirmPackDataModalOpen, setOperateConfirmPackDataModalOpen] = useState(false);
  const [operateConfirmPackDataEditingKey, setOperateConfirmPackDataEditingKey] = useState<string>('');
  const [operateConfirmPackDataModalInfo, setOperateConfirmPackDataModalInfo] = useState<{
    iuCode: string;
    sortBarcodes: string[];
    perOrderQty: number;
    operateQty: number;
    packagingLabel: string;
    packageRecommendation: string;
    packageRecommendationCode?: string;
    packageRecommendationName?: string;
  } | null>(null);
  const [operateConfirmPackDataLengthIn, setOperateConfirmPackDataLengthIn] = useState<number | null>(null);
  const [operateConfirmPackDataWidthIn, setOperateConfirmPackDataWidthIn] = useState<number | null>(null);
  const [operateConfirmPackDataHeightIn, setOperateConfirmPackDataHeightIn] = useState<number | null>(null);
  const [operateConfirmPackDataWeightLb, setOperateConfirmPackDataWeightLb] = useState<number | null>(null);
  const [operateConfirmPackDataByPackagingKey, setOperateConfirmPackDataByPackagingKey] = useState<Record<string, {
    lengthIn: number;
    widthIn: number;
    heightIn: number;
    weightLb: number;
  }>>({});
  const [operateConfirmBuyWaybillModalOpen, setOperateConfirmBuyWaybillModalOpen] = useState(false);
  const [operateConfirmBuyWaybillInstr, setOperateConfirmBuyWaybillInstr] = useState<any>(null);
  const [operateConfirmBuyWaybillCarrier, setOperateConfirmBuyWaybillCarrier] = useState('');
  const [operateConfirmBuyWaybillTrackingNo, setOperateConfirmBuyWaybillTrackingNo] = useState('');
  const [operateConfirmBuyWaybillChargeAmount, setOperateConfirmBuyWaybillChargeAmount] = useState<number | null>(null);
  const [operateConfirmBuyWaybillChargeCurrency, setOperateConfirmBuyWaybillChargeCurrency] = useState<'CNY' | 'USD' | 'EUR'>('USD');
  const [operateConfirmBuyWaybillChargeNote, setOperateConfirmBuyWaybillChargeNote] = useState('');
  const [operateConfirmBuyWaybillFileList, setOperateConfirmBuyWaybillFileList] = useState<any[]>([]);
  const [operateConfirmBuyWaybillSubmitting, setOperateConfirmBuyWaybillSubmitting] = useState(false);
  const [operateConfirmLogisticsProviderOptions, setOperateConfirmLogisticsProviderOptions] = useState<Array<{ label: string; value: string }>>([]);
  const operateConfirmScanInputRef = useRef<any>(null);
  const directOperateCloseAfterUnoperatedSubmitRef = useRef(false);
  const directOperateWaybillScanResultRef = useRef('');
  const directOperateWaybillScanMatchedRef = useRef<boolean | null>(null);

  const DROPSHIP_PACK_STORAGE_PREFIX = 'dropship-pack';
  const getMarksStorageKey = (batchId: number | string) => `${DROPSHIP_PACK_STORAGE_PREFIX}-marks-${batchId}`;
  const getRecordsStorageKey = (batchId: number | string) => `${DROPSHIP_PACK_STORAGE_PREFIX}-records-${batchId}`;
  const getScanRecordsStorageKey = (batchId: number | string) => `${DROPSHIP_PACK_STORAGE_PREFIX}-scan-records-${batchId}`;
  const getPickedQtyStorageKey = (batchId: number | string) => `${DROPSHIP_PACK_STORAGE_PREFIX}-picked-qty-${batchId}`;
  const getIocMapStorageKey = (batchId: number | string) => `${DROPSHIP_PACK_STORAGE_PREFIX}-ioc-map-${batchId}`;
  const getDirectPackDataStorageKey = (batchId: number | string) => `${DROPSHIP_PACK_STORAGE_PREFIX}-direct-pack-data-${batchId}`;
  const [operateConfirmIocCodeByInstrId, setOperateConfirmIocCodeByInstrId] = useState<Record<string, string>>({});
  const [directOperateIocSummaryByInstrId, setDirectOperateIocSummaryByInstrId] = useState<Record<string, { code: string; totalQty: number }>>({});
  const [directOperateIocLabelLoadingByInstrId, setDirectOperateIocLabelLoadingByInstrId] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const batchId = batchDetail?.id;
    if (batchId == null) return;
    setSortedPickedQtyMap({});
    try {
      const marksKey = getMarksStorageKey(batchId);
      const rawMarks = localStorage.getItem(marksKey);
      if (rawMarks) {
        const parsed = JSON.parse(rawMarks);
        if (Array.isArray(parsed?.markedDoneIds)) setOperateConfirmMarkedDoneIds(new Set(parsed.markedDoneIds));
        if (Array.isArray(parsed?.markedUnoperatedIds)) setOperateConfirmMarkedUnoperatedIds(new Set(parsed.markedUnoperatedIds));
        if (Array.isArray(parsed?.markedUnmarkedIds)) setOperateConfirmMarkedUnmarkedIds(new Set(parsed.markedUnmarkedIds));
        if (parsed?.unoperatedReasons && typeof parsed.unoperatedReasons === 'object') setOperateConfirmUnoperatedReasons(parsed.unoperatedReasons);
      }
      const recordsKey = getRecordsStorageKey(batchId);
      const rawRecords = localStorage.getItem(recordsKey);
      if (rawRecords) {
        const parsed = JSON.parse(rawRecords);
        if (Array.isArray(parsed)) {
          const normalized: OperationRecord[] = parsed.slice(0, 50).map((rec: any) => ({
            time: String(rec?.time || ''),
            mark_mode: (rec?.mark_mode === '直接标注' ? '直接标注' : '波次操作') as OperationRecord['mark_mode'],
            instructions: Array.isArray(rec?.instructions) ? rec.instructions : [],
          }));
          setOperationRecords(normalized);
        } else if (parsed && typeof parsed === 'object') {
          const recArr = Array.isArray((parsed as any)?.records) ? (parsed as any).records : [];
          const normalized: OperationRecord[] = recArr.slice(0, 50).map((rec: any) => ({
            time: String(rec?.time || ''),
            mark_mode: (rec?.mark_mode === '直接标注' ? '直接标注' : '波次操作') as OperationRecord['mark_mode'],
            instructions: Array.isArray(rec?.instructions) ? rec.instructions : [],
          }));
          setOperationRecords(normalized);
          const mapRaw = (parsed as any)?.iocCodeByInstrId;
          if (mapRaw && typeof mapRaw === 'object' && !Array.isArray(mapRaw)) {
            const nextMap: Record<string, string> = {};
            Object.entries(mapRaw).forEach(([k, v]) => {
              const key = String(k || '').trim();
              const code = String(v || '').trim();
              if (!key || !code) return;
              nextMap[key] = code;
            });
            setOperateConfirmIocCodeByInstrId(nextMap);
          }
        }
      }
      const iocMapKey = getIocMapStorageKey(batchId);
      const rawIocMap = localStorage.getItem(iocMapKey);
      if (rawIocMap) {
        const parsed = JSON.parse(rawIocMap);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const nextMap: Record<string, string> = {};
          Object.entries(parsed).forEach(([k, v]) => {
            const key = String(k || '').trim();
            const code = String(v || '').trim();
            if (!key || !code) return;
            nextMap[key] = code;
          });
          setOperateConfirmIocCodeByInstrId(nextMap);
        }
      }
      const scanRecordsKey = getScanRecordsStorageKey(batchId);
      const rawScanRecords = localStorage.getItem(scanRecordsKey);
      if (rawScanRecords) {
        const parsed = JSON.parse(rawScanRecords);
        if (Array.isArray(parsed)) {
          const normalized: ScanMatchRecord[] = parsed.map((rec: any) => ({
            time: String(rec?.time || ''),
            scanValue: String(rec?.scanValue || ''),
            matchedInstruction: String(rec?.matchedInstruction || ''),
            matchMethod: String(rec?.matchMethod || ''),
          }));
          setOperateConfirmGlobalScanMatchRecords(normalized);
        } else {
          setOperateConfirmGlobalScanMatchRecords([]);
        }
      } else {
        setOperateConfirmGlobalScanMatchRecords([]);
      }
      const pickedQtyKey = getPickedQtyStorageKey(batchId);
      const rawPickedQty = localStorage.getItem(pickedQtyKey);
      if (rawPickedQty) {
        const parsed = JSON.parse(rawPickedQty);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const normalized: Record<string, number> = {};
          Object.entries(parsed).forEach(([k, v]) => {
            const key = String(k || '').trim();
            if (!key) return;
            const n = Number(v ?? 0);
            if (Number.isFinite(n) && n > 0) normalized[key] = n;
          });
          setSortedPickedQtyMap(normalized);
        } else {
          setSortedPickedQtyMap({});
        }
      } else {
        setSortedPickedQtyMap({});
      }
    } catch {
      /* ignore */
    }
  }, [batchDetail?.id]);

  const saveToStorageRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const batchId = batchDetail?.id;
    if (batchId == null) return;
    if (saveToStorageRef.current) clearTimeout(saveToStorageRef.current);
    saveToStorageRef.current = setTimeout(() => {
      try {
        const marksKey = getMarksStorageKey(batchId);
        localStorage.setItem(marksKey, JSON.stringify({
          markedDoneIds: Array.from(operateConfirmMarkedDoneIds),
          markedUnoperatedIds: Array.from(operateConfirmMarkedUnoperatedIds),
          markedUnmarkedIds: Array.from(operateConfirmMarkedUnmarkedIds),
          unoperatedReasons: operateConfirmUnoperatedReasons,
        }));
        const recordsKey = getRecordsStorageKey(batchId);
        localStorage.setItem(recordsKey, JSON.stringify(operationRecords.slice(0, 50)));
        const scanRecordsKey = getScanRecordsStorageKey(batchId);
        localStorage.setItem(scanRecordsKey, JSON.stringify(operateConfirmGlobalScanMatchRecords));
        const pickedQtyKey = getPickedQtyStorageKey(batchId);
        localStorage.setItem(pickedQtyKey, JSON.stringify(sortedPickedQtyMap));
        const iocMapKey = getIocMapStorageKey(batchId);
        localStorage.setItem(iocMapKey, JSON.stringify(operateConfirmIocCodeByInstrId));
      } catch {
        /* ignore */
      }
      saveToStorageRef.current = null;
    }, 400);
    return () => {
      if (saveToStorageRef.current) clearTimeout(saveToStorageRef.current);
    };
  }, [batchDetail?.id, operateConfirmGlobalScanMatchRecords, operateConfirmIocCodeByInstrId, operateConfirmMarkedDoneIds, operateConfirmMarkedUnoperatedIds, operateConfirmMarkedUnmarkedIds, operateConfirmUnoperatedReasons, operationRecords, sortedPickedQtyMap]);

  const getOperateConfirmInstrKey = useCallback((instr: any): string => {
    const id = instr?.id ?? instr?.instruction?.id;
    if (id != null) return String(id);
    const code = instr?.instruction_system_code ?? instr?.instruction?.instruction_system_code ?? instr?.system_code ?? '';
    return String(code).trim() || `_idx_${(instr as any)?._idx ?? Date.now()}`;
  }, []);

  const getInstructionSystemCode = useCallback((instr: any): string => (
    String(instr?.instruction_system_code ?? instr?.instruction?.instruction_system_code ?? instr?.system_code ?? '').trim() || '-'
  ), []);
  const hasCreatedIocForInstr = useCallback((instr: any): boolean => {
    const instructionId = Number(instr?.instruction ?? instr?.instruction_id ?? instr?.id ?? 0);
    const codeFromMap = instructionId > 0
      ? String(operateConfirmIocCodeByInstrId[String(instructionId)] || '').trim()
      : '';
    const code = codeFromMap || String(
      instr?.created_ioc_code
      ?? instr?.instruction?.created_ioc_code
      ?? instr?.metrics?.created_ioc_code
      ?? '',
    ).trim();
    return !!code;
  }, [operateConfirmIocCodeByInstrId]);

  const appendOperationRecord = useCallback((instructions: OperationRecord['instructions'], markMode: OperationRecord['mark_mode']) => {
    if (!Array.isArray(instructions) || instructions.length === 0) return;
    setOperationRecords((prev) => [{
      time: new Date().toLocaleString('zh-CN'),
      mark_mode: markMode,
      instructions,
    }, ...prev].slice(0, 50));
  }, []);

  const appendDirectOperateWaybillScanRecord = useCallback((
    instr: any,
    scanValueRaw: string,
    matchedFlag: boolean | null,
    forceAppendDuplicate: boolean = false,
  ): { ok: boolean; duplicateRecords?: ScanMatchRecord[] } => {
    const scanValue = String(scanValueRaw || '').trim();
    if (!scanValue) {
      // 无扫描值时跳过写入，不阻断后续标注流程
      return { ok: true };
    }
    const scanNorm = scanValue.toLowerCase();
    const duplicateRecords = operateConfirmGlobalScanMatchRecords.filter((r: any) => (
      String(r?.scanValue || '').trim().toLowerCase() === scanNorm
    ));
    if (!forceAppendDuplicate && duplicateRecords.length > 0) {
      return { ok: false, duplicateRecords };
    }
    const matchedInstruction = getInstructionSystemCode(instr);
    const matchMethod = matchedFlag === true
      ? '运单验证-跟踪号一致'
      : matchedFlag === false
        ? '运单验证-跟踪号不一致'
        : '运单验证';
    setOperateConfirmGlobalScanMatchRecords((prev) => [{
      time: new Date().toLocaleString('zh-CN'),
      scanValue,
      matchedInstruction,
      matchMethod,
    }, ...prev]);
    return { ok: true };
  }, [getInstructionSystemCode, operateConfirmGlobalScanMatchRecords]);

  const showDuplicateScanConfirm = useCallback((
    scanValue: string,
    duplicateRecords: ScanMatchRecord[],
    onContinue: () => void,
  ) => {
    Modal.confirm({
      title: '扫描值重复提醒',
      width: 860,
      okText: '继续',
      cancelText: '取消标注',
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>
            扫描值 <span style={{ color: '#1677ff', fontWeight: 600 }}>{scanValue}</span> 已存在重复记录：
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 140 }}>时间</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 130 }}>匹配指令</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 120 }}>扫描值</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 220 }}>匹配方式</th>
                </tr>
              </thead>
              <tbody>
                {duplicateRecords.map((r, idx) => (
                  <tr key={`${r.time}-${r.scanValue}-${idx}`} style={{ borderTop: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '6px 8px' }}>{r.time || '-'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.matchedInstruction || '-'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.scanValue || '-'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.matchMethod || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ),
      onOk: onContinue,
    });
  }, []);

  const isDone = useMemo(() => (row: any) => {
    try {
      const v = String(row?.status || '').toLowerCase();
      return v === 'done' || v === 'completed' || v === 'finished' || v === 'success';
    } catch {
      return false;
    }
  }, []);

  const isDoneInOperateConfirm = useCallback((instr: any): boolean => {
    const key = getOperateConfirmInstrKey(instr);
    if (operateConfirmMarkedDoneIds.has(key)) return true;
    if (operateConfirmMarkedUnmarkedIds.has(key) || operateConfirmMarkedUnoperatedIds.has(key)) return false;
    return false;
  }, [getOperateConfirmInstrKey, operateConfirmMarkedDoneIds, operateConfirmMarkedUnmarkedIds, operateConfirmMarkedUnoperatedIds]);

  const isUnoperatedInOperateConfirm = useCallback((instr: any): boolean => {
    const key = getOperateConfirmInstrKey(instr);
    if (operateConfirmMarkedUnoperatedIds.has(key)) return true;
    if (operateConfirmMarkedDoneIds.has(key) || operateConfirmMarkedUnmarkedIds.has(key)) return false;
    return false;
  }, [getOperateConfirmInstrKey, operateConfirmMarkedDoneIds, operateConfirmMarkedUnmarkedIds, operateConfirmMarkedUnoperatedIds]);

  const isDoneInOperateConfirmDraft = useCallback((instr: any): boolean => {
    const key = getOperateConfirmInstrKey(instr);
    if (operateConfirmDraftDoneIds.has(key)) return true;
    if (operateConfirmDraftUnmarkedIds.has(key) || operateConfirmDraftUnoperatedIds.has(key)) return false;
    return isDoneInOperateConfirm(instr);
  }, [getOperateConfirmInstrKey, isDoneInOperateConfirm, operateConfirmDraftDoneIds, operateConfirmDraftUnmarkedIds, operateConfirmDraftUnoperatedIds]);

  const isUnoperatedInOperateConfirmDraft = useCallback((instr: any): boolean => {
    const key = getOperateConfirmInstrKey(instr);
    if (operateConfirmDraftUnoperatedIds.has(key)) return true;
    if (operateConfirmDraftDoneIds.has(key) || operateConfirmDraftUnmarkedIds.has(key)) return false;
    return isUnoperatedInOperateConfirm(instr);
  }, [getOperateConfirmInstrKey, isUnoperatedInOperateConfirm, operateConfirmDraftDoneIds, operateConfirmDraftUnmarkedIds, operateConfirmDraftUnoperatedIds]);

  const isProcessedInOperateConfirm = useCallback((instr: any): boolean =>
    isDoneInOperateConfirm(instr) || isUnoperatedInOperateConfirm(instr),
  [isDoneInOperateConfirm, isUnoperatedInOperateConfirm]);

  const takeLast12 = useCallback((v: any): string => {
    const s = String(v || '').trim();
    if (!s) return '';
    return s.length <= 12 ? s : s.slice(-12);
  }, []);

  const tailMatchIgnoreCase = useCallback((a: string, b: string): boolean => {
    const aa = String(a || '').trim().toLowerCase();
    const bb = String(b || '').trim().toLowerCase();
    if (!aa || !bb) return false;
    if (aa.length === bb.length) return aa === bb;
    if (aa.length > bb.length) return aa.slice(-bb.length) === bb;
    return bb.slice(-aa.length) === aa;
  }, []);

  const markDraftInstructionCompleted = useCallback((instr: any) => {
    const key = getOperateConfirmInstrKey(instr);
    setOperateConfirmDraftDoneIds((prev) => { const n = new Set(prev); n.add(key); return n; });
    setOperateConfirmDraftUnoperatedIds((prev) => { const n = new Set(prev); n.delete(key); return n; });
    setOperateConfirmDraftUnmarkedIds((prev) => { const n = new Set(prev); n.delete(key); return n; });
    setOperateConfirmDraftUnoperatedReasons((prev) => { const o = { ...prev }; delete o[key]; return o; });
  }, [getOperateConfirmInstrKey]);

  const commitOperateConfirmScanMatch = useCallback((scanValueRaw: string, instr: any, matchMethod: '跟踪号匹配' | '运单内容匹配' | '手动匹配') => {
    const code = getInstructionSystemCode(instr);
    markDraftInstructionCompleted(instr);
    setOperateConfirmDraftScanMatchRecords((prev) => [{
      time: new Date().toLocaleString('zh-CN'),
      scanValue: scanValueRaw,
      matchedInstruction: code,
      matchMethod,
    }, ...prev]);
    setOperateConfirmScanRecordPage(1);
    setOperateConfirmScanMatchedCode(code);
    setOperateConfirmScanInputValue('');
    setOperateConfirmPendingScanValue('');
    setOperateConfirmManualMatchCandidates([]);
    setOperateConfirmManualMatchSelectedKey('');
    setOperateConfirmManualCodeFilter('');
    setOperateConfirmManualOcrFilter('');
    setOperateConfirmManualMatchModalOpen(false);
    setTimeout(() => operateConfirmScanInputRef.current?.focus(), 0);
  }, [getInstructionSystemCode, markDraftInstructionCompleted]);

  const submitOperateConfirmScan = useCallback((skipDuplicateConfirm = false) => {
    const raw = String(operateConfirmScanInputValue || '').trim();
    setOperateConfirmScanInputValue(raw);
    if (!raw) return;
    const duplicatedRecords = operateConfirmDraftScanMatchRecords.filter((r) => String(r?.scanValue || '').trim().toLowerCase() === raw.toLowerCase());
    const duplicated = duplicatedRecords.length > 0;
    if (duplicated && !skipDuplicateConfirm) {
      Modal.confirm({
        title: '重复提醒',
        width: 760,
        content: (
          <div>
            <div style={{ marginBottom: 8 }}>
              扫描值 <span style={{ color: '#1677ff', fontWeight: 600 }}>{raw}</span> 已存在于匹配记录，是否继续匹配？
            </div>
            <div style={{ maxHeight: 220, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 4 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 180 }}>时间</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 170 }}>扫描值</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, minWidth: 140 }}>匹配指令</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 130 }}>匹配方式</th>
                  </tr>
                </thead>
                <tbody>
                  {duplicatedRecords.map((rec, idx) => (
                    <tr key={`${rec.time}-${rec.scanValue}-${rec.matchedInstruction}-${idx}`} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: '#8c8c8c' }}>{rec.time}</td>
                      <td style={{ padding: '6px 8px', wordBreak: 'break-all' }}>{rec.scanValue}</td>
                      <td style={{ padding: '6px 8px', wordBreak: 'break-all' }}>{rec.matchedInstruction}</td>
                      <td style={{ padding: '6px 8px' }}>{rec.matchMethod}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ),
        okText: '继续',
        cancelText: '取消',
        onCancel: () => {
          setOperateConfirmScanInputValue('');
          setTimeout(() => operateConfirmScanInputRef.current?.focus(), 0);
        },
        onOk: () => submitOperateConfirmScan(true),
      });
      return;
    }
    const a = takeLast12(raw);
    const pendingUnmarked = (operateConfirmCurrentBatchInstrs || []).filter((instr: any) =>
      !isDoneInOperateConfirmDraft(instr) && !isUnoperatedInOperateConfirmDraft(instr),
    );
    for (const instr of pendingUnmarked) {
      const trackingNo = String(instr?.waybill_tracking_no ?? instr?.instruction?.waybill_tracking_no ?? '').trim();
      if (!trackingNo) continue;
      const b = takeLast12(trackingNo);
      if (tailMatchIgnoreCase(a, b)) {
        commitOperateConfirmScanMatch(raw, instr, '跟踪号匹配');
        return;
      }
    }
    for (const instr of pendingUnmarked) {
      const waybillAtts = (Array.isArray(instr?.attachments_detail) ? instr.attachments_detail : []).filter((att: any) => {
        const label = String(att?.label || '').trim();
        return label === '物流运单';
      });
      const ocrText = waybillAtts.map((att: any) => String(att?.parse_text ?? '').trim()).filter((t: string) => t.length > 0).join(' ').toLowerCase();
      if (ocrText && ocrText.includes(String(a).toLowerCase())) {
        commitOperateConfirmScanMatch(raw, instr, '运单内容匹配');
        return;
      }
    }
    setOperateConfirmPendingScanValue(raw);
    setOperateConfirmManualMatchCandidates(pendingUnmarked);
    setOperateConfirmManualMatchSelectedKey('');
    setOperateConfirmManualCodeFilter('');
    setOperateConfirmManualOcrFilter('');
    setOperateConfirmManualMatchModalOpen(true);
  }, [
    commitOperateConfirmScanMatch,
    getOperateConfirmInstrKey,
    isDoneInOperateConfirmDraft,
    isUnoperatedInOperateConfirmDraft,
    operateConfirmCurrentBatchInstrs,
    operateConfirmScanInputValue,
    operateConfirmDraftScanMatchRecords,
    tailMatchIgnoreCase,
    takeLast12,
  ]);

  useEffect(() => {
    const total = operateConfirmDraftScanMatchRecords.length;
    const maxPage = Math.max(1, Math.ceil(total / operateConfirmScanRecordPageSize));
    if (operateConfirmScanRecordPage > maxPage) {
      setOperateConfirmScanRecordPage(maxPage);
    }
  }, [operateConfirmDraftScanMatchRecords.length, operateConfirmScanRecordPage, operateConfirmScanRecordPageSize]);

  useEffect(() => {
    if (!operateConfirmOpen || !operateConfirmContext) {
      setOperateConfirmDraftDoneIds(new Set());
      setOperateConfirmDraftUnoperatedIds(new Set());
      setOperateConfirmDraftUnmarkedIds(new Set());
      setOperateConfirmDraftUnoperatedReasons({});
      setOperateConfirmDraftScanMatchRecords([]);
      setOperateConfirmScanRecordPage(1);
      return;
    }
    if (skipDraftRebuildOnceRef.current) {
      skipDraftRebuildOnceRef.current = false;
      return;
    }
    const instrs = (operateConfirmContext.row?.byWaybillInstructions?.[operateConfirmContext.carrierKey] || []) as any[];
    const nextDone = new Set<string>();
    const nextUnoperated = new Set<string>();
    const nextUnmarked = new Set<string>();
    const nextReasons: Record<string, string> = {};
    instrs.forEach((instr: any) => {
      const key = getOperateConfirmInstrKey(instr);
      if (operateConfirmMarkedDoneIds.has(key)) nextDone.add(key);
      else if (operateConfirmMarkedUnoperatedIds.has(key)) nextUnoperated.add(key);
      else nextUnmarked.add(key);
      const reason = String(operateConfirmUnoperatedReasons[key] || '').trim();
      if (reason) nextReasons[key] = reason;
    });
    setOperateConfirmDraftDoneIds(nextDone);
    setOperateConfirmDraftUnoperatedIds(nextUnoperated);
    setOperateConfirmDraftUnmarkedIds(nextUnmarked);
    setOperateConfirmDraftUnoperatedReasons(nextReasons);
    setOperateConfirmDraftScanMatchRecords([]);
    setOperateConfirmScanRecordPage(1);
  }, [
    getOperateConfirmInstrKey,
    operateConfirmContext,
    operateConfirmMarkedDoneIds,
    operateConfirmMarkedUnoperatedIds,
    operateConfirmOpen,
    operateConfirmUnoperatedReasons,
  ]);

  useEffect(() => {
    const ctx = operateConfirmContext;
    if (!ctx) {
      setOperateConfirmPackSnapshot(null);
      return;
    }
    const instrs = (ctx.row?.byWaybillInstructions?.[ctx.carrierKey] || []) as any[];
    // 包装方式快照只在进入确认弹窗时计算一次，不随右侧草稿标注实时变化
    const unmarkedInstrs = instrs.filter((i: any) => {
      const key = getOperateConfirmInstrKey(i);
      return !operateConfirmMarkedDoneIds.has(key) && !operateConfirmMarkedUnoperatedIds.has(key);
    });
    const needPackInstrs = unmarkedInstrs.filter((i: any) => i?.is_pack_required === true);
    const packGroups = new Map<string, number>();
    needPackInstrs.forEach((i: any) => {
      const key = String(i?.estimated_packing_method ?? '').trim() || '_other';
      packGroups.set(key, (packGroups.get(key) || 0) + 1);
    });
    const isNoPack = needPackInstrs.length === 0;
    const packEntries = Array.from(packGroups.entries())
      .filter(([k]) => k !== '_other')
      .sort((a, b) => b[1] - a[1]);
    const otherCount = packGroups.get('_other') || 0;
    if (otherCount > 0) packEntries.push(['_other', otherCount]);
    const entries = packEntries.map(([key, count]) => ({ key, count }));
    setOperateConfirmPackSnapshot({
      isNoPack,
      noPackCount: unmarkedInstrs.length,
      entries,
    });
  }, [operateConfirmContext]);

  const applyGlobalMarkByKeys = useCallback((keysToUpdate: string[], status: 'completed' | 'unoperated' | 'unmarked', reason?: string) => {
    if (!Array.isArray(keysToUpdate) || keysToUpdate.length === 0) return;
    if (status === 'completed') {
      setOperateConfirmMarkedDoneIds((prev) => {
        const n = new Set(prev);
        keysToUpdate.forEach((k) => n.add(k));
        return n;
      });
      setOperateConfirmMarkedUnoperatedIds((prev) => {
        const n = new Set(prev);
        keysToUpdate.forEach((k) => n.delete(k));
        return n;
      });
      setOperateConfirmMarkedUnmarkedIds((prev) => {
        const n = new Set(prev);
        keysToUpdate.forEach((k) => n.delete(k));
        return n;
      });
      setOperateConfirmUnoperatedReasons((prev) => {
        const o = { ...prev };
        keysToUpdate.forEach((k) => delete o[k]);
        return o;
      });
      return;
    }
    if (status === 'unoperated') {
      const finalReason = String(reason || '').trim();
      setOperateConfirmMarkedUnoperatedIds((prev) => {
        const n = new Set(prev);
        keysToUpdate.forEach((k) => n.add(k));
        return n;
      });
      setOperateConfirmMarkedDoneIds((prev) => {
        const n = new Set(prev);
        keysToUpdate.forEach((k) => n.delete(k));
        return n;
      });
      setOperateConfirmMarkedUnmarkedIds((prev) => {
        const n = new Set(prev);
        keysToUpdate.forEach((k) => n.delete(k));
        return n;
      });
      setOperateConfirmUnoperatedReasons((prev) => {
        const o = { ...prev };
        keysToUpdate.forEach((k) => (o[k] = finalReason));
        return o;
      });
      return;
    }
    setOperateConfirmMarkedDoneIds((prev) => {
      const n = new Set(prev);
      keysToUpdate.forEach((k) => n.delete(k));
      return n;
    });
    setOperateConfirmMarkedUnoperatedIds((prev) => {
      const n = new Set(prev);
      keysToUpdate.forEach((k) => n.delete(k));
      return n;
    });
    setOperateConfirmMarkedUnmarkedIds((prev) => {
      const n = new Set(prev);
      keysToUpdate.forEach((k) => n.add(k));
      return n;
    });
    setOperateConfirmUnoperatedReasons((prev) => {
      const o = { ...prev };
      keysToUpdate.forEach((k) => delete o[k]);
      return o;
    });
  }, []);

  const handleSetGlobalWaybillStatus = useCallback((instr: any, status: 'completed' | 'unoperated' | 'unmarked') => {
    const key = getOperateConfirmInstrKey(instr);
    if (status === 'unoperated') {
      setOperateConfirmUnoperatedReasonMode('global');
      setOperateConfirmUnoperatedReasonInstr(instr);
      setOperateConfirmUnoperatedReasonInput(operateConfirmUnoperatedReasons[key] || '');
      setOperateConfirmUnoperatedReasonModalOpen(true);
      return;
    }
    applyGlobalMarkByKeys([key], status);
    appendOperationRecord([{
      system_code: getInstructionSystemCode(instr),
      status: status === 'completed' ? '已完成' : '未标注',
    }], '直接标注');
  }, [appendOperationRecord, applyGlobalMarkByKeys, getInstructionSystemCode, getOperateConfirmInstrKey, operateConfirmUnoperatedReasons]);

  const handleSetOperateConfirmWaybillStatus = useCallback((instr: any, status: 'completed' | 'unoperated' | 'unmarked') => {
    const key = getOperateConfirmInstrKey(instr);
    if (status === 'completed') {
      setOperateConfirmDraftDoneIds((prev) => { const n = new Set(prev); n.add(key); return n; });
      setOperateConfirmDraftUnoperatedIds((prev) => { const n = new Set(prev); n.delete(key); return n; });
      setOperateConfirmDraftUnmarkedIds((prev) => { const n = new Set(prev); n.delete(key); return n; });
      setOperateConfirmDraftUnoperatedReasons((prev) => { const o = { ...prev }; delete o[key]; return o; });
    } else if (status === 'unoperated') {
      setOperateConfirmUnoperatedReasonMode('draft');
      setOperateConfirmUnoperatedReasonInstr(instr);
      setOperateConfirmUnoperatedReasonInput(operateConfirmDraftUnoperatedReasons[key] || '');
      setOperateConfirmUnoperatedReasonModalOpen(true);
    } else {
      setOperateConfirmDraftDoneIds((prev) => { const n = new Set(prev); n.delete(key); return n; });
      setOperateConfirmDraftUnoperatedIds((prev) => { const n = new Set(prev); n.delete(key); return n; });
      setOperateConfirmDraftUnmarkedIds((prev) => { const n = new Set(prev); n.add(key); return n; });
      setOperateConfirmDraftUnoperatedReasons((prev) => { const o = { ...prev }; delete o[key]; return o; });
    }
  }, [getOperateConfirmInstrKey, operateConfirmDraftUnoperatedReasons]);

  const handleConfirmUnoperatedWithReason = useCallback((reason: string) => {
    if (!operateConfirmUnoperatedReasonInstr) return;
    const key = getOperateConfirmInstrKey(operateConfirmUnoperatedReasonInstr);
    const finalizeUnoperatedFlow = () => {
      setOperateConfirmUnoperatedReasonModalOpen(false);
      setOperateConfirmUnoperatedReasonInstr(null);
      setOperateConfirmUnoperatedReasonInput('');
      setOperateConfirmUnoperatedReasonMode('draft');
      directOperateCloseAfterUnoperatedSubmitRef.current = false;
    };
    if (operateConfirmUnoperatedReasonMode === 'global') {
      const continueApply = () => {
        applyGlobalMarkByKeys([key], 'unoperated', reason);
        appendOperationRecord([{
          system_code: getInstructionSystemCode(operateConfirmUnoperatedReasonInstr),
          status: '无法操作',
          error_reason: reason,
        }], '直接标注');
        if (directOperateCloseAfterUnoperatedSubmitRef.current) {
          setDirectOperateModalOpen(false);
          setDirectOperateInstruction(null);
          setDirectOperatePackingEstimate(null);
          setDirectOperatePackingEstimateLoading(false);
          setDirectOperateSortVerifyOpen(false);
          setDirectOperateSortVerifyInput('');
          setDirectOperateUnitPriorityCode('');
          setDirectOperateWaybillScanTargetKey('');
          setDirectOperateWaybillScanInput('');
          setDirectOperateWaybillScanResult('');
          setDirectOperateWaybillScanMatched(null);
        }
        finalizeUnoperatedFlow();
      };
      if (directOperateCloseAfterUnoperatedSubmitRef.current) {
        const ret = appendDirectOperateWaybillScanRecord(
          operateConfirmUnoperatedReasonInstr,
          directOperateWaybillScanResultRef.current,
          directOperateWaybillScanMatchedRef.current,
        );
        if (!ret.ok) {
          showDuplicateScanConfirm(
            String(directOperateWaybillScanResultRef.current || '').trim(),
            ret.duplicateRecords || [],
            () => {
              const retry = appendDirectOperateWaybillScanRecord(
                operateConfirmUnoperatedReasonInstr,
                directOperateWaybillScanResultRef.current,
                directOperateWaybillScanMatchedRef.current,
                true,
              );
              if (!retry.ok) return;
              continueApply();
            },
          );
          return;
        }
      }
      continueApply();
      return;
    } else {
      setOperateConfirmDraftUnoperatedIds((prev) => { const n = new Set(prev); n.add(key); return n; });
      setOperateConfirmDraftDoneIds((prev) => { const n = new Set(prev); n.delete(key); return n; });
      setOperateConfirmDraftUnmarkedIds((prev) => { const n = new Set(prev); n.delete(key); return n; });
      setOperateConfirmDraftUnoperatedReasons((prev) => ({ ...prev, [key]: reason }));
    }
    finalizeUnoperatedFlow();
  }, [appendDirectOperateWaybillScanRecord, appendOperationRecord, applyGlobalMarkByKeys, getInstructionSystemCode, getOperateConfirmInstrKey, operateConfirmUnoperatedReasonInstr, operateConfirmUnoperatedReasonMode, showDuplicateScanConfirm]);

  const handleMarkAllDone = useCallback(() => {
    const list = operateConfirmCurrentBatchInstrs || [];
    if (list.length === 0) {
      message.info('暂无指令可标记');
      return;
    }
    const keysToUpdate = list.map((i: any) => getOperateConfirmInstrKey(i));
    setOperateConfirmDraftDoneIds((prev) => {
      const next = new Set(prev);
      keysToUpdate.forEach((k) => next.add(k));
      return next;
    });
    setOperateConfirmDraftUnoperatedIds((prev) => {
      const n = new Set(prev);
      keysToUpdate.forEach((k) => n.delete(k));
      return n;
    });
    setOperateConfirmDraftUnmarkedIds((prev) => {
      const n = new Set(prev);
      keysToUpdate.forEach((k) => n.delete(k));
      return n;
    });
    setOperateConfirmDraftUnoperatedReasons((prev) => {
      const o = { ...prev };
      keysToUpdate.forEach((k) => delete o[k]);
      return o;
    });
    message.success(`已标记${list.length}条指令为已完成`);
  }, [operateConfirmCurrentBatchInstrs, getOperateConfirmInstrKey]);

  const handleMarkAllUnoperated = useCallback(() => {
    const list = operateConfirmCurrentBatchInstrs || [];
    if (list.length === 0) {
      message.info('暂无指令可标记');
      return;
    }
    setOperateConfirmMarkAllUnoperatedReason('');
    setOperateConfirmMarkAllUnoperatedModalOpen(true);
  }, [operateConfirmCurrentBatchInstrs]);

  const handleConfirmMarkAllUnoperated = useCallback(() => {
    const reason = String(operateConfirmMarkAllUnoperatedReason || '').trim();
    if (!reason) {
      message.warning('请输入无法操作的原因');
      return;
    }
    const list = operateConfirmCurrentBatchInstrs || [];
    const keysToUpdate = list.map((i: any) => getOperateConfirmInstrKey(i));
    setOperateConfirmDraftUnoperatedIds((prev) => {
      const n = new Set(prev);
      keysToUpdate.forEach((k) => n.add(k));
      return n;
    });
    setOperateConfirmDraftDoneIds((prev) => {
      const n = new Set(prev);
      keysToUpdate.forEach((k) => n.delete(k));
      return n;
    });
    setOperateConfirmDraftUnmarkedIds((prev) => {
      const n = new Set(prev);
      keysToUpdate.forEach((k) => n.delete(k));
      return n;
    });
    setOperateConfirmDraftUnoperatedReasons((prev) => {
      const o = { ...prev };
      keysToUpdate.forEach((k) => (o[k] = reason));
      return o;
    });
    message.success(`已标记${list.length}条指令为无法操作`);
    setOperateConfirmMarkAllUnoperatedModalOpen(false);
    setOperateConfirmMarkAllUnoperatedReason('');
  }, [operateConfirmCurrentBatchInstrs, operateConfirmMarkAllUnoperatedReason, getOperateConfirmInstrKey]);

  const handleClearAllOperateConfirmMarks = useCallback(() => {
    const list = operateConfirmCurrentBatchInstrs || [];
    if (list.length === 0) {
      message.info('暂无指令可重置');
      return;
    }
    const keysToUpdate = list.map((i: any) => getOperateConfirmInstrKey(i));
    setOperateConfirmDraftDoneIds((prev) => {
      const n = new Set(prev);
      keysToUpdate.forEach((k) => n.delete(k));
      return n;
    });
    setOperateConfirmDraftUnoperatedIds((prev) => {
      const n = new Set(prev);
      keysToUpdate.forEach((k) => n.delete(k));
      return n;
    });
    setOperateConfirmDraftUnmarkedIds((prev) => {
      const n = new Set(prev);
      keysToUpdate.forEach((k) => n.add(k));
      return n;
    });
    setOperateConfirmDraftUnoperatedReasons((prev) => {
      const o = { ...prev };
      keysToUpdate.forEach((k) => delete o[k]);
      return o;
    });
    message.success(`已将${list.length}条指令重置为未标注`);
  }, [getOperateConfirmInstrKey, operateConfirmCurrentBatchInstrs]);

  const handleResetCurrentStageAllRecords = useCallback(() => {
    const allKeys = (Array.isArray(items) ? items : [])
      .map((instr: any) => getOperateConfirmInstrKey(instr))
      .filter((k: string) => !!String(k || '').trim());
    setOperationRecords([]);
    setOperateConfirmGlobalScanMatchRecords([]);
    setOperationRecordPage(1);
    setScanRecordPage(1);
    setOperateConfirmMarkedDoneIds(new Set());
    setOperateConfirmMarkedUnoperatedIds(new Set());
    setOperateConfirmMarkedUnmarkedIds(new Set(allKeys));
    setOperateConfirmUnoperatedReasons({});
    setOperateConfirmDraftDoneIds(new Set());
    setOperateConfirmDraftUnoperatedIds(new Set());
    setOperateConfirmDraftUnmarkedIds(new Set());
    setOperateConfirmDraftUnoperatedReasons({});
    setOperateConfirmDraftScanMatchRecords([]);
    setOperateConfirmScanRecordPage(1);
    setOperateConfirmScanRecordPageSize(10);
    setSortedPickedQtyMap({});
    message.success('已清空本阶段记录并回退为未标注，请重新操作');
  }, [getOperateConfirmInstrKey, items]);

  const rollbackDraftStatusByDeletedScanRecords = useCallback((deletedRecords: ScanMatchRecord[], remainingRecords: ScanMatchRecord[]) => {
    if (!Array.isArray(deletedRecords) || deletedRecords.length === 0) return;
    const deletedCodes = new Set(
      deletedRecords.map((r) => String(r?.matchedInstruction || '').trim()).filter((v) => !!v && v !== '-'),
    );
    if (deletedCodes.size === 0) return;
    const remainingCodes = new Set(
      (Array.isArray(remainingRecords) ? remainingRecords : [])
        .map((r) => String(r?.matchedInstruction || '').trim())
        .filter((v) => !!v),
    );
    const codesToRollback = Array.from(deletedCodes).filter((code) => !remainingCodes.has(code));
    if (codesToRollback.length === 0) return;
    const codeSet = new Set(codesToRollback);
    const keysToRollback = (operateConfirmCurrentBatchInstrs || [])
      .filter((instr: any) => codeSet.has(getInstructionSystemCode(instr)))
      .map((instr: any) => getOperateConfirmInstrKey(instr));
    if (keysToRollback.length === 0) return;
    setOperateConfirmDraftDoneIds((prev) => {
      const n = new Set(prev);
      keysToRollback.forEach((k) => n.delete(k));
      return n;
    });
    setOperateConfirmDraftUnoperatedIds((prev) => {
      const n = new Set(prev);
      keysToRollback.forEach((k) => n.delete(k));
      return n;
    });
    setOperateConfirmDraftUnmarkedIds((prev) => {
      const n = new Set(prev);
      keysToRollback.forEach((k) => n.add(k));
      return n;
    });
    setOperateConfirmDraftUnoperatedReasons((prev) => {
      const o = { ...prev };
      keysToRollback.forEach((k) => delete o[k]);
      return o;
    });
  }, [getInstructionSystemCode, getOperateConfirmInstrKey, operateConfirmCurrentBatchInstrs]);

  useEffect(() => {
    setOperateConfirmInstrPage(1);
  }, [operateConfirmSelectedPackaging, operateConfirmTipsOnly, operateConfirmPrintOnly, operateConfirmWaybillStatusFilter, operateConfirmTrackingNoFilter, operateConfirmSystemCodeFilter, operateConfirmOcrTextFilter]);
  const [packingEstimateLoading, setPackingEstimateLoading] = useState(false);
  const [packingEstimateByMethod, setPackingEstimateByMethod] = useState<Record<string, { code: string; name: string }>>({});
  const [attPreviewOpen, setAttPreviewOpen] = useState(false);
  const [attPreviewLoading, setAttPreviewLoading] = useState(false);
  const [attPreviewTitle, setAttPreviewTitle] = useState('');
  const [attPreviewTitlePrintSuffix, setAttPreviewTitlePrintSuffix] = useState('');
  const [attPreviewKind, setAttPreviewKind] = useState<'image' | 'pdf' | 'text' | 'xlsx' | 'other'>('other');
  const [attPreviewUrl, setAttPreviewUrl] = useState('');
  const [attPreviewText, setAttPreviewText] = useState('');
  const attPreviewObjectUrlRef = useRef<string | null>(null);
  const attPreviewXlsxWbRef = useRef<any>(null);
  const attPreviewXlsxLibRef = useRef<any>(null);
  const attPreviewXlsxCacheRef = useRef<Record<string, { cols: any[]; data: any[] }>>({});
  const [attPreviewXlsxSheets, setAttPreviewXlsxSheets] = useState<string[]>([]);
  const [attPreviewXlsxActiveSheet, setAttPreviewXlsxActiveSheet] = useState('');
  const [attPreviewXlsxCols, setAttPreviewXlsxCols] = useState<any[]>([]);
  const [attPreviewXlsxData, setAttPreviewXlsxData] = useState<any[]>([]);
  const [directOperateModalOpen, setDirectOperateModalOpen] = useState(false);
  const [directOperateInstruction, setDirectOperateInstruction] = useState<any>(null);
  const [directOperatePackingEstimateLoading, setDirectOperatePackingEstimateLoading] = useState(false);
  const [directOperatePackingEstimate, setDirectOperatePackingEstimate] = useState<{ code: string; name: string } | null>(null);
  const [directOperatePackDataModalOpen, setDirectOperatePackDataModalOpen] = useState(false);
  const [directOperatePackDataLengthCm, setDirectOperatePackDataLengthCm] = useState<number | null>(null);
  const [directOperatePackDataWidthCm, setDirectOperatePackDataWidthCm] = useState<number | null>(null);
  const [directOperatePackDataHeightCm, setDirectOperatePackDataHeightCm] = useState<number | null>(null);
  const [directOperatePackDataWeightG, setDirectOperatePackDataWeightG] = useState<number | null>(null);
  const [directOperatePackDataByInstrKey, setDirectOperatePackDataByInstrKey] = useState<Record<string, {
    lengthCm: number;
    widthCm: number;
    heightCm: number;
    weightG: number;
  }>>({});
  const [directOperateSortVerifyOpen, setDirectOperateSortVerifyOpen] = useState(false);
  const [directOperateSortVerifyInput, setDirectOperateSortVerifyInput] = useState('');
  const [directOperateUnitPriorityCode, setDirectOperateUnitPriorityCode] = useState('');
  const directOperateSortVerifyInputRef = useRef<any>(null);
  const [mixedSortVerifyOpen, setMixedSortVerifyOpen] = useState(false);
  const [mixedSortVerifyInstruction, setMixedSortVerifyInstruction] = useState<any>(null);
  const [mixedSortVerifySortPosition, setMixedSortVerifySortPosition] = useState('');
  const [mixedSortVerifyInput, setMixedSortVerifyInput] = useState('');
  const [mixedSortVerifyPriorityIu, setMixedSortVerifyPriorityIu] = useState('');
  const mixedSortVerifyInputRef = useRef<any>(null);
  const [singleTabSortVerifyOpen, setSingleTabSortVerifyOpen] = useState(false);
  const [singleTabSortVerifyContext, setSingleTabSortVerifyContext] = useState<{
    sourceTabName: '仓库提供运单' | '先申请包装数据';
    sortingPosition: string;
    unitRows: Array<{ iuCode: string; sortBarcode: string; shipQty: number }>;
    instructionCount: number;
    instructionCode?: string;
  } | null>(null);
  const [singleTabSortVerifyInput, setSingleTabSortVerifyInput] = useState('');
  const [singleTabSortVerifyPriorityIu, setSingleTabSortVerifyPriorityIu] = useState('');
  const singleTabSortVerifyInputRef = useRef<any>(null);
  const [directOperateWaybillScanTargetKey, setDirectOperateWaybillScanTargetKey] = useState('');
  const [directOperateWaybillScanInput, setDirectOperateWaybillScanInput] = useState('');
  const [directOperateWaybillScanResult, setDirectOperateWaybillScanResult] = useState('');
  const [directOperateWaybillScanMatched, setDirectOperateWaybillScanMatched] = useState<boolean | null>(null);
  const directOperateWaybillScanInputRef = useRef<any>(null);
  const [otherAttModalOpen, setOtherAttModalOpen] = useState(false);
  const [otherAttList, setOtherAttList] = useState<any[]>([]);
  const [mergedWaybillPdfOpen, setMergedWaybillPdfOpen] = useState(false);
  const [mergedWaybillPdfUrl, setMergedWaybillPdfUrl] = useState('');
  const [mergedWaybillPdfLoading, setMergedWaybillPdfLoading] = useState(false);
  const [mergedWaybillAutoPrint, setMergedWaybillAutoPrint] = useState(false);
  const [mergedPreviewTitle, setMergedPreviewTitle] = useState('合并运单预览');
  const [mergedPreviewSource, setMergedPreviewSource] = useState<'waybill' | 'ioc' | ''>('');
  const mergedWaybillPdfUrlRef = useRef<string | null>(null);
  const [operateConfirmCreateIocLoadingByKey, setOperateConfirmCreateIocLoadingByKey] = useState<Record<string, boolean>>({});
  const skipDraftRebuildOnceRef = useRef(false);
  const directPackDataSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const batchId = batchDetail?.id;
    if (batchId == null) return;
    try {
      const storageKey = getDirectPackDataStorageKey(batchId);
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        setDirectOperatePackDataByInstrKey({});
        return;
      }
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setDirectOperatePackDataByInstrKey({});
        return;
      }
      const next: Record<string, { lengthCm: number; widthCm: number; heightCm: number; weightG: number }> = {};
      Object.entries(parsed).forEach(([k, v]) => {
        const key = String(k || '').trim();
        const row = v as any;
        const l = Number(row?.lengthCm ?? 0);
        const w = Number(row?.widthCm ?? 0);
        const h = Number(row?.heightCm ?? 0);
        const g = Number(row?.weightG ?? 0);
        if (!key || !(l > 0 && w > 0 && h > 0 && g > 0)) return;
        next[key] = {
          lengthCm: Math.round(l),
          widthCm: Math.round(w),
          heightCm: Math.round(h),
          weightG: Math.round(g),
        };
      });
      setDirectOperatePackDataByInstrKey(next);
    } catch {
      setDirectOperatePackDataByInstrKey({});
    }
  }, [batchDetail?.id]);

  useEffect(() => {
    const batchId = batchDetail?.id;
    if (batchId == null) return;
    if (directPackDataSaveRef.current) clearTimeout(directPackDataSaveRef.current);
    directPackDataSaveRef.current = setTimeout(() => {
      try {
        const storageKey = getDirectPackDataStorageKey(batchId);
        localStorage.setItem(storageKey, JSON.stringify(directOperatePackDataByInstrKey || {}));
      } catch {
        // ignore
      }
      directPackDataSaveRef.current = null;
    }, 300);
    return () => {
      if (directPackDataSaveRef.current) clearTimeout(directPackDataSaveRef.current);
    };
  }, [batchDetail?.id, directOperatePackDataByInstrKey]);

  useEffect(() => {
    directOperateWaybillScanResultRef.current = String(directOperateWaybillScanResult || '');
    directOperateWaybillScanMatchedRef.current = directOperateWaybillScanMatched;
  }, [directOperateWaybillScanMatched, directOperateWaybillScanResult]);
  useEffect(() => {
    if (!mixedSortVerifyOpen) return;
    setTimeout(() => {
      try { mixedSortVerifyInputRef.current?.focus?.(); } catch {}
    }, 0);
  }, [mixedSortVerifyOpen]);
  useEffect(() => {
    if (!singleTabSortVerifyOpen) return;
    setTimeout(() => {
      try { singleTabSortVerifyInputRef.current?.focus?.(); } catch {}
    }, 0);
  }, [singleTabSortVerifyOpen]);

  const displayDash = (v: any): string => {
    const s = String(v ?? '').trim();
    if (!s || s.toLowerCase() === 'none') return '-';
    return s;
  };
  const getPackagingMethodLabel = (m: string): string => {
    const v = String(m || '').trim();
    if (v === 'warehouse_free') return '使用仓库免费包材';
    if (v === 'warehouse_paid') return '使用仓库收费包材';
    if (v === 'customer_carton') return '客户自购包材(纸箱)';
    if (v === 'customer_non_carton') return '客户自购包材(非纸箱)';
    if (v === 'special_packing') return '特殊包装';
    return v || '-';
  };
  const resetOperateConfirmState = useCallback(() => {
    setOperateConfirmOpen(false);
    setOperateConfirmContext(null);
    setOperateConfirmPackSnapshot(null);
    setOperateConfirmSelectedPackaging(null);
    setOperateConfirmQty(0);
    setOperateConfirmPhase('idle');
    setOperateConfirmBatchFetched(false);
    setOperateConfirmCurrentBatchInstrs([]);
    setOperateConfirmMergedMeta({});
    setOperateConfirmTipsOnly(false);
    setOperateConfirmPrintOnly(false);
    setOperateConfirmWaybillStatusFilter('');
    setOperateConfirmTrackingNoFilter('');
    setOperateConfirmSystemCodeFilter('');
    setOperateConfirmOcrTextFilter('');
    setOperateConfirmScanAreaVisible(false);
    setOperateConfirmScanInputValue('');
    setOperateConfirmScanMatchedCode('');
    setOperateConfirmDraftScanMatchRecords([]);
    setOperateConfirmScanRecordPage(1);
    setOperateConfirmScanRecordPageSize(10);
    setOperateConfirmManualMatchModalOpen(false);
    setOperateConfirmManualMatchCandidates([]);
    setOperateConfirmManualMatchSelectedKey('');
    setOperateConfirmManualCodeFilter('');
    setOperateConfirmManualOcrFilter('');
    setOperateConfirmPendingScanValue('');
    setOperateConfirmSubmitModalOpen(false);
    setOperateConfirmMarkAllUnoperatedModalOpen(false);
    setOperateConfirmMarkAllUnoperatedReason('');
    setOperateConfirmUnoperatedReasonModalOpen(false);
    setOperateConfirmUnoperatedReasonInstr(null);
    setOperateConfirmUnoperatedReasonInput('');
    setOperateConfirmUnoperatedReasonMode('draft');
    setOperateConfirmDraftDoneIds(new Set());
    setOperateConfirmDraftUnoperatedIds(new Set());
    setOperateConfirmDraftUnmarkedIds(new Set());
    setOperateConfirmDraftUnoperatedReasons({});
    setPackingEstimateByMethod({});
    setOperateConfirmPackDataModalOpen(false);
    setOperateConfirmPackDataEditingKey('');
    setOperateConfirmPackDataModalInfo(null);
    setOperateConfirmPackDataLengthIn(null);
    setOperateConfirmPackDataWidthIn(null);
    setOperateConfirmPackDataHeightIn(null);
    setOperateConfirmPackDataWeightLb(null);
    setOperateConfirmPackDataByPackagingKey({});
    setOperateConfirmBuyWaybillModalOpen(false);
    setOperateConfirmBuyWaybillInstr(null);
    setOperateConfirmBuyWaybillCarrier('');
    setOperateConfirmBuyWaybillTrackingNo('');
    setOperateConfirmBuyWaybillChargeAmount(null);
    setOperateConfirmBuyWaybillChargeCurrency('USD');
    setOperateConfirmBuyWaybillChargeNote('');
    setOperateConfirmBuyWaybillFileList([]);
    setOperateConfirmBuyWaybillSubmitting(false);
    setOperateConfirmCreateIocLoadingByKey({});
  }, []);

  const handleConfirmSubmit = useCallback(async () => {
    if (operateConfirmSubmitLoading) return;
    const list = operateConfirmCurrentBatchInstrs || [];
    if (list.length === 0) {
      message.info('无指令可提交');
      return;
    }
    const hasUnmarked = list.some((instr: any) => !isDoneInOperateConfirmDraft(instr) && !isUnoperatedInOperateConfirmDraft(instr));
    if (hasUnmarked) {
      message.warning('本次操作指令必须全部标注后才能提交');
      return;
    }
    const needIocForDone = String(operateConfirmContext?.carrierKey || '').trim() === 'after_pack';
    if (needIocForDone) {
      const missingIocForDone = list.filter((instr: any) => isDoneInOperateConfirmDraft(instr) && !hasCreatedIocForInstr(instr));
      if (missingIocForDone.length > 0) {
        message.warning(`仍有 ${missingIocForDone.length} 条“已完成”指令未创建产出容器，无法提交`);
        return;
      }
    }
    const records: OperationRecord['instructions'] = [];
    const keysToCommit = list.map((i: any) => getOperateConfirmInstrKey(i));
    for (const instr of list) {
      const instrId = instr?.instruction ?? instr?.id;
      if (instrId == null) continue;
      const key = getOperateConfirmInstrKey(instr);
      const done = isDoneInOperateConfirmDraft(instr);
      const unoperated = isUnoperatedInOperateConfirmDraft(instr);
      const error_reason = unoperated ? (operateConfirmDraftUnoperatedReasons[key] || '') : undefined;
      records.push({
        system_code: String(instr?.instruction_system_code ?? instr?.instruction?.instruction_system_code ?? instr?.system_code ?? '').trim() || '-',
        status: done ? '已完成' : unoperated ? '无法操作' : '未标注',
        ...(error_reason ? { error_reason } : {}),
      });
    }
    if (records.length === 0) {
      message.info('无有效指令可提交');
      return;
    }
    const batchId = Number(batchDetail?.id);
    if (!Number.isFinite(batchId) || batchId <= 0) {
      message.error('缺少批次上下文，无法提交');
      return;
    }
    try {
      setOperateConfirmSubmitLoading(true);
      const updates = list
        .map((instr: any) => {
          const instrIdRaw = instr?.instruction ?? instr?.id;
          const instrId = Number(instrIdRaw);
          if (!Number.isFinite(instrId) || instrId <= 0) return null;
          const key = getOperateConfirmInstrKey(instr);
          const done = isDoneInOperateConfirmDraft(instr);
          const unoperated = isUnoperatedInOperateConfirmDraft(instr);
          return {
            instruction_id: instrId,
            status: done ? 'done' : unoperated ? 'excluded' : 'pending',
            error_reason: unoperated ? String(operateConfirmDraftUnoperatedReasons[key] || '').trim() : '',
          };
        })
        .filter(Boolean);
      await request.post(`/api/v1/instruction/batches/${batchId}/dropship-pack-submit/`, { updates });
      try {
        await Promise.resolve(onBatchRefresh?.());
      } catch {}
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '提交失败');
      return;
    } finally {
      setOperateConfirmSubmitLoading(false);
    }
    setOperateConfirmMarkedDoneIds((prev) => {
      const n = new Set(prev);
      keysToCommit.forEach((k) => {
        if (operateConfirmDraftDoneIds.has(k)) n.add(k);
        else n.delete(k);
      });
      return n;
    });
    setOperateConfirmMarkedUnoperatedIds((prev) => {
      const n = new Set(prev);
      keysToCommit.forEach((k) => {
        if (operateConfirmDraftUnoperatedIds.has(k)) n.add(k);
        else n.delete(k);
      });
      return n;
    });
    setOperateConfirmMarkedUnmarkedIds((prev) => {
      const n = new Set(prev);
      keysToCommit.forEach((k) => {
        if (operateConfirmDraftUnmarkedIds.has(k)) n.add(k);
        else n.delete(k);
      });
      return n;
    });
    setOperateConfirmUnoperatedReasons((prev) => {
      const o = { ...prev };
      keysToCommit.forEach((k) => {
        if (operateConfirmDraftUnoperatedIds.has(k)) {
          o[k] = operateConfirmDraftUnoperatedReasons[k] || '';
        } else {
          delete o[k];
        }
      });
      return o;
    });
    message.success(`已记录${records.length}条指令处理结果`);
    appendOperationRecord(records, '波次操作');
    const latestCtx = operateConfirmContext;
    if (latestCtx) {
      const carrierInstrs = (latestCtx.row?.byWaybillInstructions?.[latestCtx.carrierKey] || []) as any[];
      const total = carrierInstrs.length || Number(latestCtx.total || 0);
      const processed = carrierInstrs.filter((instr: any) => (
        isDoneInOperateConfirmDraft(instr) || isUnoperatedInOperateConfirmDraft(instr)
      )).length;
      setOperateConfirmContext({
        ...latestCtx,
        total,
        done: processed,
        undone: Math.max(total - processed, 0),
      });
    }
    setOperateConfirmGlobalScanMatchRecords((prev) => [...operateConfirmDraftScanMatchRecords, ...prev]);
    // 提交成功后刷新确认操作弹窗为最新已提交状态，并清空当前操作面板
    setOperateConfirmSelectedPackaging(null);
    setOperateConfirmQty(0);
    setOperateConfirmPhase('idle');
    setOperateConfirmBatchFetched(false);
    setOperateConfirmCurrentBatchInstrs([]);
    setOperateConfirmMergedMeta({});
    setOperateConfirmTipsOnly(false);
    setOperateConfirmPrintOnly(false);
    setOperateConfirmWaybillStatusFilter('');
    setOperateConfirmTrackingNoFilter('');
    setOperateConfirmSystemCodeFilter('');
    setOperateConfirmOcrTextFilter('');
    setOperateConfirmScanAreaVisible(false);
    setOperateConfirmScanInputValue('');
    setOperateConfirmScanMatchedCode('');
    setOperateConfirmDraftScanMatchRecords([]);
    setOperateConfirmScanRecordPage(1);
    setOperateConfirmScanRecordPageSize(10);
    setOperateConfirmManualMatchModalOpen(false);
    setOperateConfirmManualMatchCandidates([]);
    setOperateConfirmManualMatchSelectedKey('');
    setOperateConfirmManualCodeFilter('');
    setOperateConfirmManualOcrFilter('');
    setOperateConfirmPendingScanValue('');
    setOperateConfirmDraftDoneIds(new Set());
    setOperateConfirmDraftUnoperatedIds(new Set());
    setOperateConfirmDraftUnmarkedIds(new Set());
    setOperateConfirmDraftUnoperatedReasons({});
    setOperateConfirmPackDataModalOpen(false);
    setOperateConfirmPackDataEditingKey('');
    setOperateConfirmPackDataModalInfo(null);
    setOperateConfirmPackDataLengthIn(null);
    setOperateConfirmPackDataWidthIn(null);
    setOperateConfirmPackDataHeightIn(null);
    setOperateConfirmPackDataWeightLb(null);
    setOperateConfirmPackDataByPackagingKey({});
    setOperateConfirmUnoperatedReasonModalOpen(false);
    setOperateConfirmUnoperatedReasonInstr(null);
    setOperateConfirmUnoperatedReasonInput('');
    setOperateConfirmSubmitModalOpen(false);
  }, [
    appendOperationRecord,
    batchDetail?.id,
    getOperateConfirmInstrKey,
    hasCreatedIocForInstr,
    isDoneInOperateConfirmDraft,
    isUnoperatedInOperateConfirmDraft,
    operateConfirmContext,
    operateConfirmCurrentBatchInstrs,
    operateConfirmDraftDoneIds,
    operateConfirmDraftScanMatchRecords,
    operateConfirmDraftUnmarkedIds,
    operateConfirmDraftUnoperatedIds,
    operateConfirmDraftUnoperatedReasons,
    operateConfirmSubmitLoading,
    onBatchRefresh,
  ]);

  const submitBuyWaybillUpload = useCallback(async () => {
    const instr = operateConfirmBuyWaybillInstr || {};
    const instructionId = Number(instr?.instruction ?? instr?.instruction_id ?? instr?.id ?? 0);
    if (!instructionId) {
      message.warning('缺少指令ID');
      return;
    }
    const carrier = String(operateConfirmBuyWaybillCarrier || '').trim();
    const trackingNo = String(operateConfirmBuyWaybillTrackingNo || '').trim();
    const chargeAmountInput = operateConfirmBuyWaybillChargeAmount;
    const chargeAmount = Number(chargeAmountInput);
    const chargeCurrency = String(operateConfirmBuyWaybillChargeCurrency || 'USD').trim().toUpperCase();
    const chargeNote = String(operateConfirmBuyWaybillChargeNote || '').trim();
    const fileObj = operateConfirmBuyWaybillFileList?.[0]?.originFileObj;
    const instrAtts = Array.isArray(instr?.attachments_detail) ? instr.attachments_detail : [];
    const hasExistingWaybill = instrAtts.some((a: any) => String(a?.label || '').trim() === '物流运单');
    if (!carrier) {
      message.warning('请填写运单所属承运商');
      return;
    }
    if (!trackingNo) {
      message.warning('请填写物流跟踪号');
      return;
    }
    if (!fileObj && !hasExistingWaybill) {
      message.warning('请上传运单附件');
      return;
    }
    if (chargeAmountInput == null || !Number.isFinite(chargeAmount)) {
      message.warning('请填写运单费用金额');
      return;
    }
    if (chargeAmount < 0) {
      message.warning('运单费用金额不能小于0');
      return;
    }
    if (chargeAmount === 0) {
      const proceedZero = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: '运单费用金额为0，确认提交吗？',
          content: '该运单将视为仓库免费提供，不会产生“出库尾程物流”费用。',
          okText: '确认提交',
          cancelText: '返回修改',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!proceedZero) return;
    }
    if (!chargeCurrency) {
      message.warning('请填写运单费用币种');
      return;
    }
    const confirmTrackingDupWarnings = (warnings: Array<{ provider: string; tracking_number: string }>) => (
      new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: `检测到${warnings.length}条物流跟踪号重复记录，是否继续提交？`,
          width: 640,
          centered: true,
          content: (
            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6 }}>
              <div style={{ display: 'flex', background: '#fafafa', fontWeight: 600 }}>
                <div style={{ width: 70, padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>序号</div>
                <div style={{ flex: 1, padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>物流服务商</div>
                <div style={{ flex: 1, padding: '6px 8px' }}>物流跟踪号</div>
              </div>
              {warnings.map((item, index) => (
                <div key={`${item.provider}-${item.tracking_number}-${index}`} style={{ display: 'flex', borderTop: '1px solid #f0f0f0' }}>
                  <div style={{ width: 70, padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>{index + 1}</div>
                  <div style={{ flex: 1, padding: '6px 8px', borderRight: '1px solid #f0f0f0' }}>{item.provider}</div>
                  <div style={{ flex: 1, padding: '6px 8px' }}>{item.tracking_number}</div>
                </div>
              ))}
            </div>
          ),
          okText: '继续提交',
          cancelText: '取消',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      })
    );
    let ignoreDuplicateWarning = false;
    try {
      const precheckResp = await request.post('/api/v1/instruction/instructions/precheck/', {
        waybill_carrier: carrier,
        waybill_tracking_no: trackingNo,
        exclude_instruction_ids: [instructionId],
      });
      const warnings = Array.isArray((precheckResp as any)?.data?.warnings)
        ? (precheckResp as any).data.warnings
        : (Array.isArray((precheckResp as any)?.warnings) ? (precheckResp as any).warnings : []);
      if (warnings.length > 0) {
        const proceed = await confirmTrackingDupWarnings(warnings);
        if (!proceed) return;
        ignoreDuplicateWarning = true;
      }
    } catch {
      message.error('物流跟踪号唯一性校验失败，请稍后重试');
      return;
    }
    const formData = new FormData();
    formData.append('waybill_carrier', carrier);
    formData.append('waybill_tracking_no', trackingNo);
    formData.append('waybill_charge_amount', String(chargeAmount));
    formData.append('waybill_charge_currency', chargeCurrency);
    formData.append('waybill_charge_note', chargeNote);
    formData.append('ignore_duplicate_warning', ignoreDuplicateWarning ? '1' : '0');
    if (fileObj) formData.append('file', fileObj);
    setOperateConfirmBuyWaybillSubmitting(true);
    try {
      const resp = await request.post(`/api/v1/instruction/instructions/${instructionId}/waybill-upload/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const payload = resp?.data || {};
      const mergeInstr = (src: any) => {
        const sid = Number(src?.instruction ?? src?.instruction_id ?? src?.id ?? 0);
        if (sid !== instructionId) return src;
        return {
          ...src,
          waybill_carrier: String(payload?.waybill_carrier ?? carrier).trim(),
          waybill_tracking_no: String(payload?.waybill_tracking_no ?? trackingNo).trim(),
          buy_waybill_info: {
            ...(src?.buy_waybill_info || {}),
            charge_info: {
              charge_item_code: 'OUTBOUND_LAST_MILE',
              charge_item_name: '出库尾程物流',
              amount: Number(payload?.waybill_charge_amount ?? chargeAmount),
              currency: String(payload?.waybill_charge_currency ?? chargeCurrency).trim(),
              note: chargeNote,
            },
          },
          attachments_detail: Array.isArray(payload?.attachments_detail) ? payload.attachments_detail : (src?.attachments_detail || []),
        };
      };
      setOperateConfirmCurrentBatchInstrs((prev) => (Array.isArray(prev) ? prev.map((it: any) => mergeInstr(it)) : prev));
      setOperateConfirmBuyWaybillInstr((prev) => (prev ? mergeInstr(prev) : prev));
      setDirectOperateInstruction((prev) => (prev ? mergeInstr(prev) : prev));
      setLocalWaybillOverrideByInstrId((prev) => ({
        ...prev,
        [instructionId]: {
          ...(prev[instructionId] || {}),
          waybill_carrier: String(payload?.waybill_carrier ?? carrier).trim(),
          waybill_tracking_no: String(payload?.waybill_tracking_no ?? trackingNo).trim(),
          attachments_detail: Array.isArray(payload?.attachments_detail)
            ? payload.attachments_detail
            : (Array.isArray(instr?.attachments_detail) ? instr.attachments_detail : []),
        },
      }));
      setOperateConfirmContext((prev) => {
        if (!prev) return prev;
        const byWb = prev?.row?.byWaybillInstructions || {};
        const list = Array.isArray(byWb?.[prev.carrierKey]) ? byWb[prev.carrierKey] : [];
        return {
          ...prev,
          row: {
            ...prev.row,
            byWaybillInstructions: {
              ...byWb,
              [prev.carrierKey]: list.map((it: any) => mergeInstr(it)),
            },
          },
        };
      });
      setOperateConfirmBuyWaybillCarrier(String(payload?.waybill_carrier ?? carrier).trim());
      setOperateConfirmBuyWaybillTrackingNo(String(payload?.waybill_tracking_no ?? trackingNo).trim());
      setOperateConfirmBuyWaybillChargeAmount(
        Number.isFinite(Number(payload?.waybill_charge_amount)) ? Number(payload?.waybill_charge_amount) : chargeAmount,
      );
      setOperateConfirmBuyWaybillChargeCurrency(
        (String(payload?.waybill_charge_currency ?? chargeCurrency).trim().toUpperCase() || 'USD') as 'CNY' | 'USD' | 'EUR',
      );
      setOperateConfirmBuyWaybillChargeNote(chargeNote);
      setOperateConfirmBuyWaybillFileList([]);
      appendOperationRecord([{
        system_code: getInstructionSystemCode(instr),
        status: '仓库购买运单信息上传',
      }], '波次操作');
      message.success('运单信息和附件上传成功');
      setOperateConfirmBuyWaybillModalOpen(false);
      setOperateConfirmBuyWaybillInstr(null);
      setOperateConfirmBuyWaybillCarrier('');
      setOperateConfirmBuyWaybillTrackingNo('');
      setOperateConfirmBuyWaybillChargeAmount(null);
      setOperateConfirmBuyWaybillChargeCurrency('USD');
      setOperateConfirmBuyWaybillChargeNote('');
      setOperateConfirmBuyWaybillFileList([]);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '上传失败');
    } finally {
      setOperateConfirmBuyWaybillSubmitting(false);
    }
  }, [
    appendOperationRecord,
    getInstructionSystemCode,
    operateConfirmBuyWaybillCarrier,
    operateConfirmBuyWaybillChargeAmount,
    operateConfirmBuyWaybillChargeCurrency,
    operateConfirmBuyWaybillChargeNote,
    operateConfirmBuyWaybillFileList,
    operateConfirmBuyWaybillInstr,
    operateConfirmBuyWaybillTrackingNo,
  ]);
  const applyLocalActualPackResultPatch = useCallback((instructionId: number, pack: {
    actual_pack_length_cm: number;
    actual_pack_width_cm: number;
    actual_pack_height_cm: number;
    actual_gross_weight_g: number;
    pack_result_status: string;
  }) => {
    if (!Number.isFinite(instructionId) || instructionId <= 0) return;
    const mergeInstr = (src: any) => {
      const sid = Number(src?.instruction ?? src?.instruction_id ?? src?.id ?? 0);
      if (sid !== instructionId) return src;
      return {
        ...src,
        ...pack,
        instruction: (src && typeof src.instruction === 'object')
          ? {
              ...src.instruction,
              ...pack,
            }
          : src?.instruction,
      };
    };
    setOperateConfirmCurrentBatchInstrs((prev) => (Array.isArray(prev) ? prev.map((it: any) => mergeInstr(it)) : prev));
    setOperateConfirmBuyWaybillInstr((prev) => (prev ? mergeInstr(prev) : prev));
    setDirectOperateInstruction((prev) => (prev ? mergeInstr(prev) : prev));
    setInstrModalList((prev) => (Array.isArray(prev) ? prev.map((it: any) => mergeInstr(it)) : prev));
    skipDraftRebuildOnceRef.current = true;
    setOperateConfirmContext((prev) => {
      if (!prev) return prev;
      const byWb = prev?.row?.byWaybillInstructions || {};
      const list = Array.isArray(byWb?.[prev.carrierKey]) ? byWb[prev.carrierKey] : [];
      return {
        ...prev,
        row: {
          ...prev.row,
          byWaybillInstructions: {
            ...byWb,
            [prev.carrierKey]: list.map((it: any) => mergeInstr(it)),
          },
        },
      };
    });
    setLocalWaybillOverrideByInstrId((prev) => ({
      ...prev,
      [instructionId]: {
        ...(prev[instructionId] || {}),
        ...pack,
      },
    }));
    setDirectOperatePackDataByInstrKey((prev) => ({
      ...prev,
      [String(instructionId)]: {
        lengthCm: Math.round(Number(pack?.actual_pack_length_cm || 0)),
        widthCm: Math.round(Number(pack?.actual_pack_width_cm || 0)),
        heightCm: Math.round(Number(pack?.actual_pack_height_cm || 0)),
        weightG: Math.round(Number(pack?.actual_gross_weight_g || 0)),
      },
    }));
  }, []);

  const buildPackingItemsFromInstructions = useCallback((instrs: any[], perOrderQty: number): { length_cm: number; width_cm: number; height_cm: number; weight_g: number; quantity: number }[] | null => {
    if (!Array.isArray(instrs) || instrs.length === 0 || !Number.isFinite(perOrderQty) || perOrderQty <= 0) return null;
    const instr = instrs[0];
    const targets = Array.isArray(instr?.targets_detail) ? instr.targets_detail : [];
    for (const t of targets) {
      if (String(t?.target_type) !== 'inventory_unit') continue;
      const snap = t?.snapshot || {};
      const l = Number(t?.actual_length ?? snap?.actual_length ?? 0);
      const w = Number(t?.actual_width ?? snap?.actual_width ?? 0);
      const h = Number(t?.actual_height ?? snap?.actual_height ?? 0);
      if (!Number.isFinite(l) || !Number.isFinite(w) || !Number.isFinite(h) || l <= 0 || w <= 0 || h <= 0) continue;
      return [{ length_cm: l, width_cm: w, height_cm: h, weight_g: 0, quantity: perOrderQty }];
    }
    const t = targets[0];
    if (!t) return null;
    const snap = t?.snapshot || {};
    const l = Number(t?.actual_length ?? snap?.actual_length ?? 0);
    const w = Number(t?.actual_width ?? snap?.actual_width ?? 0);
    const h = Number(t?.actual_height ?? snap?.actual_height ?? 0);
    if (!Number.isFinite(l) || !Number.isFinite(w) || !Number.isFinite(h) || l <= 0 || w <= 0 || h <= 0) return null;
    return [{ length_cm: l, width_cm: w, height_cm: h, weight_g: 0, quantity: perOrderQty }];
  }, []);
  const fetchPackingEstimateForInstruction = useCallback(async (instr: any): Promise<{ code: string; name: string } | null> => {
    if (!instr || instr?.is_pack_required !== true) return null;
    const category = String(instr?.estimated_packing_method ?? '').trim() || '_other';
    if (!['warehouse_free', 'warehouse_paid', 'customer_carton'].includes(category)) return null;
    const warehouseId = instr?.instruction_warehouse_id ?? instr?.warehouse_id ?? batchDetail?.warehouse_id;
    const customerId = instr?.instruction_customer_id ?? instr?.customer_id ?? instr?.project?.customer_id ?? batchDetail?.customer_id;
    const targets = Array.isArray(instr?.targets_detail) ? instr.targets_detail : [];
    const iuTargets = targets.filter((t: any) => String(t?.target_type || '').trim() === 'inventory_unit');
    const perOrderQty = Math.max(1, iuTargets.reduce((sum: number, t: any) => sum + (Number(t?.expected_qty ?? 0) || 0), 0));

    let items: { length_cm: number; width_cm: number; height_cm: number; weight_g: number; quantity: number }[] | null = null;
    const firstTarget = iuTargets[0] || targets[0];
    const iuId = firstTarget?.inventory_unit_id;
    if (iuId && Number.isFinite(Number(iuId))) {
      try {
        const iuResp = await getInventoryUnitDetail(Number(iuId), warehouseId ? { warehouse_id: warehouseId } : undefined);
        const iu = (iuResp as any)?.data ?? iuResp;
        const l = Number(iu?.actual_length ?? iu?.length ?? 0);
        const w = Number(iu?.actual_width ?? iu?.width ?? 0);
        const h = Number(iu?.actual_height ?? iu?.height ?? 0);
        if (Number.isFinite(l) && Number.isFinite(w) && Number.isFinite(h) && l > 0 && w > 0 && h > 0) {
          items = [{ length_cm: l, width_cm: w, height_cm: h, weight_g: 0, quantity: perOrderQty }];
        }
      } catch {
        /* ignore */
      }
    }
    if (!items && firstTarget) {
      items = buildPackingItemsFromInstructions([instr], perOrderQty);
    }
    if (!items || items.length === 0) return null;

    try {
      const resp = await request.post('/api/v1/logistics/packaging-recommendation/', {
        items,
        customer_id: customerId,
        warehouse_id: warehouseId,
        category,
      });
      const box = (resp?.data as any)?.recommended_box;
      if (!box) return null;
      return {
        code: String(box?.system_code || ''),
        name: String(box?.name || box?.title || ''),
      };
    } catch {
      return null;
    }
  }, [batchDetail, buildPackingItemsFromInstructions]);

  useEffect(() => {
    if (!directOperateModalOpen || !directOperateInstruction) {
      setDirectOperatePackingEstimate(null);
      setDirectOperatePackingEstimateLoading(false);
      return;
    }
    let active = true;
    (async () => {
      setDirectOperatePackingEstimateLoading(true);
      setDirectOperatePackingEstimate(null);
      const estimate = await fetchPackingEstimateForInstruction(directOperateInstruction);
      if (active) setDirectOperatePackingEstimate(estimate);
      if (active) setDirectOperatePackingEstimateLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [directOperateInstruction, directOperateModalOpen, fetchPackingEstimateForInstruction]);

  const normalizeText = (v: any) => String(v ?? '').trim().toLowerCase();
  const formatShipUnits = useMemo(() => (row: any) => {
    try {
      const targets = Array.isArray(row?.targets_detail) ? row.targets_detail : [];
      const seen = new Set<string>();
      const lines: string[] = [];
      targets.forEach((t: any) => {
        const snap = t?.snapshot || {};
        const code = t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '';
        const sortArr = Array.isArray(t?.sorting_barcodes)
          ? t.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
          : (Array.isArray(snap?.sorting_barcodes)
            ? snap.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
            : []);
        const sort = sortArr.length > 0 ? sortArr.join(',') : (snap?.sorting_barcode || '');
        const qty = Number(t?.expected_qty || 0);
        const key = `${code}|${sort}|${qty}`;
        if (!code || !sort || seen.has(key)) return;
        seen.add(key);
        const parts = String(sort || '').split(',').map((s) => s.trim()).filter(Boolean);
        if (parts.length > 0) {
          const joined = parts.join(',');
          const qtyText = `${Number.isFinite(qty) ? qty : 0}`;
          if (parts.length === 1) {
            lines.push(`${joined}×${qtyText}`);
          } else {
            lines.push(`(${joined})×${qtyText}`);
          }
        }
      });
      return lines;
    } catch {
      return [];
    }
  }, []);
  const getUnitStats = useMemo(() => (row: any) => {
    try {
      const targets = Array.isArray(row?.targets_detail) ? row.targets_detail : [];
      const byCode = new Set<string>();
      let totalQty = 0;
      targets.forEach((t: any) => {
        const snap = t?.snapshot || {};
        const code = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
        const qty = Number(t?.expected_qty || 0);
        if (code) byCode.add(code);
        if (Number.isFinite(qty)) totalQty += qty;
      });
      return `${byCode.size || 0}款${totalQty || 0}件`;
    } catch {
      return '-';
    }
  }, []);
  const getInstructionType = useMemo(() => (row: any) => {
    try {
      const targets = Array.isArray(row?.targets_detail) ? row.targets_detail : [];
      const byCode = new Map<string, number>();
      let totalQty = 0;
      targets.forEach((t: any) => {
        const snap = t?.snapshot || {};
        const code = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
        const qty = Number(t?.expected_qty || 0);
        if (!code) return;
        if (Number.isFinite(qty)) totalQty += qty;
        byCode.set(code, (byCode.get(code) || 0) + (Number.isFinite(qty) ? qty : 0));
      });
      const kindCount = byCode.size;
      if (kindCount <= 0) return '-';
      if (kindCount === 1) {
        return totalQty <= 1 ? '单款单件' : '单款多件';
      }
      return '多款多件';
    } catch {
      return '-';
    }
  }, []);
  const getWaybillStatus = useMemo(() => (row: any) => {
    try {
      const source = String(row?.waybill_source || '').trim().toLowerCase();
      const mode = String(row?.waybill_submit_mode || '').trim().toLowerCase();
      if (source === 'upload' && mode === 'immediate') return '有运单';
      if (source === 'warehouse') return '仓库提供运单';
      if (source === 'upload' && mode === 'after_pack') return '先申请包装数据';
      return '-';
    } catch {
      return '-';
    }
  }, []);
  const carrierOptions = useMemo(() => {
    const set = new Set<string>();
    (items || []).forEach((r: any) => {
      const v = String(r?.waybill_carrier || '').trim();
      if (!v || v.toLowerCase() === 'none') return;
      set.add(v);
    });
    const list = Array.from(set).sort((a, b) => normalizeText(a).localeCompare(normalizeText(b)));
    return [{ label: '无承运商', value: '__no_carrier__' }, ...list.map((v) => ({ label: v, value: v }))];
  }, [items]);
  const getAttachmentSearchText = useMemo(() => (row: any) => {
    try {
      const list = Array.isArray(row?.attachments_detail) ? row.attachments_detail : [];
      const parts: string[] = [];
      list.forEach((a: any) => {
        const txt = a?.parse_text || a?.ocr_text || a?.text || '';
        if (txt) parts.push(String(txt));
      });
      return parts.join(' ');
    } catch {
      return '';
    }
  }, []);
  const getSortingBarcodeSearchText = useMemo(() => (row: any) => {
    try {
      const targets = Array.isArray(row?.targets_detail) ? row.targets_detail : [];
      const parts: string[] = [];
      targets.forEach((t: any) => {
        const snap = t?.snapshot || {};
        const sortArr = Array.isArray(t?.sorting_barcodes)
          ? t.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
          : (Array.isArray(snap?.sorting_barcodes)
            ? snap.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
            : []);
        if (sortArr.length) {
          sortArr.forEach((s: any) => parts.push(String(s)));
        } else if (snap?.sorting_barcode) {
          parts.push(String(snap.sorting_barcode));
        }
      });
      return parts.join(' ');
    } catch {
      return '';
    }
  }, []);

  const resolveAttachmentUrl = useMemo(() => (att: any): string | undefined => {
    try {
      const raw = att?.file || att?.file_path || att?.file_name || '';
      if (!raw) return undefined;
      const isAbs = /^https?:\/\//i.test(raw);
      let backendOrigin = window.location.origin;
      try {
        const u = new URL(backendOrigin);
        if (u.port === '3000') { u.port = '8000'; backendOrigin = u.toString().replace(/\/$/, ''); }
      } catch {}
      try {
        const anyReq: any = request as any;
        const baseURL = anyReq?.defaults?.baseURL || anyReq?.getUri?.();
        if (baseURL && /^https?:\/\//i.test(baseURL)) {
          const b = new URL(baseURL);
          backendOrigin = `${b.protocol}//${b.host}`;
        }
      } catch {}
      let path = raw;
      if (!isAbs) {
        if (!path.startsWith('/')) path = '/' + path;
        if (/^\/(instruction|attachments?)/i.test(path)) path = '/media' + path; else if (!/^\/media\//i.test(path)) path = '/media' + path;
      }
      return isAbs ? raw : `${backendOrigin}${path}`;
    } catch {
      return undefined;
    }
  }, []);
  const getExtLower = useMemo(() => (att: any): string => {
    try {
      const raw = String(att?.file_name || att?.file || att?.file_path || '');
      const name = raw.split('?')[0].split('#')[0];
      const base = name.split('/').pop() || name;
      const ext = base.includes('.') ? base.split('.').pop() || '' : '';
      return String(ext).trim().toLowerCase();
    } catch {
      return '';
    }
  }, []);
  const getDisplayName = useMemo(() => (att: any): string => {
    return String(att?.file_name || att?.file || att?.file_path || att?.label || '').trim();
  }, []);

  const getWaybillAttachments = useMemo(() => (instr: any): any[] => {
    const list = Array.isArray(instr?.attachments_detail) ? instr.attachments_detail : [];
    return list.filter((a: any) => {
      const label = String(a?.label || '').trim();
      return label === '物流运单';
    });
  }, []);

  const hasPrintableAttachments = useCallback((instr: any): boolean => {
    const list = Array.isArray(instr?.attachments_detail) ? instr.attachments_detail : [];
    for (const a of list) {
      const m = a?.meta;
      const parsed = typeof m === 'string' ? (() => { try { return JSON.parse(m); } catch { return {}; } })() : (m || {});
      const cfg = parsed?.print_config;
      if (cfg?.print === true || Number(cfg?.copies ?? 0) > 0) return true;
    }
    return false;
  }, []);

  const handleMergedWaybillClose = useCallback(() => {
    const batchInstrs = mergedWaybillBatchInstrsRef.current;
    mergedWaybillBatchInstrsRef.current = [];
    setMergedWaybillPdfOpen(false);
    setMergedWaybillPdfUrl('');
    setMergedWaybillAutoPrint(false);
    const shouldRestoreWaybillBatch = mergedPreviewSource === 'waybill';
    setMergedPreviewSource('');
    if (mergedWaybillPdfUrlRef.current) {
      try { URL.revokeObjectURL(mergedWaybillPdfUrlRef.current); } catch {}
      mergedWaybillPdfUrlRef.current = null;
    }
    if (shouldRestoreWaybillBatch && Array.isArray(batchInstrs) && batchInstrs.length > 0 && operateConfirmOpen) {
      setOperateConfirmCurrentBatchInstrs(batchInstrs);
      setOperateConfirmBatchFetched(true);
      setOperateConfirmPhase('batch_active');
      setOperateConfirmInstrPage(1);
      const hasPrintable = batchInstrs.some((i: any) => hasPrintableAttachments(i));
      if (hasPrintable) {
        setTimeout(() => {
          Modal.warning({ title: '打印提醒', content: '本波次部分指令需打印其他附件，请先打印附件再执行后续操作' });
        }, 100);
      }
    }
  }, [mergedPreviewSource, operateConfirmOpen, hasPrintableAttachments]);

  const getInstructionSequence = useMemo(() => (instr: any): number => {
    const code = String(instr?.instruction_system_code ?? instr?.instruction?.instruction_system_code ?? '').trim();
    const lastDash = code.lastIndexOf('-');
    if (lastDash < 0) return 0;
    const numStr = code.slice(lastDash + 1).replace(/\D/g, '');
    const n = parseInt(numStr, 10);
    return Number.isFinite(n) ? n : 0;
  }, []);

  const PAPER_LABELS: Record<string, string> = { '4x6_thermal': '4×6英寸热敏纸打印', 'A4': 'A4激光打印' };
  const PLACEMENT_LABELS: Record<string, string> = { 'inside': '放入包裹内', 'outside': '贴在包裹外' };
  const getAttachmentPrintConfig = useCallback((att: any): any => {
    const m = att?.meta;
    const parsed = typeof m === 'string' ? (() => { try { return JSON.parse(m); } catch { return {}; } })() : (m || {});
    return parsed?.print_config || {};
  }, []);
  const isAttachmentNeedPrint = useCallback((att: any): boolean => {
    const cfg = getAttachmentPrintConfig(att);
    return cfg?.print === true || Number(cfg?.copies ?? 0) > 0;
  }, [getAttachmentPrintConfig]);
  const getAttachmentPrintNeedCount = useCallback((atts: any[]): number => {
    const list = Array.isArray(atts) ? atts : [];
    let cnt = 0;
    for (const a of list) {
      if (isAttachmentNeedPrint(a)) cnt += 1;
    }
    return cnt;
  }, [isAttachmentNeedPrint]);
  const formatPrintConfigForTitle = useCallback((att: any): string => {
    const cfg = getAttachmentPrintConfig(att);
    const copies = Number(cfg?.copies ?? 0);
    if (!(cfg?.print === true || copies > 0)) return '';
    const parts: string[] = [`${copies}份`];
    if (cfg?.paper_size) parts.push(PAPER_LABELS[cfg.paper_size] || cfg.paper_size);
    if (cfg?.placement) parts.push(PLACEMENT_LABELS[cfg.placement] || cfg.placement);
    return parts.length > 0 ? parts.join(' · ') : '';
  }, [getAttachmentPrintConfig]);

  const closeAttPreviewModal = useCallback(() => {
    setAttPreviewOpen(false);
    setAttPreviewTitlePrintSuffix('');
    setAttPreviewUrl('');
    setAttPreviewText('');
    attPreviewXlsxWbRef.current = null;
    attPreviewXlsxLibRef.current = null;
    attPreviewXlsxCacheRef.current = {};
    setAttPreviewXlsxSheets([]);
    setAttPreviewXlsxActiveSheet('');
    setAttPreviewXlsxCols([]);
    setAttPreviewXlsxData([]);
    if (attPreviewObjectUrlRef.current) {
      try { URL.revokeObjectURL(attPreviewObjectUrlRef.current); } catch {}
      attPreviewObjectUrlRef.current = null;
    }
  }, []);

  const handleAttPreviewPrint = useCallback(() => {
    if (!attPreviewUrl) return;
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = attPreviewUrl;
    document.body.appendChild(iframe);
    const cleanup = () => {
      try { document.body.removeChild(iframe); } catch {}
    };
    iframe.onload = () => {
      const win = iframe.contentWindow;
      if (win) {
        win.addEventListener('afterprint', () => {
          closeAttPreviewModal();
          cleanup();
        }, { once: true });
        try { win.print(); } catch {}
        setTimeout(cleanup, 2000);
      } else {
        cleanup();
      }
    };
    // 点击“打印”后立即关闭预览，回到直接操作主弹窗
    closeAttPreviewModal();
  }, [attPreviewUrl, closeAttPreviewModal]);

  const openAttachmentPreview = React.useCallback(async (att: any) => {
    const url = resolveAttachmentUrl(att);
    if (!url) { message.warning('无可用文件'); return; }
    const extLower = getExtLower(att);
    const isImg = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extLower);
    const isPdf = extLower === 'pdf';
    const isExcel = ['xlsx', 'xls', 'xlsm', 'xlsb'].includes(extLower);
    const isText = ['txt', 'csv', 'json', 'xml', 'log', 'md', 'text'].includes(extLower);
    const baseName = getDisplayName(att) || '附件预览';
    const printSuffix = formatPrintConfigForTitle(att);
    if (attPreviewObjectUrlRef.current) {
      try { URL.revokeObjectURL(attPreviewObjectUrlRef.current); } catch {}
      attPreviewObjectUrlRef.current = null;
    }
    attPreviewXlsxWbRef.current = null;
    attPreviewXlsxLibRef.current = null;
    attPreviewXlsxCacheRef.current = {};
    setAttPreviewXlsxSheets([]);
    setAttPreviewXlsxActiveSheet('');
    setAttPreviewXlsxCols([]);
    setAttPreviewXlsxData([]);
    if (!isImg && !isPdf && !isExcel && !isText) {
      window.open(url, '_blank');
      return;
    }
    setAttPreviewLoading(true);
    setAttPreviewTitle(baseName);
    setAttPreviewTitlePrintSuffix(printSuffix);
    setAttPreviewOpen(true);
    try {
      const resp = await fetch(url, { credentials: 'include' });
      const blob = await resp.blob();
      if (isText) {
        const text = await blob.text();
        setAttPreviewKind('text');
        setAttPreviewText(text);
        setAttPreviewUrl('');
      } else if (isExcel) {
        let XLSX: any = null;
        try { XLSX = await import('xlsx'); } catch {}
        if (!XLSX) {
          message.error('缺少 Excel 解析库（xlsx）');
          setAttPreviewOpen(false);
          return;
        }
        const buf = await blob.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const sheetNames = Array.isArray(wb?.SheetNames) ? (wb.SheetNames as string[]) : [];
        const sheetName = String(sheetNames?.[0] || '').trim();
        const sh = sheetName ? wb.Sheets?.[sheetName] : null;
        if (!sheetName || !sh) {
          message.error('未找到工作表');
          setAttPreviewOpen(false);
          return;
        }
        attPreviewXlsxWbRef.current = wb;
        attPreviewXlsxLibRef.current = XLSX;
        attPreviewXlsxCacheRef.current = {};
        setAttPreviewXlsxSheets(sheetNames);
        setAttPreviewXlsxActiveSheet(sheetName);
        const aoa: any[] = XLSX.utils.sheet_to_json(sh, { header: 1, defval: '', blankrows: false });
        const arr = Array.isArray(aoa) ? aoa.filter((r: any) => Array.isArray(r) && r.length > 0) : [];
        const headers = (Array.isArray(arr[0]) ? arr[0] : []) as any[];
        const dataRows = arr.slice(1);
        const cols = headers.map((h: any, i: number) => ({
          title: String(h || `列${i + 1}`),
          dataIndex: `c${i}`,
          align: 'center',
          width: undefined,
        }));
        const data = dataRows.map((row: any[], idx: number) => {
          const obj: any = { __idx: idx + 1 };
          cols.forEach((c: any, i: number) => { obj[c.dataIndex] = String(row?.[i] ?? ''); });
          return obj;
        });
        attPreviewXlsxCacheRef.current[sheetName] = { cols, data };
        setAttPreviewXlsxCols(cols);
        setAttPreviewXlsxData(data);
        setAttPreviewKind('xlsx');
        setAttPreviewUrl('');
        setAttPreviewText('');
      } else {
        const objUrl = URL.createObjectURL(blob);
        attPreviewObjectUrlRef.current = objUrl;
        setAttPreviewUrl(objUrl);
        setAttPreviewText('');
        setAttPreviewKind(isImg ? 'image' : isPdf ? 'pdf' : 'other');
      }
    } catch (e: any) {
      message.error(e?.message || '预览失败');
      setAttPreviewOpen(false);
    } finally {
      setAttPreviewLoading(false);
    }
  }, [formatPrintConfigForTitle, getDisplayName, getExtLower, resolveAttachmentUrl]);
  const previewBuyWaybillUploadFile = useCallback(async (file: any) => {
    const rawFile = file?.originFileObj;
    if (!rawFile) {
      const fileUrl = String(file?.url || '').trim();
      if (!fileUrl) {
        message.warning('无可预览文件');
        return;
      }
      await openAttachmentPreview({
        file_path: fileUrl,
        file_name: String(file?.name || '运单附件').trim() || '运单附件',
        label: '物流运单',
      });
      return;
    }
    const fileName = String(file?.name || rawFile?.name || '运单附件').trim() || '运单附件';
    const ext = String(fileName.split('.').pop() || '').toLowerCase();
    const mime = String(rawFile?.type || '').toLowerCase();
    const isImg = mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
    const isPdf = mime === 'application/pdf' || ext === 'pdf';
    if (!isImg && !isPdf) {
      message.warning('当前文件类型暂不支持弹窗预览');
      return;
    }
    if (attPreviewObjectUrlRef.current) {
      try { URL.revokeObjectURL(attPreviewObjectUrlRef.current); } catch {}
      attPreviewObjectUrlRef.current = null;
    }
    const objUrl = URL.createObjectURL(rawFile);
    attPreviewObjectUrlRef.current = objUrl;
    setAttPreviewTitle(fileName);
    setAttPreviewTitlePrintSuffix('');
    setAttPreviewKind(isImg ? 'image' : 'pdf');
    setAttPreviewUrl(objUrl);
    setAttPreviewText('');
    setAttPreviewLoading(false);
    setAttPreviewOpen(true);
  }, [openAttachmentPreview]);

  const getInstructionCodeSuffix = useCallback((instr: any): string => {
    const code = String(instr?.instruction_system_code ?? instr?.instruction?.instruction_system_code ?? instr?.system_code ?? '').trim();
    if (!code) return '';
    const parts = code.split('-');
    return parts.length >= 2 ? parts.slice(-2).join('-') : code;
  }, []);

  const isWaybillAttachment = useCallback((att: any): boolean => {
    const label = String(att?.label || '').trim();
    return label === '物流运单';
  }, []);
  const getDirectOperatePackDataKey = useCallback((instr: any): string => (
    String(instr?.instruction ?? instr?.instruction_id ?? instr?.id ?? instr?.instruction_system_code ?? instr?.instruction?.instruction_system_code ?? '').trim()
  ), []);
  const formatLbOzFromG = useCallback((gram: number): string => {
    const g = Number(gram || 0);
    if (!Number.isFinite(g) || g <= 0) return '-';
    const totalOz = g / 28.349523125;
    const lb = Math.floor(totalOz / 16);
    const oz = totalOz - lb * 16;
    return `${lb} lb ${oz.toFixed(2)} oz`;
  }, []);

  /** 其他附件（非运单）标识：[编码后缀] 份数-In/Out */
  const buildPrintOverlayText = useCallback((instr: any, att: any): string => {
    const suffix = getInstructionCodeSuffix(instr);
    const m = att?.meta;
    const parsed = typeof m === 'string' ? (() => { try { return JSON.parse(m); } catch { return {}; } })() : (m || {});
    const cfg = parsed?.print_config || {};
    const copies = Math.max(1, Number(cfg?.copies ?? 0));
    const p = String(cfg?.placement ?? '').toLowerCase();
    const placementLabel = p === 'outside' ? 'Out' : 'In';
    return `[${suffix || '-'}] ${copies}-${placementLabel}`;
  }, [getInstructionCodeSuffix]);

  /** 运单附件标识：[指令编码][指令类型]-产品信息 */
  const buildWaybillOverlayText = useCallback((instr: any): string => {
    const fullCode = String(instr?.instruction_system_code ?? instr?.instruction?.instruction_system_code ?? instr?.system_code ?? '').trim();
    const parts = fullCode.split('-');
    const codeSuffix = parts.length >= 1 ? (parts[parts.length - 1] || '-') : (fullCode || '-');

    const targets = Array.isArray(instr?.targets_detail) ? instr.targets_detail : [];
    const iuTargets = targets.filter((t: any) => String(t?.target_type || '').trim() === 'inventory_unit');
    if (iuTargets.length === 0) return `[${codeSuffix}][?-0]`;

    const byIu = new Map<string, number>();
    let totalQty = 0;
    for (const t of iuTargets) {
      const iuKey = String(t?.inventory_unit_id ?? t?.inventory_unit_system_barcode ?? t?.snapshot?.system_barcode ?? '').trim() || `_${t?.id ?? ''}`;
      const qty = Number(t?.expected_qty ?? t?.snapshot?.expected_qty ?? 1);
      byIu.set(iuKey, (byIu.get(iuKey) || 0) + (Number.isFinite(qty) ? qty : 1));
      totalQty += Number.isFinite(qty) ? qty : 1;
    }
    const skuCount = byIu.size;
    const typeChar = skuCount <= 1 ? 'S' : 'M';
    const typeStr = `${typeChar}${totalQty}`;

    const seenIu = new Set<string>();
    const iuOrder: Array<{ target: any; qty: number }> = [];
    for (const t of iuTargets) {
      const iuKey = String(t?.inventory_unit_id ?? t?.inventory_unit_system_barcode ?? t?.snapshot?.system_barcode ?? '').trim() || `_${t?.id ?? ''}`;
      if (seenIu.has(iuKey)) continue;
      seenIu.add(iuKey);
      const qty = byIu.get(iuKey) || 0;
      iuOrder.push({ target: t, qty });
    }
    const getProductPart = (t: any, qty: number): string => {
      const snap = t?.snapshot || {};
      const sortArr = Array.isArray(t?.sorting_barcodes)
        ? t.sorting_barcodes.map((x: any) => (typeof x === 'object' ? x?.code : x) ?? x).filter((x: any) => x != null && String(x).trim())
        : (Array.isArray(snap?.sorting_barcodes)
          ? snap.sorting_barcodes.map((x: any) => (typeof x === 'object' ? x?.code : x) ?? x).filter((x: any) => x != null && String(x).trim())
          : []);
      const sortCode = sortArr.length > 0 ? String(sortArr[0]).trim() : String(snap?.sorting_barcode ?? '').trim();
      const last4 = sortCode.length >= 4 ? sortCode.slice(-4).toUpperCase() : sortCode.toUpperCase();
      return last4 ? `${last4}×${qty}` : `×${qty}`;
    };
    const productParts = iuOrder.slice(0, 2).map(({ target, qty }) => getProductPart(target, qty));
    const productStr = productParts.join('/');
    const suffix = skuCount > 2 ? '...' : '';

    return `[${codeSuffix}][${typeStr}]-${productStr}${suffix}`;
  }, []);

  /** 运单附件标识（精简版，仅指令编码+类型，用于找不到空白区域时重试） */
  const buildWaybillOverlayTextShort = useCallback((instr: any): string => {
    const fullCode = String(instr?.instruction_system_code ?? instr?.instruction?.instruction_system_code ?? instr?.system_code ?? '').trim();
    const parts = fullCode.split('-');
    const codeSuffix = parts.length >= 1 ? (parts[parts.length - 1] || '-') : (fullCode || '-');
    const targets = Array.isArray(instr?.targets_detail) ? instr.targets_detail : [];
    const iuTargets = targets.filter((t: any) => String(t?.target_type || '').trim() === 'inventory_unit');
    if (iuTargets.length === 0) return `[${codeSuffix}][?-0]`;
    const byIu = new Map<string, number>();
    let totalQty = 0;
    for (const t of iuTargets) {
      const iuKey = String(t?.inventory_unit_id ?? t?.inventory_unit_system_barcode ?? t?.snapshot?.system_barcode ?? '').trim() || `_${t?.id ?? ''}`;
      const qty = Number(t?.expected_qty ?? t?.snapshot?.expected_qty ?? 1);
      byIu.set(iuKey, (byIu.get(iuKey) || 0) + (Number.isFinite(qty) ? qty : 1));
      totalQty += Number.isFinite(qty) ? qty : 1;
    }
    const skuCount = byIu.size;
    const typeChar = skuCount <= 1 ? 'S' : 'M';
    const typeStr = `${typeChar}${totalQty}`;
    return `[${codeSuffix}][${typeStr}]`;
  }, []);

  const OVERLAY_CONFIG_OTHER: Record<string, any> = {
    layout_mode: 'sliding_window',
    content_source: 'custom_text',
    show_title: false,
    font_size: 50,
    font_bold: true,
    text_color: '#c41e3a',
    max_text_length: 16,
    max_label_area: 50000,
    blank_ratio_threshold: 0.98,
    blank_ratio_step: 2,
    bottom_band_ratio: 0.25,
    side_band_ratio: 0.2,
    slide_step: 30,
    margin: 24,
  };

  const OVERLAY_CONFIG_WAYBILL: Record<string, any> = {
    layout_mode: 'sliding_window',
    content_source: 'custom_text',
    show_title: false,
    font_size: 42,
    font_bold: true,
    text_color: '#c41e3a',
    max_text_length: 50,
    max_label_area: 80000,
    blank_ratio_threshold: 0.98,
    blank_ratio_step: 2,
    bottom_band_ratio: 0.25,
    side_band_ratio: 0.2,
    slide_step: 30,
    margin: 24,
  };

  const applyWaybillOverlayToBlob = useCallback(async (blob: Blob, ext: string, instr: any): Promise<Blob> => {
    const overlayText = buildWaybillOverlayText(instr);
    const configObj: Record<string, any> = {
      ...OVERLAY_CONFIG_WAYBILL,
      custom_text: overlayText,
      custom_text_fallback: buildWaybillOverlayTextShort(instr),
    };
    const base64 = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const s = (r.result as string) || '';
        resolve(s.includes(',') ? s.split(',')[1]! : s);
      };
      r.onerror = () => reject(new Error('文件读取失败'));
      r.readAsDataURL(blob);
    });
    const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(/\/$/, '');
    const overlayUrl = `${apiBase}/api/v1/logistics/label-overlay/`;
    const token = localStorage.getItem('token');
    const resp = await fetch(overlayUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        file_base64: base64,
        file_name: `attachment.${ext === 'pdf' ? 'pdf' : 'png'}`,
        config: configObj,
      }),
    });
    if (!resp.ok) {
      const errText = await resp.text();
      try {
        const errJson = JSON.parse(errText);
        throw new Error(errJson?.error || errJson?.detail || resp.statusText);
      } catch (e: any) {
        if (e?.message && e.message !== resp.statusText) throw e;
        throw new Error(errText || resp.statusText);
      }
    }
    return resp.blob();
  }, [buildWaybillOverlayText, buildWaybillOverlayTextShort]);

  const mergeWaybillAttachmentsToPdf = useCallback(
    async (items: { att: any; instr: any }[]): Promise<{ blob: Blob; pageCount: number; instructionPageCounts: Record<string, number> } | null> => {
      if (!items.length) return null;
      try {
        const pdfDoc = await PDFDocument.create();
        const instructionPageCounts: Record<string, number> = {};
        for (const { att, instr } of items) {
          const url = resolveAttachmentUrl(att);
          if (!url) continue;
          const instrCode = getInstructionSystemCode(instr) || '-';
          const resp = await fetch(url, { credentials: 'include' });
          if (!resp.ok) continue;
          let buf: ArrayBuffer;
          let ext = getExtLower(att);
          let blob = await resp.blob();
          if (instr && isWaybillAttachment(att)) {
            try {
              blob = await applyWaybillOverlayToBlob(blob, ext, instr);
              ext = blob.type?.includes('pdf') ? 'pdf' : 'png';
            } catch (e) {
              console.warn('[mergeWaybillPdf] overlay failed, use original:', e);
            }
          }
          buf = await blob.arrayBuffer();
          if (ext === 'pdf') {
            const srcDoc = await PDFDocument.load(buf);
            const srcPageCount = srcDoc.getPageCount();
            if (srcPageCount > 0) {
              const indices = Array.from({ length: srcPageCount }, (_, i) => i);
              const copiedPages = await pdfDoc.copyPages(srcDoc, indices);
              copiedPages.forEach((p) => pdfDoc.addPage(p));
              instructionPageCounts[instrCode] = (instructionPageCounts[instrCode] || 0) + srcPageCount;
            }
          } else if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext)) {
            const bytes = new Uint8Array(buf);
            const tryPng = ['png', 'gif', 'webp', 'bmp'].includes(ext);
            let img: any;
            try {
              img = tryPng ? await pdfDoc.embedPng(bytes) : await pdfDoc.embedJpg(bytes);
            } catch {
              try {
                img = await pdfDoc.embedJpg(bytes);
              } catch {
                try {
                  img = await pdfDoc.embedPng(bytes);
                } catch {}
              }
            }
            if (img) {
              const w = img.width;
              const h = img.height;
              const page = pdfDoc.addPage([w, h]);
              (page.drawImage as (img: any, opts: any) => void)(img, { x: 0, y: 0, width: w, height: h });
              instructionPageCounts[instrCode] = (instructionPageCounts[instrCode] || 0) + 1;
            }
          }
        }
        const pageCount = pdfDoc.getPageCount();
        if (pageCount === 0) return null;
        const mergedBytes = await pdfDoc.save();
        const outBlob = new Blob([mergedBytes as BlobPart], { type: 'application/pdf' });
        return { blob: outBlob, pageCount, instructionPageCounts };
      } catch (e) {
        console.error('[mergeWaybillPdf]', e);
        return null;
      }
    },
    [applyWaybillOverlayToBlob, getExtLower, getInstructionSystemCode, isWaybillAttachment, resolveAttachmentUrl],
  );

  const mergePdfBlobs = useCallback(async (blobs: Blob[]): Promise<Blob | null> => {
    const valid = (Array.isArray(blobs) ? blobs : []).filter((b) => b && Number(b.size || 0) > 0);
    if (valid.length === 0) return null;
    try {
      const pdfDoc = await PDFDocument.create();
      for (const blob of valid) {
        const mime = String((blob as any)?.type || '').toLowerCase();
        const buf = await blob.arrayBuffer();
        if (mime.includes('pdf')) {
          const srcDoc = await PDFDocument.load(buf);
          const pageCount = srcDoc.getPageCount();
          if (pageCount > 0) {
            const indices = Array.from({ length: pageCount }, (_, i) => i);
            const copiedPages = await pdfDoc.copyPages(srcDoc, indices);
            copiedPages.forEach((p) => pdfDoc.addPage(p));
          }
          continue;
        }
        const bytes = new Uint8Array(buf);
        let img: any = null;
        try {
          img = await pdfDoc.embedPng(bytes);
        } catch {
          try {
            img = await pdfDoc.embedJpg(bytes);
          } catch {
            img = null;
          }
        }
        if (img) {
          const page = pdfDoc.addPage([img.width, img.height]);
          (page.drawImage as (i: any, opts: any) => void)(img, { x: 0, y: 0, width: img.width, height: img.height });
        }
      }
      if (pdfDoc.getPageCount() <= 0) return null;
      const mergedBytes = await pdfDoc.save();
      return new Blob([mergedBytes as BlobPart], { type: 'application/pdf' });
    } catch (e) {
      console.error('[mergePdfBlobs]', e);
      return null;
    }
  }, []);

  const createInstructionOutputContainersForBatchWave = useCallback(async (args: {
    packagingKey: string;
    packData: { lengthIn: number; widthIn: number; heightIn: number; weightLb: number } | null;
    instructions: any[];
    rowContext?: any;
  }) => {
    const packagingKey = String(args?.packagingKey || '').trim();
    const packData = args?.packData || null;
    const instructionsRaw = Array.isArray(args?.instructions) ? args.instructions : [];
    const instructions = instructionsRaw.filter((instr: any) => isDoneInOperateConfirmDraft(instr));
    const rowContext = args?.rowContext || null;
    if (!packagingKey) {
      message.warning('缺少包装方式');
      return;
    }
    if (!packData) {
      message.warning('请先输入包装数据');
      return;
    }
    if (instructions.length === 0) {
      message.warning('当前波次无“已完成”指令可创建产出容器');
      return;
    }
    setOperateConfirmCreateIocLoadingByKey((prev) => ({ ...prev, [packagingKey]: true }));
    const successRows: Array<{ instructionId: number; instructionCode: string; iocCode: string; itemKinds: number; totalQty: number; updated: boolean }> = [];
    const failRows: Array<{ instructionCode: string; reason: string }> = [];
    const labelBlobs: Blob[] = [];
    const patchPayload = {
      actual_pack_length_cm: Math.round(Number(packData.lengthIn || 0)),
      actual_pack_width_cm: Math.round(Number(packData.widthIn || 0)),
      actual_pack_height_cm: Math.round(Number(packData.heightIn || 0)),
      actual_gross_weight_g: Math.round(Number(packData.weightLb || 0)),
      pack_result_status: 'provided',
    };
    try {
      for (const instr of instructions) {
        const instructionId = Number(instr?.instruction ?? instr?.instruction_id ?? instr?.id ?? 0);
        const instructionCode = getInstructionSystemCode(instr);
        if (!Number.isFinite(instructionId) || instructionId <= 0) {
          failRows.push({ instructionCode: instructionCode || '-', reason: '缺少有效 instruction_id' });
          continue;
        }
        const qtyByIu: Record<number, number> = {};
        const iuCodeById: Record<number, string> = {};
        const targets = Array.isArray(instr?.targets_detail) ? instr.targets_detail : [];
        for (const t of targets) {
          const iuId = Number(t?.target?.inventory_unit_id ?? t?.inventory_unit_id ?? 0);
          const qty = Math.floor(Number(t?.expected_qty ?? t?.target_qty ?? 0) || 0);
          if (!Number.isFinite(iuId) || iuId <= 0 || !Number.isFinite(qty) || qty <= 0) continue;
          qtyByIu[iuId] = Number(qtyByIu[iuId] || 0) + qty;
          const iuCode = String(
            t?.snapshot?.system_barcode
            ?? t?.inventory_unit_system_barcode
            ?? t?.target?.inventory_unit_system_barcode
            ?? t?.fingerprint
            ?? ''
          ).trim();
          if (iuCode) iuCodeById[iuId] = iuCode;
        }
        if (Object.keys(qtyByIu).length === 0) {
          const fallbackIuId = Number(instr?.inventory_unit_id ?? rowContext?.inventory_unit_id ?? 0);
          const fallbackQty = Math.floor(Number(instr?.target_qty ?? rowContext?.perOrderQty ?? 0) || 0);
          if (fallbackIuId > 0 && fallbackQty > 0) {
            qtyByIu[fallbackIuId] = fallbackQty;
            const fallbackCode = String(instr?.inventory_unit_system_barcode ?? rowContext?.iuCode ?? '').trim();
            if (fallbackCode) iuCodeById[fallbackIuId] = fallbackCode;
          }
        }
        const items = Object.entries(qtyByIu).map(([iuId, qty]) => ({
          inventory_unit_id: Number(iuId),
          quantity: Number(qty),
        })).filter((x) => Number.isFinite(x.inventory_unit_id) && x.inventory_unit_id > 0 && Number.isFinite(x.quantity) && x.quantity > 0);
        if (items.length === 0) {
          failRows.push({ instructionCode, reason: '缺少可写入 IOC 的库存单元与数量' });
          continue;
        }
        try {
          let iocCode = String(
            operateConfirmIocCodeByInstrId[String(instructionId)]
            || instr?.created_ioc_code
            || instr?.instruction?.created_ioc_code
            || '',
          ).trim();
          const updatingExisting = !!iocCode;
          if (!iocCode) {
            const reserveResp = await request.post('/api/v1/storage/output-containers/reserve-code/', {
              instruction_id: instructionId,
              container_type: 'box',
            });
            iocCode = String((reserveResp as any)?.data?.code || '').trim();
            if (!iocCode) throw new Error('预生成 IOC 编码失败');
          }
          const finalizePayload: any = {
            instruction_id: instructionId,
            code: iocCode,
            container_type: 'box',
            status: 'closed',
            length_cm: patchPayload.actual_pack_length_cm,
            width_cm: patchPayload.actual_pack_width_cm,
            height_cm: patchPayload.actual_pack_height_cm,
            weight_g: patchPayload.actual_gross_weight_g,
            defer_inventory_settlement: true,
          };
          if (!updatingExisting) finalizePayload.items = items;
          await request.post('/api/v1/storage/output-containers/finalize/', finalizePayload);
          setOperateConfirmIocCodeByInstrId((prev) => ({ ...prev, [String(instructionId)]: iocCode }));
          try {
            await request.patch(`/api/v1/instruction/instructions/${instructionId}/`, patchPayload);
            applyLocalActualPackResultPatch(instructionId, patchPayload);
          } catch (e: any) {
            message.warning(`${instructionCode} 已创建IOC，但回写包装结果失败`);
          }
          const totalQty = items.reduce((sum, it) => sum + Number(it.quantity || 0), 0);
          successRows.push({
            instructionId,
            instructionCode,
            iocCode,
            itemKinds: items.length,
            totalQty,
            updated: updatingExisting,
          });
          try {
            const labelPayload: any = {
              instruction_id: instructionId,
              container_code: iocCode,
              instruction_code: instructionCode,
              project_summary: String(instr?.project_name ?? rowContext?.project ?? '-').trim() || '-',
              container_type: 'box',
              container_type_display: '纸箱',
              created_at: new Date().toLocaleString(),
              dims_cm: {
                l: patchPayload.actual_pack_length_cm,
                w: patchPayload.actual_pack_width_cm,
                h: patchPayload.actual_pack_height_cm,
              },
              dims_in: {
                l: Number((Number(patchPayload.actual_pack_length_cm || 0) / 2.54).toFixed(2)),
                w: Number((Number(patchPayload.actual_pack_width_cm || 0) / 2.54).toFixed(2)),
                h: Number((Number(patchPayload.actual_pack_height_cm || 0) / 2.54).toFixed(2)),
              },
              weight: {
                kg: Number((Number(patchPayload.actual_gross_weight_g || 0) / 1000).toFixed(3)),
                lb: Number((Number(patchPayload.actual_gross_weight_g || 0) / 453.59237).toFixed(3)),
              },
              new_kinds: items.length,
              total_qty: totalQty,
              details: items.slice(0, 20).map((it) => ({
                new_code: String(iuCodeById[it.inventory_unit_id] || it.inventory_unit_id),
                qty: Number(it.quantity || 0),
              })),
            };
            const labelResp = await request.post('/api/v1/storage/output-containers/labels/preview/', labelPayload, { responseType: 'blob' as any });
            const labelBlob = (labelResp as any)?.data as Blob;
            if (labelBlob && Number(labelBlob.size || 0) > 0) labelBlobs.push(labelBlob);
          } catch (e) {
            console.warn('[Batch.create_ioc] label preview failed', e);
          }
        } catch (e: any) {
          failRows.push({
            instructionCode,
            reason: String(e?.response?.data?.detail || e?.message || '创建 IOC 失败'),
          });
        }
      }
      if (successRows.length > 0) {
        appendOperationRecord(
          successRows.map((r) => ({
            system_code: r.instructionCode,
            status: r.updated ? '已更新IOC' : '已创建IOC',
            error_reason: `IOC:${r.iocCode}`,
          })),
          '波次操作',
        );
      }
      if (labelBlobs.length > 0) {
        mergedWaybillBatchInstrsRef.current = [];
        setMergedWaybillAutoPrint(false);
        setMergedPreviewTitle('合并IOC标签预览');
        setMergedPreviewSource('ioc');
        if (mergedWaybillPdfUrlRef.current) {
          try { URL.revokeObjectURL(mergedWaybillPdfUrlRef.current); } catch {}
          mergedWaybillPdfUrlRef.current = null;
        }
        setMergedWaybillPdfUrl('');
        setMergedWaybillPdfLoading(true);
        setMergedWaybillPdfOpen(true);
        try {
          const mergedBlob = await mergePdfBlobs(labelBlobs);
          if (!mergedBlob || Number(mergedBlob.size || 0) <= 0) {
            message.warning('IOC 标签已生成，但合并预览失败');
            setMergedWaybillPdfOpen(false);
          } else {
            const mergedUrl = URL.createObjectURL(mergedBlob);
            mergedWaybillPdfUrlRef.current = mergedUrl;
            setMergedWaybillPdfUrl(mergedUrl);
          }
        } finally {
          setMergedWaybillPdfLoading(false);
        }
      }
      const createdCount = successRows.filter((x) => !x.updated).length;
      const updatedCount = successRows.filter((x) => x.updated).length;
      if (successRows.length > 0 && failRows.length === 0) {
        message.success(`已创建 ${createdCount} 条，已更新 ${updatedCount} 条`);
      } else if (successRows.length > 0 && failRows.length > 0) {
        message.warning(`已创建 ${createdCount} 条，已更新 ${updatedCount} 条，失败 ${failRows.length} 条`);
      } else {
        message.error('创建指令产出容器失败');
      }
      if (failRows.length > 0) {
        Modal.warning({
          title: '部分指令创建 IOC 失败',
          content: (
            <div style={{ maxHeight: 320, overflowY: 'auto', lineHeight: 1.8 }}>
              {failRows.slice(0, 100).map((r, idx) => (
                <div key={`${r.instructionCode}-${idx}`}>{`${idx + 1}. ${r.instructionCode}：${r.reason}`}</div>
              ))}
              {failRows.length > 100 ? <div>{`...其余 ${failRows.length - 100} 条`}</div> : null}
            </div>
          ),
        });
      }
    } finally {
      setOperateConfirmCreateIocLoadingByKey((prev) => ({ ...prev, [packagingKey]: false }));
    }
  }, [appendOperationRecord, applyLocalActualPackResultPatch, getInstructionSystemCode, isDoneInOperateConfirmDraft, mergePdfBlobs, operateConfirmIocCodeByInstrId]);

  const createInstructionOutputContainerForDirectOperate = useCallback(async (instr: any, packData: { lengthCm: number; widthCm: number; heightCm: number; weightG: number } | null | undefined) => {
    const instructionId = Number(instr?.instruction ?? instr?.instruction_id ?? instr?.id ?? 0);
    if (!Number.isFinite(instructionId) || instructionId <= 0) {
      message.warning('缺少有效指令ID');
      return;
    }
    const lockKey = `direct:${instructionId}`;
    if (operateConfirmCreateIocLoadingByKey[lockKey]) return;
    const l = Math.round(Number(packData?.lengthCm || 0));
    const w = Math.round(Number(packData?.widthCm || 0));
    const h = Math.round(Number(packData?.heightCm || 0));
    const g = Math.round(Number(packData?.weightG || 0));
    if (!(l > 0 && w > 0 && h > 0 && g > 0)) {
      message.warning('请先输入包装数据');
      return;
    }
    const instructionCode = getInstructionSystemCode(instr);
    setOperateConfirmCreateIocLoadingByKey((prev) => ({ ...prev, [lockKey]: true }));
    try {
      const qtyByIu: Record<number, number> = {};
      const iuCodeById: Record<number, string> = {};
      const targets = Array.isArray(instr?.targets_detail) ? instr.targets_detail : [];
      targets.forEach((t: any) => {
        const iuId = Number(t?.target?.inventory_unit_id ?? t?.inventory_unit_id ?? 0);
        const qty = Math.floor(Number(t?.expected_qty ?? t?.target_qty ?? 0) || 0);
        if (!Number.isFinite(iuId) || iuId <= 0 || !Number.isFinite(qty) || qty <= 0) return;
        qtyByIu[iuId] = Number(qtyByIu[iuId] || 0) + qty;
        const iuCode = String(
          t?.snapshot?.system_barcode
          ?? t?.inventory_unit_system_barcode
          ?? t?.target?.inventory_unit_system_barcode
          ?? t?.fingerprint
          ?? ''
        ).trim();
        if (iuCode) iuCodeById[iuId] = iuCode;
      });
      const items = Object.entries(qtyByIu).map(([iuId, qty]) => ({
        inventory_unit_id: Number(iuId),
        quantity: Number(qty),
      })).filter((x) => Number.isFinite(x.inventory_unit_id) && x.inventory_unit_id > 0 && Number.isFinite(x.quantity) && x.quantity > 0);
      if (items.length === 0) {
        message.warning('缺少可写入 IOC 的库存单元与数量');
        return;
      }

      let iocCode = String(
        operateConfirmIocCodeByInstrId[String(instructionId)]
        || instr?.created_ioc_code
        || instr?.instruction?.created_ioc_code
        || '',
      ).trim();
      if (!iocCode) {
        try {
          const listResp = await request.get('/api/v1/storage/output-containers/', {
            params: {
              instruction_id: instructionId,
              t: Date.now(),
            } as any,
          });
          const list = Array.isArray(listResp?.data?.results) ? listResp.data.results : [];
          const firstCode = String(list?.[0]?.code || '').trim();
          if (firstCode) iocCode = firstCode;
        } catch {
          // ignore list fallback errors; creation path below still works
        }
      }
      const updatingExisting = !!iocCode;
      if (!iocCode) {
        const reserveResp = await request.post('/api/v1/storage/output-containers/reserve-code/', {
          instruction_id: instructionId,
          container_type: 'box',
        });
        iocCode = String((reserveResp as any)?.data?.code || '').trim();
        if (!iocCode) throw new Error('预生成 IOC 编码失败');
      }
      const patchPayload = {
        actual_pack_length_cm: l,
        actual_pack_width_cm: w,
        actual_pack_height_cm: h,
        actual_gross_weight_g: g,
        pack_result_status: 'provided',
      };
      const finalizePayload: any = {
        instruction_id: instructionId,
        code: iocCode,
        container_type: 'box',
        status: 'closed',
        length_cm: patchPayload.actual_pack_length_cm,
        width_cm: patchPayload.actual_pack_width_cm,
        height_cm: patchPayload.actual_pack_height_cm,
        weight_g: patchPayload.actual_gross_weight_g,
        defer_inventory_settlement: true,
      };
      if (!updatingExisting) finalizePayload.items = items;
      await request.post('/api/v1/storage/output-containers/finalize/', finalizePayload);
      const totalQty = items.reduce((sum, it) => sum + Number(it.quantity || 0), 0);
      setOperateConfirmIocCodeByInstrId((prev) => ({ ...prev, [String(instructionId)]: iocCode }));
      setDirectOperateIocSummaryByInstrId((prev) => ({
        ...prev,
        [String(instructionId)]: { code: iocCode, totalQty: Number(totalQty || 0) || 0 },
      }));
      try {
        await request.patch(`/api/v1/instruction/instructions/${instructionId}/`, patchPayload);
        applyLocalActualPackResultPatch(instructionId, patchPayload);
      } catch {
        message.warning(`${instructionCode} 已创建IOC，但回写包装结果失败`);
      }

      const labelPayload: any = {
        instruction_id: instructionId,
        container_code: iocCode,
        instruction_code: instructionCode,
        project_summary: String(instr?.project_name ?? '-').trim() || '-',
        container_type: 'box',
        container_type_display: '纸箱',
        created_at: new Date().toLocaleString(),
        dims_cm: { l, w, h },
        dims_in: {
          l: Number((Number(l) / 2.54).toFixed(2)),
          w: Number((Number(w) / 2.54).toFixed(2)),
          h: Number((Number(h) / 2.54).toFixed(2)),
        },
        weight: {
          kg: Number((Number(g) / 1000).toFixed(3)),
          lb: Number((Number(g) / 453.59237).toFixed(3)),
        },
        new_kinds: items.length,
        total_qty: totalQty,
        details: items.slice(0, 20).map((it) => ({
          new_code: String(iuCodeById[it.inventory_unit_id] || it.inventory_unit_id),
          qty: Number(it.quantity || 0),
        })),
      };
      try {
        const labelResp = await request.post('/api/v1/storage/output-containers/labels/preview/', labelPayload, { responseType: 'blob' as any });
        const labelBlob = (labelResp as any)?.data as Blob;
        if (labelBlob && Number(labelBlob.size || 0) > 0) {
          mergedWaybillBatchInstrsRef.current = [];
          setMergedWaybillAutoPrint(false);
          setMergedPreviewTitle('IOC标签预览');
          setMergedPreviewSource('ioc');
          if (mergedWaybillPdfUrlRef.current) {
            try { URL.revokeObjectURL(mergedWaybillPdfUrlRef.current); } catch {}
            mergedWaybillPdfUrlRef.current = null;
          }
          const url = URL.createObjectURL(labelBlob);
          mergedWaybillPdfUrlRef.current = url;
          setMergedWaybillPdfUrl(url);
          setMergedWaybillPdfLoading(false);
          setMergedWaybillPdfOpen(true);
        }
      } catch {
        message.warning('IOC 已创建，但标签预览生成失败');
      }
      appendOperationRecord([{
        system_code: instructionCode,
        status: updatingExisting ? '已更新IOC' : '已创建IOC',
        error_reason: `IOC:${iocCode}`,
      }], '直接标注');
      message.success(updatingExisting ? '已更新指令产出容器' : '已创建指令产出容器');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '创建指令产出容器失败');
    } finally {
      setOperateConfirmCreateIocLoadingByKey((prev) => ({ ...prev, [lockKey]: false }));
    }
  }, [appendOperationRecord, applyLocalActualPackResultPatch, getInstructionSystemCode, operateConfirmCreateIocLoadingByKey, operateConfirmIocCodeByInstrId]);

  const previewDirectOperateIocLabel = useCallback(async (instr: any, iocCodeRaw: string, packData: { lengthCm: number; widthCm: number; heightCm: number; weightG: number } | null | undefined, fallbackTotalQty: number) => {
    const instructionId = Number(instr?.instruction ?? instr?.instruction_id ?? instr?.id ?? 0);
    if (!Number.isFinite(instructionId) || instructionId <= 0) {
      message.warning('缺少有效指令ID');
      return;
    }
    const iocCode = String(iocCodeRaw || '').trim();
    if (!iocCode) {
      message.warning('当前指令尚未创建IOC');
      return;
    }
    const lockKey = String(instructionId);
    if (directOperateIocLabelLoadingByInstrId[lockKey]) return;
    setDirectOperateIocLabelLoadingByInstrId((prev) => ({ ...prev, [lockKey]: true }));
    try {
      const qtyByIu: Record<number, number> = {};
      const iuCodeById: Record<number, string> = {};
      const targets = Array.isArray(instr?.targets_detail) ? instr.targets_detail : [];
      targets.forEach((t: any) => {
        const iuId = Number(t?.target?.inventory_unit_id ?? t?.inventory_unit_id ?? 0);
        const qty = Math.floor(Number(t?.expected_qty ?? t?.target_qty ?? 0) || 0);
        if (!Number.isFinite(iuId) || iuId <= 0 || !Number.isFinite(qty) || qty <= 0) return;
        qtyByIu[iuId] = Number(qtyByIu[iuId] || 0) + qty;
        const iuCode = String(
          t?.snapshot?.system_barcode
          ?? t?.inventory_unit_system_barcode
          ?? t?.target?.inventory_unit_system_barcode
          ?? t?.fingerprint
          ?? ''
        ).trim();
        if (iuCode) iuCodeById[iuId] = iuCode;
      });
      const items = Object.entries(qtyByIu).map(([iuId, qty]) => ({
        inventory_unit_id: Number(iuId),
        quantity: Number(qty),
      })).filter((x) => Number.isFinite(x.inventory_unit_id) && x.inventory_unit_id > 0 && Number.isFinite(x.quantity) && x.quantity > 0);
      const totalQty = items.length > 0
        ? items.reduce((sum, it) => sum + Number(it.quantity || 0), 0)
        : Math.max(Number(fallbackTotalQty || 0), 0);
      const l = Math.round(Number(packData?.lengthCm ?? instr?.actual_pack_length_cm ?? instr?.instruction?.actual_pack_length_cm ?? 0));
      const w = Math.round(Number(packData?.widthCm ?? instr?.actual_pack_width_cm ?? instr?.instruction?.actual_pack_width_cm ?? 0));
      const h = Math.round(Number(packData?.heightCm ?? instr?.actual_pack_height_cm ?? instr?.instruction?.actual_pack_height_cm ?? 0));
      const g = Math.round(Number(packData?.weightG ?? instr?.actual_gross_weight_g ?? instr?.instruction?.actual_gross_weight_g ?? 0));
      const labelPayload: any = {
        instruction_id: instructionId,
        container_code: iocCode,
        instruction_code: getInstructionSystemCode(instr),
        project_summary: String(instr?.project_name ?? '-').trim() || '-',
        container_type: 'box',
        container_type_display: '纸箱',
        created_at: new Date().toLocaleString(),
        dims_cm: { l: l > 0 ? l : '-', w: w > 0 ? w : '-', h: h > 0 ? h : '-' },
        dims_in: {
          l: l > 0 ? Number((Number(l) / 2.54).toFixed(2)) : '-',
          w: w > 0 ? Number((Number(w) / 2.54).toFixed(2)) : '-',
          h: h > 0 ? Number((Number(h) / 2.54).toFixed(2)) : '-',
        },
        weight: {
          kg: g > 0 ? Number((Number(g) / 1000).toFixed(3)) : '-',
          lb: g > 0 ? Number((Number(g) / 453.59237).toFixed(3)) : '-',
        },
        new_kinds: items.length > 0 ? items.length : '-',
        total_qty: totalQty > 0 ? totalQty : '-',
        details: items.slice(0, 20).map((it) => ({
          new_code: String(iuCodeById[it.inventory_unit_id] || it.inventory_unit_id),
          qty: Number(it.quantity || 0),
        })),
      };
      const labelResp = await request.post('/api/v1/storage/output-containers/labels/preview/', labelPayload, { responseType: 'blob' as any });
      const labelBlob = (labelResp as any)?.data as Blob;
      if (!labelBlob || Number(labelBlob.size || 0) <= 0) {
        message.warning('未生成可预览标签');
        return;
      }
      mergedWaybillBatchInstrsRef.current = [];
      setMergedWaybillAutoPrint(false);
      setMergedPreviewTitle('IOC标签预览');
      setMergedPreviewSource('ioc');
      if (mergedWaybillPdfUrlRef.current) {
        try { URL.revokeObjectURL(mergedWaybillPdfUrlRef.current); } catch {}
        mergedWaybillPdfUrlRef.current = null;
      }
      const url = URL.createObjectURL(labelBlob);
      mergedWaybillPdfUrlRef.current = url;
      setMergedWaybillPdfUrl(url);
      setMergedWaybillPdfLoading(false);
      setMergedWaybillPdfOpen(true);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '生成IOC标签预览失败');
    } finally {
      setDirectOperateIocLabelLoadingByInstrId((prev) => ({ ...prev, [lockKey]: false }));
    }
  }, [directOperateIocLabelLoadingByInstrId, getInstructionSystemCode]);

  const handlePrintableAttachmentClick = useCallback(async (att: any, instr?: any) => {
    const url = resolveAttachmentUrl(att);
    if (!url) { message.warning('无可用文件'); return; }
    const extLower = getExtLower(att);
    const isImg = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extLower);
    const isPdf = extLower === 'pdf';
    if (isImg || isPdf) {
      if (instr) {
        const isWaybill = isWaybillAttachment(att);
        const overlayText = isWaybill ? buildWaybillOverlayText(instr) : buildPrintOverlayText(instr, att);
        const baseConfig = isWaybill ? OVERLAY_CONFIG_WAYBILL : OVERLAY_CONFIG_OTHER;
        const configObj: Record<string, any> = { ...baseConfig, custom_text: overlayText };
        if (isWaybill) configObj.custom_text_fallback = buildWaybillOverlayTextShort(instr);
        try {
          setAttPreviewLoading(true);
          setAttPreviewOpen(true);
          setAttPreviewTitle(getDisplayName(att) || '附件预览');
          setAttPreviewTitlePrintSuffix(formatPrintConfigForTitle(att));
          const blob = await fetch(url, { credentials: 'include' }).then((r) => r.blob());
          const ext = extLower === 'pdf' ? 'pdf' : 'png';
          const base64 = await new Promise<string>((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => {
              const s = (r.result as string) || '';
              resolve(s.includes(',') ? s.split(',')[1]! : s);
            };
            r.onerror = () => reject(new Error('文件读取失败'));
            r.readAsDataURL(blob);
          });
          const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(/\/$/, '');
          const overlayUrl = `${apiBase}/api/v1/logistics/label-overlay/`;
          const token = localStorage.getItem('token');
          const resp = await fetch(overlayUrl, {
            method: 'POST',
            credentials: 'include',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ file_base64: base64, file_name: `attachment.${ext}`, config: configObj }),
          });
          if (!resp.ok) {
            const errText = await resp.text();
            try {
              const errJson = JSON.parse(errText);
              throw new Error(errJson?.error || errJson?.detail || resp.statusText);
            } catch (e: any) {
              if (e?.message && e.message !== resp.statusText) throw e;
              throw new Error(errText || resp.statusText);
            }
          }
          const overlayBlob = await resp.blob();
          if (attPreviewObjectUrlRef.current) {
            try { URL.revokeObjectURL(attPreviewObjectUrlRef.current); } catch {}
            attPreviewObjectUrlRef.current = null;
          }
          const objUrl = URL.createObjectURL(overlayBlob);
          attPreviewObjectUrlRef.current = objUrl;
          setAttPreviewUrl(objUrl);
          setAttPreviewText('');
          setAttPreviewKind('pdf');
          message.success(`已添加打印标识：${overlayText}`);
        } catch (e: any) {
          message.error(e?.message || '添加打印标识失败，将预览原文件');
          openAttachmentPreview(att);
        } finally {
          setAttPreviewLoading(false);
        }
      } else {
        openAttachmentPreview(att);
      }
      return;
    }
    try {
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) throw new Error(resp.statusText);
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const fileName = String(getDisplayName(att) || att?.file_name || 'download').trim() || 'download';
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
      message.success('下载成功');
    } catch (e: any) {
      message.error(e?.message || '下载失败');
    }
  }, [buildPrintOverlayText, buildWaybillOverlayText, formatPrintConfigForTitle, getDisplayName, getExtLower, isWaybillAttachment, openAttachmentPreview, resolveAttachmentUrl]);

  const handlePrintableAttachmentDirectPrint = useCallback(async (att: any, instr?: any) => {
    const url = resolveAttachmentUrl(att);
    if (!url) { message.warning('无可用文件'); return; }
    const extLower = getExtLower(att);
    const isImg = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extLower);
    const isPdf = extLower === 'pdf';
    if (!(isImg || isPdf)) {
      window.open(url, '_blank');
      return;
    }

    const printBlob = (blob: Blob) => {
      const objUrl = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = objUrl;
      document.body.appendChild(iframe);
      const cleanup = () => {
        try { document.body.removeChild(iframe); } catch {}
        try { URL.revokeObjectURL(objUrl); } catch {}
      };
      iframe.onload = () => {
        const win = iframe.contentWindow;
        if (win) {
          win.addEventListener('afterprint', cleanup, { once: true });
          try { win.print(); } catch { cleanup(); }
        } else {
          cleanup();
        }
      };
      setTimeout(cleanup, 8000);
    };

    try {
      let finalBlob = await fetch(url, { credentials: 'include' }).then((r) => r.blob());
      if (instr) {
        const isWaybill = isWaybillAttachment(att);
        const overlayText = isWaybill ? buildWaybillOverlayText(instr) : buildPrintOverlayText(instr, att);
        const baseConfig = isWaybill ? OVERLAY_CONFIG_WAYBILL : OVERLAY_CONFIG_OTHER;
        const configObj: Record<string, any> = { ...baseConfig, custom_text: overlayText };
        if (isWaybill) configObj.custom_text_fallback = buildWaybillOverlayTextShort(instr);
        try {
          const ext = extLower === 'pdf' ? 'pdf' : 'png';
          const base64 = await new Promise<string>((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => {
              const s = (r.result as string) || '';
              resolve(s.includes(',') ? s.split(',')[1]! : s);
            };
            r.onerror = () => reject(new Error('文件读取失败'));
            r.readAsDataURL(finalBlob);
          });
          const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(/\/$/, '');
          const overlayUrl = `${apiBase}/api/v1/logistics/label-overlay/`;
          const token = localStorage.getItem('token');
          const resp = await fetch(overlayUrl, {
            method: 'POST',
            credentials: 'include',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ file_base64: base64, file_name: `attachment.${ext}`, config: configObj }),
          });
          if (!resp.ok) throw new Error(await resp.text());
          finalBlob = await resp.blob();
          message.success(`已添加打印标识：${overlayText}`);
        } catch (e: any) {
          message.error(e?.message || '添加打印标识失败，将直接打印原文件');
        }
      }
      printBlob(finalBlob);
    } catch (e: any) {
      message.error(e?.message || '打印失败');
    }
  }, [buildPrintOverlayText, buildWaybillOverlayText, getExtLower, isWaybillAttachment, resolveAttachmentUrl]);

  const carrierColors = useMemo(() => ([
    '#f5f7ff', '#f3fbff', '#f6ffed', '#fff7e6', '#fff0f6',
    '#f9f0ff', '#f6ffed', '#e6fffb', '#fffbe6', '#f0f5ff',
  ]), []);
  const getCarrierBg = useMemo(() => (carrier: any) => {
    const raw = String(carrier || '').trim();
    const lower = raw.toLowerCase();
    if (!raw || raw === '-' || lower === 'none' || lower === 'null' || lower === 'n/a') return '#f0f0f0';
    const v = String(carrier || 'UNKNOWN').trim().toUpperCase();
    let hash = 0;
    for (let i = 0; i < v.length; i += 1) {
      hash = ((hash << 5) - hash) + v.charCodeAt(i);
      hash |= 0;
    }
    const idx = Math.abs(hash) % carrierColors.length;
    return carrierColors[idx];
  }, [carrierColors]);

  const instructionColumns = useMemo(() => ([
    {
      title: '处理结果',
      key: 'waybillStatus',
      width: 90,
      render: (_: any, r: any) => {
        const done = isDoneInOperateConfirm(r);
        const unoperated = isUnoperatedInOperateConfirm(r);
        return (
          <Tooltip title="点击修改">
            <Dropdown
              menu={{
                items: [
                  { key: 'completed', label: '已完成' },
                  { key: 'unoperated', label: '无法操作' },
                  { key: 'unmarked', label: '未标注' },
                ],
                onClick: ({ key }) => handleSetGlobalWaybillStatus(r, key as 'completed' | 'unoperated' | 'unmarked'),
              }}
              trigger={['click']}
            >
              <span
                style={{
                  display: 'inline-block',
                  minWidth: 70,
                  padding: '2px 8px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  background: done ? '#f6ffed' : unoperated ? '#fff2f0' : 'transparent',
                  border: '1px solid transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#d9d9d9';
                  e.currentTarget.style.background = done ? '#f6ffed' : unoperated ? '#fff2f0' : '#fafafa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.background = done ? '#f6ffed' : unoperated ? '#fff2f0' : 'transparent';
                }}
              >
                {done ? <Tag color="green" style={{ margin: 0 }}>已完成</Tag> : unoperated ? (
                  <Tooltip title={operateConfirmUnoperatedReasons[getOperateConfirmInstrKey(r)] || '无法操作'}>
                    <Tag color="red" style={{ margin: 0 }}>无法操作</Tag>
                  </Tooltip>
                ) : <span style={{ color: '#bfbfbf' }}>未标注</span>}
              </span>
            </Dropdown>
          </Tooltip>
        );
      },
    },
    {
      title: '指令系统编码',
      dataIndex: 'instruction_system_code',
      key: 'instruction_system_code',
      width: 120,
      render: (v: any, r: any) => {
        const code = displayDash(v);
        if (code === '-') return '-';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Button
              type="link"
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                try {
                  const path = String((window as any)?.location?.pathname || '');
                  const base = path.startsWith('/dashboard/staff')
                    ? '/dashboard/staff'
                    : path.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/customer';
                  navigate(`${base}/instructions/${(r as any)?.instruction}`);
                } catch {}
              }}
            >
              {code}
            </Button>
            <Tooltip title="复制">
              <CopyOutlined
                onClick={async (e) => {
                  e?.stopPropagation?.();
                  try {
                    await navigator.clipboard.writeText(String(code));
                    message.success('已复制');
                  } catch {
                    message.error('复制失败');
                  }
                }}
              />
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: '发货库存单元',
      key: 'ship_units',
      width: 140,
      render: (_: any, r: any) => {
        const lines = formatShipUnits(r);
        if (!lines.length) return '-';
        const truncateLine = (text: string, maxLen = 18) => {
          const t = String(text || '').trim();
          if (t.length <= maxLen) return t;
          const tailLen = 7;
          const headLen = maxLen - tailLen - 3;
          return `${t.slice(0, headLen)}...${t.slice(-tailLen)}`;
        };
        return (
          <div>
            {lines.map((line, idx) => (
              <Tooltip key={`${line}-${idx}`} title={line}>
                <div
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {truncateLine(line)}
                </div>
              </Tooltip>
            ))}
          </div>
        );
      },
    },
    {
      title: '库存单元统计',
      key: 'unit_stats',
      width: 80,
      render: (_: any, r: any) => getUnitStats(r),
    },
    {
      title: '运单状态',
      key: 'waybill_status',
      width: 100,
      render: (_: any, r: any) => getWaybillStatus(r),
    },
    {
      title: '承运商',
      key: 'waybill_carrier',
      width: 90,
      render: (_: any, r: any) => displayDash(r?.waybill_carrier),
    },
    {
      title: '物流跟踪号',
      key: 'waybill_tracking_no',
      width: 140,
      render: (_: any, r: any) => {
        const text = displayDash(r?.waybill_tracking_no);
        if (text === '-') return '-';
        return (
          <Tooltip title={text}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  display: 'inline-block',
                  maxWidth: 90,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'bottom',
                }}
              >
                {text}
              </span>
              <CopyOutlined
                onClick={async (e) => {
                  e?.stopPropagation?.();
                  try {
                    await navigator.clipboard.writeText(String(text));
                    message.success('已复制');
                  } catch {
                    message.error('复制失败');
                  }
                }}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: '运单附件',
      key: 'waybill_attachments',
      width: 120,
      render: (_: any, r: any) => {
        const list = Array.isArray(r?.attachments_detail) ? r.attachments_detail : [];
        const filtered = list.filter((a: any) => {
          const label = String(a?.label || '').trim();
          return label === '物流运单';
        });
        if (!filtered.length) return '-';
        const maxChars = 22;
        return (
          <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
            {filtered.map((a: any, idx: number) => {
              const name = displayDash(a?.file_name || a?.file || a?.file_path || a?.label || '');
              if (name === '-') return null;
              const display = name.length > maxChars ? `${name.slice(0, maxChars)}…` : name;
              return (
                <Tooltip key={`${name}-${idx}`} title={name}>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto' }}
                    onClick={() => openAttachmentPreview(a)}
                  >
                    {display}
                  </Button>
                </Tooltip>
              );
            })}
          </div>
        );
      },
    },
    {
      title: '其他附件',
      key: 'other_attachments',
      width: 220,
      render: (_: any, r: any) => {
        const list = Array.isArray(r?.attachments_detail) ? r.attachments_detail : [];
        const otherList = list.filter((a: any) => {
          const label = String(a?.label || '').trim();
          return label !== '物流运单';
        });
        if (!otherList.length) return '-';
        const needPrintCount = getAttachmentPrintNeedCount(otherList);
        const needPrintBadge = needPrintCount > 0 ? (
          <Tooltip title={needPrintCount === 1 ? '该指令有1个其他附件需打印' : `该指令有${needPrintCount}个其他附件需打印`}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#cf1322', marginLeft: 6, fontSize: 12 }}>
              <PrinterOutlined />
              <span>需打印</span>
            </span>
          </Tooltip>
        ) : null;
        const first = otherList[0];
        const firstName = displayDash(first?.file_name || first?.file || first?.file_path || first?.label || '');
        if (otherList.length === 1) {
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto' }}
                onClick={() => openAttachmentPreview(first)}
              >
                {firstName === '-' ? '附件' : firstName}
              </Button>
              {needPrintBadge}
            </span>
          );
        }
        const displayName = firstName === '-' ? '附件' : firstName;
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                setOtherAttList(otherList);
                setOtherAttModalOpen(true);
              }}
            >
              {displayName}等{otherList.length}个
            </Button>
            {needPrintBadge}
          </span>
        );
      },
    },
    {
      title: '是否需打包',
      key: 'is_pack_required',
      width: 90,
      render: (_: any, r: any) => {
        if (r?.is_pack_required === true) return <Tag color="red">是</Tag>;
        if (r?.is_pack_required === false) return <Tag color="green">否</Tag>;
        return '-';
      },
    },
    {
      title: '指令备注',
      key: 'instruction_remarks',
      width: 140,
      render: (_: any, r: any) => {
        const remarks = String(r?.instruction_remarks ?? '').trim();
        if (!remarks) return '-';
        const display = remarks.length > 20 ? `${remarks.slice(0, 20)}…` : remarks;
        return (
          <Tooltip title={remarks}>
            <span style={{ cursor: 'default' }}>{display}</span>
          </Tooltip>
        );
      },
    },
    {
      title: '执行提醒',
      key: 'execution_tips',
      width: 140,
      render: (_: any, r: any) => {
        const tips = String(r?.execution_tips ?? '').trim();
        if (!tips) return '-';
        const display = tips.length > 20 ? `${tips.slice(0, 20)}…` : tips;
        return (
          <Tooltip title={tips}>
            <span style={{ cursor: 'default' }}>{display}</span>
          </Tooltip>
        );
      },
    },
  ]), [displayDash, formatShipUnits, getOperateConfirmInstrKey, getUnitStats, getWaybillStatus, handleSetGlobalWaybillStatus, isDoneInOperateConfirm, isUnoperatedInOperateConfirm, navigate, openAttachmentPreview, operateConfirmUnoperatedReasons]);

  const filteredItems = useMemo(() => {
    const list = Array.isArray(itemsPatched) ? itemsPatched : [];
    const instrCodeKey = normalizeText(filters.instructionCode);
    const doneKey = normalizeText(filters.done);
    const waybillKey = normalizeText(filters.waybillStatus);
    const typeKey = normalizeText(filters.instructionType);
    const carrierKey = normalizeText(filters.carrier);
    const trackingKey = normalizeText(filters.trackingNo);
    const ocrKey = normalizeText(filters.ocrText);
    const sortingKey = normalizeText(filters.sortingBarcodeText);
    return list.filter((r: any) => {
      if (instrCodeKey) {
        const code = normalizeText(r?.instruction_system_code);
        if (!code || !code.includes(instrCodeKey)) return false;
      }
      if (doneKey) {
        const done = isDoneInOperateConfirm(r);
        const unoperated = isUnoperatedInOperateConfirm(r);
        const flag = done ? '已完成' : unoperated ? '无法操作' : '未标注';
        if (normalizeText(flag) !== doneKey) return false;
      }
      if (waybillKey) {
        if (normalizeText(getWaybillStatus(r)) !== waybillKey) return false;
      }
      if (typeKey) {
        if (normalizeText(getInstructionType(r)) !== typeKey) return false;
      }
      if (carrierKey) {
        const c = normalizeText(r?.waybill_carrier);
        if (carrierKey === '__no_carrier__') {
          if (c && c !== '-' && c !== 'none' && c !== 'null' && c !== 'n/a') return false;
        } else if (!c.includes(carrierKey)) {
          return false;
        }
      }
      if (trackingKey) {
        const t = normalizeText(r?.waybill_tracking_no);
        if (!t.includes(trackingKey)) return false;
      }
      if (ocrKey) {
        const text = normalizeText(getAttachmentSearchText(r));
        if (!text.includes(ocrKey)) return false;
      }
      if (sortingKey) {
        const text = normalizeText(getSortingBarcodeSearchText(r));
        if (!text.includes(sortingKey)) return false;
      }
      if (filters.isPackRequired === 'yes') {
        if (r?.is_pack_required !== true) return false;
      }
      if (filters.isPackRequired === 'no') {
        if (r?.is_pack_required !== false) return false;
      }
      if (filters.remarksOnly) {
        const remarks = String(r?.instruction_remarks ?? '').trim();
        if (!remarks) return false;
      }
      if (filters.executionTipsOnly) {
        const tips = String(r?.execution_tips ?? '').trim();
        if (!tips) return false;
      }
      return true;
    });
  }, [filters, getAttachmentSearchText, getInstructionType, getSortingBarcodeSearchText, getWaybillStatus, isDoneInOperateConfirm, isUnoperatedInOperateConfirm, itemsPatched]);

  const batchMarkSelectedCodes = useMemo(() => (
    filteredItems
      .filter((r: any) => progressSelectedKeys.includes(String(r?.id || r?.instruction || '')))
      .map((r: any) => getInstructionSystemCode(r))
  ), [filteredItems, getInstructionSystemCode, progressSelectedKeys]);

  const handleBatchMarkConfirm = useCallback(() => {
    if (batchMarkStatus === 'unoperated' && !batchMarkUnoperatedReason.trim()) {
      message.warning('选择无法操作时，请填写原因');
      return;
    }
    const selectedItems = filteredItems.filter((r: any) =>
      progressSelectedKeys.includes(String(r?.id || r?.instruction || ''))
    );
    if (selectedItems.length === 0) {
      message.info('未选中任何记录');
      setBatchMarkModalOpen(false);
      return;
    }
    const keysToUpdate = selectedItems.map((i: any) => getOperateConfirmInstrKey(i));
    applyGlobalMarkByKeys(keysToUpdate, batchMarkStatus, batchMarkStatus === 'unoperated' ? batchMarkUnoperatedReason.trim() : '');
    const recordStatus = batchMarkStatus === 'completed' ? '已完成' : batchMarkStatus === 'unoperated' ? '无法操作' : '未标注';
    const recordReason = batchMarkStatus === 'unoperated' ? batchMarkUnoperatedReason.trim() : '';
    appendOperationRecord(selectedItems.map((i: any) => ({
      system_code: getInstructionSystemCode(i),
      status: recordStatus,
      ...(recordReason ? { error_reason: recordReason } : {}),
    })), '直接标注');
    message.success(`已批量标注${selectedItems.length}条记录为${batchMarkStatus === 'completed' ? '已完成' : batchMarkStatus === 'unoperated' ? '无法操作' : '未标注'}`);
    setProgressSelectedKeys([]);
    setBatchMarkModalOpen(false);
    setBatchMarkStatus('completed');
    setBatchMarkUnoperatedReason('');
  }, [appendOperationRecord, applyGlobalMarkByKeys, batchMarkStatus, batchMarkUnoperatedReason, filteredItems, getInstructionSystemCode, getOperateConfirmInstrKey, progressSelectedKeys]);

  useEffect(() => {
    setItemsPage(1);
  }, [filters, itemsPageSize]);

  useEffect(() => {
    const total = filteredItems.length;
    const maxPage = Math.max(1, Math.ceil(total / itemsPageSize));
    if (itemsPage > maxPage) setItemsPage(maxPage);
  }, [filteredItems, itemsPage, itemsPageSize]);

  const pagedItems = useMemo(() => {
    const list = Array.isArray(filteredItems) ? filteredItems : [];
    const start = (itemsPage - 1) * itemsPageSize;
    return list.slice(start, start + itemsPageSize);
  }, [filteredItems, itemsPage, itemsPageSize]);

  const progressStats = useMemo(() => {
    const list = Array.isArray(itemsPatched) ? itemsPatched : [];
    let totalOrders = 0;
    let totalQty = 0;
    let doneOrders = 0;
    let doneQty = 0;
    let unoperatedOrders = 0;
    let unoperatedQty = 0;
    list.forEach((r: any) => {
      totalOrders += 1;
      const targets = Array.isArray(r?.targets_detail) ? r.targets_detail : [];
      let rowQty = 0;
      targets.forEach((t: any) => {
        const qty = Number(t?.expected_qty || 0);
        if (Number.isFinite(qty)) rowQty += qty;
      });
      totalQty += rowQty;
      if (isDoneInOperateConfirm(r)) {
        doneOrders += 1;
        doneQty += rowQty;
      } else if (isUnoperatedInOperateConfirm(r)) {
        unoperatedOrders += 1;
        unoperatedQty += rowQty;
      }
    });
    const unmarkedOrders = Math.max(totalOrders - doneOrders - unoperatedOrders, 0);
    const unmarkedQty = Math.max(totalQty - doneQty - unoperatedQty, 0);
    return {
      totalOrders,
      totalQty,
      doneOrders,
      doneQty,
      unoperatedOrders,
      unoperatedQty,
      unmarkedOrders,
      unmarkedQty,
    };
  }, [isDoneInOperateConfirm, isUnoperatedInOperateConfirm, itemsPatched]);
  const showAllCompletedMark = progressStats.unmarkedOrders === 0 && progressStats.unmarkedQty === 0;

  const operationRecordGroups = useMemo(() => {
    const codeKeyword = String(operationRecordCodeFilter || '').trim().toLowerCase();
    const statusFilter = String(operationRecordStatusFilter || '').trim();
    const markModeFilter = String(operationRecordMarkModeFilter || '').trim();
    const groups = operationRecords.map((rec) => {
      const mode: OperationRecord['mark_mode'] = rec?.mark_mode === '直接标注' ? '直接标注' : '波次操作';
      if (markModeFilter && mode !== markModeFilter) {
        return {
          time: String(rec?.time || ''),
          instructions: [] as Array<{ system_code: string; status: string; mark_mode: OperationRecord['mark_mode']; error_reason?: string }>,
        };
      }
      const instructions = (Array.isArray(rec?.instructions) ? rec.instructions : []).reduce((acc: Array<{ system_code: string; status: string; mark_mode: OperationRecord['mark_mode']; error_reason?: string }>, item: any) => {
        const code = String(item?.system_code ?? '').trim();
        const status = String(item?.status ?? '').trim();
        if (codeKeyword && !code.toLowerCase().includes(codeKeyword)) return acc;
        if (statusFilter && status !== statusFilter) return acc;
        acc.push({
          system_code: code || '-',
          status: status || '-',
          mark_mode: mode,
          error_reason: String(item?.error_reason || '').trim() || undefined,
        });
        return acc;
      }, []);
      return {
        time: String(rec?.time || ''),
        instructions,
      };
    }).filter((g) => g.instructions.length > 0);
    return groups;
  }, [operationRecordCodeFilter, operationRecordMarkModeFilter, operationRecordStatusFilter, operationRecords]);

  useEffect(() => {
    setOperationRecordPage(1);
  }, [operationRecordCodeFilter, operationRecordMarkModeFilter, operationRecordStatusFilter, operationRecordPageSize]);

  useEffect(() => {
    const total = operationRecordGroups.length;
    const maxPage = Math.max(1, Math.ceil(total / operationRecordPageSize));
    if (operationRecordPage > maxPage) setOperationRecordPage(maxPage);
  }, [operationRecordGroups, operationRecordPage, operationRecordPageSize]);

  const pagedOperationRecordGroups = useMemo(() => {
    const start = (operationRecordPage - 1) * operationRecordPageSize;
    return operationRecordGroups.slice(start, start + operationRecordPageSize);
  }, [operationRecordGroups, operationRecordPage, operationRecordPageSize]);

  const filteredScanRecords = useMemo(() => {
    const codeKeyword = String(scanRecordMatchedInstructionFilter || '').trim().toLowerCase();
    const scanKeyword = String(scanRecordScanValueFilter || '').trim().toLowerCase();
    return operateConfirmGlobalScanMatchRecords.filter((rec: any) => {
      const instruction = String(rec?.matchedInstruction || '').trim().toLowerCase();
      const scanValue = String(rec?.scanValue || '').trim().toLowerCase();
      if (codeKeyword && !instruction.includes(codeKeyword)) return false;
      if (scanKeyword && !scanValue.includes(scanKeyword)) return false;
      return true;
    });
  }, [operateConfirmGlobalScanMatchRecords, scanRecordMatchedInstructionFilter, scanRecordScanValueFilter]);

  useEffect(() => {
    setScanRecordPage(1);
  }, [scanRecordMatchedInstructionFilter, scanRecordScanValueFilter, scanRecordPageSize]);

  useEffect(() => {
    const total = filteredScanRecords.length;
    const maxPage = Math.max(1, Math.ceil(total / scanRecordPageSize));
    if (scanRecordPage > maxPage) setScanRecordPage(maxPage);
  }, [filteredScanRecords.length, scanRecordPage, scanRecordPageSize]);

  const pagedScanRecords = useMemo(() => {
    const start = (scanRecordPage - 1) * scanRecordPageSize;
    return filteredScanRecords.slice(start, start + scanRecordPageSize);
  }, [filteredScanRecords, scanRecordPage, scanRecordPageSize]);

  const filterItemsByWaybillTab = useMemo(() => (list: any[], tabKey: string): any[] => {
    if (!Array.isArray(list)) return [];
    return list.filter((r: any) => {
      const source = String(r?.waybill_source || '').trim().toLowerCase();
      const mode = String(r?.waybill_submit_mode || '').trim().toLowerCase();
      if (tabKey === 'upload_immediate') return source === 'upload' && mode === 'immediate';
      if (tabKey === 'warehouse') return source === 'warehouse';
      if (tabKey === 'after_pack') return source === 'upload' && mode === 'after_pack';
      return false;
    });
  }, []);

  const getWaybillColumnKey = useMemo(() => (row: any): string => {
    const source = String(row?.waybill_source || '').trim().toLowerCase();
    const mode = String(row?.waybill_submit_mode || '').trim().toLowerCase();
    const carrierRaw = String(row?.waybill_carrier || '').trim();
    const carrier = carrierRaw.toUpperCase();
    if (source === 'upload' && mode === 'immediate') {
      if (carrier === 'USPS') return 'upload_immediate_usps';
      if (carrier === 'UPS') return 'upload_immediate_ups';
      if (carrier === 'FEDEX') return 'upload_immediate_fedex';
      // "其他物流商" 是指令承运商字段的特定值，非 catch-all
      if (carrierRaw === '其他物流商') return 'upload_immediate_other';
      if (carrierRaw) return `upload_immediate_carrier_${carrierRaw}`;
    }
    if (source === 'warehouse') return 'warehouse';
    if (source === 'upload' && mode === 'after_pack') return 'after_pack';
    return '';
  }, []);

  const itemsByTab = useMemo(() => ({
    upload_immediate: filterItemsByWaybillTab(itemsPatched, 'upload_immediate'),
    warehouse: filterItemsByWaybillTab(itemsPatched, 'warehouse'),
    after_pack: filterItemsByWaybillTab(itemsPatched, 'after_pack'),
  }), [filterItemsByWaybillTab, itemsPatched]);

  const tabStats = useMemo(() => {
    const calc = (list: any[]) => {
      let orders = 0;
      let qty = 0;
      (list || []).forEach((r: any) => {
        orders += 1;
        (Array.isArray(r?.targets_detail) ? r.targets_detail : []).forEach((t: any) => {
          qty += Number(t?.expected_qty || 0) || 0;
        });
      });
      return { orders, qty };
    };
    return {
      upload_immediate: calc(itemsByTab.upload_immediate),
      warehouse: calc(itemsByTab.warehouse),
      after_pack: calc(itemsByTab.after_pack),
    };
  }, [itemsByTab]);

  const buildSingleSkuMatrix = useCallback((sourceItems: any[]) => {
    const list = Array.isArray(sourceItems) ? sourceItems : [];
    const singleSkuItems = list.filter((r: any) => {
      const targets = Array.isArray(r?.targets_detail) ? r.targets_detail : [];
      const byCode = new Set<string>();
      targets.forEach((t: any) => {
        const snap = t?.snapshot || {};
        const code = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
        if (code) byCode.add(code);
      });
      return byCode.size === 1;
    });

    const waybillKeys = new Set<string>();
    const carrierDisplayName = (k: string): string => {
      if (k === 'upload_immediate_usps') return 'USPS';
      if (k === 'upload_immediate_ups') return 'UPS';
      if (k === 'upload_immediate_fedex') return 'FedEx';
      if (k === 'upload_immediate_other') return '其他物流商';
      if (k.startsWith('upload_immediate_carrier_')) return k.replace('upload_immediate_carrier_', '');
      if (k === 'warehouse') return '仓库提供运单';
      if (k === 'after_pack') return '先申请包装数据';
      return k;
    };
    const waybillLabels: Record<string, string> = {
      upload_immediate_usps: '已有运单USPS',
      upload_immediate_ups: '已有运单UPS',
      upload_immediate_fedex: '已有运单FedEx',
      upload_immediate_other: '已有运单(其他物流商)',
      warehouse: '仓库提供运单',
      after_pack: '先申请包装数据',
    };
    const uploadImmediateFixed = ['upload_immediate_usps', 'upload_immediate_ups', 'upload_immediate_fedex'];

    type RowKey = string;
    const rowData = new Map<RowKey, {
      iuCode: string;
      sortBarcodes: string[];
      perOrderQty: number;
      totalOrders: number;
      totalShipQty: number;
      byWaybill: Record<string, number>;
      byWaybillInstructions: Record<string, any[]>;
      allInstructions: any[];
    }>();

    singleSkuItems.forEach((r: any) => {
      const wbKey = getWaybillColumnKey(r);
      if (wbKey) waybillKeys.add(wbKey);

      const targets = Array.isArray(r?.targets_detail) ? r.targets_detail : [];
      let instrTotalQty = 0;
      let iuCode = '';
      const barcodeSet = new Set<string>();
      targets.forEach((t: any) => {
        const snap = t?.snapshot || {};
        const code = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
        const sortArr = Array.isArray(t?.sorting_barcodes)
          ? t.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
          : (Array.isArray(snap?.sorting_barcodes)
            ? snap.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
            : []);
        const sort = sortArr.length > 0 ? sortArr : (snap?.sorting_barcode ? [String(snap.sorting_barcode)] : []);
        const qty = Number(t?.expected_qty || 0);
        if (code) {
          iuCode = code;
          sort.forEach((s: string) => { if (s) barcodeSet.add(String(s).trim()); });
          instrTotalQty += Number.isFinite(qty) ? qty : 0;
        }
      });

      if (!iuCode) return;
      const perOrderQty = instrTotalQty;
      const rk = `${iuCode}|${perOrderQty}`;
      if (!rowData.has(rk)) {
        rowData.set(rk, {
          iuCode,
          sortBarcodes: [],
          perOrderQty,
          totalOrders: 0,
          totalShipQty: 0,
          byWaybill: {},
          byWaybillInstructions: {},
          allInstructions: [],
        });
      }
      const rec = rowData.get(rk)!;
      rec.totalOrders += 1;
      rec.totalShipQty += instrTotalQty;
      rec.allInstructions.push(r);
      if (wbKey) {
        rec.byWaybill[wbKey] = (rec.byWaybill[wbKey] || 0) + 1;
        if (!rec.byWaybillInstructions[wbKey]) rec.byWaybillInstructions[wbKey] = [];
        rec.byWaybillInstructions[wbKey].push(r);
      }
      barcodeSet.forEach((b) => { if (b && !rec.sortBarcodes.includes(b)) rec.sortBarcodes.push(b); });
    });

    rowData.forEach((v) => {
      v.sortBarcodes.sort();
    });

    const iuTotalByCode = new Map<string, number>();
    rowData.forEach((v) => {
      const cur = iuTotalByCode.get(v.iuCode) || 0;
      iuTotalByCode.set(v.iuCode, cur + v.totalShipQty);
    });

    const sortedRows = Array.from(rowData.entries())
      .map(([k, v]) => ({ key: k, ...v }))
      .sort((a, b) => {
        const totalA = iuTotalByCode.get(a.iuCode) || 0;
        const totalB = iuTotalByCode.get(b.iuCode) || 0;
        if (totalB !== totalA) return totalB - totalA;
        if (a.iuCode !== b.iuCode) return (a.iuCode || '').localeCompare(b.iuCode || '');
        return a.perOrderQty - b.perOrderQty;
      });

    const uploadImmediateKeys = Array.from(waybillKeys).filter((k) => k.startsWith('upload_immediate_'));
    const order: string[] =
      uploadImmediateKeys.length > 0
        ? [
            ...uploadImmediateFixed.filter((k) => waybillKeys.has(k)),
            ...uploadImmediateKeys
              .filter((k) => !uploadImmediateFixed.includes(k) && k !== 'upload_immediate_other')
              .sort((a, b) => carrierDisplayName(a).localeCompare(carrierDisplayName(b))),
            ...(waybillKeys.has('upload_immediate_other') ? ['upload_immediate_other'] : []),
          ]
        : ['warehouse', 'after_pack'].filter((k) => waybillKeys.has(k));
    const dynamicCols = order.map((k) => ({
      key: k,
      title: waybillLabels[k] || carrierDisplayName(k),
      carrierDisplayName: carrierDisplayName(k),
    }));

    let totalOrders = 0;
    let totalQty = 0;
    const iuCodes = new Set<string>();
    sortedRows.forEach((r) => {
      totalOrders += r.totalOrders || 0;
      totalQty += r.totalShipQty || 0;
      if (r.iuCode) iuCodes.add(r.iuCode);
    });

    return { rows: sortedRows, dynamicCols, stats: { orders: totalOrders, qty: totalQty, iuCount: iuCodes.size } };
  }, [getWaybillColumnKey]);

  const singleSkuMatrixByTab = useMemo(() => ({
    upload_immediate: buildSingleSkuMatrix(itemsByTab.upload_immediate),
    warehouse: buildSingleSkuMatrix(itemsByTab.warehouse),
    after_pack: buildSingleSkuMatrix(itemsByTab.after_pack),
  }), [buildSingleSkuMatrix, itemsByTab]);

  const findMatchByScan = useCallback((scanText: string): { source: string; rows: any[] } | null => {
    const norm = String(scanText || '').trim().toLowerCase();
    if (!norm) return null;

    const hasUndoneCarrier = (r: any) => {
      const byWb = r?.byWaybill || {};
      const byWbInstr = r?.byWaybillInstructions || {};
      return Object.keys(byWb).some((key) => {
        const total = Number(byWb[key]) || 0;
        if (total <= 0) return false;
        const instrs = Array.isArray(byWbInstr[key]) ? byWbInstr[key] : [];
        const processed = instrs.filter((i: any) => isProcessedInOperateConfirm(i)).length;
        return processed < total;
      });
    };

    const matchInUploadImmediateSingle = () => {
      const list = singleSkuMatrixByTab.upload_immediate.rows || [];
      return list.filter((r: any) => {
        const iu = String(r?.iuCode ?? '').trim().toLowerCase();
        if (iu === norm) return true;
        const sortArr = Array.isArray(r?.sortBarcodes) ? r.sortBarcodes : [];
        if (sortArr.some((s: any) => String(s ?? '').trim().toLowerCase() === norm)) return true;
        const instrs = Array.isArray(r?.allInstructions) ? r.allInstructions : [];
        return instrs.some((i: any) => {
          const code = String(i?.instruction_system_code ?? i?.instruction?.instruction_system_code ?? '').trim().toLowerCase();
          return code === norm;
        });
      }).filter((r: any) => hasUndoneCarrier(r));
    };

    const rows1 = matchInUploadImmediateSingle();
    if (rows1.length > 0) return { source: 'upload_immediate_single', rows: rows1 };

    return null;
  }, [singleSkuMatrixByTab, isProcessedInOperateConfirm]);

  const applyInlineRecommendPickOnce = useCallback((recommendResult: ScanRecommendPositionResult) => {
    const iuCode = String(recommendResult?.iuCode || '').trim();
    if (!iuCode) return;
    const key = `${recommendResult.sourceTabName}|${String(recommendResult.sortingPosition || '').trim()}|${iuCode.toLowerCase()}`;
    setSortedPickedQtyMap((prev) => ({
      ...prev,
      [key]: (Number(prev[key] ?? 0) || 0) + 1,
    }));
  }, []);

  const refocusScanQueryInput = useCallback(() => {
    setTimeout(() => scanQueryInputRef.current?.focus(), 0);
  }, []);

  const handleScanSubmit = useCallback(() => {
    const scanText = String(scanInputValue || '').trim();
    if (!scanText) return;
    setScanInlineScanValue('');
    setScanInlineRecommendPositionResult(null);
    setScanInlineOnlySingleHint(null);
    setScanRecommendPositionResult(null);
    if (scanSortOnly) {
      // 仅分拣打开：先推荐位，再已有运单-单品指令
      const recommendResult = findRecommendPositionByScanCore(scanText, []);
      if (recommendResult) {
        applyInlineRecommendPickOnce(recommendResult);
        setScanInlineRecommendPositionResult({ ...recommendResult, gapQty: 1 });
        setScanInlineScanValue(scanText);
        setScanInputValue('');
        refocusScanQueryInput();
        return;
      }
      const singleResult = findMatchByScan(scanText);
      if (singleResult) {
        setScanInlineOnlySingleHint('无分拣推荐位,仅存在于已有运单-单品指令');
        setScanInlineScanValue(scanText);
        setScanInputValue('');
        refocusScanQueryInput();
        return;
      }
      message.warning('无匹配记录');
      setScanInputValue('');
      refocusScanQueryInput();
      return;
    }

    // 仅分拣关闭：先已有运单-单品指令，再推荐位
    const singleResult = findMatchByScan(scanText);
    const recommendResult = findRecommendPositionByScanCore(scanText, singleResult?.rows || []);
    if (singleResult) {
      setScanMatchedRows(singleResult.rows);
      setScanMatchSource(singleResult.source);
      setScanRecommendPositionResult(recommendResult);
      setScanResultPage(1);
      setScanResultOpen(true);
      setScanInputValue('');
      refocusScanQueryInput();
      return;
    }
    if (recommendResult) {
      applyInlineRecommendPickOnce(recommendResult);
      setScanInlineRecommendPositionResult({ ...recommendResult, gapQty: 1 });
      setScanInlineScanValue(scanText);
      setScanInputValue('');
      refocusScanQueryInput();
      return;
    }
    message.warning('无匹配记录');
    setScanInputValue('');
    refocusScanQueryInput();
  }, [applyInlineRecommendPickOnce, findMatchByScan, findRecommendPositionByScanCore, refocusScanQueryInput, scanInputValue, scanSortOnly]);

  useEffect(() => {
    if (!scanRecommendPositionResult) {
      setScanRecommendPutQty(1);
      return;
    }
    const gap = Number(scanRecommendPositionResult.gapQty ?? 0);
    setScanRecommendPutQty(gap > 0 ? gap : 1);
  }, [scanRecommendPositionResult]);

  const handlePutIntoRecommendPosition = useCallback(() => {
    if (!scanRecommendPositionResult) return;
    const gap = Math.max(1, Number(scanRecommendPositionResult.gapQty ?? 0) || 1);
    const qty = Math.min(gap, Math.max(1, Number(scanRecommendPutQty) || 1));
    const iuCode = String(scanRecommendPositionResult.iuCode || '').trim();
    if (!iuCode) {
      message.warning('未获取到库存单元，无法放入分拣位');
      return;
    }
    const key = `${scanRecommendPositionResult.sourceTabName}|${String(scanRecommendPositionResult.sortingPosition || '').trim()}|${iuCode.toLowerCase()}`;
    setSortedPickedQtyMap((prev) => ({
      ...prev,
      [key]: (Number(prev[key] ?? 0) || 0) + qty,
    }));
    message.success('已放入分拣位');
    setScanResultOpen(false);
    setScanMatchedRows([]);
    setScanMatchSource(null);
    setScanRecommendPositionResult(null);
  }, [scanRecommendPositionResult, scanRecommendPutQty]);

  useEffect(() => {
    const log = (...args: unknown[]) => { if (process.env.NODE_ENV === 'development') console.log('[包装预估]', ...args); };
    log('useEffect 执行', { operateConfirmOpen, hasContext: !!operateConfirmContext });

    if (!operateConfirmOpen || !operateConfirmContext) {
      log('提前返回: 弹窗未打开或 context 为空');
      setPackingEstimateByMethod({});
      setPackingEstimateLoading(false);
      return;
    }
    const ctx = operateConfirmContext;
    const instrs = (ctx.row?.byWaybillInstructions?.[ctx.carrierKey] || []) as any[];
    const needPackInstrs = instrs.filter((i: any) => !isDoneInOperateConfirm(i) && i?.is_pack_required === true);
    log('needPackInstrs 数量:', needPackInstrs.length, 'perOrderQty:', ctx.row?.perOrderQty);

    if (needPackInstrs.length === 0) {
      log('提前返回: 无需打包指令数量为0');
      setPackingEstimateByMethod({});
      setPackingEstimateLoading(false);
      return;
    }
    const firstInstr = needPackInstrs[0];
    const warehouseId = firstInstr?.instruction_warehouse_id ?? firstInstr?.warehouse_id ?? batchDetail?.warehouse_id;
    const customerId = firstInstr?.instruction_customer_id ?? firstInstr?.customer_id ?? firstInstr?.project?.customer_id ?? batchDetail?.customer_id;
    const perOrderQty = Number(ctx.row?.perOrderQty ?? 0);

    const packKeys = new Set<string>();
    const rawMethods: string[] = [];
    needPackInstrs.forEach((i: any) => {
      const k = String(i?.estimated_packing_method ?? '').trim() || '_other';
      rawMethods.push(k);
      if (k !== '_other' && ['warehouse_free', 'warehouse_paid', 'customer_carton'].includes(k)) packKeys.add(k);
    });
    log('指令中的包装方式(raw):', rawMethods, '将调用的 category:', Array.from(packKeys), 'warehouse_id:', warehouseId, 'customer_id:', customerId);

    let active = true;
    (async () => {
      try {
        setPackingEstimateLoading(true);
        setPackingEstimateByMethod({});

        let items: { length_cm: number; width_cm: number; height_cm: number; weight_g: number; quantity: number }[] | null = null;
        const firstTarget = Array.isArray(firstInstr?.targets_detail)
          ? firstInstr.targets_detail.find((t: any) => String(t?.target_type) === 'inventory_unit') || firstInstr.targets_detail[0]
          : null;
        const iuId = firstTarget?.inventory_unit_id;

        if (iuId && Number.isFinite(Number(iuId))) {
          try {
            const iuResp = await getInventoryUnitDetail(Number(iuId), warehouseId ? { warehouse_id: warehouseId } : undefined);
            const iu = (iuResp as any)?.data ?? iuResp;
            const l = Number(iu?.actual_length ?? iu?.length ?? 0);
            const w = Number(iu?.actual_width ?? iu?.width ?? 0);
            const h = Number(iu?.actual_height ?? iu?.height ?? 0);
            if (Number.isFinite(l) && Number.isFinite(w) && Number.isFinite(h) && l > 0 && w > 0 && h > 0 && perOrderQty > 0) {
              items = [{ length_cm: l, width_cm: w, height_cm: h, weight_g: 0, quantity: perOrderQty }];
              log('从库存单元接口获取尺寸:', { l, w, h, perOrderQty });
            }
          } catch (iuErr) {
            log('获取库存单元失败:', iuErr);
          }
        }

        if (!items && firstTarget) {
          items = buildPackingItemsFromInstructions(needPackInstrs, perOrderQty);
          if (items) log('从 snapshot 获取尺寸');
        }

        if (!items || items.length === 0) {
          log('提前返回: 无法获取库存单元尺寸(接口与snapshot均无)');
          if (active) setPackingEstimateLoading(false);
          return;
        }

        const basePayload: any = { items, customer_id: customerId, warehouse_id: warehouseId };
        const results: Record<string, { code: string; name: string }> = {};
        const categories = Array.from(packKeys);
        if (categories.length === 0) {
          if (active) setPackingEstimateLoading(false);
          return;
        }
        await Promise.all(
          categories.map(async (cat) => {
            if (!active) return;
            try {
              const resp = await request.post('/api/v1/logistics/packaging-recommendation/', { ...basePayload, category: cat });
              const respData = resp?.data as any;
              const box = respData?.recommended_box;
              log(`API ${cat} 响应:`, { hasRecommendedBox: !!box, reason: respData?.reason, boxKeys: box ? Object.keys(box) : [] });
              if (box && active) {
                results[cat] = {
                  code: String(box?.system_code || ''),
                  name: String(box?.name || box?.title || ''),
                };
              }
            } catch (err) {
              log(`API ${cat} 请求异常:`, err);
            }
          })
        );
        log('包装预估最终 results:', results);
        if (active) setPackingEstimateByMethod(results);
      } catch (err) {
        log('包装预估整体异常:', err);
        if (active) setPackingEstimateByMethod({});
      } finally {
        if (active) setPackingEstimateLoading(false);
      }
    })();
    return () => { active = false; };
  }, [operateConfirmOpen, operateConfirmContext, buildPackingItemsFromInstructions, isDoneInOperateConfirm, batchDetail]);

  const mixedInstructionSortKey = useMemo(() => (row: any): number => {
    const source = String(row?.waybill_source || '').trim().toLowerCase();
    const mode = String(row?.waybill_submit_mode || '').trim().toLowerCase();
    const carrier = String(row?.waybill_carrier || '').trim().toUpperCase();
    if (source === 'upload' && mode === 'immediate') {
      if (carrier === 'USPS') return 0;
      if (carrier === 'UPS') return 1;
      if (carrier === 'FEDEX') return 2;
      if (carrier) return 3;
      return 4;
    }
    if (source === 'warehouse') return 100;
    if (source === 'upload' && mode === 'after_pack') return 200;
    return 300;
  }, []);

  const buildMixedInstructionRows = useCallback((
    sourceItems: any[],
    sourceTab: 'upload_immediate' | 'warehouse' | 'after_pack',
    singleSkuCount: number,
  ) => {
    const list = Array.isArray(sourceItems) ? sourceItems : [];
    const mixed = list.filter((r: any) => getInstructionType(r) === '多款多件');
    mixed.sort((a: any, b: any) => {
      const ka = mixedInstructionSortKey(a);
      const kb = mixedInstructionSortKey(b);
      if (ka !== kb) return ka - kb;
      const ca = (a?.waybill_carrier || '').toString();
      const cb = (b?.waybill_carrier || '').toString();
      if (ka < 100 && ca !== cb) return ca.localeCompare(cb);
      return (a?.instruction_system_code || '').localeCompare(b?.instruction_system_code || '');
    });

    type FlatRow = {
      key: string;
      instrIndex: number;
      sortingPosition: string;
      instruction: any;
      target: any;
      targetIdx: number;
      isFirstRow: boolean;
      rowSpan: number;
    };
    const flat: FlatRow[] = [];
    let instrIdx = 1;
    let totalQty = 0;
    const iuCodes = new Set<string>();
    mixed.forEach((r: any) => {
      const sortingPosition = sourceTab === 'upload_immediate'
        ? `A${instrIdx}`
        : sourceTab === 'warehouse'
          ? `B${Number(singleSkuCount || 0) + instrIdx}`
          : `C${Number(singleSkuCount || 0) + instrIdx}`;
      const targets = Array.isArray(r?.targets_detail) ? r.targets_detail : [];
      const rowSpan = Math.max(targets.length, 1);
      targets.forEach((t: any) => {
        const snap = t?.snapshot || {};
        const code = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
        if (code) iuCodes.add(code);
        totalQty += Number(t?.expected_qty || 0) || 0;
      });
      targets.forEach((t: any, ti: number) => {
        flat.push({
          key: `${r?.instruction ?? r?.id ?? ''}-${ti}`,
          instrIndex: instrIdx,
          sortingPosition,
          instruction: r,
          target: t,
          targetIdx: ti,
          isFirstRow: ti === 0,
          rowSpan,
        });
      });
      if (targets.length === 0) {
        flat.push({
          key: `${r?.instruction ?? r?.id ?? ''}-0`,
          instrIndex: instrIdx,
          sortingPosition,
          instruction: r,
          target: null,
          targetIdx: 0,
          isFirstRow: true,
          rowSpan: 1,
        });
      }
      instrIdx += 1;
    });
    return { rows: flat, stats: { orders: mixed.length, qty: totalQty, iuCount: iuCodes.size } };
  }, [getInstructionType, mixedInstructionSortKey]);

  const mixedInstructionRowsByTab = useMemo(() => ({
    upload_immediate: buildMixedInstructionRows(itemsByTab.upload_immediate, 'upload_immediate', 0),
    warehouse: buildMixedInstructionRows(itemsByTab.warehouse, 'warehouse', (singleSkuMatrixByTab.warehouse.rows || []).length),
    after_pack: buildMixedInstructionRows(itemsByTab.after_pack, 'after_pack', (singleSkuMatrixByTab.after_pack.rows || []).length),
  }), [buildMixedInstructionRows, itemsByTab, singleSkuMatrixByTab.after_pack.rows, singleSkuMatrixByTab.warehouse.rows]);

  const buildPickedQtyKey = useCallback((sourceTabName: '已有运单' | '仓库提供运单' | '先申请包装数据', sortingPosition: string, iuCode: string): string => (
    `${sourceTabName}|${String(sortingPosition || '').trim()}|${String(iuCode || '').trim().toLowerCase()}`
  ), []);

  const getPickedQty = useCallback((sourceTabName: '已有运单' | '仓库提供运单' | '先申请包装数据', sortingPosition: string, iuCode: string): number => {
    const key = buildPickedQtyKey(sourceTabName, sortingPosition, iuCode);
    return Number(sortedPickedQtyMap[key] ?? 0) || 0;
  }, [buildPickedQtyKey, sortedPickedQtyMap]);
  const playSortVerifyAlertBeep = useCallback(() => {
    try {
      const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = 880;
      gain.gain.value = 0.06;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.12);
      osc.onended = () => { try { ctx.close(); } catch {} };
    } catch {}
  }, []);
  const runSortVerifyScanCore = useCallback((params: {
    sourceTabName: '已有运单' | '仓库提供运单' | '先申请包装数据';
    sortingPosition: string;
    scanText: string;
    unitRows: Array<{ iuCode: string; sortBarcode: string; shipQty: number; pickedQty: number }>;
  }) => {
    const scanText = String(params?.scanText || '').trim();
    if (!scanText) return { status: 'empty' as const };
    const norm = scanText.toLowerCase();
    const sortPosition = String(params?.sortingPosition || '').trim();
    const sourceTabName = params?.sourceTabName;
    const unitRows = Array.isArray(params?.unitRows) ? params.unitRows : [];
    const matchedRow = unitRows.find((row: any) => {
      const iuCode = String(row?.iuCode || '').trim().toLowerCase();
      if (iuCode && iuCode === norm) return true;
      const sortTokens = String(row?.sortBarcode || '')
        .split(/[,\n，\s]+/)
        .map((x: string) => String(x || '').trim().toLowerCase())
        .filter(Boolean);
      return sortTokens.includes(norm);
    });
    if (!matchedRow || !sortPosition || !sourceTabName) return { status: 'no_match' as const };
    const prevPicked = Number(matchedRow?.pickedQty ?? 0) || 0;
    const shipQty = Number(matchedRow?.shipQty ?? 0) || 0;
    if (prevPicked >= shipQty) return { status: 'reached' as const };
    const iu = String(matchedRow?.iuCode || '').trim().toLowerCase();
    const key = buildPickedQtyKey(sourceTabName, sortPosition, iu);
    const nextPicked = Math.min(shipQty, prevPicked + 1);
    setSortedPickedQtyMap((prev) => ({ ...prev, [key]: nextPicked }));
    const reachedAll = unitRows.every((row: any) => {
      const rowIu = String(row?.iuCode || '').trim().toLowerCase();
      const picked = rowIu === iu ? nextPicked : (Number(row?.pickedQty ?? 0) || 0);
      const ship = Number(row?.shipQty ?? 0) || 0;
      return picked >= ship;
    });
    return {
      status: 'ok' as const,
      matchedIuCode: String(matchedRow?.iuCode || '').trim(),
      reachedAll,
    };
  }, [buildPickedQtyKey]);
  function findRecommendPositionByScanCore(
    scanText: string,
    matchedRows: any[],
  ): ScanRecommendPositionResult | null {
    const norm = String(scanText || '').trim().toLowerCase();
    const matchedFirst = Array.isArray(matchedRows) && matchedRows.length > 0 ? matchedRows[0] : null;
    const refIu = String(matchedFirst?.iuCode || '').trim().toLowerCase();
    const refSorts = Array.isArray(matchedFirst?.sortBarcodes) ? matchedFirst.sortBarcodes.map((s: any) => String(s || '').trim().toLowerCase()) : [];
    const tokens = new Set<string>([norm, refIu, ...refSorts].filter((v) => !!v));
    if (tokens.size === 0) return null;

    const isSingleRowMatched = (row: any): boolean => {
      const iu = String(row?.iuCode || '').trim().toLowerCase();
      if (iu && tokens.has(iu)) return true;
      const sorts = Array.isArray(row?.sortBarcodes) ? row.sortBarcodes.map((s: any) => String(s || '').trim().toLowerCase()) : [];
      return sorts.some((s: string) => tokens.has(s));
    };

    const isSingleRowNotFullyCompleted = (row: any): boolean => {
      const total = Number(row?.totalOrders ?? 0);
      if (!Number.isFinite(total) || total <= 0) return false;
      const instrs = Array.isArray(row?.allInstructions) ? row.allInstructions : [];
      const processed = instrs.filter((i: any) => isProcessedInOperateConfirm(i)).length;
      return processed < total;
    };

    const groupMixedRowsByInstr = (rows: any[]): Array<{ instrIndex: number; rows: any[] }> => {
      const order: number[] = [];
      const map = new Map<number, any[]>();
      (Array.isArray(rows) ? rows : []).forEach((r: any) => {
        const idx = Number(r?.instrIndex || 0);
        if (!Number.isFinite(idx) || idx <= 0) return;
        if (!map.has(idx)) {
          map.set(idx, []);
          order.push(idx);
        }
        map.get(idx)!.push(r);
      });
      return order.map((idx) => ({ instrIndex: idx, rows: map.get(idx) || [] }));
    };

    const getMixedGroupMatchedIuAndShippedQty = (groupRows: any[]): { iuCode: string; shippedQty: number } | null => {
      let matchedIuCode = '';
      let shipped = 0;
      (Array.isArray(groupRows) ? groupRows : []).forEach((r: any) => {
        const t = r?.target;
        if (!t) return;
        const snap = t?.snapshot || {};
        const iu = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim().toLowerCase();
        const sortArr = Array.isArray(t?.sorting_barcodes)
          ? t.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
          : (Array.isArray(snap?.sorting_barcodes)
            ? snap.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
            : []);
        const sorts = sortArr.map((s: any) => String(s).trim().toLowerCase());
        const matched = (iu && tokens.has(iu)) || sorts.some((s: string) => tokens.has(s));
        if (!matched) return;
        const iuRaw = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
        const iuNorm = iuRaw.toLowerCase();
        if (!matchedIuCode && iuRaw) matchedIuCode = iuRaw;
        if (matchedIuCode && iuNorm === matchedIuCode.toLowerCase()) shipped += Number(t?.expected_qty ?? 0) || 0;
      });
      return matchedIuCode ? { iuCode: matchedIuCode, shippedQty: shipped } : null;
    };

    const findInSingleRows = (
      rows: any[],
      sourceTabName: '仓库提供运单' | '先申请包装数据',
      prefix: 'B' | 'C',
    ) => {
      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        if (!isSingleRowNotFullyCompleted(row)) continue;
        if (!isSingleRowMatched(row)) continue;
        const iuCode = String(row?.iuCode || '').trim();
        if (!iuCode) continue;
        const sortingPosition = `${prefix}${i + 1}`;
        const shippedQty = Number(row?.totalShipQty ?? 0) || 0;
        const pickedQty = getPickedQty(sourceTabName, sortingPosition, iuCode);
        if (shippedQty <= pickedQty) continue;
        return {
          sortingPosition,
          sourceTabName,
          shippedQty,
          pickedQty,
          gapQty: shippedQty - pickedQty,
          iuCode,
        };
      }
      return null;
    };

    const findInMixedRows = (
      rows: any[],
      sourceTabName: '已有运单' | '仓库提供运单' | '先申请包装数据',
      sortingPositionBuilder: (instrIndex: number) => string,
    ) => {
      const groups = groupMixedRowsByInstr(rows);
      for (const group of groups) {
        const groupInstr = Array.isArray(group.rows) && group.rows.length > 0 ? group.rows[0]?.instruction : null;
        if (!groupInstr || isProcessedInOperateConfirm(groupInstr)) continue;
        const matchedData = getMixedGroupMatchedIuAndShippedQty(group.rows);
        if (!matchedData) continue;
        const sortingPosition = String(group?.rows?.[0]?.sortingPosition || '').trim() || sortingPositionBuilder(group.instrIndex);
        const shippedQty = Number(matchedData.shippedQty ?? 0) || 0;
        const pickedQty = getPickedQty(sourceTabName, sortingPosition, matchedData.iuCode);
        if (shippedQty <= pickedQty) continue;
        return {
          sortingPosition,
          sourceTabName,
          shippedQty,
          pickedQty,
          gapQty: shippedQty - pickedQty,
          iuCode: matchedData.iuCode,
        };
      }
      return null;
    };

    const seq1 = findInMixedRows(
      mixedInstructionRowsByTab.upload_immediate.rows || [],
      '已有运单',
      (instrIndex) => `A${instrIndex}`,
    );
    if (seq1) return seq1;

    const seq2 = findInSingleRows(singleSkuMatrixByTab.warehouse.rows || [], '仓库提供运单', 'B');
    if (seq2) return seq2;

    const seq3 = findInMixedRows(
      mixedInstructionRowsByTab.warehouse.rows || [],
      '仓库提供运单',
      (instrIndex) => `B${(singleSkuMatrixByTab.warehouse.rows || []).length + instrIndex}`,
    );
    if (seq3) return seq3;

    const seq4 = findInSingleRows(singleSkuMatrixByTab.after_pack.rows || [], '先申请包装数据', 'C');
    if (seq4) return seq4;

    const seq5 = findInMixedRows(
      mixedInstructionRowsByTab.after_pack.rows || [],
      '先申请包装数据',
      (instrIndex) => `C${(singleSkuMatrixByTab.after_pack.rows || []).length + instrIndex}`,
    );
    if (seq5) return seq5;

    return null;
  }

  const getMixedWaybillStatusText = useMemo(() => (row: any) => {
    const source = String(row?.waybill_source || '').trim().toLowerCase();
    const mode = String(row?.waybill_submit_mode || '').trim().toLowerCase();
    const carrier = String(row?.waybill_carrier || '').trim();
    if (source === 'upload' && mode === 'immediate' && carrier) return `已有运单${carrier}`;
    if (source === 'warehouse') return '仓库提供运单';
    if (source === 'upload' && mode === 'after_pack') return '先申请包装数据';
    return '-';
  }, []);

  const currentSingleSkuMatrix = singleSkuMatrixByTab[opAreaTab as keyof typeof singleSkuMatrixByTab] || singleSkuMatrixByTab.upload_immediate;
  const currentMixedRows = mixedInstructionRowsByTab[opAreaTab as keyof typeof mixedInstructionRowsByTab] || mixedInstructionRowsByTab.upload_immediate;

  const renderSortPositionTag = useCallback((text: string, completed: boolean) => (
    <span
      style={{
        display: 'inline-block',
        minWidth: 42,
        padding: '1px 8px',
        borderRadius: 4,
        border: `1px solid ${completed ? '#52c41a' : '#ff4d4f'}`,
        color: completed ? '#389e0d' : '#cf1322',
        background: completed ? '#f6ffed' : '#fff2f0',
        fontWeight: 700,
        lineHeight: 1.4,
      }}
    >
      {text}
    </span>
  ), []);

  const mixedSortPositionDoneMap = useMemo(() => {
    const map = new Map<number, boolean>();
    const grouped = new Map<number, any[]>();
    (currentMixedRows.rows || []).forEach((row: any) => {
      const idx = Number(row?.instrIndex || 0);
      if (!idx) return;
      if (!grouped.has(idx)) grouped.set(idx, []);
      grouped.get(idx)!.push(row);
    });
    grouped.forEach((rows, instrIndex) => {
      const sortingPosition = opAreaTab === 'upload_immediate'
        ? `A${instrIndex}`
        : opAreaTab === 'warehouse'
        ? `B${currentSingleSkuMatrix.rows.length + instrIndex}`
        : `C${currentSingleSkuMatrix.rows.length + instrIndex}`;
      const sourceTabName: '已有运单' | '仓库提供运单' | '先申请包装数据' = opAreaTab === 'upload_immediate'
        ? '已有运单'
        : opAreaTab === 'warehouse'
        ? '仓库提供运单'
        : '先申请包装数据';
      let hasTarget = false;
      let done = true;
      rows.forEach((row: any) => {
        const t = row?.target;
        if (!t) return;
        hasTarget = true;
        const snap = t?.snapshot || {};
        const iuCode = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
        const shippedQty = Number(t?.expected_qty ?? snap?.expected_qty ?? 0) || 0;
        const pickedQty = iuCode ? getPickedQty(sourceTabName, sortingPosition, iuCode) : 0;
        if (pickedQty !== shippedQty) done = false;
      });
      if (!hasTarget) done = false;
      map.set(instrIndex, done);
    });
    return map;
  }, [currentMixedRows.rows, currentSingleSkuMatrix.rows.length, getPickedQty, opAreaTab]);

  const singleSkuIuFilterNorm = singleSkuIuFilter.trim().toLowerCase();
  const mixedIuFilterNorm = mixedIuFilter.trim().toLowerCase();
  const singleSkuInstrCodeFilterNorm = singleSkuInstrCodeFilter.trim().toLowerCase();
  const mixedInstrCodeFilterNorm = mixedInstrCodeFilter.trim().toLowerCase();

  const singleSkuCarrierOptions = useMemo(() => {
    if (opAreaTab !== 'upload_immediate') return [];
    const cols = currentSingleSkuMatrix.dynamicCols || [];
    return cols
      .filter((c: any) => c.key?.startsWith('upload_immediate_'))
      .map((c: any) => ({
        label: c.carrierDisplayName ?? c.title ?? c.key,
        value: c.key,
      }));
  }, [opAreaTab, currentSingleSkuMatrix.dynamicCols]);

  const filteredSingleSkuRows = useMemo(() => {
    let list = currentSingleSkuMatrix.rows;
    if (opAreaTab === 'upload_immediate' && singleSkuCarrierFilter) {
      list = list.filter((r: any) => {
        const n = Number(r?.byWaybill?.[singleSkuCarrierFilter] ?? 0);
        return n > 0;
      });
    }
    if (singleSkuUnmarkedOnly) {
      list = list.filter((r: any) => {
        const total = Number(r?.totalOrders ?? 0);
        if (!Number.isFinite(total) || total <= 0) return false;
        const instrs = Array.isArray(r?.allInstructions) ? r.allInstructions : [];
        const processed = instrs.filter((i: any) => isProcessedInOperateConfirm(i)).length;
        return processed < total;
      });
    }
    if (singleSkuInstrCodeFilterNorm) {
      list = list.filter((r: any) => {
        const instrs = Array.isArray(r?.allInstructions) ? r.allInstructions : [];
        return instrs.some((i: any) => {
          const code = String(i?.instruction_system_code ?? i?.instruction?.instruction_system_code ?? i?.system_code ?? '').trim().toLowerCase();
          return !!code && code.includes(singleSkuInstrCodeFilterNorm);
        });
      });
    }
    if ((opAreaTab === 'warehouse' || opAreaTab === 'after_pack') && singleSkuSortStatusFilter) {
      const sourceTabName: '仓库提供运单' | '先申请包装数据' = opAreaTab === 'warehouse' ? '仓库提供运单' : '先申请包装数据';
      const rowIndexMap = new Map<string, number>();
      (currentSingleSkuMatrix.rows || []).forEach((r: any, idx: number) => {
        const key = String(r?.key || '');
        if (key) rowIndexMap.set(key, idx);
      });
      list = list.filter((r: any) => {
        const key = String(r?.key || '');
        if (!key || !rowIndexMap.has(key)) return false;
        const idx = rowIndexMap.get(key)!;
        const sortingPosition = opAreaTab === 'warehouse' ? `B${idx + 1}` : `C${idx + 1}`;
        const iuCode = String(r?.iuCode || '').trim();
        const total = Number(r?.totalShipQty ?? 0) || 0;
        const picked = iuCode ? getPickedQty(sourceTabName, sortingPosition, iuCode) : 0;
        const completed = total > 0 && picked === total;
        return singleSkuSortStatusFilter === 'completed' ? completed : !completed;
      });
    }
    if (!singleSkuIuFilterNorm) return list;
    return list.filter((r: any) => {
      const iu = String(r?.iuCode ?? '').trim().toLowerCase();
      const sortArr = Array.isArray(r?.sortBarcodes) ? r.sortBarcodes : [];
      const sortStr = sortArr.map((s: any) => String(s ?? '').trim().toLowerCase()).join(' ');
      return iu.includes(singleSkuIuFilterNorm) || sortStr.includes(singleSkuIuFilterNorm);
    });
  }, [
    currentSingleSkuMatrix.rows,
    isProcessedInOperateConfirm,
    opAreaTab,
    singleSkuCarrierFilter,
    singleSkuIuFilterNorm,
    singleSkuInstrCodeFilterNorm,
    singleSkuSortStatusFilter,
    singleSkuUnmarkedOnly,
    getPickedQty,
  ]);

  const filteredMixedRows = useMemo(() => {
    let list = currentMixedRows.rows;
    if (mixedUnmarkedOnly || mixedInstrCodeFilterNorm) {
      const allowedInstrIndices = new Set<number>();
      list.forEach((row: any) => {
        const instr = row?.instruction;
        if (!instr) return;
        if (mixedUnmarkedOnly && isProcessedInOperateConfirm(instr)) return;
        if (mixedInstrCodeFilterNorm) {
          const code = String(instr?.instruction_system_code ?? instr?.system_code ?? '').trim().toLowerCase();
          if (!code.includes(mixedInstrCodeFilterNorm)) return;
        }
        allowedInstrIndices.add(Number(row?.instrIndex || 0));
      });
      list = list.filter((row: any) => allowedInstrIndices.has(Number(row?.instrIndex || 0)));
    }
    if (mixedSortStatusFilter) {
      const sourceTabName: '已有运单' | '仓库提供运单' | '先申请包装数据' = opAreaTab === 'upload_immediate'
        ? '已有运单'
        : opAreaTab === 'warehouse'
        ? '仓库提供运单'
        : '先申请包装数据';
      const mixedSortingPosition = (instrIndex: number) => opAreaTab === 'upload_immediate'
        ? `A${instrIndex}`
        : opAreaTab === 'warehouse'
        ? `B${currentSingleSkuMatrix.rows.length + instrIndex}`
        : `C${currentSingleSkuMatrix.rows.length + instrIndex}`;
      const grouped = new Map<number, any[]>();
      list.forEach((row: any) => {
        const idx = Number(row?.instrIndex || 0);
        if (!idx) return;
        if (!grouped.has(idx)) grouped.set(idx, []);
        grouped.get(idx)!.push(row);
      });
      const allowedInstrIndices = new Set<number>();
      grouped.forEach((rows, idx) => {
        const sortingPosition = mixedSortingPosition(idx);
        let hasTarget = false;
        let completed = true;
        rows.forEach((row: any) => {
          const t = row?.target;
          if (!t) return;
          hasTarget = true;
          const snap = t?.snapshot || {};
          const iuCode = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
          const shippedQty = Number(t?.expected_qty ?? snap?.expected_qty ?? 0) || 0;
          const pickedQty = iuCode ? getPickedQty(sourceTabName, sortingPosition, iuCode) : 0;
          if (pickedQty !== shippedQty) completed = false;
        });
        if (!hasTarget) completed = false;
        if ((mixedSortStatusFilter === 'completed' && completed) || (mixedSortStatusFilter === 'uncompleted' && !completed)) {
          allowedInstrIndices.add(idx);
        }
      });
      list = list.filter((row: any) => allowedInstrIndices.has(Number(row?.instrIndex || 0)));
    }
    if (!mixedIuFilterNorm) return list;
    const matchInstrIndices = new Set<number>();
    list.forEach((row: any) => {
      const t = row?.target;
      if (!t) return;
      const snap = t?.snapshot || {};
      const iu = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim().toLowerCase();
      const sortArr = Array.isArray(t?.sorting_barcodes) ? t.sorting_barcodes.map((x: any) => x?.code ?? x) : (snap?.sorting_barcode ? [String(snap.sorting_barcode)] : []);
      const sortStr = sortArr.map((s: any) => String(s ?? '').trim().toLowerCase()).join(' ');
      if (iu.includes(mixedIuFilterNorm) || sortStr.includes(mixedIuFilterNorm)) {
        matchInstrIndices.add(row.instrIndex);
      }
    });
    return list.filter((row: any) => matchInstrIndices.has(row.instrIndex));
  }, [currentMixedRows.rows, currentSingleSkuMatrix.rows.length, getPickedQty, isProcessedInOperateConfirm, mixedInstrCodeFilterNorm, mixedIuFilterNorm, mixedSortStatusFilter, mixedUnmarkedOnly, opAreaTab]);

  const filteredMixedUniqueInstrIndices = useMemo(() => {
    const set = new Set<number>();
    filteredMixedRows.forEach((r: any) => set.add(r.instrIndex));
    return Array.from(set).sort((a, b) => a - b);
  }, [filteredMixedRows]);

  const pagedSingleSkuRows = useMemo(() => {
    const list = filteredSingleSkuRows;
    const start = (singleSkuPage - 1) * singleSkuPageSize;
    return list.slice(start, start + singleSkuPageSize);
  }, [filteredSingleSkuRows, singleSkuPage, singleSkuPageSize]);

  const pagedMixedRows = useMemo(() => {
    const indices = filteredMixedUniqueInstrIndices;
    const start = (mixedPage - 1) * mixedPageSize;
    const pageIndices = new Set(indices.slice(start, start + mixedPageSize));
    return filteredMixedRows.filter((row: any) => pageIndices.has(row.instrIndex));
  }, [filteredMixedRows, filteredMixedUniqueInstrIndices, mixedPage, mixedPageSize]);
  const singleSkuPickedShipSummary = useMemo(() => {
    const sourceTabName: '仓库提供运单' | '先申请包装数据' | '' = opAreaTab === 'warehouse'
      ? '仓库提供运单'
      : opAreaTab === 'after_pack'
      ? '先申请包装数据'
      : '';
    if (!sourceTabName) return { pickedQty: 0, shipQty: 0, done: false };
    const rowIndexMap = new Map<string, number>();
    (currentSingleSkuMatrix.rows || []).forEach((r: any, idx: number) => {
      const k = String(r?.key || '');
      if (k) rowIndexMap.set(k, idx);
    });
    let pickedQty = 0;
    let shipQty = 0;
    (filteredSingleSkuRows || []).forEach((row: any) => {
      const key = String(row?.key || '');
      const idx = rowIndexMap.get(key);
      if (idx == null) return;
      const sortingPosition = opAreaTab === 'warehouse' ? `B${idx + 1}` : `C${idx + 1}`;
      const iuCode = String(row?.iuCode || '').trim();
      const total = Number(row?.totalShipQty ?? 0) || 0;
      shipQty += total;
      pickedQty += iuCode ? getPickedQty(sourceTabName, sortingPosition, iuCode) : 0;
    });
    return { pickedQty, shipQty, done: shipQty > 0 && pickedQty === shipQty };
  }, [currentSingleSkuMatrix.rows, filteredSingleSkuRows, getPickedQty, opAreaTab]);
  const mixedPickedShipSummary = useMemo(() => {
    const sourceTabName: '已有运单' | '仓库提供运单' | '先申请包装数据' = opAreaTab === 'upload_immediate'
      ? '已有运单'
      : opAreaTab === 'warehouse'
      ? '仓库提供运单'
      : '先申请包装数据';
    const shipByUnitKey = new Map<string, number>();
    (filteredMixedRows || []).forEach((row: any) => {
      const t = row?.target;
      if (!t) return;
      const snap = t?.snapshot || {};
      const iuCode = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
      if (!iuCode) return;
      const instrIndex = Number(row?.instrIndex || 0);
      if (!instrIndex) return;
      const sortingPosition = opAreaTab === 'upload_immediate'
        ? `A${instrIndex}`
        : opAreaTab === 'warehouse'
        ? `B${currentSingleSkuMatrix.rows.length + instrIndex}`
        : `C${currentSingleSkuMatrix.rows.length + instrIndex}`;
      const unitKey = `${sortingPosition}|${iuCode.toLowerCase()}`;
      const shippedQty = Number(t?.expected_qty ?? snap?.expected_qty ?? 0) || 0;
      shipByUnitKey.set(unitKey, (shipByUnitKey.get(unitKey) || 0) + shippedQty);
    });
    let shipQty = 0;
    let pickedQty = 0;
    shipByUnitKey.forEach((sumShip, unitKey) => {
      const splitIdx = unitKey.indexOf('|');
      const sortingPosition = splitIdx >= 0 ? unitKey.slice(0, splitIdx) : '';
      const iuCode = splitIdx >= 0 ? unitKey.slice(splitIdx + 1) : '';
      shipQty += sumShip;
      pickedQty += (sortingPosition && iuCode) ? getPickedQty(sourceTabName, sortingPosition, iuCode) : 0;
    });
    return { pickedQty, shipQty, done: shipQty > 0 && pickedQty === shipQty };
  }, [currentSingleSkuMatrix.rows.length, filteredMixedRows, getPickedQty, opAreaTab]);
  const filteredMixedSelectableInstrKeys = useMemo(() => {
    const set = new Set<string>();
    filteredMixedRows.forEach((row: any) => {
      if (isProcessedInOperateConfirm(row?.instruction)) return;
      const k = getOperateConfirmInstrKey(row?.instruction);
      if (k) set.add(k);
    });
    return Array.from(set);
  }, [filteredMixedRows, getOperateConfirmInstrKey, isProcessedInOperateConfirm]);
  const pagedMixedSelectableInstrKeys = useMemo(() => {
    const set = new Set<string>();
    pagedMixedRows.forEach((row: any) => {
      if (isProcessedInOperateConfirm(row?.instruction)) return;
      const k = getOperateConfirmInstrKey(row?.instruction);
      if (k) set.add(k);
    });
    return Array.from(set);
  }, [getOperateConfirmInstrKey, isProcessedInOperateConfirm, pagedMixedRows]);
  const mixedSelectedInstrKeySet = useMemo(() => (
    new Set(mixedSelectedInstrKeys.map((k) => String(k)))
  ), [mixedSelectedInstrKeys]);
  const mixedPageAllChecked = pagedMixedSelectableInstrKeys.length > 0 && pagedMixedSelectableInstrKeys.every((k) => mixedSelectedInstrKeySet.has(k));
  const mixedPagePartiallyChecked = pagedMixedSelectableInstrKeys.some((k) => mixedSelectedInstrKeySet.has(k)) && !mixedPageAllChecked;

  useEffect(() => {
    setSingleSkuPage(1);
    setMixedPage(1);
    setSingleSkuCarrierFilter('');
  }, [opAreaTab]);

  useEffect(() => {
    setSingleSkuPage(1);
  }, [currentSingleSkuMatrix.rows.length, singleSkuCarrierFilter, singleSkuInstrCodeFilterNorm, singleSkuIuFilterNorm, singleSkuSortStatusFilter, singleSkuUnmarkedOnly]);

  useEffect(() => {
    const max = Math.max(1, Math.ceil(filteredSingleSkuRows.length / singleSkuPageSize));
    if (singleSkuPage > max) setSingleSkuPage(max);
  }, [filteredSingleSkuRows.length, singleSkuPage, singleSkuPageSize]);

  useEffect(() => {
    setMixedPage(1);
  }, [currentMixedRows.stats.orders, mixedInstrCodeFilterNorm, mixedIuFilterNorm, mixedSortStatusFilter, mixedUnmarkedOnly]);

  useEffect(() => {
    const max = Math.max(1, Math.ceil(filteredMixedUniqueInstrIndices.length / mixedPageSize));
    if (mixedPage > max) setMixedPage(max);
  }, [filteredMixedUniqueInstrIndices.length, mixedPage, mixedPageSize]);
  useEffect(() => {
    const allowed = new Set(filteredMixedSelectableInstrKeys);
    setMixedSelectedInstrKeys((prev) => prev.filter((k) => allowed.has(String(k))));
  }, [filteredMixedSelectableInstrKeys]);
  useEffect(() => {
    if (!mixedBatchModalOpen) return;
    setMixedBatchResultFilter('all');
    setMixedBatchTrackingFilter('');
    setMixedBatchSystemCodeFilter('');
    setMixedBatchOcrFilter('');
    setMixedBatchPage(1);
    setMixedBatchPageSize(10);
    setMixedBatchTipsOnly(false);
    setMixedBatchPrintOnly(false);
    setMixedBatchUnoperatedReasonModalOpen(false);
    setMixedBatchUnoperatedReasonInstr(null);
    setMixedBatchUnoperatedReasonInput('');
    setMixedBatchMarkAllUnoperatedModalOpen(false);
    setMixedBatchMarkAllUnoperatedReason('');
    setMixedBatchScanAreaVisible(false);
    setMixedBatchScanInputValue('');
    setMixedBatchScanMatchedCode('');
    setMixedBatchDraftScanMatchRecords([]);
    setMixedBatchScanRecordPage(1);
    setMixedBatchScanRecordPageSize(10);
    setMixedBatchPackingEstimateByInstrKey({});
    setMixedBatchPackingEstimateLoadingByInstrKey({});
  }, [mixedBatchModalOpen]);
  useEffect(() => {
    setLocalWaybillOverrideByInstrId({});
  }, [batchDetail?.id]);
  useEffect(() => {
    if (!mixedBatchScanAreaVisible) return;
    setTimeout(() => {
      try { mixedBatchScanInputRef.current?.focus?.(); } catch {}
    }, 0);
  }, [mixedBatchScanAreaVisible]);
  const normalizeBuyWaybillText = useCallback((v: any) => {
    const text = String(v ?? '').trim();
    if (!text) return '';
    const lower = text.toLowerCase();
    if (lower === 'none' || lower === 'null' || lower === 'undefined') return '';
    return text;
  }, []);
  useEffect(() => {
    if (!operateConfirmBuyWaybillModalOpen) return;
    const instr = operateConfirmBuyWaybillInstr;
    const carrier = normalizeBuyWaybillText(instr?.waybill_carrier ?? instr?.instruction?.waybill_carrier ?? '');
    const trackingNo = normalizeBuyWaybillText(instr?.waybill_tracking_no ?? instr?.instruction?.waybill_tracking_no ?? '');
    const amountRaw = instr?.buy_waybill_info?.charge_info?.amount ?? instr?.instruction?.buy_waybill_info?.charge_info?.amount;
    const currencyRaw = normalizeBuyWaybillText(instr?.buy_waybill_info?.charge_info?.currency ?? instr?.instruction?.buy_waybill_info?.charge_info?.currency ?? 'USD');
    const noteRaw = normalizeBuyWaybillText(instr?.buy_waybill_info?.charge_info?.note ?? instr?.instruction?.buy_waybill_info?.charge_info?.note ?? '');
    const waybillAtt = getWaybillAttachments(instr || {})[0];
    setOperateConfirmBuyWaybillCarrier(carrier);
    setOperateConfirmBuyWaybillTrackingNo(trackingNo);
    setOperateConfirmBuyWaybillChargeAmount(
      Number.isFinite(Number(amountRaw)) && String(amountRaw ?? '').trim() !== '' && Number(amountRaw) >= 0
        ? Number(amountRaw)
        : null,
    );
    setOperateConfirmBuyWaybillChargeCurrency(
      ['CNY', 'USD', 'EUR'].includes(currencyRaw) ? (currencyRaw as 'CNY' | 'USD' | 'EUR') : 'USD',
    );
    setOperateConfirmBuyWaybillChargeNote(noteRaw);
    if (waybillAtt) {
      const resolvedUrl = resolveAttachmentUrl(waybillAtt);
      setOperateConfirmBuyWaybillFileList([
        {
          uid: `existing-${waybillAtt?.id || 'waybill'}`,
          name: waybillAtt?.name || waybillAtt?.file_name || '已上传运单附件',
          status: 'done',
          url: resolvedUrl,
        } as any,
      ]);
    } else {
      setOperateConfirmBuyWaybillFileList([]);
    }
  }, [getWaybillAttachments, normalizeBuyWaybillText, operateConfirmBuyWaybillInstr, operateConfirmBuyWaybillModalOpen]);
  useEffect(() => {
    if (!operateConfirmBuyWaybillModalOpen) return;
    if (operateConfirmLogisticsProviderOptions.length > 0) return;
    (async () => {
      try {
        const resp = await request.get('/api/v1/logistics/providers/', { params: { is_active: 1, page_size: 1000 } });
        const list = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : []);
        const options = list
          .map((item: any) => {
            const code = String(item?.code || '').trim();
            const name = String(item?.name || '').trim();
            if (!code) return null;
            return {
              value: code,
              label: name ? `${code} (${name})` : code,
            };
          })
          .filter(Boolean) as Array<{ label: string; value: string }>;
        setOperateConfirmLogisticsProviderOptions(options);
      } catch {
        setOperateConfirmLogisticsProviderOptions([]);
      }
    })();
  }, [operateConfirmBuyWaybillModalOpen, operateConfirmLogisticsProviderOptions.length]);
  const getMixedBatchInstrKey = useCallback((instr: any): string => {
    const id = instr?.id ?? instr?.instruction?.id;
    if (id != null) return String(id);
    const code = String(instr?.instruction_system_code ?? instr?.instruction?.instruction_system_code ?? instr?.system_code ?? '').trim();
    if (code) return code;
    return `mixed_${Date.now()}_${Math.random()}`;
  }, []);
  const isMixedBatchDone = useCallback((instr: any): boolean => (
    mixedBatchDoneIds.has(getMixedBatchInstrKey(instr))
  ), [getMixedBatchInstrKey, mixedBatchDoneIds]);
  const isMixedBatchUnoperated = useCallback((instr: any): boolean => (
    mixedBatchUnoperatedIds.has(getMixedBatchInstrKey(instr))
  ), [getMixedBatchInstrKey, mixedBatchUnoperatedIds]);
  const hasMixedBatchPrintableAttachments = useCallback((instr: any): boolean => {
    const list = Array.isArray(instr?.attachments_detail) ? instr.attachments_detail : [];
    return list.some((att: any) => {
      if (isWaybillAttachment(att)) return false;
      const m = att?.meta;
      const parsed = typeof m === 'string' ? (() => { try { return JSON.parse(m); } catch { return {}; } })() : (m || {});
      const cfg = parsed?.print_config;
      const copies = Number(cfg?.copies ?? 0);
      return Number.isFinite(copies) && copies > 0;
    });
  }, [isWaybillAttachment]);
  const getMixedBatchPrintableAttachments = useCallback((instr: any): any[] => {
    const list = Array.isArray(instr?.attachments_detail) ? instr.attachments_detail : [];
    return list.filter((att: any) => {
      if (isWaybillAttachment(att)) return false;
      const m = att?.meta;
      const parsed = typeof m === 'string' ? (() => { try { return JSON.parse(m); } catch { return {}; } })() : (m || {});
      const cfg = parsed?.print_config;
      const copies = Number(cfg?.copies ?? 0);
      return Number.isFinite(copies) && copies > 0;
    });
  }, [isWaybillAttachment]);
  const getMixedBatchWaybillOcrText = useCallback((instr: any): string => {
    const atts = Array.isArray(instr?.attachments_detail) ? instr.attachments_detail : [];
    const waybillAtts = atts.filter((att: any) => isWaybillAttachment(att));
    return waybillAtts
      .map((att: any) => String(att?.parse_text ?? att?.ocr_text ?? '').trim())
      .filter((t: string) => t.length > 0)
      .join(' | ');
  }, [isWaybillAttachment]);
  useEffect(() => {
    if (!mixedBatchModalOpen) return;
    const list = mixedBatchSelectedItems || [];
    if (list.length === 0) {
      setMixedBatchPackingEstimateByInstrKey({});
      setMixedBatchPackingEstimateLoadingByInstrKey({});
      return;
    }
    const loadingMap: Record<string, boolean> = {};
    list.forEach((item) => {
      const key = getMixedBatchInstrKey(item?.instr);
      if (key) loadingMap[key] = true;
    });
    setMixedBatchPackingEstimateLoadingByInstrKey(loadingMap);
    let active = true;
    (async () => {
      const results: Record<string, { code: string; name: string } | null> = {};
      await Promise.all(list.map(async (item) => {
        const instr = item?.instr;
        const key = getMixedBatchInstrKey(instr);
        if (!instr || !key) return;
        try {
          results[key] = await fetchPackingEstimateForInstruction(instr);
        } catch {
          results[key] = null;
        }
      }));
      if (!active) return;
      setMixedBatchPackingEstimateByInstrKey(results);
      const doneLoading: Record<string, boolean> = {};
      Object.keys(loadingMap).forEach((k) => { doneLoading[k] = false; });
      setMixedBatchPackingEstimateLoadingByInstrKey(doneLoading);
    })();
    return () => { active = false; };
  }, [fetchPackingEstimateForInstruction, getMixedBatchInstrKey, mixedBatchModalOpen, mixedBatchSelectedItems]);
  const mixedBatchFilteredItems = useMemo(() => {
    let list = mixedBatchSelectedItems;
    const trackingKey = String(mixedBatchTrackingFilter || '').trim().toLowerCase();
    const codeKey = String(mixedBatchSystemCodeFilter || '').trim().toLowerCase();
    const ocrKey = String(mixedBatchOcrFilter || '').trim().toLowerCase();
    list = list.filter((item) => {
      const instr = item?.instr;
      if (!instr) return false;
      if (mixedBatchResultFilter === 'done' && !isMixedBatchDone(instr)) return false;
      if (mixedBatchResultFilter === 'unoperated' && !isMixedBatchUnoperated(instr)) return false;
      if (mixedBatchResultFilter === 'unmarked' && (isMixedBatchDone(instr) || isMixedBatchUnoperated(instr))) return false;
      const tracking = String(instr?.waybill_tracking_no ?? '').trim().toLowerCase();
      const code = String(instr?.instruction_system_code ?? instr?.instruction?.instruction_system_code ?? instr?.system_code ?? '').trim().toLowerCase();
      const ocrText = getMixedBatchWaybillOcrText(instr).toLowerCase();
      if (trackingKey && !tracking.includes(trackingKey)) return false;
      if (codeKey && !code.includes(codeKey)) return false;
      if (ocrKey && !ocrText.includes(ocrKey)) return false;
      if (mixedBatchTipsOnly && !String(instr?.execution_tips ?? '').trim()) return false;
      if (mixedBatchPrintOnly && !hasMixedBatchPrintableAttachments(instr)) return false;
      return true;
    });
    return list;
  }, [
    getMixedBatchWaybillOcrText,
    hasMixedBatchPrintableAttachments,
    isMixedBatchDone,
    isMixedBatchUnoperated,
    mixedBatchOcrFilter,
    mixedBatchPrintOnly,
    mixedBatchResultFilter,
    mixedBatchSelectedItems,
    mixedBatchSystemCodeFilter,
    mixedBatchTipsOnly,
    mixedBatchTrackingFilter,
  ]);
  useEffect(() => {
    setMixedBatchPage(1);
  }, [mixedBatchOcrFilter, mixedBatchPrintOnly, mixedBatchResultFilter, mixedBatchSystemCodeFilter, mixedBatchTipsOnly, mixedBatchTrackingFilter]);
  useEffect(() => {
    const max = Math.max(1, Math.ceil(mixedBatchFilteredItems.length / mixedBatchPageSize));
    if (mixedBatchPage > max) setMixedBatchPage(max);
  }, [mixedBatchFilteredItems.length, mixedBatchPage, mixedBatchPageSize]);
  const mixedBatchPagedItems = useMemo(() => {
    const start = (mixedBatchPage - 1) * mixedBatchPageSize;
    return mixedBatchFilteredItems.slice(start, start + mixedBatchPageSize);
  }, [mixedBatchFilteredItems, mixedBatchPage, mixedBatchPageSize]);
  const mixedBatchRows = useMemo(() => {
    const rows: any[] = [];
    mixedBatchPagedItems.forEach((item) => {
      const instr = item?.instr;
      const instrIndex = Number(item?.instrIndex || 0);
      const targets = (Array.isArray(instr?.targets_detail) ? instr.targets_detail : []).filter((t: any) => String(t?.target_type || '').trim() === 'inventory_unit');
      const rowSpan = Math.max(1, targets.length);
      if (targets.length === 0) {
        rows.push({ key: `${getMixedBatchInstrKey(instr)}-0`, instr, instrIndex, target: null, isFirstRow: true, rowSpan });
        return;
      }
      targets.forEach((t: any, idx: number) => {
        rows.push({
          key: `${getMixedBatchInstrKey(instr)}-${idx}`,
          instr,
          instrIndex,
          target: t,
          isFirstRow: idx === 0,
          rowSpan,
        });
      });
    });
    return rows;
  }, [getMixedBatchInstrKey, mixedBatchPagedItems]);
  const mixedBatchSummary = useMemo(() => {
    const total = mixedBatchSelectedItems.length;
    let done = 0;
    let unoperated = 0;
    mixedBatchSelectedItems.forEach((item) => {
      const instr = item?.instr;
      if (!instr) return;
      if (isMixedBatchDone(instr)) done += 1;
      else if (isMixedBatchUnoperated(instr)) unoperated += 1;
    });
    const unmarked = Math.max(0, total - done - unoperated);
    return { total, done, unoperated, unmarked };
  }, [isMixedBatchDone, isMixedBatchUnoperated, mixedBatchSelectedItems]);
  const mixedBatchDoneAllHaveIoc = useMemo(() => {
    const doneInstrs = (mixedBatchSelectedItems || [])
      .map((item) => item?.instr)
      .filter((instr: any) => !!instr && isMixedBatchDone(instr));
    return doneInstrs.every((instr: any) => hasCreatedIocForInstr(instr));
  }, [hasCreatedIocForInstr, isMixedBatchDone, mixedBatchSelectedItems]);
  const handleSetMixedBatchWaybillStatus = useCallback((instr: any, status: 'completed' | 'unoperated' | 'unmarked') => {
    const key = getMixedBatchInstrKey(instr);
    if (status === 'completed') {
      setMixedBatchDoneIds((prev) => { const n = new Set(prev); n.add(key); return n; });
      setMixedBatchUnoperatedIds((prev) => { const n = new Set(prev); n.delete(key); return n; });
      setMixedBatchUnoperatedReasons((prev) => { const o = { ...prev }; delete o[key]; return o; });
      return;
    }
    if (status === 'unoperated') {
      setMixedBatchUnoperatedReasonInstr(instr);
      setMixedBatchUnoperatedReasonInput(mixedBatchUnoperatedReasons[key] || '');
      setMixedBatchUnoperatedReasonModalOpen(true);
      return;
    }
    setMixedBatchDoneIds((prev) => { const n = new Set(prev); n.delete(key); return n; });
    setMixedBatchUnoperatedIds((prev) => { const n = new Set(prev); n.delete(key); return n; });
    setMixedBatchUnoperatedReasons((prev) => { const o = { ...prev }; delete o[key]; return o; });
  }, [getMixedBatchInstrKey, mixedBatchUnoperatedReasons]);
  const confirmMixedBatchUnoperatedReason = useCallback(() => {
    const instr = mixedBatchUnoperatedReasonInstr;
    if (!instr) return;
    const reason = String(mixedBatchUnoperatedReasonInput || '').trim();
    if (!reason) {
      message.warning('请输入无法操作原因');
      return;
    }
    const key = getMixedBatchInstrKey(instr);
    setMixedBatchDoneIds((prev) => { const n = new Set(prev); n.delete(key); return n; });
    setMixedBatchUnoperatedIds((prev) => { const n = new Set(prev); n.add(key); return n; });
    setMixedBatchUnoperatedReasons((prev) => ({ ...prev, [key]: reason }));
    setMixedBatchUnoperatedReasonModalOpen(false);
    setMixedBatchUnoperatedReasonInstr(null);
    setMixedBatchUnoperatedReasonInput('');
  }, [getMixedBatchInstrKey, mixedBatchUnoperatedReasonInput, mixedBatchUnoperatedReasonInstr]);
  const handleMixedBatchSubmit = useCallback(() => {
    const list = mixedBatchSelectedItems || [];
    if (list.length === 0) {
      message.info('无指令可提交');
      return;
    }
    if (opAreaTab === 'after_pack') {
      const doneInstrs = list
        .map((item) => item?.instr)
        .filter((instr: any) => !!instr && isMixedBatchDone(instr));
      const missingIoc = doneInstrs.filter((instr: any) => !hasCreatedIocForInstr(instr));
      if (missingIoc.length > 0) {
        message.warning(`仍有 ${missingIoc.length} 条“已完成”指令未创建指令产出容器，无法提交`);
        return;
      }
    }
    const hasUnmarked = list.some((item) => {
      const instr = item?.instr;
      if (!instr) return true;
      return !isMixedBatchDone(instr) && !isMixedBatchUnoperated(instr);
    });
    if (hasUnmarked) {
      message.warning('本次操作指令必须全部标注后才能提交');
      return;
    }
    const records: OperationRecord['instructions'] = [];
    const keysToCommit = list
      .map((item) => getOperateConfirmInstrKey(item?.instr))
      .filter((k: string) => !!k);
    list.forEach((item) => {
      const instr = item?.instr;
      if (!instr) return;
      const mixedKey = getMixedBatchInstrKey(instr);
      const done = isMixedBatchDone(instr);
      const unoperated = isMixedBatchUnoperated(instr);
      const error_reason = unoperated ? (mixedBatchUnoperatedReasons[mixedKey] || '') : undefined;
      records.push({
        system_code: String(instr?.instruction_system_code ?? instr?.instruction?.instruction_system_code ?? instr?.system_code ?? '').trim() || '-',
        status: done ? '已完成' : unoperated ? '无法操作' : '未标注',
        ...(error_reason ? { error_reason } : {}),
      });
    });
    if (records.length === 0) {
      message.info('无有效指令可提交');
      return;
    }

    setOperateConfirmMarkedDoneIds((prev) => {
      const n = new Set(prev);
      list.forEach((item) => {
        const instr = item?.instr;
        if (!instr) return;
        const operateKey = getOperateConfirmInstrKey(instr);
        if (!operateKey) return;
        if (isMixedBatchDone(instr)) n.add(operateKey);
        else n.delete(operateKey);
      });
      return n;
    });
    setOperateConfirmMarkedUnoperatedIds((prev) => {
      const n = new Set(prev);
      list.forEach((item) => {
        const instr = item?.instr;
        if (!instr) return;
        const operateKey = getOperateConfirmInstrKey(instr);
        if (!operateKey) return;
        if (isMixedBatchUnoperated(instr)) n.add(operateKey);
        else n.delete(operateKey);
      });
      return n;
    });
    setOperateConfirmMarkedUnmarkedIds((prev) => {
      const n = new Set(prev);
      list.forEach((item) => {
        const instr = item?.instr;
        if (!instr) return;
        const operateKey = getOperateConfirmInstrKey(instr);
        if (!operateKey) return;
        if (!isMixedBatchDone(instr) && !isMixedBatchUnoperated(instr)) n.add(operateKey);
        else n.delete(operateKey);
      });
      return n;
    });
    setOperateConfirmUnoperatedReasons((prev) => {
      const o = { ...prev };
      list.forEach((item) => {
        const instr = item?.instr;
        if (!instr) return;
        const operateKey = getOperateConfirmInstrKey(instr);
        const mixedKey = getMixedBatchInstrKey(instr);
        if (!operateKey) return;
        if (isMixedBatchUnoperated(instr)) {
          o[operateKey] = mixedBatchUnoperatedReasons[mixedKey] || '';
        } else {
          delete o[operateKey];
        }
      });
      return o;
    });

    appendOperationRecord(records, '波次操作');
    if (mixedBatchDraftScanMatchRecords.length > 0) {
      setOperateConfirmGlobalScanMatchRecords((prev) => [...mixedBatchDraftScanMatchRecords, ...prev]);
    }
    message.success(`已记录${records.length}条指令处理结果`);

    setMixedBatchModalOpen(false);
    setMixedBatchSelectedItems([]);
    setMixedBatchDoneIds(new Set());
    setMixedBatchUnoperatedIds(new Set());
    setMixedBatchUnoperatedReasons({});
    setMixedBatchScanAreaVisible(false);
    setMixedBatchScanInputValue('');
    setMixedBatchScanMatchedCode('');
    setMixedBatchDraftScanMatchRecords([]);
    setMixedBatchPackingEstimateByInstrKey({});
    setMixedBatchPackingEstimateLoadingByInstrKey({});
    setMixedBatchUnoperatedReasonModalOpen(false);
    setMixedBatchUnoperatedReasonInstr(null);
    setMixedBatchUnoperatedReasonInput('');
    setMixedBatchMarkAllUnoperatedModalOpen(false);
    setMixedBatchMarkAllUnoperatedReason('');
  }, [
    hasCreatedIocForInstr,
    appendOperationRecord,
    getMixedBatchInstrKey,
    getOperateConfirmInstrKey,
    isMixedBatchDone,
    isMixedBatchUnoperated,
    mixedBatchDraftScanMatchRecords,
    mixedBatchSelectedItems,
    mixedBatchUnoperatedReasons,
    opAreaTab,
  ]);
  const markMixedBatchInstructionCompleted = useCallback((instr: any) => {
    const key = getMixedBatchInstrKey(instr);
    setMixedBatchDoneIds((prev) => { const n = new Set(prev); n.add(key); return n; });
    setMixedBatchUnoperatedIds((prev) => { const n = new Set(prev); n.delete(key); return n; });
    setMixedBatchUnoperatedReasons((prev) => { const o = { ...prev }; delete o[key]; return o; });
  }, [getMixedBatchInstrKey]);
  const commitMixedBatchScanMatch = useCallback((scanValueRaw: string, instr: any, matchMethod: '跟踪号匹配' | '运单内容匹配') => {
    const code = getInstructionSystemCode(instr);
    markMixedBatchInstructionCompleted(instr);
    setMixedBatchDraftScanMatchRecords((prev) => [{
      time: new Date().toLocaleString('zh-CN'),
      scanValue: scanValueRaw,
      matchedInstruction: code,
      matchMethod,
    }, ...prev]);
    setMixedBatchScanRecordPage(1);
    setMixedBatchScanMatchedCode(code);
    setMixedBatchScanInputValue('');
    setTimeout(() => {
      try { mixedBatchScanInputRef.current?.focus?.(); } catch {}
    }, 0);
  }, [getInstructionSystemCode, markMixedBatchInstructionCompleted]);
  const submitMixedBatchScan = useCallback((skipDuplicateConfirm = false) => {
    const raw = String(mixedBatchScanInputValue || '').trim();
    setMixedBatchScanInputValue(raw);
    if (!raw) return;
    const duplicatedRecords = mixedBatchDraftScanMatchRecords.filter((r) => String(r?.scanValue || '').trim().toLowerCase() === raw.toLowerCase());
    if (duplicatedRecords.length > 0 && !skipDuplicateConfirm) {
      Modal.confirm({
        title: '重复提醒',
        width: 760,
        content: (
          <div>
            <div style={{ marginBottom: 8 }}>
              扫描值 <span style={{ color: '#1677ff', fontWeight: 600 }}>{raw}</span> 已存在于匹配记录，是否继续匹配？
            </div>
            <div style={{ maxHeight: 220, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 4 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 180 }}>时间</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 170 }}>扫描值</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, minWidth: 140 }}>匹配指令</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 130 }}>匹配方式</th>
                  </tr>
                </thead>
                <tbody>
                  {duplicatedRecords.map((rec, idx) => (
                    <tr key={`${rec.time}-${rec.scanValue}-${rec.matchedInstruction}-${idx}`} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: '#8c8c8c' }}>{rec.time}</td>
                      <td style={{ padding: '6px 8px', wordBreak: 'break-all' }}>{rec.scanValue}</td>
                      <td style={{ padding: '6px 8px', wordBreak: 'break-all' }}>{rec.matchedInstruction}</td>
                      <td style={{ padding: '6px 8px' }}>{rec.matchMethod}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ),
        okText: '继续',
        cancelText: '取消',
        onCancel: () => {
          setMixedBatchScanInputValue('');
          setTimeout(() => {
            try { mixedBatchScanInputRef.current?.focus?.(); } catch {}
          }, 0);
        },
        onOk: () => submitMixedBatchScan(true),
      });
      return;
    }
    const a = takeLast12(raw);
    const pendingUnmarked = mixedBatchSelectedItems
      .map((item) => item?.instr)
      .filter((instr: any) => !!instr && !isMixedBatchDone(instr) && !isMixedBatchUnoperated(instr));
    for (const instr of pendingUnmarked) {
      const trackingNo = String(instr?.waybill_tracking_no ?? instr?.instruction?.waybill_tracking_no ?? '').trim();
      if (!trackingNo) continue;
      const b = takeLast12(trackingNo);
      if (tailMatchIgnoreCase(a, b)) {
        commitMixedBatchScanMatch(raw, instr, '跟踪号匹配');
        return;
      }
    }
    for (const instr of pendingUnmarked) {
      const waybillAtts = getWaybillAttachments(instr);
      const ocrText = waybillAtts.map((att: any) => String(att?.parse_text ?? '').trim()).filter((t: string) => t.length > 0).join(' ').toLowerCase();
      if (ocrText && ocrText.includes(String(a).toLowerCase())) {
        commitMixedBatchScanMatch(raw, instr, '运单内容匹配');
        return;
      }
    }
    message.warning('未找到匹配指令');
    setMixedBatchScanMatchedCode('');
  }, [
    commitMixedBatchScanMatch,
    getWaybillAttachments,
    isMixedBatchDone,
    isMixedBatchUnoperated,
    mixedBatchDraftScanMatchRecords,
    mixedBatchScanInputValue,
    mixedBatchSelectedItems,
    tailMatchIgnoreCase,
    takeLast12,
  ]);
  const mixedBatchScanMatchedCount = useMemo(() => {
    const codeSet = new Set(
      mixedBatchSelectedItems
        .map((item) => getInstructionSystemCode(item?.instr))
        .filter((code: string) => !!code && code !== '-'),
    );
    if (codeSet.size === 0) return 0;
    const matchedCodeSet = new Set(
      mixedBatchDraftScanMatchRecords
        .map((rec) => String(rec?.matchedInstruction || '').trim())
        .filter((code) => codeSet.has(code)),
    );
    return matchedCodeSet.size;
  }, [getInstructionSystemCode, mixedBatchDraftScanMatchRecords, mixedBatchSelectedItems]);
  useEffect(() => {
    const total = mixedBatchDraftScanMatchRecords.length;
    const maxPage = Math.max(1, Math.ceil(total / mixedBatchScanRecordPageSize));
    if (mixedBatchScanRecordPage > maxPage) {
      setMixedBatchScanRecordPage(maxPage);
    }
  }, [mixedBatchDraftScanMatchRecords.length, mixedBatchScanRecordPage, mixedBatchScanRecordPageSize]);
  const applyWaybillOverlayToBlobWithSortingPrefix = useCallback(async (
    blob: Blob,
    ext: string,
    instr: any,
    sortingPosition: string,
  ): Promise<Blob> => {
    const baseText = buildWaybillOverlayText(instr);
    const baseShort = buildWaybillOverlayTextShort(instr);
    const prefix = `[${String(sortingPosition || '').trim() || '-'}]`;
    const configObj: Record<string, any> = {
      ...OVERLAY_CONFIG_WAYBILL,
      custom_text: `${prefix}${baseText}`,
      custom_text_fallback: `${prefix}${baseShort}`,
    };
    const base64 = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const s = (r.result as string) || '';
        resolve(s.includes(',') ? s.split(',')[1]! : s);
      };
      r.onerror = () => reject(new Error('文件读取失败'));
      r.readAsDataURL(blob);
    });
    const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(/\/$/, '');
    const overlayUrl = `${apiBase}/api/v1/logistics/label-overlay/`;
    const token = localStorage.getItem('token');
    const resp = await fetch(overlayUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        file_base64: base64,
        file_name: `attachment.${ext === 'pdf' ? 'pdf' : 'png'}`,
        config: configObj,
      }),
    });
    if (!resp.ok) {
      const errText = await resp.text();
      try {
        const errJson = JSON.parse(errText);
        throw new Error(errJson?.error || errJson?.detail || resp.statusText);
      } catch (e: any) {
        if (e?.message && e.message !== resp.statusText) throw e;
        throw new Error(errText || resp.statusText);
      }
    }
    return resp.blob();
  }, [buildWaybillOverlayText, buildWaybillOverlayTextShort]);
  const mergeMixedBatchWaybillAttachmentsToPdf = useCallback(async (
    items: Array<{ att: any; instr: any; sortingPosition: string }>,
  ): Promise<{ blob: Blob; pageCount: number; instructionPageCounts: Record<string, number> } | null> => {
    if (!items.length) return null;
    try {
      const pdfDoc = await PDFDocument.create();
      const instructionPageCounts: Record<string, number> = {};
      for (const { att, instr, sortingPosition } of items) {
        const url = resolveAttachmentUrl(att);
        if (!url) continue;
        const instrCode = getInstructionSystemCode(instr) || '-';
        const resp = await fetch(url, { credentials: 'include' });
        if (!resp.ok) continue;
        let ext = getExtLower(att);
        let blob = await resp.blob();
        if (instr && isWaybillAttachment(att)) {
          try {
            blob = await applyWaybillOverlayToBlobWithSortingPrefix(blob, ext, instr, sortingPosition);
            ext = blob.type?.includes('pdf') ? 'pdf' : 'png';
          } catch (e) {
            console.warn('[mergeMixedBatchWaybillPdf] overlay failed, use original:', e);
          }
        }
        const buf = await blob.arrayBuffer();
        if (ext === 'pdf') {
          const srcDoc = await PDFDocument.load(buf);
          const srcPageCount = srcDoc.getPageCount();
          if (srcPageCount > 0) {
            const indices = Array.from({ length: srcPageCount }, (_, i) => i);
            const copiedPages = await pdfDoc.copyPages(srcDoc, indices);
            copiedPages.forEach((p) => pdfDoc.addPage(p));
            instructionPageCounts[instrCode] = (instructionPageCounts[instrCode] || 0) + srcPageCount;
          }
        } else if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext)) {
          const bytes = new Uint8Array(buf);
          const tryPng = ['png', 'gif', 'webp', 'bmp'].includes(ext);
          let img: any;
          try {
            img = tryPng ? await pdfDoc.embedPng(bytes) : await pdfDoc.embedJpg(bytes);
          } catch {
            try {
              img = await pdfDoc.embedJpg(bytes);
            } catch {
              try {
                img = await pdfDoc.embedPng(bytes);
              } catch {}
            }
          }
          if (img) {
            const w = img.width;
            const h = img.height;
            const page = pdfDoc.addPage([w, h]);
            (page.drawImage as (img: any, opts: any) => void)(img, { x: 0, y: 0, width: w, height: h });
            instructionPageCounts[instrCode] = (instructionPageCounts[instrCode] || 0) + 1;
          }
        }
      }
      const pageCount = pdfDoc.getPageCount();
      if (pageCount === 0) return null;
      const mergedBytes = await pdfDoc.save();
      const outBlob = new Blob([mergedBytes as BlobPart], { type: 'application/pdf' });
      return { blob: outBlob, pageCount, instructionPageCounts };
    } catch (e) {
      console.error('[mergeMixedBatchWaybillPdf]', e);
      return null;
    }
  }, [applyWaybillOverlayToBlobWithSortingPrefix, getExtLower, getInstructionSystemCode, isWaybillAttachment, resolveAttachmentUrl]);
  const handleMixedBatchMergeWaybill = useCallback(async () => {
    const sorted = [...mixedBatchSelectedItems].sort((a, b) => Number(a?.instrIndex || 0) - Number(b?.instrIndex || 0));
    if (sorted.length === 0) {
      message.warning('当前无可合并指令');
      return;
    }
    const toMerge: Array<{ att: any; instr: any; sortingPosition: string }> = [];
    sorted.forEach((item) => {
      const instr = item?.instr;
      const instrIndex = Number(item?.instrIndex || 0);
      const sortingPosition = opAreaTab === 'upload_immediate'
        ? `A${instrIndex}`
        : opAreaTab === 'warehouse'
          ? `B${currentSingleSkuMatrix.rows.length + instrIndex}`
          : `C${currentSingleSkuMatrix.rows.length + instrIndex}`;
      getWaybillAttachments(instr).forEach((att: any) => toMerge.push({ att, instr, sortingPosition }));
    });
    if (toMerge.length === 0) {
      message.warning('未找到可合并的运单附件');
      return;
    }
    setMixedBatchMergeLoading(true);
    setMergedPreviewTitle('合并运单预览');
    setMergedPreviewSource('waybill');
    setMergedWaybillPdfLoading(true);
    setMergedWaybillPdfOpen(true);
    try {
      const result = await mergeMixedBatchWaybillAttachmentsToPdf(toMerge);
      if (!result || !result.blob || result.blob.size === 0) {
        message.error('合并运单 PDF 失败');
        setMergedWaybillPdfOpen(false);
        return;
      }
      if (mergedWaybillPdfUrlRef.current) {
        try { URL.revokeObjectURL(mergedWaybillPdfUrlRef.current); } catch {}
        mergedWaybillPdfUrlRef.current = null;
      }
      const url = URL.createObjectURL(result.blob);
      mergedWaybillPdfUrlRef.current = url;
      setMergedWaybillPdfUrl(url);
      const badInstrs = sorted
        .map((item) => {
          const instr = item?.instr;
          const code = getInstructionSystemCode(instr) || '-';
          const pages = Number(result?.instructionPageCounts?.[code] ?? 0);
          return { code, pages };
        })
        .filter((x: { code: string; pages: number }) => x.pages !== 1);
      if (badInstrs.length > 0) {
        setTimeout(() => {
          Modal.warning({
            title: '运单页数提醒',
            content: (
              <div>
                <div style={{ marginBottom: 8 }}>以下指令的运单附件页数不是 1 页：</div>
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {badInstrs.map((x: { code: string; pages: number }, idx: number) => (
                    <div key={`${x.code}-${idx}`}>{x.code}（{x.pages}页）</div>
                  ))}
                </div>
              </div>
            ),
          });
        }, 80);
      }
    } catch (e: any) {
      message.error(e?.message || '合并运单 PDF 失败');
      setMergedWaybillPdfOpen(false);
    } finally {
      setMergedWaybillPdfLoading(false);
      setMixedBatchMergeLoading(false);
    }
  }, [currentSingleSkuMatrix.rows.length, getInstructionSystemCode, getWaybillAttachments, mergeMixedBatchWaybillAttachmentsToPdf, mixedBatchSelectedItems, opAreaTab]);
  const submitMixedBatchPackData = useCallback(async () => {
    const l = Number(mixedBatchPackDataLengthCm ?? 0);
    const w = Number(mixedBatchPackDataWidthCm ?? 0);
    const h = Number(mixedBatchPackDataHeightCm ?? 0);
    const g = Number(mixedBatchPackDataWeightG ?? 0);
    if (!Number.isInteger(l) || !Number.isInteger(w) || !Number.isInteger(h) || !Number.isInteger(g) || l <= 0 || w <= 0 || h <= 0 || g <= 0) {
      message.warning('请填写大于0的整数（长宽高cm、重量g）');
      return;
    }
    const instructionIds = Array.from(new Set(
      (mixedBatchSelectedItems || [])
        .map((item: any) => Number(item?.instr?.instruction ?? item?.instr?.instruction_id ?? item?.instr?.id ?? 0))
        .filter((id: number) => Number.isFinite(id) && id > 0),
    ));
    if (instructionIds.length === 0) {
      message.warning('当前无可更新的指令');
      return;
    }
    const payload = {
      actual_pack_length_cm: l,
      actual_pack_width_cm: w,
      actual_pack_height_cm: h,
      actual_gross_weight_g: g,
      pack_result_status: 'provided',
    } as const;
    setMixedBatchPackDataSaving(true);
    try {
      const results = await Promise.allSettled(
        instructionIds.map((instructionId) => request.patch(`/api/v1/instruction/instructions/${instructionId}/`, payload)),
      );
      let success = 0;
      let failed = 0;
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') {
          success += 1;
          applyLocalActualPackResultPatch(instructionIds[idx], payload);
        } else {
          failed += 1;
        }
      });
      if (success === 0) {
        message.error('包装数据保存失败');
        return;
      }
      setMixedBatchSelectedItems((prev) => (Array.isArray(prev) ? prev.map((item: any) => {
        const id = Number(item?.instr?.instruction ?? item?.instr?.instruction_id ?? item?.instr?.id ?? 0);
        if (!id || !instructionIds.includes(id)) return item;
        return {
          ...item,
          instr: {
            ...(item?.instr || {}),
            ...payload,
          },
        };
      }) : prev));
      if (failed > 0) message.warning(`包装数据已保存${success}条，失败${failed}条`);
      else message.success(`包装数据已保存${success}条`);
      setMixedBatchPackDataModalOpen(false);
      setMixedBatchPackDataLengthCm(null);
      setMixedBatchPackDataWidthCm(null);
      setMixedBatchPackDataHeightCm(null);
      setMixedBatchPackDataWeightG(null);
    } finally {
      setMixedBatchPackDataSaving(false);
    }
  }, [
    applyLocalActualPackResultPatch,
    mixedBatchPackDataHeightCm,
    mixedBatchPackDataLengthCm,
    mixedBatchPackDataWeightG,
    mixedBatchPackDataWidthCm,
    mixedBatchSelectedItems,
  ]);
  const createInstructionOutputContainersForMixedBatchAfterPack = useCallback(async () => {
    if (mixedBatchCreateIocLoading) return;
    const doneInstrs = (mixedBatchSelectedItems || [])
      .map((item) => item?.instr)
      .filter((instr: any) => !!instr && isMixedBatchDone(instr));
    if (doneInstrs.length === 0) {
      message.warning('当前无“已完成”指令可创建产出容器');
      return;
    }
    const hasPackData = (instr: any) => {
      const l = Number(instr?.actual_pack_length_cm ?? instr?.instruction?.actual_pack_length_cm ?? 0);
      const w = Number(instr?.actual_pack_width_cm ?? instr?.instruction?.actual_pack_width_cm ?? 0);
      const h = Number(instr?.actual_pack_height_cm ?? instr?.instruction?.actual_pack_height_cm ?? 0);
      const g = Number(instr?.actual_gross_weight_g ?? instr?.instruction?.actual_gross_weight_g ?? 0);
      return l > 0 && w > 0 && h > 0 && g > 0;
    };
    const missingPack = doneInstrs.filter((instr: any) => !hasPackData(instr));
    if (missingPack.length > 0) {
      message.warning(`仍有 ${missingPack.length} 条“已完成”指令缺少包装数据`);
      return;
    }
    setMixedBatchCreateIocLoading(true);
    const successRows: Array<{ instructionId: number; instructionCode: string; iocCode: string; itemKinds: number; totalQty: number; updated: boolean }> = [];
    const failRows: Array<{ instructionCode: string; reason: string }> = [];
    const labelBlobs: Blob[] = [];
    try {
      for (const instr of doneInstrs) {
        const instructionId = Number(instr?.instruction ?? instr?.instruction_id ?? instr?.id ?? 0);
        const instructionCode = getInstructionSystemCode(instr);
        if (!Number.isFinite(instructionId) || instructionId <= 0) {
          failRows.push({ instructionCode: instructionCode || '-', reason: '缺少有效 instruction_id' });
          continue;
        }
        const patchPayload = {
          actual_pack_length_cm: Math.round(Number(instr?.actual_pack_length_cm ?? instr?.instruction?.actual_pack_length_cm ?? 0)),
          actual_pack_width_cm: Math.round(Number(instr?.actual_pack_width_cm ?? instr?.instruction?.actual_pack_width_cm ?? 0)),
          actual_pack_height_cm: Math.round(Number(instr?.actual_pack_height_cm ?? instr?.instruction?.actual_pack_height_cm ?? 0)),
          actual_gross_weight_g: Math.round(Number(instr?.actual_gross_weight_g ?? instr?.instruction?.actual_gross_weight_g ?? 0)),
          pack_result_status: 'provided',
        };
        if (!(patchPayload.actual_pack_length_cm > 0 && patchPayload.actual_pack_width_cm > 0 && patchPayload.actual_pack_height_cm > 0 && patchPayload.actual_gross_weight_g > 0)) {
          failRows.push({ instructionCode, reason: '包装数据无效' });
          continue;
        }
        const qtyByIu: Record<number, number> = {};
        const iuCodeById: Record<number, string> = {};
        const targets = Array.isArray(instr?.targets_detail) ? instr.targets_detail : [];
        targets.forEach((t: any) => {
          const iuId = Number(t?.target?.inventory_unit_id ?? t?.inventory_unit_id ?? 0);
          const qty = Math.floor(Number(t?.expected_qty ?? t?.target_qty ?? 0) || 0);
          if (!Number.isFinite(iuId) || iuId <= 0 || !Number.isFinite(qty) || qty <= 0) return;
          qtyByIu[iuId] = Number(qtyByIu[iuId] || 0) + qty;
          const iuCode = String(
            t?.snapshot?.system_barcode
            ?? t?.inventory_unit_system_barcode
            ?? t?.target?.inventory_unit_system_barcode
            ?? t?.fingerprint
            ?? ''
          ).trim();
          if (iuCode) iuCodeById[iuId] = iuCode;
        });
        const items = Object.entries(qtyByIu).map(([iuId, qty]) => ({
          inventory_unit_id: Number(iuId),
          quantity: Number(qty),
        })).filter((x) => Number.isFinite(x.inventory_unit_id) && x.inventory_unit_id > 0 && Number.isFinite(x.quantity) && x.quantity > 0);
        if (items.length === 0) {
          failRows.push({ instructionCode, reason: '缺少可写入 IOC 的库存单元与数量' });
          continue;
        }
        try {
          let iocCode = String(
            operateConfirmIocCodeByInstrId[String(instructionId)]
            || instr?.created_ioc_code
            || instr?.instruction?.created_ioc_code
            || '',
          ).trim();
          if (!iocCode) {
            try {
              const listResp = await request.get('/api/v1/storage/output-containers/', {
                params: { instruction_id: instructionId, t: Date.now() } as any,
              });
              const list = Array.isArray(listResp?.data?.results) ? listResp.data.results : [];
              iocCode = String(list?.[0]?.code || '').trim();
            } catch {}
          }
          const updatingExisting = !!iocCode;
          if (!iocCode) {
            const reserveResp = await request.post('/api/v1/storage/output-containers/reserve-code/', {
              instruction_id: instructionId,
              container_type: 'box',
            });
            iocCode = String((reserveResp as any)?.data?.code || '').trim();
            if (!iocCode) throw new Error('预生成 IOC 编码失败');
          }
          const finalizePayload: any = {
            instruction_id: instructionId,
            code: iocCode,
            container_type: 'box',
            status: 'closed',
            length_cm: patchPayload.actual_pack_length_cm,
            width_cm: patchPayload.actual_pack_width_cm,
            height_cm: patchPayload.actual_pack_height_cm,
            weight_g: patchPayload.actual_gross_weight_g,
            defer_inventory_settlement: true,
          };
          if (!updatingExisting) finalizePayload.items = items;
          await request.post('/api/v1/storage/output-containers/finalize/', finalizePayload);
          setOperateConfirmIocCodeByInstrId((prev) => ({ ...prev, [String(instructionId)]: iocCode }));
          try {
            await request.patch(`/api/v1/instruction/instructions/${instructionId}/`, patchPayload);
            applyLocalActualPackResultPatch(instructionId, patchPayload);
          } catch {
            message.warning(`${instructionCode} 已创建IOC，但回写包装结果失败`);
          }
          const totalQty = items.reduce((sum, it) => sum + Number(it.quantity || 0), 0);
          successRows.push({ instructionId, instructionCode, iocCode, itemKinds: items.length, totalQty, updated: updatingExisting });
          try {
            const labelPayload: any = {
              instruction_id: instructionId,
              container_code: iocCode,
              instruction_code: instructionCode,
              project_summary: String(instr?.project_name ?? '-').trim() || '-',
              container_type: 'box',
              container_type_display: '纸箱',
              created_at: new Date().toLocaleString(),
              dims_cm: {
                l: patchPayload.actual_pack_length_cm,
                w: patchPayload.actual_pack_width_cm,
                h: patchPayload.actual_pack_height_cm,
              },
              dims_in: {
                l: Number((Number(patchPayload.actual_pack_length_cm || 0) / 2.54).toFixed(2)),
                w: Number((Number(patchPayload.actual_pack_width_cm || 0) / 2.54).toFixed(2)),
                h: Number((Number(patchPayload.actual_pack_height_cm || 0) / 2.54).toFixed(2)),
              },
              weight: {
                kg: Number((Number(patchPayload.actual_gross_weight_g || 0) / 1000).toFixed(3)),
                lb: Number((Number(patchPayload.actual_gross_weight_g || 0) / 453.59237).toFixed(3)),
              },
              new_kinds: items.length,
              total_qty: totalQty,
              details: items.slice(0, 20).map((it) => ({
                new_code: String(iuCodeById[it.inventory_unit_id] || it.inventory_unit_id),
                qty: Number(it.quantity || 0),
              })),
            };
            const labelResp = await request.post('/api/v1/storage/output-containers/labels/preview/', labelPayload, { responseType: 'blob' as any });
            const labelBlob = (labelResp as any)?.data as Blob;
            if (labelBlob && Number(labelBlob.size || 0) > 0) labelBlobs.push(labelBlob);
          } catch {}
        } catch (e: any) {
          failRows.push({
            instructionCode,
            reason: String(e?.response?.data?.detail || e?.message || '创建 IOC 失败'),
          });
        }
      }
      if (successRows.length > 0) {
        appendOperationRecord(
          successRows.map((r) => ({
            system_code: r.instructionCode,
            status: r.updated ? '已更新IOC' : '已创建IOC',
            error_reason: `IOC:${r.iocCode}`,
          })),
          '波次操作',
        );
      }
      if (labelBlobs.length > 0) {
        mergedWaybillBatchInstrsRef.current = [];
        setMergedWaybillAutoPrint(false);
        setMergedPreviewTitle('合并IOC标签预览');
        setMergedPreviewSource('ioc');
        if (mergedWaybillPdfUrlRef.current) {
          try { URL.revokeObjectURL(mergedWaybillPdfUrlRef.current); } catch {}
          mergedWaybillPdfUrlRef.current = null;
        }
        setMergedWaybillPdfUrl('');
        setMergedWaybillPdfLoading(true);
        setMergedWaybillPdfOpen(true);
        try {
          const mergedBlob = await mergePdfBlobs(labelBlobs);
          if (!mergedBlob || Number(mergedBlob.size || 0) <= 0) {
            message.warning('IOC 标签已生成，但合并预览失败');
            setMergedWaybillPdfOpen(false);
          } else {
            const mergedUrl = URL.createObjectURL(mergedBlob);
            mergedWaybillPdfUrlRef.current = mergedUrl;
            setMergedWaybillPdfUrl(mergedUrl);
          }
        } finally {
          setMergedWaybillPdfLoading(false);
        }
      }
      const createdCount = successRows.filter((x) => !x.updated).length;
      const updatedCount = successRows.filter((x) => x.updated).length;
      if (successRows.length > 0 && failRows.length === 0) {
        message.success(`已创建 ${createdCount} 条，已更新 ${updatedCount} 条`);
      } else if (successRows.length > 0 && failRows.length > 0) {
        message.warning(`已创建 ${createdCount} 条，已更新 ${updatedCount} 条，失败 ${failRows.length} 条`);
      } else {
        message.error('创建指令产出容器失败');
      }
      if (failRows.length > 0) {
        Modal.warning({
          title: '部分指令创建 IOC 失败',
          content: (
            <div style={{ maxHeight: 320, overflowY: 'auto', lineHeight: 1.8 }}>
              {failRows.slice(0, 100).map((r, idx) => (
                <div key={`${r.instructionCode}-${idx}`}>{`${idx + 1}. ${r.instructionCode}：${r.reason}`}</div>
              ))}
            </div>
          ),
        });
      }
    } finally {
      setMixedBatchCreateIocLoading(false);
    }
  }, [appendOperationRecord, applyLocalActualPackResultPatch, getInstructionSystemCode, isMixedBatchDone, mergePdfBlobs, mixedBatchCreateIocLoading, mixedBatchSelectedItems, operateConfirmIocCodeByInstrId]);
  const handleMixedBatchPrintableAttachmentPreview = useCallback(async (att: any, instr: any, sortingPositionRaw: string) => {
    const sortingPosition = String(sortingPositionRaw || '').trim();
    const url = resolveAttachmentUrl(att);
    if (!url) { message.warning('无可用文件'); return; }
    const extLower = getExtLower(att);
    const isImg = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extLower);
    const isPdf = extLower === 'pdf';
    if (isImg || isPdf) {
      if (instr) {
        const isWaybill = isWaybillAttachment(att);
        const baseOverlayText = isWaybill ? buildWaybillOverlayText(instr) : buildPrintOverlayText(instr, att);
        const overlayText = `[${sortingPosition || '-'}] ${baseOverlayText}`;
        const baseConfig = isWaybill ? OVERLAY_CONFIG_WAYBILL : OVERLAY_CONFIG_OTHER;
        const configObj: Record<string, any> = { ...baseConfig, custom_text: overlayText };
        if (isWaybill) configObj.custom_text_fallback = `[${sortingPosition || '-'}] ${buildWaybillOverlayTextShort(instr)}`;
        try {
          setAttPreviewLoading(true);
          setAttPreviewOpen(true);
          setAttPreviewTitle(getDisplayName(att) || '附件预览');
          setAttPreviewTitlePrintSuffix(formatPrintConfigForTitle(att));
          const blob = await fetch(url, { credentials: 'include' }).then((r) => r.blob());
          const ext = extLower === 'pdf' ? 'pdf' : 'png';
          const base64 = await new Promise<string>((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => {
              const s = (r.result as string) || '';
              resolve(s.includes(',') ? s.split(',')[1]! : s);
            };
            r.onerror = () => reject(new Error('文件读取失败'));
            r.readAsDataURL(blob);
          });
          const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(/\/$/, '');
          const overlayUrl = `${apiBase}/api/v1/logistics/label-overlay/`;
          const token = localStorage.getItem('token');
          const resp = await fetch(overlayUrl, {
            method: 'POST',
            credentials: 'include',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ file_base64: base64, file_name: `attachment.${ext}`, config: configObj }),
          });
          if (!resp.ok) {
            const errText = await resp.text();
            try {
              const errJson = JSON.parse(errText);
              throw new Error(errJson?.error || errJson?.detail || resp.statusText);
            } catch (e: any) {
              if (e?.message && e.message !== resp.statusText) throw e;
              throw new Error(errText || resp.statusText);
            }
          }
          const overlayBlob = await resp.blob();
          if (attPreviewObjectUrlRef.current) {
            try { URL.revokeObjectURL(attPreviewObjectUrlRef.current); } catch {}
            attPreviewObjectUrlRef.current = null;
          }
          const objUrl = URL.createObjectURL(overlayBlob);
          attPreviewObjectUrlRef.current = objUrl;
          setAttPreviewUrl(objUrl);
          setAttPreviewText('');
          setAttPreviewKind('pdf');
          message.success(`已添加打印标识：${overlayText}`);
        } catch (e: any) {
          message.error(e?.message || '添加打印标识失败，将预览原文件');
          openAttachmentPreview(att);
        } finally {
          setAttPreviewLoading(false);
        }
      } else {
        openAttachmentPreview(att);
      }
      return;
    }
    try {
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) throw new Error(resp.statusText);
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const fileName = String(getDisplayName(att) || att?.file_name || 'download').trim() || 'download';
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
      message.success('下载成功');
    } catch (e: any) {
      message.error(e?.message || '下载失败');
    }
  }, [
    buildPrintOverlayText,
    buildWaybillOverlayText,
    buildWaybillOverlayTextShort,
    formatPrintConfigForTitle,
    getDisplayName,
    getExtLower,
    isWaybillAttachment,
    openAttachmentPreview,
    resolveAttachmentUrl,
  ]);

  useEffect(() => {
    if (!mergedWaybillAutoPrint || !mergedWaybillPdfUrl) return;
    setMergedWaybillAutoPrint(false);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = mergedWaybillPdfUrl;
    document.body.appendChild(iframe);
    const closePreview = () => {
      setMergedWaybillPdfOpen(false);
      setMergedWaybillPdfUrl('');
      if (mergedWaybillPdfUrlRef.current) {
        try { URL.revokeObjectURL(mergedWaybillPdfUrlRef.current); } catch {}
        mergedWaybillPdfUrlRef.current = null;
      }
      try { document.body.removeChild(iframe); } catch {}
    };
    iframe.onload = () => {
      const win = iframe.contentWindow;
      if (win) {
        win.addEventListener('afterprint', closePreview);
        try { win.print(); } catch {}
      } else {
        setTimeout(closePreview, 2000);
      }
    };
  }, [mergedWaybillAutoPrint, mergedWaybillPdfUrl]);

  const renderMatrixTabContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ minWidth: 0, overflowX: 'auto', border: '2px solid #52c41a', borderRadius: 6, padding: 8, width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 600 }}>单品指令</span>
            {currentSingleSkuMatrix.rows.length > 0 ? (
              <span style={{ marginLeft: 12, fontWeight: 'normal', color: '#666', fontSize: 14 }}>
                共{currentSingleSkuMatrix.stats.orders}单{currentSingleSkuMatrix.stats.qty}件 涉及库存单元{currentSingleSkuMatrix.stats.iuCount}种
              </span>
            ) : null}
            {(opAreaTab === 'warehouse' || opAreaTab === 'after_pack') && currentSingleSkuMatrix.rows.length > 0 ? (
              <span
                style={{
                  marginLeft: 10,
                  display: 'inline-block',
                  padding: '1px 10px',
                  borderRadius: 6,
                  border: `1px solid ${singleSkuPickedShipSummary.done ? '#52c41a' : '#ff4d4f'}`,
                  background: singleSkuPickedShipSummary.done ? '#52c41a' : '#ff4d4f',
                  color: '#fff',
                  fontSize: 12,
                  lineHeight: '20px',
                  fontWeight: 600,
                }}
              >
                已分拣{singleSkuPickedShipSummary.pickedQty}件/发货总数量{singleSkuPickedShipSummary.shipQty}件
              </span>
            ) : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {opAreaTab === 'upload_immediate' && singleSkuCarrierOptions.length > 0 ? (
              <Select
                placeholder="承运商"
                allowClear
                value={singleSkuCarrierFilter || undefined}
                onChange={(v) => setSingleSkuCarrierFilter(v ?? '')}
                options={singleSkuCarrierOptions}
                style={{ width: 120 }}
              />
            ) : null}
            <Input
              placeholder="指令编码"
              allowClear
              value={singleSkuInstrCodeFilter}
              onChange={(e) => setSingleSkuInstrCodeFilter(e.target.value)}
              style={{ width: 140 }}
            />
            {opAreaTab === 'warehouse' || opAreaTab === 'after_pack' ? (
              <Select
                placeholder="分拣状态"
                allowClear
                value={singleSkuSortStatusFilter || undefined}
                onChange={(v) => setSingleSkuSortStatusFilter(String(v || ''))}
                options={[
                  { label: '已完成分拣', value: 'completed' },
                  { label: '未完成分拣', value: 'uncompleted' },
                ]}
                style={{ width: 140 }}
              />
            ) : null}
            <Input
              placeholder="库存单元"
              allowClear
              value={singleSkuIuFilter}
              onChange={(e) => setSingleSkuIuFilter(e.target.value)}
              style={{ width: 140 }}
            />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <Switch size="small" checked={singleSkuUnmarkedOnly} onChange={setSingleSkuUnmarkedOnly} />
              <span>仅显示未标注</span>
            </span>
          </div>
        </div>
        {currentSingleSkuMatrix.rows.length > 0 ? (
          <div style={{ overflowX: 'auto', minWidth: 0 }}>
            <Table
              size="small"
              rowKey="key"
              dataSource={pagedSingleSkuRows}
              rowClassName={(record: any) => {
                const total = Number(record?.totalOrders ?? 0);
                if (total <= 0) return '';
                const instrs = Array.isArray(record?.allInstructions) ? record.allInstructions : [];
                const processed = instrs.filter((i: any) => isProcessedInOperateConfirm(i)).length;
                return processed >= total ? 'single-sku-row-completed' : '';
              }}
              columns={[
                ...(opAreaTab === 'warehouse' || opAreaTab === 'after_pack'
                  ? [
                      {
                        title: '分拣位',
                        key: 'sortPosition',
                        width: 70,
                        align: 'center' as const,
                        render: (_: any, row: any) => {
                          const rowIndex = currentSingleSkuMatrix.rows.findIndex((r: any) => r?.key === row?.key);
                          const num = (rowIndex >= 0 ? rowIndex : 0) + 1;
                          const sortingPosition = opAreaTab === 'warehouse' ? `B${num}` : `C${num}`;
                          const sourceTabName: '仓库提供运单' | '先申请包装数据' = opAreaTab === 'warehouse' ? '仓库提供运单' : '先申请包装数据';
                          const iuCode = String(row?.iuCode || '').trim();
                          const total = Number(row?.totalShipQty ?? 0) || 0;
                          const picked = iuCode ? getPickedQty(sourceTabName, sortingPosition, iuCode) : 0;
                          const done = total > 0 && picked === total;
                          return renderSortPositionTag(sortingPosition, done);
                        },
                      },
                    ]
                  : []),
                {
                  title: '库存单元',
                  dataIndex: 'iuCode',
                  key: 'iuCode',
                  width: 140,
                  align: 'center' as const,
                  render: (v: any) => {
                    const code = displayDash(v);
                    if (code === '-') return '-';
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                        <span>{code}</span>
                        <CopyOutlined
                          style={{ cursor: 'pointer', fontSize: 12 }}
                          onClick={async (e) => {
                            e?.stopPropagation?.();
                            try {
                              await navigator.clipboard.writeText(String(code));
                              message.success('已复制');
                            } catch {
                              message.error('复制失败');
                            }
                          }}
                        />
                      </span>
                    );
                  },
                },
                {
                  title: '分拣条码',
                  dataIndex: 'sortBarcodes',
                  key: 'sortBarcodes',
                  width: 140,
                  align: 'center' as const,
                  render: (arr: string[] | undefined) => {
                    const text = Array.isArray(arr) ? arr.join(',') : '';
                    if (!text) return '-';
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                        <Tooltip title={text}>
                          <span
                            style={{
                              display: 'inline-block',
                              maxWidth: 100,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              verticalAlign: 'middle',
                            }}
                          >
                            {text}
                          </span>
                        </Tooltip>
                        <CopyOutlined
                          style={{ cursor: 'pointer', fontSize: 12, flexShrink: 0 }}
                          onClick={async (e) => {
                            e?.stopPropagation?.();
                            try {
                              await navigator.clipboard.writeText(text);
                              message.success('已复制');
                            } catch {
                              message.error('复制失败');
                            }
                          }}
                        />
                      </span>
                    );
                  },
                },
                { title: '每单数量', dataIndex: 'perOrderQty', key: 'perOrderQty', width: 90, align: 'center' as const },
                ...(opAreaTab === 'warehouse' || opAreaTab === 'after_pack'
                  ? [
                      {
                        title: '已处理/总单量',
                        key: 'handled_total_orders_copy',
                        width: 130,
                        align: 'center' as const,
                        render: (_: any, row: any) => {
                          const currentTabKey = opAreaTab === 'warehouse' ? 'warehouse' : 'after_pack';
                          const n = Number(row?.byWaybill?.[currentTabKey] ?? 0) || 0;
                          const instrs = row?.byWaybillInstructions?.[currentTabKey] || [];
                          if (n <= 0) return '-';
                          const processed = (instrs || []).filter((i: any) => isProcessedInOperateConfirm(i)).length;
                          const done = processed >= n;
                          return (
                            <span
                              style={{
                                display: 'inline-block',
                                minWidth: 76,
                                padding: '1px 8px',
                                borderRadius: 4,
                                border: `1px solid ${done ? '#52c41a' : '#ff4d4f'}`,
                                color: done ? '#389e0d' : '#cf1322',
                                background: done ? '#f6ffed' : '#fff2f0',
                                fontWeight: 600,
                                lineHeight: 1.4,
                              }}
                            >
                              {processed}/{n}单
                            </span>
                          );
                        },
                      },
                      {
                        title: '预计发货总数量',
                        key: 'expected_total_ship_qty_copy',
                        width: 130,
                        align: 'center' as const,
                        render: (_: any, row: any) => {
                          const currentTabKey = opAreaTab === 'warehouse' ? 'warehouse' : 'after_pack';
                          const totalOrders = Number(row?.byWaybill?.[currentTabKey] ?? 0) || 0;
                          const perOrderQty = Number(row?.perOrderQty ?? 0) || 0;
                          const expected = perOrderQty * totalOrders;
                          return expected > 0 ? expected : '-';
                        },
                      },
                    ]
                  : []),
                ...(opAreaTab === 'upload_immediate'
                  ? [{
                      title: '已处理/总单量',
                      dataIndex: 'totalOrders',
                      key: 'totalOrders',
                      width: 120,
                      align: 'center' as const,
                      render: (v: any, row: any) => {
                        const total = Number(v);
                        if (total == null || total === 0) return '-';
                        const instrs = Array.isArray(row?.allInstructions) ? row.allInstructions : [];
                        const processed = instrs.filter((i: any) => isProcessedInOperateConfirm(i)).length;
                        const done = processed >= total;
                        return (
                          <span
                            style={{
                              display: 'inline-block',
                              minWidth: 76,
                              padding: '1px 8px',
                              borderRadius: 4,
                              border: `1px solid ${done ? '#52c41a' : '#ff4d4f'}`,
                              color: done ? '#389e0d' : '#cf1322',
                              background: done ? '#f6ffed' : '#fff2f0',
                              fontWeight: 600,
                              lineHeight: 1.4,
                            }}
                          >
                            {processed}/{total}单
                          </span>
                        );
                      },
                    }]
                  : []),
                opAreaTab === 'warehouse' || opAreaTab === 'after_pack'
                  ? {
                      title: '已分拣/总发货数量',
                      dataIndex: 'totalShipQty',
                      key: 'totalShipQty',
                      width: 150,
                      align: 'center' as const,
                      render: (_: any, row: any) => {
                        const total = Number(row?.totalShipQty ?? 0) || 0;
                        const rowIndex = currentSingleSkuMatrix.rows.findIndex((r: any) => r?.key === row?.key);
                        const idx = rowIndex >= 0 ? rowIndex : 0;
                        const sortingPosition = opAreaTab === 'warehouse' ? `B${idx + 1}` : `C${idx + 1}`;
                        const sourceTabName: '仓库提供运单' | '先申请包装数据' = opAreaTab === 'warehouse' ? '仓库提供运单' : '先申请包装数据';
                        const iuCode = String(row?.iuCode || '').trim();
                        const picked = iuCode ? getPickedQty(sourceTabName, sortingPosition, iuCode) : 0;
                        const done = picked === total;
                        return (
                          <span
                            style={{
                              display: 'inline-block',
                              minWidth: 68,
                              padding: '1px 8px',
                              borderRadius: 4,
                              border: `1px solid ${done ? '#52c41a' : '#ff4d4f'}`,
                              color: done ? '#389e0d' : '#cf1322',
                              background: done ? '#f6ffed' : '#fff2f0',
                              fontWeight: 600,
                              lineHeight: 1.4,
                            }}
                          >
                            {picked}/{total}
                          </span>
                        );
                      },
                    }
                  : opAreaTab === 'upload_immediate'
                  ? {
                      title: '预计发货总数量',
                      dataIndex: 'totalShipQty',
                      key: 'totalShipQty',
                      width: 120,
                      align: 'center' as const,
                      render: (_: any, row: any) => {
                        const perOrderQty = Number(row?.perOrderQty ?? 0) || 0;
                        const totalOrders = Number(row?.totalOrders ?? 0) || 0;
                        const expected = perOrderQty * totalOrders;
                        return expected > 0 ? expected : '-';
                      },
                    }
                  : { title: '总发货数量', dataIndex: 'totalShipQty', key: 'totalShipQty', width: 100, align: 'center' as const },
                ...currentSingleSkuMatrix.dynamicCols.map((c: { key: string; title: string; carrierDisplayName?: string }) => {
                  const isUploadImmediateCarrier = opAreaTab === 'upload_immediate' && c.key.startsWith('upload_immediate_');
                  const isWarehouseCol = c.key === 'warehouse';
                  const isAfterPackCol = c.key === 'after_pack';
                  const carrierName = c.carrierDisplayName ?? c.title;
                  const colTitle = isUploadImmediateCarrier ? (
                    <div style={{ lineHeight: 1.3, whiteSpace: 'pre-line', textAlign: 'center' }}>
                      {carrierName}
                      {'\n'}
                      已处理/单量
                    </div>
                  ) : isWarehouseCol ? '操作' : isAfterPackCol ? '操作' : c.title;
                  const colWidth = isUploadImmediateCarrier ? 130 : (isWarehouseCol || isAfterPackCol ? 180 : 110);
                  return {
                    title: colTitle,
                    key: c.key,
                    width: colWidth,
                    align: 'center' as const,
                    render: (_: any, row: any) => {
                      const n = row.byWaybill?.[c.key];
                      const instrs = row.byWaybillInstructions?.[c.key] || [];
                      if (n == null || n === 0) return '-';
                      if (isUploadImmediateCarrier) {
                        const processed = (instrs || []).filter((i: any) => isProcessedInOperateConfirm(i)).length;
                        const sortStr = Array.isArray(row.sortBarcodes) ? row.sortBarcodes.join(',') : '-';
                        const typeLabel = `已有运单${carrierName}`;
                        return (
                          <span
                            style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
                            onClick={() => {
                              setInstrModalCarrierContext({
                                iuCode: displayDash(row?.iuCode),
                                sortBarcodes: Array.isArray(row?.sortBarcodes) ? row.sortBarcodes : [],
                                perOrderQty: Number(row?.perOrderQty ?? 0),
                                totalOrders: n,
                                typeLabel,
                                row,
                                carrierKey: c.key,
                                carrierName: c.carrierDisplayName ?? c.title,
                              });
                              setInstrModalList(instrs);
                              setInstrModalOperateContext(null);
                              setInstrModalDoneFilter('');
                              setInstrModalPage(1);
                              setInstrModalOpen(true);
                            }}
                          >
                            {processed}/{n}单
                          </span>
                        );
                      }
                      const typeLabel = opAreaTab === 'warehouse' ? '仓库提供运单' : '先申请包装数据';
                      const openCarrierInstrModal = () => {
                        setInstrModalCarrierContext({
                          iuCode: displayDash(row?.iuCode),
                          sortBarcodes: Array.isArray(row?.sortBarcodes) ? row.sortBarcodes : [],
                          perOrderQty: Number(row?.perOrderQty ?? 0),
                          totalOrders: n,
                          typeLabel,
                          row,
                          carrierKey: c.key,
                          carrierName: c.carrierDisplayName ?? c.title,
                        });
                        setInstrModalList(instrs);
                        setInstrModalOperateContext(null);
                        setInstrModalDoneFilter('');
                        setInstrModalPage(1);
                        setInstrModalOpen(true);
                      };
                      const openSingleTabSortVerify = () => {
                        const rowIndex = currentSingleSkuMatrix.rows.findIndex((r: any) => r?.key === row?.key);
                        const idx = rowIndex >= 0 ? rowIndex : 0;
                        const sortingPosition = opAreaTab === 'warehouse' ? `B${idx + 1}` : `C${idx + 1}`;
                        const sourceTabName: '仓库提供运单' | '先申请包装数据' = opAreaTab === 'warehouse' ? '仓库提供运单' : '先申请包装数据';
                        const iuCode = String(row?.iuCode || '').trim();
                        const sortBarcode = Array.isArray(row?.sortBarcodes)
                          ? row.sortBarcodes.map((s: any) => String(s || '').trim()).filter(Boolean).join(',')
                          : '';
                        const shipQty = Number(row?.totalShipQty ?? 0) || 0;
                        const instructionCount = Array.isArray(instrs) ? instrs.length : 0;
                        if (!iuCode || !sortingPosition) {
                          message.warning('缺少分拣验证所需数据');
                          return;
                        }
                        const unitRows = [{ iuCode, sortBarcode, shipQty }];
                        setSortedPickedQtyMap((prev) => {
                          const next = { ...prev };
                          unitRows.forEach((r) => {
                            const key = buildPickedQtyKey(sourceTabName, sortingPosition, String(r?.iuCode || '').trim());
                            next[key] = 0;
                          });
                          return next;
                        });
                        setSingleTabSortVerifyContext({
                          sourceTabName,
                          sortingPosition,
                          unitRows,
                          instructionCount,
                        });
                        setSingleTabSortVerifyInput('');
                        setSingleTabSortVerifyPriorityIu('');
                        setSingleTabSortVerifyOpen(true);
                      };
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <Button
                              size="small"
                              onClick={openSingleTabSortVerify}
                            >
                              分拣验证
                            </Button>
                            <Button
                              size="small"
                              type="primary"
                              style={{ background: '#1677ff', borderColor: '#1677ff', color: '#fff' }}
                              onClick={openCarrierInstrModal}
                            >
                              操作
                            </Button>
                          </span>
                        );
                    },
                  };
                }),
              ]}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 8, gap: 12 }}>
              <span style={{ color: '#666', fontSize: 12 }}>
                第 {filteredSingleSkuRows.length > 0 ? ((singleSkuPage - 1) * singleSkuPageSize) + 1 : 0}-
                {Math.min(singleSkuPage * singleSkuPageSize, filteredSingleSkuRows.length)} 条，共 {filteredSingleSkuRows.length} 条
              </span>
              <Pagination
                size="small"
                current={singleSkuPage}
                pageSize={singleSkuPageSize}
                total={filteredSingleSkuRows.length}
                showSizeChanger
                showQuickJumper
                pageSizeOptions={[5, 10, 20, 50]}
                onChange={(page, pageSize) => {
                  setSingleSkuPage(page);
                  if (pageSize && pageSize !== singleSkuPageSize) {
                    setSingleSkuPageSize(pageSize);
                    setSingleSkuPage(1);
                  }
                }}
              />
            </div>
          </div>
        ) : (
          <div style={{ minHeight: 80, border: '1px dashed #d9d9d9', borderRadius: 4, color: '#8c8c8c', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            暂无单品指令
          </div>
        )}
      </div>
      <div style={{ minWidth: 0, overflowX: 'auto', border: '2px solid #1890ff', borderRadius: 6, padding: 8, width: '100%' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto minmax(0,1fr)', alignItems: 'center', marginBottom: 8, gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <span style={{ fontWeight: 600 }}>混装指令</span>
            {currentMixedRows.rows.length > 0 ? (
              <span style={{ marginLeft: 12, fontWeight: 'normal', color: '#666', fontSize: 15 }}>
                共{currentMixedRows.stats.orders}单{currentMixedRows.stats.qty}件 涉及库存单元{currentMixedRows.stats.iuCount}种
              </span>
            ) : null}
            {currentMixedRows.rows.length > 0 ? (
              <span
                style={{
                  marginLeft: 10,
                  display: 'inline-block',
                  padding: '1px 10px',
                  borderRadius: 6,
                  border: `1px solid ${mixedPickedShipSummary.done ? '#52c41a' : '#ff4d4f'}`,
                  background: mixedPickedShipSummary.done ? '#52c41a' : '#ff4d4f',
                  color: '#fff',
                  fontSize: 12,
                  lineHeight: '20px',
                  fontWeight: 600,
                }}
              >
                已分拣{mixedPickedShipSummary.pickedQty}件/发货总数量{mixedPickedShipSummary.shipQty}件
              </span>
            ) : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Button
              type="primary"
              style={{ background: '#fa8c16', borderColor: '#fa8c16' }}
              disabled={mixedSelectedInstrKeys.length === 0}
              onClick={() => {
                const keySet = new Set(mixedSelectedInstrKeys.map((k) => String(k)));
                const indexMap = new Map<string, number>();
                currentMixedRows.rows.forEach((row: any) => {
                  const k = getMixedBatchInstrKey(row?.instruction);
                  if (!k || !keySet.has(k) || isProcessedInOperateConfirm(row?.instruction)) return;
                  const idx = Number(row?.instrIndex || 0);
                  const prev = indexMap.get(k);
                  if (prev == null || idx < prev) indexMap.set(k, idx);
                });
                const instrMap = new Map<string, any>();
                currentMixedRows.rows.forEach((row: any) => {
                  const k = getMixedBatchInstrKey(row?.instruction);
                  if (!k || !keySet.has(k) || instrMap.has(k) || isProcessedInOperateConfirm(row?.instruction)) return;
                  instrMap.set(k, row?.instruction);
                });
                const items = Array.from(instrMap.entries())
                  .map(([k, instr]) => ({ instr, instrIndex: Number(indexMap.get(k) || 0) }))
                  .sort((a, b) => a.instrIndex - b.instrIndex);
                setMixedBatchSelectedItems(items);
                setMixedBatchDoneIds(new Set());
                setMixedBatchUnoperatedIds(new Set());
                setMixedBatchUnoperatedReasons({});
                setMixedBatchModalOpen(true);
              }}
            >
              波次操作(共{mixedSelectedInstrKeys.length}单)
            </Button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifySelf: 'end' }}>
            <Input
              placeholder="指令编码"
              allowClear
              value={mixedInstrCodeFilter}
              onChange={(e) => setMixedInstrCodeFilter(e.target.value)}
              style={{ width: 140 }}
            />
            <Select
              placeholder="分拣状态"
              allowClear
              value={mixedSortStatusFilter || undefined}
              onChange={(v) => setMixedSortStatusFilter(String(v || ''))}
              options={[
                { label: '已完成分拣', value: 'completed' },
                { label: '未完成分拣', value: 'uncompleted' },
              ]}
              style={{ width: 140 }}
            />
            <Input
              placeholder="库存单元"
              allowClear
              value={mixedIuFilter}
              onChange={(e) => setMixedIuFilter(e.target.value)}
              style={{ width: 140 }}
            />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <Switch size="small" checked={mixedUnmarkedOnly} onChange={setMixedUnmarkedOnly} />
              <span>仅显示未标注</span>
            </span>
          </div>
        </div>
        {currentMixedRows.rows.length > 0 ? (
          <div style={{ overflowX: 'auto', minWidth: 0 }}>
            <Table
              size="small"
              rowKey="key"
              dataSource={pagedMixedRows}
              rowClassName={(rec: any) => (isProcessedInOperateConfirm(rec?.instruction) ? 'single-sku-row-completed' : '')}
              pagination={false}
              scroll={{ x: 'max-content' }}
              columns={[
                {
                  title: (
                    <Checkbox
                      checked={mixedPageAllChecked}
                      indeterminate={mixedPagePartiallyChecked}
                      onChange={(e) => {
                        const checked = !!e?.target?.checked;
                        setMixedSelectedInstrKeys((prev) => {
                          const set = new Set(prev.map((k) => String(k)));
                          if (checked) {
                            pagedMixedSelectableInstrKeys.forEach((k) => set.add(k));
                          } else {
                            pagedMixedSelectableInstrKeys.forEach((k) => set.delete(k));
                          }
                          return Array.from(set);
                        });
                      }}
                    />
                  ),
                  key: 'select',
                  width: 48,
                  align: 'center' as const,
                  render: (_: any, rec: any) => {
                    const k = getOperateConfirmInstrKey(rec?.instruction);
                    if (!k) return null;
                  const disabled = isProcessedInOperateConfirm(rec?.instruction);
                    return (
                      <Checkbox
                      checked={!disabled && mixedSelectedInstrKeySet.has(k)}
                      disabled={disabled}
                        onChange={(e) => {
                          const checked = !!e?.target?.checked;
                          setMixedSelectedInstrKeys((prev) => {
                            const set = new Set(prev.map((v) => String(v)));
                            if (checked) set.add(k);
                            else set.delete(k);
                            return Array.from(set);
                          });
                        }}
                      />
                    );
                  },
                  onCell: (rec: any) => (rec.isFirstRow ? { rowSpan: rec.rowSpan } : { rowSpan: 0 }),
                },
                {
                  title: '分拣位',
                  key: 'instrIndex',
                  width: 70,
                  align: 'center' as const,
                  render: (_: any, rec: any) => {
                    const base = rec.instrIndex;
                    const sortingPosition = opAreaTab === 'upload_immediate'
                      ? `A${base}`
                      : opAreaTab === 'warehouse'
                      ? `B${currentSingleSkuMatrix.rows.length + base}`
                      : `C${currentSingleSkuMatrix.rows.length + base}`;
                    const done = mixedSortPositionDoneMap.get(Number(base || 0)) === true;
                    return renderSortPositionTag(sortingPosition, done);
                  },
                  onCell: (rec: any) => (rec.isFirstRow ? { rowSpan: rec.rowSpan } : { rowSpan: 0 }),
                },
                {
                  title: '指令编码',
                  key: 'instruction_code',
                  width: 130,
                  align: 'center' as const,
                  render: (_: any, rec: any) => {
                    const code = displayDash(rec.instruction?.instruction_system_code || rec.instruction?.system_code);
                    if (code === '-') return '-';
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                        <span>{code}</span>
                        <CopyOutlined
                          style={{ cursor: 'pointer', fontSize: 12 }}
                          onClick={async (e) => {
                            e?.stopPropagation?.();
                            try {
                              await navigator.clipboard.writeText(String(code));
                              message.success('已复制');
                            } catch {
                              message.error('复制失败');
                            }
                          }}
                        />
                      </span>
                    );
                  },
                  onCell: (rec: any) => (rec.isFirstRow ? { rowSpan: rec.rowSpan } : { rowSpan: 0 }),
                },
                {
                  title: '处理结果',
                  key: 'process_result',
                  width: 90,
                  align: 'center' as const,
                  render: (_: any, rec: any) => {
                    if (isDoneInOperateConfirm(rec?.instruction)) return <Tag color="green">已完成</Tag>;
                    if (isUnoperatedInOperateConfirm(rec?.instruction)) {
                      const reason = operateConfirmUnoperatedReasons[getOperateConfirmInstrKey(rec?.instruction)] || '暂无填写原因';
                      return (
                        <Tooltip title={reason}>
                          <Tag color="red" style={{ cursor: 'pointer' }}>无法操作</Tag>
                        </Tooltip>
                      );
                    }
                    return <Tag color="default">未标注</Tag>;
                  },
                  onCell: (rec: any) => (rec.isFirstRow ? { rowSpan: rec.rowSpan } : { rowSpan: 0 }),
                },
                {
                  title: '运单状态',
                  key: 'waybill_status',
                  width: 130,
                  align: 'center' as const,
                  render: (_: any, rec: any) => getMixedWaybillStatusText(rec.instruction),
                  onCell: (rec: any) => (rec.isFirstRow ? { rowSpan: rec.rowSpan } : { rowSpan: 0 }),
                },
                {
                  title: '跟踪号',
                  key: 'tracking',
                  width: 120,
                  align: 'center' as const,
                  render: (_: any, rec: any) => {
                    const text = displayDash(rec.instruction?.waybill_tracking_no);
                    if (text === '-') return '-';
                    return (
                      <Tooltip title={text}>
                        <span style={{ display: 'inline-block', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {text}
                        </span>
                      </Tooltip>
                    );
                  },
                  onCell: (rec: any) => (rec.isFirstRow ? { rowSpan: rec.rowSpan } : { rowSpan: 0 }),
                },
                {
                  title: '运单附件',
                  key: 'attachments',
                  width: 120,
                  align: 'center' as const,
                  render: (_: any, rec: any) => {
                    const r = rec.instruction;
                    const list = Array.isArray(r?.attachments_detail) ? r.attachments_detail : [];
                    const filtered = list.filter((a: any) => {
                      const label = String(a?.label || '').trim();
                      return label === '物流运单';
                    });
                    const useList = filtered;
                    if (!useList.length) return '-';
                    return (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                        {useList.map((a: any, i: number) => {
                          const name = displayDash(a?.file_name || a?.file || a?.file_path || a?.label || '');
                          if (name === '-') return null;
                          const display = name.length > 12 ? `${name.slice(0, 12)}…` : name;
                          return (
                            <Tooltip key={`${name}-${i}`} title={name}>
                              <Button
                                type="link"
                                size="small"
                                style={{ padding: 0, height: 'auto', fontSize: 12 }}
                                onClick={() => openAttachmentPreview(a)}
                              >
                                {display}
                              </Button>
                            </Tooltip>
                          );
                        })}
                      </div>
                    );
                  },
                  onCell: (rec: any) => (rec.isFirstRow ? { rowSpan: rec.rowSpan } : { rowSpan: 0 }),
                },
                {
                  title: '库存单元',
                  key: 'iu',
                  width: 120,
                  align: 'center' as const,
                  render: (_: any, rec: any) => {
                    const t = rec.target;
                    if (!t) return '-';
                    const snap = t?.snapshot || {};
                    return displayDash(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id);
                  },
                },
                {
                  title: '分拣条码',
                  key: 'sortBarcode',
                  width: 130,
                  align: 'center' as const,
                  render: (_: any, rec: any) => {
                    const t = rec.target;
                    if (!t) return '-';
                    const snap = t?.snapshot || {};
                    const sortArr = Array.isArray(t?.sorting_barcodes)
                      ? t.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
                      : (Array.isArray(snap?.sorting_barcodes)
                        ? snap.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
                        : []);
                    const text = sortArr.length > 0 ? sortArr.join(',') : (snap?.sorting_barcode || '-');
                    return (
                      <Tooltip title={text}>
                        <span style={{ display: 'inline-block', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {text}
                        </span>
                      </Tooltip>
                    );
                  },
                },
                {
                  title: '已分拣/发货数量',
                  key: 'picked_target',
                  width: 110,
                  align: 'center' as const,
                  render: (_: any, rec: any) => {
                    const t = rec.target;
                    const qty = Number(t?.expected_qty ?? t?.snapshot?.expected_qty ?? 0);
                    const target = Number.isFinite(qty) ? qty : 0;
                    const sourceTabName: '已有运单' | '仓库提供运单' | '先申请包装数据' = opAreaTab === 'upload_immediate'
                      ? '已有运单'
                      : opAreaTab === 'warehouse'
                      ? '仓库提供运单'
                      : '先申请包装数据';
                    const sortingPosition = opAreaTab === 'upload_immediate'
                      ? `A${rec?.instrIndex || 0}`
                      : opAreaTab === 'warehouse'
                      ? `B${currentSingleSkuMatrix.rows.length + (rec?.instrIndex || 0)}`
                      : `C${currentSingleSkuMatrix.rows.length + (rec?.instrIndex || 0)}`;
                    const snap = t?.snapshot || {};
                    const iuCode = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
                    const picked = iuCode ? getPickedQty(sourceTabName, sortingPosition, iuCode) : 0;
                    const done = picked === target;
                    return (
                      <span
                        style={{
                          display: 'inline-block',
                          minWidth: 62,
                          padding: '1px 8px',
                          borderRadius: 4,
                          border: `1px solid ${done ? '#52c41a' : '#ff4d4f'}`,
                          color: done ? '#389e0d' : '#cf1322',
                          background: done ? '#f6ffed' : '#fff2f0',
                          fontWeight: 600,
                          lineHeight: 1.4,
                        }}
                      >
                        {picked}/{target}
                      </span>
                    );
                  },
                },
                ...(opAreaTab === 'upload_immediate'
                  ? [{
                      title: '操作',
                      key: 'action',
                      width: 170,
                      align: 'center' as const,
                      render: (_: any, rec: any) => (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <Button
                            size="small"
                            disabled={isProcessedInOperateConfirm(rec?.instruction)}
                            onClick={() => {
                              const base = rec?.instrIndex;
                              const sortingPosition = opAreaTab === 'upload_immediate'
                                ? `A${base}`
                                : opAreaTab === 'warehouse'
                                ? `B${currentSingleSkuMatrix.rows.length + base}`
                                : `C${currentSingleSkuMatrix.rows.length + base}`;
                              const iuCodes = new Set<string>();
                              const targets = Array.isArray(rec?.instruction?.targets_detail) ? rec.instruction.targets_detail : [];
                              targets.forEach((t: any) => {
                                if (String(t?.target_type || '').trim() !== 'inventory_unit') return;
                                const snap = t?.snapshot || {};
                                const iuCode = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
                                if (iuCode) iuCodes.add(iuCode.toLowerCase());
                              });
                              setSortedPickedQtyMap((prev) => {
                                const next = { ...prev };
                                iuCodes.forEach((iu) => {
                                  const key = `已有运单|${String(sortingPosition).trim()}|${iu}`;
                                  next[key] = 0;
                                });
                                return next;
                              });
                              setMixedSortVerifyInstruction(rec?.instruction || null);
                              setMixedSortVerifySortPosition(sortingPosition);
                              setMixedSortVerifyInput('');
                              setMixedSortVerifyPriorityIu('');
                              setMixedSortVerifyOpen(true);
                            }}
                          >
                            分拣验证
                          </Button>
                          <Button
                            size="small"
                            type="primary"
                            disabled={isProcessedInOperateConfirm(rec?.instruction)}
                            onClick={() => {
                              const base = rec?.instrIndex;
                              const sortingPosition = opAreaTab === 'upload_immediate'
                                ? `A${base}`
                                : opAreaTab === 'warehouse'
                                ? `B${currentSingleSkuMatrix.rows.length + base}`
                                : `C${currentSingleSkuMatrix.rows.length + base}`;
                              setDirectOperateInstruction({
                                ...(rec?.instruction || {}),
                                __directSortPosition: sortingPosition,
                                __directSourceTabName: opAreaTab === 'upload_immediate'
                                  ? '已有运单'
                                  : opAreaTab === 'warehouse'
                                  ? '仓库提供运单'
                                  : '先申请包装数据',
                              });
                              setDirectOperateModalOpen(true);
                            }}
                          >
                            操作
                          </Button>
                        </span>
                      ),
                      onCell: (rec: any) => (rec.isFirstRow ? { rowSpan: rec.rowSpan } : { rowSpan: 0 }),
                    }]
                  : []),
                ...(opAreaTab === 'warehouse' || opAreaTab === 'after_pack'
                  ? [{
                      title: '操作',
                      key: 'action_warehouse_after_pack',
                      width: 170,
                      align: 'center' as const,
                      render: (_: any, rec: any) => (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <Button
                            size="small"
                            disabled={isProcessedInOperateConfirm(rec?.instruction)}
                            onClick={() => {
                              const base = Number(rec?.instrIndex || 0);
                              const sortingPosition = opAreaTab === 'warehouse'
                                ? `B${currentSingleSkuMatrix.rows.length + base}`
                                : `C${currentSingleSkuMatrix.rows.length + base}`;
                              const sourceTabName: '仓库提供运单' | '先申请包装数据' = opAreaTab === 'warehouse' ? '仓库提供运单' : '先申请包装数据';
                              const rowsMap = new Map<string, { iuCode: string; sortBarcode: string; shipQty: number }>();
                              const targets = Array.isArray(rec?.instruction?.targets_detail) ? rec.instruction.targets_detail : [];
                              targets.forEach((t: any) => {
                                if (String(t?.target_type || '').trim() !== 'inventory_unit') return;
                                const snap = t?.snapshot || {};
                                const iuCode = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
                                if (!iuCode) return;
                                const sortArr = Array.isArray(t?.sorting_barcodes)
                                  ? t.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
                                  : (Array.isArray(snap?.sorting_barcodes)
                                    ? snap.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
                                    : []);
                                const sortBarcode = sortArr.length > 0 ? sortArr.join(',') : String(snap?.sorting_barcode || '').trim() || '-';
                                const shipQty = Number(t?.expected_qty ?? snap?.expected_qty ?? 0) || 0;
                                const prev = rowsMap.get(iuCode);
                                if (!prev) {
                                  rowsMap.set(iuCode, { iuCode, sortBarcode, shipQty });
                                } else {
                                  prev.shipQty += shipQty;
                                  if (prev.sortBarcode === '-' && sortBarcode !== '-') prev.sortBarcode = sortBarcode;
                                }
                              });
                              const unitRows = Array.from(rowsMap.values());
                              if (unitRows.length === 0) {
                                message.warning('缺少分拣验证所需数据');
                                return;
                              }
                              setSortedPickedQtyMap((prev) => {
                                const next = { ...prev };
                                unitRows.forEach((r) => {
                                  const key = buildPickedQtyKey(sourceTabName, sortingPosition, String(r?.iuCode || '').trim());
                                  next[key] = 0;
                                });
                                return next;
                              });
                              setSingleTabSortVerifyContext({
                                sourceTabName,
                                sortingPosition,
                                unitRows,
                                instructionCount: 1,
                                instructionCode: String(rec?.instruction?.instruction_system_code ?? rec?.instruction?.instruction?.instruction_system_code ?? rec?.instruction?.system_code ?? '').trim() || '-',
                              });
                              setSingleTabSortVerifyInput('');
                              setSingleTabSortVerifyPriorityIu('');
                              setSingleTabSortVerifyOpen(true);
                            }}
                          >
                            分拣验证
                          </Button>
                          <Button
                            size="small"
                            type="primary"
                            disabled={isProcessedInOperateConfirm(rec?.instruction)}
                            onClick={() => {
                              const base = Number(rec?.instrIndex || 0);
                              const sortingPosition = opAreaTab === 'warehouse'
                                ? `B${currentSingleSkuMatrix.rows.length + base}`
                                : `C${currentSingleSkuMatrix.rows.length + base}`;
                              const sourceTabName: '仓库提供运单' | '先申请包装数据' = opAreaTab === 'warehouse' ? '仓库提供运单' : '先申请包装数据';
                              setDirectOperateInstruction({
                                ...(rec?.instruction || {}),
                                __directSortPosition: sortingPosition,
                                __directSourceTabName: opAreaTab === 'warehouse' ? '仓库提供运单' : '先申请包装数据',
                              });
                              setDirectOperateModalOpen(true);
                            }}
                          >
                            操作
                          </Button>
                        </span>
                      ),
                      onCell: (rec: any) => (rec.isFirstRow ? { rowSpan: rec.rowSpan } : { rowSpan: 0 }),
                    }]
                  : []),
              ]}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 8, gap: 12 }}>
              <span style={{ color: '#666', fontSize: 12 }}>
                第 {filteredMixedUniqueInstrIndices.length > 0 ? ((mixedPage - 1) * mixedPageSize) + 1 : 0}-
                {Math.min(mixedPage * mixedPageSize, filteredMixedUniqueInstrIndices.length)} 单，共 {filteredMixedUniqueInstrIndices.length} 单
              </span>
              <Pagination
                size="small"
                current={mixedPage}
                pageSize={mixedPageSize}
                total={filteredMixedUniqueInstrIndices.length}
                showSizeChanger
                showQuickJumper
                pageSizeOptions={[5, 10, 20, 50]}
                onChange={(page, pageSize) => {
                  setMixedPage(page);
                  if (pageSize && pageSize !== mixedPageSize) {
                    setMixedPageSize(pageSize);
                    setMixedPage(1);
                  }
                }}
              />
            </div>
          </div>
        ) : (
          <div style={{ minHeight: 80, border: '1px dashed #d9d9d9', borderRadius: 4, color: '#8c8c8c', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            暂无混装指令
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={readonly ? 'dropship-pack-readonly' : undefined}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 3fr)', gap: 12 }}>
      <Card
        size="small"
        title={(
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <span style={{ flex: '0 0 auto' }}>操作进度总表</span>
            <div style={{ marginLeft: 50, display: 'flex', alignItems: 'center', gap: 16, fontSize: 14 }}>
              <span>{`目标${progressStats.totalOrders}单${progressStats.totalQty}件`}</span>
              <span style={{ color: '#52c41a' }}>{`已完成${progressStats.doneOrders}单${progressStats.doneQty}件`}</span>
              <span style={{ color: '#ff4d4f' }}>{`无法操作${progressStats.unoperatedOrders}单${progressStats.unoperatedQty}件`}</span>
              <span style={{ color: '#8c8c8c' }}>{`未标注${progressStats.unmarkedOrders}单${progressStats.unmarkedQty}件`}</span>
            </div>
            {showAllCompletedMark ? (
              <span
                style={{
                  marginLeft: 'auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 12px',
                  borderRadius: 999,
                  background: '#52c41a',
                  color: '#fff',
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                <CheckCircleFilled />
                已全部标注
              </span>
            ) : null}
          </div>
        )}
        style={{ minHeight: 260 }}
      >
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Select
              allowClear
              placeholder="处理结果"
              value={filters.done || undefined}
              onChange={(v) => setFilters((prev) => ({ ...prev, done: String(v || '') }))}
              style={{ width: 120 }}
              options={[
                { label: '已完成', value: '已完成' },
                { label: '无法操作', value: '无法操作' },
                { label: '未标注', value: '未标注' },
              ]}
            />
            <Input
              allowClear
              placeholder="指令编码"
              value={filters.instructionCode}
              onChange={(e) => setFilters((prev) => ({ ...prev, instructionCode: e.target.value }))}
              style={{ width: 140 }}
            />
            <Select
              allowClear
              placeholder="运单状态"
              value={filters.waybillStatus || undefined}
              onChange={(v) => setFilters((prev) => ({ ...prev, waybillStatus: String(v || '') }))}
              style={{ width: 160 }}
              options={[
                { label: '有运单', value: '有运单' },
                { label: '仓库提供运单', value: '仓库提供运单' },
                { label: '先申请包装数据', value: '先申请包装数据' },
              ]}
            />
            <Select
              allowClear
              placeholder="指令类型"
              value={filters.instructionType || undefined}
              onChange={(v) => setFilters((prev) => ({ ...prev, instructionType: String(v || '') }))}
              style={{ width: 140 }}
              options={[
                { label: '单款单件', value: '单款单件' },
                { label: '单款多件', value: '单款多件' },
                { label: '多款多件', value: '多款多件' },
              ]}
            />
            <Select
              allowClear
              placeholder="承运商"
              value={filters.carrier || undefined}
              onChange={(v) => setFilters((prev) => ({ ...prev, carrier: String(v || '') }))}
              style={{ width: 140 }}
              options={carrierOptions}
              showSearch
              optionFilterProp="label"
            />
            <Input
              allowClear
              placeholder="物流单号"
              value={filters.trackingNo}
              onChange={(e) => setFilters((prev) => ({ ...prev, trackingNo: e.target.value }))}
              style={{ width: 160 }}
            />
            <Input
              allowClear
              placeholder="运单内容搜索"
              value={filters.ocrText}
              onChange={(e) => setFilters((prev) => ({ ...prev, ocrText: e.target.value }))}
              style={{ width: 180 }}
            />
            <Input
              allowClear
              placeholder="库存单元条码搜索"
              value={filters.sortingBarcodeText}
              onChange={(e) => setFilters((prev) => ({ ...prev, sortingBarcodeText: e.target.value }))}
              style={{ width: 180 }}
            />
            <Select
              allowClear
              placeholder="是否需打包"
              value={filters.isPackRequired || undefined}
              onChange={(v) => setFilters((prev) => ({ ...prev, isPackRequired: (v as '' | 'yes' | 'no') || '' }))}
              style={{ width: 120 }}
              options={[
                { label: '是', value: 'yes' },
                { label: '否', value: 'no' },
              ]}
            />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Switch
                checked={filters.remarksOnly}
                onChange={(v) => setFilters((prev) => ({ ...prev, remarksOnly: !!v }))}
                size="small"
              />
              <span style={{ fontSize: 13, color: '#666' }}>仅显示带备注的指令</span>
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Switch
                checked={filters.executionTipsOnly}
                onChange={(v) => setFilters((prev) => ({ ...prev, executionTipsOnly: !!v }))}
                size="small"
              />
              <span style={{ fontSize: 13, color: '#666' }}>仅显示带执行提醒的指令</span>
            </span>
            <Button
              type="primary"
              size="small"
              disabled={progressSelectedKeys.length === 0}
              onClick={() => {
                setBatchMarkStatus('completed');
                setBatchMarkUnoperatedReason('');
                setBatchMarkModalOpen(true);
              }}
            >
              批量标注{progressSelectedKeys.length > 0 ? `(${progressSelectedKeys.length})` : ''}
            </Button>
          </div>
        </div>
        <div style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
          <Table
            size="small"
            rowKey={(r: any) => String(r?.id || r?.instruction || '')}
            dataSource={pagedItems}
            columns={instructionColumns as any}
            rowSelection={{
              columnTitle: () => {
                const currentPageKeys = pagedItems.map((r: any) => String(r?.id || r?.instruction || ''));
                const allPageSelected = currentPageKeys.length > 0 && currentPageKeys.every((k) => progressSelectedKeys.includes(k));
                const somePageSelected = currentPageKeys.some((k) => progressSelectedKeys.includes(k));
                return (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Checkbox
                      checked={allPageSelected}
                      indeterminate={somePageSelected && !allPageSelected}
                      onChange={() => {
                        setProgressSelectedKeys((prev) => {
                          const keysFromOtherPages = prev.filter((k: React.Key) => !currentPageKeys.includes(String(k)));
                          if (allPageSelected) return keysFromOtherPages;
                          return [...keysFromOtherPages, ...currentPageKeys];
                        });
                      }}
                    />
                    <span>选择</span>
                  </span>
                );
              },
              columnWidth: 52,
              selectedRowKeys: progressSelectedKeys,
              onChange: (keys) => {
                setProgressSelectedKeys((prev) => {
                  const currentPageKeys = pagedItems.map((r: any) => String(r?.id || r?.instruction || ''));
                  const keysFromOtherPages = prev.filter((k: React.Key) => !currentPageKeys.includes(String(k)));
                  return [...keysFromOtherPages, ...keys];
                });
              },
            }}
            pagination={false}
            style={{ minWidth: 1600 }}
            onRow={(r: any) => ({
              style: { background: getCarrierBg(r?.waybill_carrier) }
            })}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 8, gap: 12 }}>
          <span style={{ color: '#666' }}>
            {(() => {
              const total = filteredItems.length;
              const start = total > 0 ? ((itemsPage - 1) * itemsPageSize) + 1 : 0;
              const end = total > 0 ? Math.min(start + itemsPageSize - 1, total) : 0;
              return `第 ${start}-${end} 条，共 ${total} 条记录`;
            })()}
          </span>
          <Pagination
            size="small"
            current={itemsPage}
            pageSize={itemsPageSize}
            total={filteredItems.length}
            showSizeChanger
            showQuickJumper
            pageSizeOptions={[10, 20, 50, 100]}
            onChange={(page, pageSize) => {
              setItemsPage(page);
              if (pageSize && pageSize !== itemsPageSize) {
                setItemsPageSize(pageSize);
                setItemsPage(1);
              }
            }}
          />
        </div>
      </Card>
      <Card size="small" style={{ minHeight: 260 }}>
        <Tabs
          size="small"
          activeKey={operationRecordTabKey}
          onChange={(k) => setOperationRecordTabKey((k as 'operation' | 'scan') || 'operation')}
          tabBarExtraContent={(
            <Button
              size="small"
              danger
              onClick={handleResetCurrentStageAllRecords}
            >
              清空所有记录(重新操作)
            </Button>
          )}
          items={[
            {
              key: 'operation',
              label: '操作记录',
              children: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Input
                      allowClear
                      placeholder="指令编码(模糊匹配)"
                      value={operationRecordCodeFilter}
                      onChange={(e) => setOperationRecordCodeFilter(e.target.value)}
                      style={{ width: 180 }}
                      size="small"
                    />
                    <Select
                      allowClear
                      placeholder="处理结果"
                      value={operationRecordStatusFilter || undefined}
                      onChange={(v) => setOperationRecordStatusFilter(String(v || ''))}
                      style={{ width: 120 }}
                      size="small"
                      options={[
                        { label: '已完成', value: '已完成' },
                        { label: '无法操作', value: '无法操作' },
                        { label: '未标注', value: '未标注' },
                      ]}
                    />
                    <Select
                      allowClear
                      placeholder="标注方式"
                      value={operationRecordMarkModeFilter || undefined}
                      onChange={(v) => setOperationRecordMarkModeFilter(String(v || ''))}
                      style={{ width: 120 }}
                      size="small"
                      options={[
                        { label: '波次操作', value: '波次操作' },
                        { label: '直接标注', value: '直接标注' },
                      ]}
                    />
                  </div>
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {operationRecordGroups.length === 0 ? (
                      <div style={{ color: '#8c8c8c', padding: '8px 0' }}>{operationRecords.length === 0 ? '暂无操作记录' : '无匹配记录'}</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {pagedOperationRecordGroups.map((group, idx) => (
                          <div key={`${group.time}-${idx}`} style={{ padding: 8, border: '1px solid #f0f0f0', borderRadius: 4, fontSize: 12 }}>
                            <div style={{ color: '#8c8c8c', marginBottom: 6 }}>{group.time}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {group.instructions.map((item, i) => (
                                <div key={`${item.system_code}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                  <span>{item.system_code}</span>
                                  <Tag color={item.status === '已完成' ? 'green' : item.status === '无法操作' ? 'red' : 'default'} style={{ margin: 0 }}>{item.status}</Tag>
                                  <Tag color={item.mark_mode === '波次操作' ? 'blue' : 'purple'} style={{ margin: 0 }}>{item.mark_mode}</Tag>
                                  {item.error_reason ? <span style={{ color: '#ff4d4f', fontSize: 11 }}>（{item.error_reason}）</span> : null}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {operationRecordGroups.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 8, gap: 12 }}>
                      <span style={{ color: '#666', fontSize: 12 }}>
                        {(() => {
                          const total = operationRecordGroups.length;
                          const start = total > 0 ? ((operationRecordPage - 1) * operationRecordPageSize) + 1 : 0;
                          const end = total > 0 ? Math.min(start + operationRecordPageSize - 1, total) : 0;
                          return `第 ${start}-${end} 条，共 ${total} 条记录`;
                        })()}
                      </span>
                      <Pagination
                        size="small"
                        current={operationRecordPage}
                        pageSize={operationRecordPageSize}
                        total={operationRecordGroups.length}
                        showSizeChanger
                        showQuickJumper
                        pageSizeOptions={[5, 10, 20, 50]}
                        onChange={(page, pageSize) => {
                          setOperationRecordPage(page);
                          if (pageSize && pageSize !== operationRecordPageSize) {
                            setOperationRecordPageSize(pageSize);
                            setOperationRecordPage(1);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: 'scan',
              label: '运单扫描与匹配记录',
              children: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Input
                      allowClear
                      placeholder="匹配指令(模糊匹配)"
                      value={scanRecordMatchedInstructionFilter}
                      onChange={(e) => setScanRecordMatchedInstructionFilter(e.target.value)}
                      style={{ width: 180 }}
                      size="small"
                    />
                    <Input
                      allowClear
                      placeholder="运单扫描值(模糊匹配)"
                      value={scanRecordScanValueFilter}
                      onChange={(e) => setScanRecordScanValueFilter(e.target.value)}
                      style={{ width: 200 }}
                      size="small"
                    />
                  </div>
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 150 }}>时间</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 170 }}>运单扫描值</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 100 }}>匹配指令</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 120 }}>匹配方式</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedScanRecords.length === 0 ? (
                          <tr>
                            <td colSpan={4} style={{ padding: '10px 8px', color: '#8c8c8c', textAlign: 'center' }}>
                              {operateConfirmGlobalScanMatchRecords.length === 0 ? '暂无运单扫描与匹配记录' : '无匹配记录'}
                            </td>
                          </tr>
                        ) : (
                          pagedScanRecords.map((rec, idx) => (
                            <tr key={`${rec.time}-${rec.scanValue}-${idx}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '6px 8px', color: '#8c8c8c', whiteSpace: 'nowrap' }}>{rec.time}</td>
                              <td style={{ padding: '6px 8px', wordBreak: 'break-all' }}>{rec.scanValue}</td>
                              <td style={{ padding: '6px 8px', wordBreak: 'break-all' }}>{rec.matchedInstruction}</td>
                              <td style={{ padding: '6px 8px' }}>{rec.matchMethod}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {filteredScanRecords.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 8, gap: 12 }}>
                      <span style={{ color: '#666', fontSize: 12 }}>
                        {(() => {
                          const total = filteredScanRecords.length;
                          const start = total > 0 ? ((scanRecordPage - 1) * scanRecordPageSize) + 1 : 0;
                          const end = total > 0 ? Math.min(start + scanRecordPageSize - 1, total) : 0;
                          return `第 ${start}-${end} 条，共 ${total} 条记录`;
                        })()}
                      </span>
                      <Pagination
                        size="small"
                        current={scanRecordPage}
                        pageSize={scanRecordPageSize}
                        total={filteredScanRecords.length}
                        showSizeChanger
                        showQuickJumper
                        pageSizeOptions={[5, 10, 20, 50]}
                        onChange={(page, pageSize) => {
                          setScanRecordPage(page);
                          if (pageSize && pageSize !== scanRecordPageSize) {
                            setScanRecordPageSize(pageSize);
                            setScanRecordPage(1);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>
      </div>
      <Card size="small" title="操作区域" style={{ minHeight: 260 }}>
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Input
            ref={scanQueryInputRef}
            placeholder="扫描库存单元分拣条码或系统编码"
            allowClear
            value={scanInputValue}
            onChange={(e) => setScanInputValue(e.target.value)}
            onPressEnter={handleScanSubmit}
            style={{ width: 280 }}
          />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <Switch
              size="small"
              checked={scanSortOnly}
              onChange={(v) => {
                setScanSortOnly(v);
                if (!v) {
                  setScanInlineRecommendPositionResult(null);
                  setScanInlineOnlySingleHint(null);
                  setScanInlineScanValue('');
                }
              }}
            />
            <span>快捷分拣</span>
          </span>
          <Button type="primary" onClick={handleScanSubmit}>
            查询
          </Button>
          {(scanSortOnly || !!scanInlineRecommendPositionResult || !!scanInlineOnlySingleHint) && (
            <div
              style={{
                minHeight: 42,
                border: '1px dashed #d9d9d9',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: '#595959',
                fontSize: 13,
                padding: '6px 10px',
                gap: 12,
                flexWrap: 'wrap',
                minWidth: 520,
              }}
            >
              {scanInlineRecommendPositionResult ? (
                <>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                      扫描值:{scanInlineScanValue || '-'}
                    </span>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        border: '1px solid #b7eb8f',
                        background: '#f6ffed',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 500,
                        color: '#237804',
                      }}
                    >
                      分拣推荐位
                    </span>
                    <span style={{ color: '#1677ff', fontWeight: 800, fontSize: 24, lineHeight: 1 }}>{scanInlineRecommendPositionResult.sortingPosition}</span>
                    <span style={{ color: '#595959', fontWeight: 700, fontSize: 18, lineHeight: 1 }}>x</span>
                    <span style={{ color: '#ff4d4f', fontWeight: 800, fontSize: 24, lineHeight: 1 }}>{scanInlineRecommendPositionResult.gapQty}</span>
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span>来源:{scanInlineRecommendPositionResult.sourceTabName}</span>
                    <span>发货数量:{scanInlineRecommendPositionResult.shippedQty}</span>
                    <span>已分拣数量:{scanInlineRecommendPositionResult.pickedQty}</span>
                  </span>
                </>
              ) : scanInlineOnlySingleHint ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                    扫描值:{scanInlineScanValue || '-'}
                  </span>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '4px 10px',
                      border: '1px solid #d9d9d9',
                      background: '#fafafa',
                      borderRadius: 4,
                      color: '#595959',
                      fontSize: 14,
                      fontWeight: 600,
                      lineHeight: 1.2,
                    }}
                  >
                    {scanInlineOnlySingleHint}
                  </span>
                </span>
              ) : (
                <span style={{ color: '#8c8c8c', fontSize: 12 }}>仅分拣模式：等待扫描结果</span>
              )}
            </div>
          )}
        </div>
        <Tabs
          size="small"
          activeKey={opAreaTab}
          onChange={(k) => setOpAreaTab(k || 'upload_immediate')}
          items={[
            {
              key: 'upload_immediate',
              label: `已有运单(${tabStats.upload_immediate.orders}单${tabStats.upload_immediate.qty}件)`,
              children: renderMatrixTabContent(),
            },
            {
              key: 'warehouse',
              label: `仓库提供运单(${tabStats.warehouse.orders}单${tabStats.warehouse.qty}件)`,
              children: renderMatrixTabContent(),
            },
            {
              key: 'after_pack',
              label: `先申请包装数据(${tabStats.after_pack.orders}单${tabStats.after_pack.qty}件)`,
              children: renderMatrixTabContent(),
            },
          ]}
        />
      </Card>
      <Modal
        open={attPreviewOpen}
        title={(
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: 40 }}>
            <span>{attPreviewTitle || '附件预览'}</span>
            {attPreviewTitlePrintSuffix ? (
              <span style={{ color: '#ff7a45', marginLeft: 8, flexShrink: 0 }}>{attPreviewTitlePrintSuffix}</span>
            ) : null}
          </div>
        )}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={closeAttPreviewModal}>关闭</Button>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              disabled={attPreviewLoading || !attPreviewUrl}
              onClick={handleAttPreviewPrint}
            >
              打印
            </Button>
          </div>
        )}
        width={900}
        zIndex={1350}
        onCancel={closeAttPreviewModal}
      >
        {attPreviewLoading ? (
          <div style={{ padding: 24 }}>加载中...</div>
        ) : attPreviewKind === 'text' ? (
          <pre style={{ padding: 16, background: '#f5f5f5', borderRadius: 4, maxHeight: 500, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>{attPreviewText}</pre>
        ) : attPreviewKind === 'image' ? (
          <img src={attPreviewUrl} alt="preview" style={{ maxWidth: '100%' }} />
        ) : attPreviewKind === 'xlsx' ? (
          <div>
            {attPreviewXlsxSheets.length > 1 ? (
              <Tabs
                size="small"
                activeKey={attPreviewXlsxActiveSheet || attPreviewXlsxSheets[0]}
                onChange={(k) => {
                  const name = String(k || '').trim();
                  setAttPreviewXlsxActiveSheet(name);
                  try {
                    const cached = attPreviewXlsxCacheRef.current?.[name];
                    if (cached) {
                      setAttPreviewXlsxCols(cached.cols || []);
                      setAttPreviewXlsxData(cached.data || []);
                      return;
                    }
                    const wb = attPreviewXlsxWbRef.current;
                    const XLSX = attPreviewXlsxLibRef.current;
                    const sh = wb?.Sheets?.[name];
                    const aoa: any[] = sh ? XLSX.utils.sheet_to_json(sh, { header: 1, defval: '', blankrows: false }) : [];
                    const arr = Array.isArray(aoa) ? aoa.filter((r: any) => Array.isArray(r) && r.length > 0) : [];
                    const headers = (Array.isArray(arr[0]) ? arr[0] : []) as any[];
                    const dataRows = arr.slice(1);
                    const cols = headers.map((h: any, i: number) => ({
                      title: String(h || `列${i + 1}`),
                      dataIndex: `c${i}`,
                      align: 'center',
                      width: undefined,
                    }));
                    const data = dataRows.map((row: any[], idx: number) => {
                      const obj: any = { __idx: idx + 1 };
                      cols.forEach((c: any, i: number) => { obj[c.dataIndex] = String(row?.[i] ?? ''); });
                      return obj;
                    });
                    attPreviewXlsxCacheRef.current[name] = { cols, data };
                    setAttPreviewXlsxCols(cols);
                    setAttPreviewXlsxData(data);
                  } catch {}
                }}
                items={attPreviewXlsxSheets.map((n) => ({ key: n, label: <span style={{ fontFamily: 'monospace' }}>{n}</span> }))}
              />
            ) : (attPreviewXlsxActiveSheet ? <div style={{ marginBottom: 6, color: '#666' }}>工作表：{attPreviewXlsxActiveSheet}</div> : null)}
            <Table
              size="small"
              rowKey={(r: any, i?: number) => String(r?.__idx ?? i ?? 0)}
              dataSource={attPreviewXlsxData}
              pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100] }}
              columns={(attPreviewXlsxCols || []).map((col: any) => ({
                ...col,
                onCell: () => ({ style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'center' } }),
              }))}
              scroll={{ y: 500 }}
              tableLayout="fixed"
            />
          </div>
        ) : (
          <iframe title="preview" src={attPreviewUrl} style={{ width: '100%', height: 600, border: 'none' }} />
        )}
      </Modal>
      <Modal
        open={otherAttModalOpen}
        title="其他附件"
        footer={null}
        width={500}
        zIndex={1000}
        onCancel={() => {
          setOtherAttModalOpen(false);
          setOtherAttList([]);
        }}
      >
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {otherAttList.map((a: any, idx: number) => {
            const name = String(a?.file_name || a?.file || a?.file_path || a?.label || '').trim() || `附件${idx + 1}`;
            const printText = formatPrintConfigForTitle(a);
            const needPrint = isAttachmentNeedPrint(a);
            return (
              <div key={`${name}-${idx}`} style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: 'auto' }}
                  onClick={() => openAttachmentPreview(a)}
                >
                  {name}
                </Button>
                {needPrint && printText ? (
                  <span style={{ color: '#cf1322', fontSize: 12, whiteSpace: 'nowrap' }}>
                    需打印 · {printText}
                  </span>
                ) : <span style={{ color: '#8c8c8c', fontSize: 12 }}>-</span>}
              </div>
            );
          })}
        </div>
      </Modal>
      <Modal
        open={mixedBatchModalOpen}
        width={1600}
        footer={null}
        onCancel={() => {
          setMixedBatchModalOpen(false);
          setMixedBatchSelectedItems([]);
          setMixedBatchDoneIds(new Set());
          setMixedBatchUnoperatedIds(new Set());
          setMixedBatchUnoperatedReasons({});
          setMixedBatchUnoperatedReasonModalOpen(false);
          setMixedBatchUnoperatedReasonInstr(null);
          setMixedBatchUnoperatedReasonInput('');
          setMixedBatchScanAreaVisible(false);
          setMixedBatchScanInputValue('');
          setMixedBatchScanMatchedCode('');
          setMixedBatchDraftScanMatchRecords([]);
          setMixedBatchPackingEstimateByInstrKey({});
          setMixedBatchPackingEstimateLoadingByInstrKey({});
          setMixedBatchPackDataModalOpen(false);
          setMixedBatchPackDataLengthCm(null);
          setMixedBatchPackDataWidthCm(null);
          setMixedBatchPackDataHeightCm(null);
          setMixedBatchPackDataWeightG(null);
          setMixedBatchPackDataSaving(false);
        }}
        title={(
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', width: '100%', paddingRight: 32 }}>
            <span style={{ fontWeight: 600 }}>波次操作指令列表</span>
            <span style={{ fontWeight: 500 }}>
              共{mixedBatchSummary.total}单 已完成{mixedBatchSummary.done}单 无法操作{mixedBatchSummary.unoperated}单 未标注{mixedBatchSummary.unmarked}单
            </span>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="primary"
                disabled={
                  mixedBatchSummary.total === 0
                  || mixedBatchSummary.unmarked > 0
                  || (opAreaTab === 'after_pack' && !mixedBatchDoneAllHaveIoc)
                }
                onClick={() => handleMixedBatchSubmit()}
              >
                提交本次数据
              </Button>
            </div>
          </div>
        )}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(() => {
            const isAfterPackMixedBatch = opAreaTab === 'after_pack';
            const doneInstrsForIoc = (mixedBatchSelectedItems || [])
              .map((item) => item?.instr)
              .filter((instr: any) => !!instr && isMixedBatchDone(instr));
            const hasPackDataForInstr = (instr: any) => {
              const l = Number(instr?.actual_pack_length_cm ?? instr?.instruction?.actual_pack_length_cm ?? 0);
              const w = Number(instr?.actual_pack_width_cm ?? instr?.instruction?.actual_pack_width_cm ?? 0);
              const h = Number(instr?.actual_pack_height_cm ?? instr?.instruction?.actual_pack_height_cm ?? 0);
              const g = Number(instr?.actual_gross_weight_g ?? instr?.instruction?.actual_gross_weight_g ?? 0);
              return l > 0 && w > 0 && h > 0 && g > 0;
            };
            const createMixedIocEnabled = isAfterPackMixedBatch
              && mixedBatchSelectedItems.length > 0
              && mixedBatchSummary.unmarked === 0
              && doneInstrsForIoc.length > 0
              && doneInstrsForIoc.every((instr: any) => hasPackDataForInstr(instr))
              && !mixedBatchCreateIocLoading;
            return (
              <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Select
                placeholder="处理结果"
                value={mixedBatchResultFilter}
                onChange={(v) => setMixedBatchResultFilter((v as any) || 'all')}
                style={{ width: 130 }}
                options={[
                  { label: '全部', value: 'all' },
                  { label: '已完成', value: 'done' },
                  { label: '无法操作', value: 'unoperated' },
                  { label: '未标注', value: 'unmarked' },
                ]}
              />
              <Input
                placeholder="按物流号查找"
                allowClear
                value={mixedBatchTrackingFilter}
                onChange={(e) => setMixedBatchTrackingFilter(e.target.value)}
                style={{ width: 180 }}
              />
              <Input
                placeholder="按系统编码查找"
                allowClear
                value={mixedBatchSystemCodeFilter}
                onChange={(e) => setMixedBatchSystemCodeFilter(e.target.value)}
                style={{ width: 180 }}
              />
              <Input
                placeholder="按运单解析内容查找"
                allowClear
                value={mixedBatchOcrFilter}
                onChange={(e) => setMixedBatchOcrFilter(e.target.value)}
                style={{ width: 220 }}
              />
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 14 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Switch size="small" checked={mixedBatchTipsOnly} onChange={setMixedBatchTipsOnly} />
                <span>仅显示带操作提醒</span>
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Switch size="small" checked={mixedBatchPrintOnly} onChange={setMixedBatchPrintOnly} />
                <span>仅显示带打印附件</span>
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {(opAreaTab === 'warehouse' || opAreaTab === 'after_pack') ? (
                <Button
                  style={{ background: '#722ed1', borderColor: '#722ed1', color: '#fff' }}
                  onClick={() => {
                    setMixedBatchPackDataLengthCm(null);
                    setMixedBatchPackDataWidthCm(null);
                    setMixedBatchPackDataHeightCm(null);
                    setMixedBatchPackDataWeightG(null);
                    setMixedBatchPackDataModalOpen(true);
                  }}
                >
                  输入包装数据
                </Button>
              ) : null}
              {isAfterPackMixedBatch ? (
                <Tooltip
                  title={!createMixedIocEnabled ? '需要先输入包装数据和标注完成才能创建指令产出容器' : ''}
                >
                  <Button
                    style={createMixedIocEnabled
                      ? { background: '#52c41a', borderColor: '#52c41a', color: '#fff' }
                      : { background: '#d9d9d9', borderColor: '#d9d9d9', color: '#8c8c8c' }}
                    loading={mixedBatchCreateIocLoading}
                    disabled={!createMixedIocEnabled}
                    onClick={() => {
                      void createInstructionOutputContainersForMixedBatchAfterPack();
                    }}
                  >
                    创建指令产出容器
                  </Button>
                </Tooltip>
              ) : null}
              <Button
                style={isAfterPackMixedBatch
                  ? { background: '#d9d9d9', borderColor: '#d9d9d9', color: '#8c8c8c' }
                  : { background: '#1677ff', borderColor: '#1677ff', color: '#fff' }}
                loading={mixedBatchMergeLoading}
                disabled={isAfterPackMixedBatch}
                onClick={() => handleMixedBatchMergeWaybill()}
              >
                合并运单
              </Button>
              <Button
                style={isAfterPackMixedBatch
                  ? { background: '#d9d9d9', borderColor: '#d9d9d9', color: '#8c8c8c' }
                  : { background: '#fa8c16', borderColor: '#fa8c16', color: '#fff' }}
                disabled={isAfterPackMixedBatch}
                onClick={() => {
                  if (isAfterPackMixedBatch) return;
                  setMixedBatchScanAreaVisible((prev) => !prev);
                  setTimeout(() => {
                    try { mixedBatchScanInputRef.current?.focus?.(); } catch {}
                  }, 80);
                }}
              >
                扫描运单与标记完成({mixedBatchScanMatchedCount}/{mixedBatchSelectedItems.length})
              </Button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Button
                style={{ background: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
                onClick={() => {
                  const keys = mixedBatchFilteredItems.map((item) => getMixedBatchInstrKey(item?.instr)).filter(Boolean);
                  setMixedBatchDoneIds(new Set(keys));
                  setMixedBatchUnoperatedIds(new Set());
                }}
              >
                全部标注完成
              </Button>
              <Button
                style={{ background: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' }}
                onClick={() => {
                  if (mixedBatchFilteredItems.length === 0) {
                    message.warning('当前无可标注的指令');
                    return;
                  }
                  setMixedBatchMarkAllUnoperatedReason('');
                  setMixedBatchMarkAllUnoperatedModalOpen(true);
                }}
              >
                全部标注无法操作
              </Button>
              <Button
                onClick={() => {
                  setMixedBatchDoneIds(new Set());
                  setMixedBatchUnoperatedIds(new Set());
                  setMixedBatchUnoperatedReasons({});
                }}
              >
                清空所有标注
              </Button>
            </div>
          </div>
          {mixedBatchScanAreaVisible && !isAfterPackMixedBatch ? (
            <div
              style={{
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                padding: 10,
                background: '#fafafa',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>运单扫描</span>
                <Button size="small" onClick={() => setMixedBatchScanAreaVisible(false)}>收起</Button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Input
                  ref={mixedBatchScanInputRef}
                  placeholder="请扫描已完成处理的包裹上的物流运单"
                  size="small"
                  style={{ width: 300 }}
                  value={mixedBatchScanInputValue}
                  onChange={(e) => setMixedBatchScanInputValue(e.target.value)}
                  onPressEnter={() => submitMixedBatchScan(false)}
                  allowClear
                />
                <Button size="small" type="primary" onClick={() => submitMixedBatchScan(false)}>输入</Button>
                {mixedBatchScanMatchedCode ? <span style={{ fontSize: 12, color: '#52c41a' }}>已匹配: {mixedBatchScanMatchedCode}</span> : null}
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>扫描包裹上的物流单号，自动匹配本波次指令并标记完成</div>
              <div style={{ marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
                <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontWeight: 600 }}>运单扫描与匹配记录</span>
                  <Button
                    size="small"
                    danger
                    disabled={mixedBatchDraftScanMatchRecords.length === 0}
                    onClick={() => {
                      setMixedBatchDraftScanMatchRecords([]);
                      setMixedBatchScanRecordPage(1);
                    }}
                  >
                    清空记录
                  </Button>
                </div>
                {(() => {
                  const total = mixedBatchDraftScanMatchRecords.length;
                  const start = (mixedBatchScanRecordPage - 1) * mixedBatchScanRecordPageSize;
                  const pagedRecords = mixedBatchDraftScanMatchRecords.slice(start, start + mixedBatchScanRecordPageSize);
                  return (
                    <>
                      <div style={{ maxHeight: 150, overflow: 'auto' }}>
                        <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
                              <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 180 }}>时间</th>
                              <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 170 }}>扫描值</th>
                              <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, minWidth: 140 }}>匹配指令</th>
                              <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 130 }}>匹配方式</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pagedRecords.length === 0 ? (
                              <tr>
                                <td colSpan={4} style={{ padding: '12px 8px', textAlign: 'center', color: '#8c8c8c' }}>暂无扫描匹配记录</td>
                              </tr>
                            ) : pagedRecords.map((rec, idx) => (
                              <tr key={`${rec.time}-${rec.scanValue}-${rec.matchedInstruction}-${idx}`} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: '#8c8c8c' }}>{rec.time}</td>
                                <td style={{ padding: '6px 8px', wordBreak: 'break-all' }}>{rec.scanValue}</td>
                                <td style={{ padding: '6px 8px', wordBreak: 'break-all' }}>{rec.matchedInstruction}</td>
                                <td style={{ padding: '6px 8px' }}>{rec.matchMethod}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {total > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                          <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                            第{start + 1}-{Math.min(start + mixedBatchScanRecordPageSize, total)}条 共{total}条
                          </span>
                          <Pagination
                            size="small"
                            current={mixedBatchScanRecordPage}
                            pageSize={mixedBatchScanRecordPageSize}
                            total={total}
                            showSizeChanger
                            showQuickJumper
                            pageSizeOptions={['5', '10', '20', '50']}
                            onChange={(page, size) => {
                              setMixedBatchScanRecordPage(page);
                              setMixedBatchScanRecordPageSize(size || 10);
                            }}
                          />
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          ) : null}
              </>
            );
          })()}
          <Table
            size="small"
            rowKey="key"
            dataSource={mixedBatchRows}
            rowClassName={(rec: any) => {
              const instr = rec?.instr;
              return isMixedBatchDone(instr) || isMixedBatchUnoperated(instr) ? 'single-sku-row-completed' : '';
            }}
            pagination={false}
            scroll={{ x: 'max-content', y: 560 }}
            columns={[
              {
                title: '处理结果',
                key: 'mixed_batch_result',
                width: 110,
                align: 'center' as const,
                render: (_: any, rec: any) => {
                  const instr = rec?.instr;
                  const done = isMixedBatchDone(instr);
                  const unoperated = isMixedBatchUnoperated(instr);
                  const reason = mixedBatchUnoperatedReasons[getMixedBatchInstrKey(instr)] || '';
                  return (
                    <Tooltip title="点击修改">
                      <Dropdown
                        menu={{
                          items: [
                            { key: 'completed', label: '已完成' },
                            { key: 'unoperated', label: '无法操作' },
                            { key: 'unmarked', label: '未标注' },
                          ],
                          onClick: ({ key }) => handleSetMixedBatchWaybillStatus(instr, key as 'completed' | 'unoperated' | 'unmarked'),
                        }}
                        trigger={['click']}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            minWidth: 70,
                            padding: '2px 8px',
                            borderRadius: 4,
                            cursor: 'pointer',
                            background: done ? '#f6ffed' : unoperated ? '#fff2f0' : 'transparent',
                            border: '1px solid transparent',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#d9d9d9';
                            e.currentTarget.style.background = done ? '#f6ffed' : unoperated ? '#fff2f0' : '#fafafa';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'transparent';
                            e.currentTarget.style.background = done ? '#f6ffed' : unoperated ? '#fff2f0' : 'transparent';
                          }}
                        >
                          {done ? <Tag color="green" style={{ margin: 0 }}>已完成</Tag> : unoperated ? (
                            <Tooltip title={reason || '无法操作'}>
                              <Tag color="red" style={{ margin: 0 }}>无法操作</Tag>
                            </Tooltip>
                          ) : <span style={{ color: '#bfbfbf' }}>未标注</span>}
                        </span>
                      </Dropdown>
                    </Tooltip>
                  );
                },
                onCell: (rec: any) => (rec?.isFirstRow ? { rowSpan: rec?.rowSpan } : { rowSpan: 0 }),
              },
              {
                title: '分拣位',
                key: 'mixed_batch_sort_position',
                width: 80,
                align: 'center' as const,
                render: (_: any, rec: any) => {
                  const instr = rec?.instr;
                  const instrIndex = Number(rec?.instrIndex || 0);
                  const sortingPosition = opAreaTab === 'upload_immediate'
                    ? `A${instrIndex}`
                    : opAreaTab === 'warehouse'
                      ? `B${currentSingleSkuMatrix.rows.length + instrIndex}`
                      : `C${currentSingleSkuMatrix.rows.length + instrIndex}`;
                  const sourceTabName: '已有运单' | '仓库提供运单' | '先申请包装数据' = opAreaTab === 'upload_immediate'
                    ? '已有运单'
                    : opAreaTab === 'warehouse'
                      ? '仓库提供运单'
                      : '先申请包装数据';
                  const targets = (Array.isArray(instr?.targets_detail) ? instr.targets_detail : [])
                    .filter((t: any) => String(t?.target_type || '').trim() === 'inventory_unit');
                  const done = targets.length > 0 && targets.every((t: any) => {
                    const snap = t?.snapshot || {};
                    const iuCode = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
                    const shipQty = Number(t?.expected_qty ?? snap?.expected_qty ?? 0) || 0;
                    const pickedQty = iuCode ? getPickedQty(sourceTabName, sortingPosition, iuCode) : 0;
                    return pickedQty === shipQty;
                  });
                  return renderSortPositionTag(sortingPosition, done);
                },
                onCell: (rec: any) => (rec?.isFirstRow ? { rowSpan: rec?.rowSpan } : { rowSpan: 0 }),
              },
              {
                title: '指令编码',
                key: 'mixed_batch_code',
                width: 130,
                align: 'center' as const,
                render: (_: any, rec: any) => displayDash(rec?.instr?.instruction_system_code ?? rec?.instr?.instruction?.instruction_system_code ?? rec?.instr?.system_code),
                onCell: (rec: any) => (rec?.isFirstRow ? { rowSpan: rec?.rowSpan } : { rowSpan: 0 }),
              },
              {
                title: '库存单元',
                key: 'mixed_batch_iu',
                width: 130,
                align: 'center' as const,
                render: (_: any, rec: any) => {
                  const t = rec?.target;
                  if (!t) return '-';
                  const snap = t?.snapshot || {};
                  return displayDash(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id);
                },
              },
              {
                title: '分拣条码',
                key: 'mixed_batch_sort_barcode',
                width: 150,
                align: 'center' as const,
                render: (_: any, rec: any) => {
                  const t = rec?.target;
                  if (!t) return '-';
                  const snap = t?.snapshot || {};
                  const sortArr = Array.isArray(t?.sorting_barcodes)
                    ? t.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
                    : (Array.isArray(snap?.sorting_barcodes)
                      ? snap.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
                      : []);
                  return sortArr.length > 0 ? sortArr.join(',') : (snap?.sorting_barcode || '-');
                },
              },
              {
                title: '已分拣/发货数量',
                key: 'mixed_batch_picked_ship',
                width: 120,
                align: 'center' as const,
                render: (_: any, rec: any) => {
                  const t = rec?.target;
                  if (!t) return '-';
                  const instrIndex = Number(rec?.instrIndex || 0);
                  const sortingPosition = opAreaTab === 'upload_immediate'
                    ? `A${instrIndex}`
                    : opAreaTab === 'warehouse'
                      ? `B${currentSingleSkuMatrix.rows.length + instrIndex}`
                      : `C${currentSingleSkuMatrix.rows.length + instrIndex}`;
                  const sourceTabName: '已有运单' | '仓库提供运单' | '先申请包装数据' = opAreaTab === 'upload_immediate'
                    ? '已有运单'
                    : opAreaTab === 'warehouse'
                      ? '仓库提供运单'
                      : '先申请包装数据';
                  const snap = t?.snapshot || {};
                  const iuCode = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
                  const shipQty = Number(t?.expected_qty ?? snap?.expected_qty ?? 0) || 0;
                  const pickedQty = iuCode ? getPickedQty(sourceTabName, sortingPosition, iuCode) : 0;
                  const done = pickedQty === shipQty;
                  return (
                    <span
                      style={{
                        display: 'inline-block',
                        minWidth: 62,
                        padding: '1px 8px',
                        borderRadius: 4,
                        border: `1px solid ${done ? '#52c41a' : '#ff4d4f'}`,
                        color: done ? '#389e0d' : '#cf1322',
                        background: done ? '#f6ffed' : '#fff2f0',
                        fontWeight: 600,
                        lineHeight: 1.4,
                      }}
                    >
                      {pickedQty}/{shipQty}
                    </span>
                  );
                },
              },
              {
                title: '操作提醒',
                key: 'mixed_batch_tips',
                width: 180,
                render: (_: any, rec: any) => {
                  const text = displayDash(rec?.instr?.execution_tips);
                  if (text === '-') return '-';
                  return (
                    <Tooltip title={text}>
                      <span style={{ display: 'inline-block', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {text}
                      </span>
                    </Tooltip>
                  );
                },
                onCell: (rec: any) => (rec?.isFirstRow ? { rowSpan: rec?.rowSpan } : { rowSpan: 0 }),
              },
              {
                title: '需打印附件',
                key: 'mixed_batch_print_att',
                width: 180,
                render: (_: any, rec: any) => {
                  const list = getMixedBatchPrintableAttachments(rec?.instr);
                  if (!list.length) return '-';
                  const instrIndex = Number(rec?.instrIndex || 0);
                  const sortingPosition = opAreaTab === 'upload_immediate'
                    ? `A${instrIndex}`
                    : opAreaTab === 'warehouse'
                      ? `B${currentSingleSkuMatrix.rows.length + instrIndex}`
                      : `C${currentSingleSkuMatrix.rows.length + instrIndex}`;
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {list.map((att: any, idx: number) => {
                        const name = String(att?.file_name || att?.file || att?.file_path || att?.label || '').trim() || `附件${idx + 1}`;
                        const printTip = formatPrintConfigForTitle(att) || '未设置打印';
                        return (
                          <span key={`${name}-${idx}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <Tooltip title={name}>
                              <Button
                                type="link"
                                size="small"
                                style={{ padding: 0, height: 'auto' }}
                                onClick={() => handleMixedBatchPrintableAttachmentPreview(att, rec?.instr, sortingPosition)}
                              >
                                {name.length > 16 ? `${name.slice(0, 16)}...` : name}
                              </Button>
                            </Tooltip>
                            <Tooltip title={printTip}>
                              <PrinterOutlined style={{ color: '#1890ff', cursor: 'help' }} />
                            </Tooltip>
                          </span>
                        );
                      })}
                    </div>
                  );
                },
                onCell: (rec: any) => (rec?.isFirstRow ? { rowSpan: rec?.rowSpan } : { rowSpan: 0 }),
              },
              {
                title: '指令备注',
                key: 'mixed_batch_remarks',
                width: 140,
                render: (_: any, rec: any) => {
                  const text = displayDash(rec?.instr?.instruction_remarks ?? rec?.instr?.remarks);
                  if (text === '-') return '-';
                  return (
                    <Tooltip title={text}>
                      <span style={{ display: 'inline-block', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {text}
                      </span>
                    </Tooltip>
                  );
                },
                onCell: (rec: any) => (rec?.isFirstRow ? { rowSpan: rec?.rowSpan } : { rowSpan: 0 }),
              },
              {
                title: '包装方式',
                key: 'mixed_batch_packing',
                width: 220,
                render: (_: any, rec: any) => {
                  const instr = rec?.instr;
                  const instrKey = getMixedBatchInstrKey(instr);
                  const loading = !!mixedBatchPackingEstimateLoadingByInstrKey[instrKey];
                  const packingKey = String(instr?.estimated_packing_method ?? '').trim() || '_other';
                  const packingLabel = packingKey === '_no_pack'
                    ? '无需打包'
                    : packingKey === '_other'
                    ? '其他'
                    : getPackagingMethodLabel(packingKey);
                  const est = mixedBatchPackingEstimateByInstrKey[instrKey];
                  const code = String(est?.code || '').trim();
                  const name = String(est?.name || '').trim();
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.3 }}>
                      <span>{packingLabel || '-'}</span>
                      {loading ? (
                        <span style={{ color: '#1677ff' }}>推荐包材计算中...</span>
                      ) : code || name ? (
                        <span>
                          <span style={{ color: '#ff4d4f', marginRight: 6 }}>{code || '-'}</span>
                          {name || ''}
                        </span>
                      ) : (
                        <span style={{ color: '#8c8c8c' }}>无推荐包材</span>
                      )}
                    </div>
                  );
                },
                onCell: (rec: any) => (rec?.isFirstRow ? { rowSpan: rec?.rowSpan } : { rowSpan: 0 }),
              },
              ...(opAreaTab === 'warehouse' || opAreaTab === 'after_pack'
                ? [{
                    title: '包装数据',
                    key: 'mixed_batch_pack_data',
                    width: 210,
                    render: (_: any, rec: any) => {
                      const instr = rec?.instr || {};
                      const l = Number(instr?.actual_pack_length_cm ?? instr?.instruction?.actual_pack_length_cm ?? 0);
                      const w = Number(instr?.actual_pack_width_cm ?? instr?.instruction?.actual_pack_width_cm ?? 0);
                      const h = Number(instr?.actual_pack_height_cm ?? instr?.instruction?.actual_pack_height_cm ?? 0);
                      const g = Number(instr?.actual_gross_weight_g ?? instr?.instruction?.actual_gross_weight_g ?? 0);
                      if (!(l > 0 && w > 0 && h > 0 && g > 0)) return '-';
                      return (
                        <span style={{ whiteSpace: 'pre-wrap' }}>
                          {`${Math.round(l)} × ${Math.round(w)} × ${Math.round(h)} cm / ${Math.round(g)} g`}
                        </span>
                      );
                    },
                    onCell: (rec: any) => (rec?.isFirstRow ? { rowSpan: rec?.rowSpan } : { rowSpan: 0 }),
                  }]
                : []),
              ...(opAreaTab === 'after_pack'
                ? [{
                    title: '指令产出容器',
                    key: 'mixed_batch_ioc',
                    width: 170,
                    render: (_: any, rec: any) => {
                      const instr = rec?.instr || {};
                      const instructionId = Number(instr?.instruction ?? instr?.instruction_id ?? instr?.id ?? 0);
                      const iocCode = String(
                        (instructionId > 0 ? operateConfirmIocCodeByInstrId[String(instructionId)] : '')
                        || instr?.created_ioc_code
                        || instr?.instruction?.created_ioc_code
                        || '',
                      ).trim();
                      if (!iocCode) return '-';
                      const labelLoading = instructionId > 0 ? !!directOperateIocLabelLoadingByInstrId[String(instructionId)] : false;
                      const packData = (() => {
                        const l = Number(instr?.actual_pack_length_cm ?? instr?.instruction?.actual_pack_length_cm ?? 0);
                        const w = Number(instr?.actual_pack_width_cm ?? instr?.instruction?.actual_pack_width_cm ?? 0);
                        const h = Number(instr?.actual_pack_height_cm ?? instr?.instruction?.actual_pack_height_cm ?? 0);
                        const g = Number(instr?.actual_gross_weight_g ?? instr?.instruction?.actual_gross_weight_g ?? 0);
                        if (!(l > 0 && w > 0 && h > 0 && g > 0)) return null;
                        return { lengthCm: Math.round(l), widthCm: Math.round(w), heightCm: Math.round(h), weightG: Math.round(g) };
                      })();
                      const totalQty = (Array.isArray(instr?.targets_detail) ? instr.targets_detail : []).reduce((sum: number, t: any) => {
                        if (String(t?.target_type || '').trim() !== 'inventory_unit') return sum;
                        const q = Number(t?.expected_qty ?? t?.target_qty ?? 0) || 0;
                        return sum + Math.max(Math.floor(q), 0);
                      }, 0);
                      return (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ color: '#1677ff', fontWeight: 600 }}>{iocCode}</span>
                          <Tooltip title="复制">
                            <CopyOutlined
                              style={{ color: '#1677ff', cursor: 'pointer' }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await navigator.clipboard.writeText(iocCode);
                                  message.success('已复制');
                                } catch {
                                  message.error('复制失败');
                                }
                              }}
                            />
                          </Tooltip>
                          <Button
                            size="small"
                            type="link"
                            style={{ padding: 0, height: 'auto' }}
                            loading={labelLoading}
                            onClick={(e) => {
                              e.stopPropagation();
                              void previewDirectOperateIocLabel(instr, iocCode, packData, totalQty);
                            }}
                          >
                            预览/打印标签
                          </Button>
                        </span>
                      );
                    },
                    onCell: (rec: any) => (rec?.isFirstRow ? { rowSpan: rec?.rowSpan } : { rowSpan: 0 }),
                  }]
                : []),
              {
                title: '承运商',
                key: 'mixed_batch_carrier',
                width: 110,
                align: 'center' as const,
                render: (_: any, rec: any) => displayDash(rec?.instr?.waybill_carrier),
                onCell: (rec: any) => (rec?.isFirstRow ? { rowSpan: rec?.rowSpan } : { rowSpan: 0 }),
              },
              {
                title: '物流跟踪号',
                key: 'mixed_batch_tracking',
                width: 140,
                render: (_: any, rec: any) => displayDash(rec?.instr?.waybill_tracking_no),
                onCell: (rec: any) => (rec?.isFirstRow ? { rowSpan: rec?.rowSpan } : { rowSpan: 0 }),
              },
              {
                title: '运单附件',
                key: 'mixed_batch_waybill_atts',
                width: 180,
                render: (_: any, rec: any) => {
                  const list = getWaybillAttachments(rec?.instr);
                  if (!list.length) return '-';
                  const instrIndex = Number(rec?.instrIndex || 0);
                  const sortingPosition = opAreaTab === 'upload_immediate'
                    ? `A${instrIndex}`
                    : opAreaTab === 'warehouse'
                      ? `B${currentSingleSkuMatrix.rows.length + instrIndex}`
                      : `C${currentSingleSkuMatrix.rows.length + instrIndex}`;
                  return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {list.map((a: any, idx: number) => {
                        const name = String(a?.file_name || a?.file || a?.file_path || a?.label || '').trim() || `运单附件${idx + 1}`;
                        return (
                          <Tooltip key={`${name}-${idx}`} title={name}>
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0, height: 'auto' }}
                              onClick={() => handleMixedBatchPrintableAttachmentPreview(a, rec?.instr, sortingPosition)}
                            >
                              {name.length > 14 ? `${name.slice(0, 14)}...` : name}
                            </Button>
                          </Tooltip>
                        );
                      })}
                    </div>
                  );
                },
                onCell: (rec: any) => (rec?.isFirstRow ? { rowSpan: rec?.rowSpan } : { rowSpan: 0 }),
              },
              {
                title: '运单OCR解析结果',
                key: 'mixed_batch_ocr',
                width: 220,
                render: (_: any, rec: any) => {
                  const text = displayDash(getMixedBatchWaybillOcrText(rec?.instr));
                  if (text === '-') return '-';
                  return (
                    <Tooltip title={text}>
                      <span style={{ display: 'inline-block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {text}
                      </span>
                    </Tooltip>
                  );
                },
                onCell: (rec: any) => (rec?.isFirstRow ? { rowSpan: rec?.rowSpan } : { rowSpan: 0 }),
              },
            ]}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 8, gap: 12 }}>
            <span style={{ color: '#666', fontSize: 12 }}>
              第 {mixedBatchFilteredItems.length > 0 ? ((mixedBatchPage - 1) * mixedBatchPageSize) + 1 : 0}-
              {Math.min(mixedBatchPage * mixedBatchPageSize, mixedBatchFilteredItems.length)} 单，共 {mixedBatchFilteredItems.length} 单
            </span>
            <Pagination
              size="small"
              current={mixedBatchPage}
              pageSize={mixedBatchPageSize}
              total={mixedBatchFilteredItems.length}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={[5, 10, 20, 50]}
              onChange={(page, pageSize) => {
                setMixedBatchPage(page);
                if (pageSize && pageSize !== mixedBatchPageSize) {
                  setMixedBatchPageSize(pageSize);
                  setMixedBatchPage(1);
                }
              }}
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={mixedBatchPackDataModalOpen}
        title="输入包装数据"
        zIndex={1450}
        confirmLoading={mixedBatchPackDataSaving}
        onCancel={() => {
          setMixedBatchPackDataModalOpen(false);
          setMixedBatchPackDataLengthCm(null);
          setMixedBatchPackDataWidthCm(null);
          setMixedBatchPackDataHeightCm(null);
          setMixedBatchPackDataWeightG(null);
        }}
        onOk={submitMixedBatchPackData}
      >
        <div style={{ marginBottom: 10, color: '#595959' }}>
          本次将批量更新 <span style={{ color: '#1677ff', fontWeight: 600 }}>{mixedBatchSelectedItems.length}</span> 条指令的实际包装结果
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
          <div>
            <div style={{ marginBottom: 6 }}>长(cm)</div>
            <InputNumber
              precision={0}
              min={1}
              step={1}
              style={{ width: '100%' }}
              value={mixedBatchPackDataLengthCm ?? undefined}
              onChange={(v) => setMixedBatchPackDataLengthCm(v == null ? null : Number(v))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>宽(cm)</div>
            <InputNumber
              precision={0}
              min={1}
              step={1}
              style={{ width: '100%' }}
              value={mixedBatchPackDataWidthCm ?? undefined}
              onChange={(v) => setMixedBatchPackDataWidthCm(v == null ? null : Number(v))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>高(cm)</div>
            <InputNumber
              precision={0}
              min={1}
              step={1}
              style={{ width: '100%' }}
              value={mixedBatchPackDataHeightCm ?? undefined}
              onChange={(v) => setMixedBatchPackDataHeightCm(v == null ? null : Number(v))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>重量(g)</div>
            <InputNumber
              precision={0}
              min={1}
              step={1}
              style={{ width: '100%' }}
              value={mixedBatchPackDataWeightG ?? undefined}
              onChange={(v) => setMixedBatchPackDataWeightG(v == null ? null : Number(v))}
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={mixedBatchUnoperatedReasonModalOpen}
        title={(
          <span>
            标注无法操作
            <span style={{ margin: '0 6px', color: '#8c8c8c' }}>|</span>
            <span style={{ color: '#1677ff' }}>
              {getInstructionSystemCode(mixedBatchUnoperatedReasonInstr) || '-'}
            </span>
          </span>
        )}
        onCancel={() => {
          setMixedBatchUnoperatedReasonModalOpen(false);
          setMixedBatchUnoperatedReasonInstr(null);
          setMixedBatchUnoperatedReasonInput('');
        }}
        onOk={confirmMixedBatchUnoperatedReason}
      >
        <div style={{ paddingTop: 6 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>无法操作原因：</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ marginRight: 8 }}>常用：</span>
            <Button
              size="small"
              type="default"
              onClick={() => setMixedBatchUnoperatedReasonInput((prev) => (prev ? `${prev}、` : '') + '库存不足')}
            >
              库存不足
            </Button>
            <Button
              size="small"
              type="default"
              style={{ marginLeft: 8 }}
              onClick={() => setMixedBatchUnoperatedReasonInput((prev) => (prev ? `${prev}、` : '') + '指令错误')}
            >
              指令错误
            </Button>
          </div>
          <Input.TextArea
            rows={4}
            value={mixedBatchUnoperatedReasonInput}
            onChange={(e) => setMixedBatchUnoperatedReasonInput(e.target.value)}
            placeholder="请输入无法操作原因"
          />
        </div>
      </Modal>
      <Modal
        open={mixedBatchMarkAllUnoperatedModalOpen}
        title={`全部标注无法操作（共${mixedBatchFilteredItems.length}单）`}
        onCancel={() => {
          setMixedBatchMarkAllUnoperatedModalOpen(false);
          setMixedBatchMarkAllUnoperatedReason('');
        }}
        onOk={() => {
          const reason = String(mixedBatchMarkAllUnoperatedReason || '').trim();
          if (!reason) {
            message.warning('请输入无法操作原因');
            return;
          }
          const keys = mixedBatchFilteredItems.map((item) => getMixedBatchInstrKey(item?.instr)).filter(Boolean);
          setMixedBatchUnoperatedIds(new Set(keys));
          setMixedBatchDoneIds(new Set());
          setMixedBatchUnoperatedReasons((prev) => {
            const next = { ...prev };
            keys.forEach((k) => { next[k] = reason; });
            return next;
          });
          setMixedBatchMarkAllUnoperatedModalOpen(false);
          setMixedBatchMarkAllUnoperatedReason('');
        }}
      >
        <div style={{ paddingTop: 6 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>无法操作原因：</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ marginRight: 8 }}>常用：</span>
            <Button
              size="small"
              type="default"
              onClick={() => setMixedBatchMarkAllUnoperatedReason((prev) => (prev ? `${prev}、` : '') + '库存不足')}
            >
              库存不足
            </Button>
            <Button
              size="small"
              type="default"
              style={{ marginLeft: 8 }}
              onClick={() => setMixedBatchMarkAllUnoperatedReason((prev) => (prev ? `${prev}、` : '') + '指令错误')}
            >
              指令错误
            </Button>
          </div>
          <Input.TextArea
            rows={4}
            value={mixedBatchMarkAllUnoperatedReason}
            onChange={(e) => setMixedBatchMarkAllUnoperatedReason(e.target.value)}
            placeholder="请输入无法操作原因"
          />
        </div>
      </Modal>
      <Modal
        open={singleTabSortVerifyOpen}
        title="分拣验证"
        footer={null}
        width={820}
        onCancel={() => {
          setSingleTabSortVerifyOpen(false);
          setSingleTabSortVerifyContext(null);
          setSingleTabSortVerifyInput('');
          setSingleTabSortVerifyPriorityIu('');
        }}
      >
        {(() => {
          const ctx = singleTabSortVerifyContext;
          if (!ctx) return <div style={{ color: '#8c8c8c' }}>暂无指令信息</div>;
          const sortPosition = String(ctx?.sortingPosition || '').trim();
          const refocusInput = () => {
            setTimeout(() => {
              try { singleTabSortVerifyInputRef.current?.focus?.(); } catch {}
            }, 0);
          };
          const unitRows = (() => {
            const rows = Array.isArray(ctx?.unitRows) ? ctx.unitRows : [];
            return rows.map((r: any) => {
              const iuCode = String(r?.iuCode || '').trim();
              const shipQty = Number(r?.shipQty ?? 0) || 0;
              const pickedQty = iuCode && sortPosition ? getPickedQty(ctx.sourceTabName, sortPosition, iuCode) : 0;
              return {
                iuCode,
                sortBarcode: String(r?.sortBarcode || '').trim() || '-',
                shipQty,
                pickedQty,
              };
            });
          })();
          const unitRowsOrdered = (() => {
            const list = [...unitRows];
            const pri = String(singleTabSortVerifyPriorityIu || '').trim().toLowerCase();
            if (!pri) return list;
            list.sort((a: any, b: any) => {
              const aPri = String(a?.iuCode || '').trim().toLowerCase() === pri ? 1 : 0;
              const bPri = String(b?.iuCode || '').trim().toLowerCase() === pri ? 1 : 0;
              return bPri - aPri;
            });
            return list;
          })();
          const allSortTargetsDone = unitRows.length > 0 && unitRows.every((row: any) => {
            const picked = Number(row?.pickedQty ?? 0) || 0;
            const ship = Number(row?.shipQty ?? 0) || 0;
            return picked >= ship;
          });
          const submit = () => {
            const result = runSortVerifyScanCore({
              sourceTabName: ctx.sourceTabName,
              sortingPosition: sortPosition,
              scanText: singleTabSortVerifyInput,
              unitRows: unitRowsOrdered,
            });
            if (result.status === 'empty') {
              message.warning('请输入库存单元分拣条码或系统编码');
              refocusInput();
              return;
            }
            if (result.status === 'no_match') {
              playSortVerifyAlertBeep();
              Modal.warning({ title: '匹配失败', content: '未找到匹配的发货库存单元记录' });
              setSingleTabSortVerifyInput('');
              refocusInput();
              return;
            }
            if (result.status === 'reached') {
              playSortVerifyAlertBeep();
              Modal.warning({ title: '提示', content: '此库存单元已达到分拣目标' });
              setSingleTabSortVerifyInput('');
              refocusInput();
              return;
            }
            setSingleTabSortVerifyPriorityIu(result.matchedIuCode || '');
            setSingleTabSortVerifyInput('');
            refocusInput();
            if (result.reachedAll) {
              playSortVerifyAlertBeep();
              const doneModal = Modal.warning({ title: '提示', content: '所有库存单元都已达到分拣目标' });
              setTimeout(() => {
                try { doneModal.destroy(); } catch {}
                setSingleTabSortVerifyOpen(false);
                setSingleTabSortVerifyContext(null);
                setSingleTabSortVerifyInput('');
                setSingleTabSortVerifyPriorityIu('');
              }, 900);
            }
          };
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {String(ctx?.instructionCode || '').trim() ? (
                  <>
                    <span style={{ color: '#8c8c8c' }}>指令:</span>
                    <span style={{ color: '#1677ff', fontWeight: 600 }}>
                      {String(ctx?.instructionCode || '-')}
                    </span>
                  </>
                ) : (
                  <>
                    <span style={{ color: '#8c8c8c' }}>指令数量:</span>
                    <span style={{ color: '#1677ff', fontWeight: 600 }}>
                      {Number(ctx?.instructionCount ?? 0)}单
                    </span>
                  </>
                )}
                <span style={{ color: '#8c8c8c' }}>分拣位:</span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 72,
                    padding: '4px 12px',
                    border: '1px solid #1677ff',
                    borderRadius: 6,
                    background: '#e6f4ff',
                    color: '#0958d9',
                    fontWeight: 700,
                    fontSize: 18,
                    lineHeight: 1.2,
                  }}
                >
                  {sortPosition || '-'}
                </span>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Input
                  ref={singleTabSortVerifyInputRef}
                  size="small"
                  value={singleTabSortVerifyInput}
                  onChange={(e) => setSingleTabSortVerifyInput(e.target.value)}
                  onPressEnter={submit}
                  placeholder="扫描库存单元编码"
                  style={{ width: 240 }}
                  disabled={allSortTargetsDone}
                />
                <Button size="small" type="primary" onClick={submit} disabled={allSortTargetsDone}>输入</Button>
                {allSortTargetsDone ? <Tag color="green" style={{ margin: 0 }}>已全部达标</Tag> : null}
              </div>
              <Table
                size="small"
                rowKey={(row: any) => row.iuCode}
                pagination={false}
                dataSource={unitRowsOrdered}
                onRow={(row: any) => {
                  const picked = Number(row?.pickedQty ?? 0) || 0;
                  const ship = Number(row?.shipQty ?? 0) || 0;
                  return picked >= ship ? { style: { background: '#f6ffed' } } : {};
                }}
                columns={[
                  { title: '库存单元系统编码', dataIndex: 'iuCode', key: 'iuCode', width: 260, align: 'center' as const },
                  { title: '分拣条码', dataIndex: 'sortBarcode', key: 'sortBarcode', width: 220, align: 'center' as const },
                  {
                    title: '已分拣/发货数量',
                    key: 'picked_ship',
                    width: 150,
                    align: 'center' as const,
                    render: (_: any, row: any) => {
                      const picked = Number(row?.pickedQty ?? 0) || 0;
                      const ship = Number(row?.shipQty ?? 0) || 0;
                      return `${picked}/${ship}`;
                    },
                  },
                ]}
              />
            </div>
          );
        })()}
      </Modal>
      <Modal
        open={mixedSortVerifyOpen}
        title="分拣验证"
        footer={null}
        width={820}
        onCancel={() => {
          setMixedSortVerifyOpen(false);
          setMixedSortVerifyInstruction(null);
          setMixedSortVerifySortPosition('');
          setMixedSortVerifyInput('');
          setMixedSortVerifyPriorityIu('');
        }}
      >
        {(() => {
          const instr = mixedSortVerifyInstruction;
          if (!instr) return <div style={{ color: '#8c8c8c' }}>暂无指令信息</div>;
          const sortPosition = String(mixedSortVerifySortPosition || '').trim();
          const refocusMixedSortVerifyInput = () => {
            setTimeout(() => {
              try { mixedSortVerifyInputRef.current?.focus?.(); } catch {}
            }, 0);
          };
          const playMixedSortVerifyAlertBeep = () => {
            try {
              const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
              if (!AC) return;
              const ctx = new AC();
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.type = 'square';
              osc.frequency.value = 880;
              gain.gain.value = 0.06;
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.start();
              osc.stop(ctx.currentTime + 0.12);
              osc.onended = () => { try { ctx.close(); } catch {} };
            } catch {}
          };
          const unitRows = (() => {
            const rows = new Map<string, { iuCode: string; sortBarcode: string; shipQty: number; pickedQty: number }>();
            const targets = Array.isArray(instr?.targets_detail) ? instr.targets_detail : [];
            targets.forEach((t: any) => {
              if (String(t?.target_type || '').trim() !== 'inventory_unit') return;
              const snap = t?.snapshot || {};
              const iuCode = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
              if (!iuCode) return;
              const sortArr = Array.isArray(t?.sorting_barcodes)
                ? t.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
                : (Array.isArray(snap?.sorting_barcodes)
                  ? snap.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
                  : []);
              const sortBarcode = sortArr.length > 0 ? sortArr.join(',') : String(snap?.sorting_barcode || '').trim() || '-';
              const shipQty = Number(t?.expected_qty ?? snap?.expected_qty ?? 0) || 0;
              const pickedQty = sortPosition ? getPickedQty('已有运单', sortPosition, iuCode) : 0;
              const prev = rows.get(iuCode);
              if (!prev) {
                rows.set(iuCode, { iuCode, sortBarcode, shipQty, pickedQty });
              } else {
                prev.shipQty += shipQty;
                prev.pickedQty = pickedQty;
                if (prev.sortBarcode === '-' && sortBarcode !== '-') prev.sortBarcode = sortBarcode;
              }
            });
            return Array.from(rows.values());
          })();
          const unitRowsOrdered = (() => {
            const list = [...unitRows];
            const pri = String(mixedSortVerifyPriorityIu || '').trim().toLowerCase();
            if (!pri) return list;
            list.sort((a: any, b: any) => {
              const aPri = String(a?.iuCode || '').trim().toLowerCase() === pri ? 1 : 0;
              const bPri = String(b?.iuCode || '').trim().toLowerCase() === pri ? 1 : 0;
              return bPri - aPri;
            });
            return list;
          })();
          const allSortTargetsDone = unitRows.length > 0 && unitRows.every((row: any) => {
            const picked = Number(row?.pickedQty ?? 0) || 0;
            const ship = Number(row?.shipQty ?? 0) || 0;
            return picked >= ship;
          });
          const handleMixedSortVerifySubmit = () => {
            const scanText = String(mixedSortVerifyInput || '').trim();
            if (!scanText) {
              message.warning('请输入库存单元分拣条码或系统编码');
              refocusMixedSortVerifyInput();
              return;
            }
            const norm = scanText.toLowerCase();
            const matchedRow = unitRowsOrdered.find((row: any) => {
              const iuCode = String(row?.iuCode || '').trim().toLowerCase();
              if (iuCode && iuCode === norm) return true;
              const sortTokens = String(row?.sortBarcode || '')
                .split(/[,\n，\s]+/)
                .map((x: string) => String(x || '').trim().toLowerCase())
                .filter(Boolean);
              return sortTokens.includes(norm);
            });
            if (!matchedRow || !sortPosition) {
              playMixedSortVerifyAlertBeep();
              Modal.warning({ title: '匹配失败', content: '未找到匹配的发货库存单元记录' });
              setMixedSortVerifyInput('');
              refocusMixedSortVerifyInput();
              return;
            }
            const prevPicked = Number(matchedRow?.pickedQty ?? 0) || 0;
            const shipQty = Number(matchedRow?.shipQty ?? 0) || 0;
            if (prevPicked >= shipQty) {
              playMixedSortVerifyAlertBeep();
              Modal.warning({ title: '提示', content: '此库存单元已达到分拣目标' });
              setMixedSortVerifyInput('');
              refocusMixedSortVerifyInput();
              return;
            }
            const iu = String(matchedRow?.iuCode || '').trim().toLowerCase();
            const key = `已有运单|${String(sortPosition).trim()}|${iu}`;
            const nextPicked = Math.min(shipQty, prevPicked + 1);
            setSortedPickedQtyMap((prev) => ({ ...prev, [key]: nextPicked }));
            setMixedSortVerifyPriorityIu(String(matchedRow?.iuCode || '').trim());
            setMixedSortVerifyInput('');
            refocusMixedSortVerifyInput();
            const reached = unitRows.every((row: any) => {
              const rowIu = String(row?.iuCode || '').trim().toLowerCase();
              const picked = rowIu === iu ? nextPicked : (Number(row?.pickedQty ?? 0) || 0);
              const ship = Number(row?.shipQty ?? 0) || 0;
              return picked >= ship;
            });
            if (reached) {
              playMixedSortVerifyAlertBeep();
              const doneModal = Modal.warning({ title: '提示', content: '所有库存单元都已达到分拣目标' });
              setTimeout(() => {
                try { doneModal.destroy(); } catch {}
                setMixedSortVerifyOpen(false);
                setMixedSortVerifyInstruction(null);
                setMixedSortVerifySortPosition('');
                setMixedSortVerifyInput('');
                setMixedSortVerifyPriorityIu('');
              }, 900);
            }
          };
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ color: '#8c8c8c' }}>指令:</span>
                <span style={{ color: '#1677ff', fontWeight: 600 }}>
                  {String(instr?.instruction_system_code ?? instr?.instruction?.instruction_system_code ?? instr?.system_code ?? '').trim() || '-'}
                </span>
                <span style={{ color: '#8c8c8c' }}>分拣位:</span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 72,
                    padding: '4px 12px',
                    border: '1px solid #1677ff',
                    borderRadius: 6,
                    background: '#e6f4ff',
                    color: '#0958d9',
                    fontWeight: 700,
                    fontSize: 18,
                    lineHeight: 1.2,
                  }}
                >
                  {sortPosition || '-'}
                </span>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Input
                  ref={mixedSortVerifyInputRef}
                  size="small"
                  value={mixedSortVerifyInput}
                  onChange={(e) => setMixedSortVerifyInput(e.target.value)}
                  onPressEnter={handleMixedSortVerifySubmit}
                  placeholder="扫描库存单元编码"
                  style={{ width: 240 }}
                  disabled={allSortTargetsDone}
                />
                <Button size="small" type="primary" onClick={handleMixedSortVerifySubmit} disabled={allSortTargetsDone}>输入</Button>
                {allSortTargetsDone ? <Tag color="green" style={{ margin: 0 }}>已全部达标</Tag> : null}
              </div>
              <Table
                size="small"
                rowKey={(row: any) => row.iuCode}
                pagination={false}
                dataSource={unitRowsOrdered}
                onRow={(row: any) => {
                  const picked = Number(row?.pickedQty ?? 0) || 0;
                  const ship = Number(row?.shipQty ?? 0) || 0;
                  return picked >= ship ? { style: { background: '#f6ffed' } } : {};
                }}
                columns={[
                  { title: '库存单元系统编码', dataIndex: 'iuCode', key: 'iuCode', width: 260, align: 'center' as const },
                  { title: '分拣条码', dataIndex: 'sortBarcode', key: 'sortBarcode', width: 220, align: 'center' as const },
                  {
                    title: '已分拣/发货数量',
                    key: 'picked_ship',
                    width: 150,
                    align: 'center' as const,
                    render: (_: any, row: any) => {
                      const picked = Number(row?.pickedQty ?? 0) || 0;
                      const ship = Number(row?.shipQty ?? 0) || 0;
                      return `${picked}/${ship}`;
                    },
                  },
                ]}
              />
            </div>
          );
        })()}
      </Modal>
      <Modal
        open={directOperateModalOpen}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: 40 }}>
            <span style={{ color: '#1677ff', fontWeight: 700 }}>
              {String(directOperateInstruction?.instruction_system_code ?? directOperateInstruction?.instruction?.instruction_system_code ?? directOperateInstruction?.system_code ?? '').trim() || '-'}
            </span>
            <span style={{ flex: 1, textAlign: 'center', fontWeight: 600, color: '#595959' }}>
              {(() => {
                const instr = directOperateInstruction;
                const targets = Array.isArray(instr?.targets_detail) ? instr.targets_detail : [];
                const byIu = new Set<string>();
                let totalQty = 0;
                targets.forEach((t: any) => {
                  if (String(t?.target_type || '').trim() !== 'inventory_unit') return;
                  const snap = t?.snapshot || {};
                  const iuCode = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
                  if (iuCode) byIu.add(iuCode);
                  totalQty += Number(t?.expected_qty ?? snap?.expected_qty ?? 0) || 0;
                });
                return `${byIu.size}款${totalQty}件`;
              })()}
            </span>
            <span style={{ width: 56 }} />
          </div>
        )}
        footer={null}
        width={920}
        onCancel={() => {
          setDirectOperateModalOpen(false);
          setDirectOperateInstruction(null);
          setDirectOperatePackingEstimate(null);
          setDirectOperatePackingEstimateLoading(false);
          setDirectOperateSortVerifyOpen(false);
          setDirectOperateSortVerifyInput('');
          setDirectOperateUnitPriorityCode('');
          setDirectOperateWaybillScanTargetKey('');
          setDirectOperateWaybillScanInput('');
          setDirectOperateWaybillScanResult('');
          setDirectOperateWaybillScanMatched(null);
          directOperateCloseAfterUnoperatedSubmitRef.current = false;
        }}
      >
        {(() => {
          const instr = directOperateInstruction;
          if (!instr) return <div style={{ color: '#8c8c8c' }}>暂无指令信息</div>;
          const packingKey = String(instr?.estimated_packing_method ?? '').trim() || '_other';
          const packingLabel = getPackagingMethodLabel(packingKey);
          const tipsRaw = String(instr?.execution_tips ?? '').trim();
          const remarks = String(instr?.instruction_remarks ?? instr?.remarks ?? '').trim() || '-';
          const carrier = displayDash(instr?.waybill_carrier);
          const trackingNoRaw = String(instr?.waybill_tracking_no ?? instr?.instruction?.waybill_tracking_no ?? '').trim();
          const trackingNo = displayDash(trackingNoRaw);
          const attList = Array.isArray(instr?.attachments_detail) ? instr.attachments_detail : [];
          const directSortPosition = String(instr?.__directSortPosition || '').trim();
          const directPackDataKey = getDirectOperatePackDataKey(instr);
          const directPackDataFromMap = directPackDataKey ? directOperatePackDataByInstrKey[directPackDataKey] : undefined;
          const directPackDataFromInstr = (() => {
            const l = Number(instr?.actual_pack_length_cm ?? instr?.instruction?.actual_pack_length_cm ?? 0);
            const w = Number(instr?.actual_pack_width_cm ?? instr?.instruction?.actual_pack_width_cm ?? 0);
            const h = Number(instr?.actual_pack_height_cm ?? instr?.instruction?.actual_pack_height_cm ?? 0);
            const g = Number(instr?.actual_gross_weight_g ?? instr?.instruction?.actual_gross_weight_g ?? 0);
            if (!(l > 0 && w > 0 && h > 0 && g > 0)) return undefined;
            return {
              lengthCm: Math.round(l),
              widthCm: Math.round(w),
              heightCm: Math.round(h),
              weightG: Math.round(g),
            };
          })();
          const directPackData = directPackDataFromMap || directPackDataFromInstr;
          const directInstructionId = Number(instr?.instruction ?? instr?.instruction_id ?? instr?.id ?? 0);
          const directCreateIocLoading = directInstructionId > 0 ? !!operateConfirmCreateIocLoadingByKey[`direct:${directInstructionId}`] : false;
          const directIocCode = String(
            (directInstructionId > 0 ? operateConfirmIocCodeByInstrId[String(directInstructionId)] : '')
            || instr?.created_ioc_code
            || instr?.instruction?.created_ioc_code
            || '',
          ).trim();
          const directIocSummary = directInstructionId > 0 ? directOperateIocSummaryByInstrId[String(directInstructionId)] : null;
          const directIocTotalQty = (() => {
            const fromSummary = Number(directIocSummary?.totalQty ?? 0) || 0;
            if (fromSummary > 0) return fromSummary;
            const targets = Array.isArray(instr?.targets_detail) ? instr.targets_detail : [];
            return targets.reduce((sum: number, t: any) => {
              if (String(t?.target_type || '').trim() !== 'inventory_unit') return sum;
              const qty = Number(t?.expected_qty ?? t?.target_qty ?? 0) || 0;
              return sum + Math.max(Math.floor(qty), 0);
            }, 0);
          })();
          const directIocLabelLoading = directInstructionId > 0 ? !!directOperateIocLabelLoadingByInstrId[String(directInstructionId)] : false;
          const buyWaybillInfo = instr?.buy_waybill_info || {};
          const pickText = (v: any, fallback: any = '') => {
            const raw = v ?? fallback;
            const txt = String(raw ?? '').trim();
            if (!txt) return '';
            const low = txt.toLowerCase();
            if (low === 'none' || low === 'null' || low === 'undefined') return '';
            return txt;
          };
          const recipient = buyWaybillInfo?.recipient || {};
          const insuranceRequired = buyWaybillInfo?.insurance_signature?.insurance_required;
          const signatureRequired = buyWaybillInfo?.insurance_signature?.signature_required;
          const insuredAmount = buyWaybillInfo?.insurance_signature?.insured_amount;
          const insuranceInfo = insuranceRequired === true
            ? `需要${Number.isFinite(Number(insuredAmount)) ? ` ($${Number(insuredAmount)})` : ''}`
            : insuranceRequired === false
              ? '不需要'
              : '';
          const signatureInfo = signatureRequired === true ? '需要' : signatureRequired === false ? '不需要' : '';
          const line1 = pickText(recipient?.name);
          const line2 = [pickText(recipient?.address_line_1), pickText(recipient?.address_line_2)].filter(Boolean).join(', ');
          const line3 = [pickText(recipient?.city), pickText(recipient?.state), pickText(recipient?.postal_code)].filter(Boolean).join(' ');
          const line4 = [pickText(recipient?.company), pickText(recipient?.phone), pickText(recipient?.email), pickText(recipient?.country)].filter(Boolean).join(' ');
          const directSourceTabName: '已有运单' | '仓库提供运单' | '先申请包装数据' = (() => {
            const raw = String(instr?.__directSourceTabName || '').trim();
            if (raw === '仓库提供运单' || raw === '先申请包装数据' || raw === '已有运单') return raw;
            if (directSortPosition.startsWith('B')) return '仓库提供运单';
            if (directSortPosition.startsWith('C')) return '先申请包装数据';
            return '已有运单';
          })();
          const unitRows = (() => {
            const rows = new Map<string, { iuCode: string; sortBarcode: string; shipQty: number; pickedQty: number }>();
            const targets = Array.isArray(instr?.targets_detail) ? instr.targets_detail : [];
            targets.forEach((t: any) => {
              if (String(t?.target_type || '').trim() !== 'inventory_unit') return;
              const snap = t?.snapshot || {};
              const iuCode = String(t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '').trim();
              if (!iuCode) return;
              const sortArr = Array.isArray(t?.sorting_barcodes)
                ? t.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
                : (Array.isArray(snap?.sorting_barcodes)
                  ? snap.sorting_barcodes.map((x: any) => x?.code ?? x).filter((x: any) => x && String(x).trim())
                  : []);
              const sortBarcode = sortArr.length > 0 ? sortArr.join(',') : String(snap?.sorting_barcode || '').trim() || '-';
              const shipQty = Number(t?.expected_qty ?? snap?.expected_qty ?? 0) || 0;
              const pickedQty = directSortPosition ? getPickedQty(directSourceTabName, directSortPosition, iuCode) : 0;
              const prev = rows.get(iuCode);
              if (!prev) {
                rows.set(iuCode, { iuCode, sortBarcode, shipQty, pickedQty });
              } else {
                prev.shipQty += shipQty;
                prev.pickedQty = pickedQty;
                if (prev.sortBarcode === '-' && sortBarcode !== '-') prev.sortBarcode = sortBarcode;
              }
            });
            return Array.from(rows.values());
          })();
          const waybillAtts = getWaybillAttachments(instr);
          const hasUploadedWaybillInfoAndAttachment = !!trackingNoRaw && waybillAtts.length > 0;
          const otherPrintableAtts = attList.filter((att: any) => {
            if (isWaybillAttachment(att)) return false;
            const meta = att?.meta;
            const parsed = typeof meta === 'string'
              ? (() => { try { return JSON.parse(meta); } catch { return {}; } })()
              : (meta || {});
            const copies = Number(parsed?.print_config?.copies ?? 0);
            return copies > 0;
          });
          const showDirectOperateReminder = !!tipsRaw || otherPrintableAtts.length > 0;
          const refocusDirectSortVerifyInput = () => {
            setTimeout(() => {
              try { directOperateSortVerifyInputRef.current?.focus?.(); } catch {}
            }, 0);
          };
          const unitRowsOrdered = (() => {
            const list = [...unitRows];
            const pri = String(directOperateUnitPriorityCode || '').trim().toLowerCase();
            if (!pri) return list;
            list.sort((a: any, b: any) => {
              const aPri = String(a?.iuCode || '').trim().toLowerCase() === pri ? 1 : 0;
              const bPri = String(b?.iuCode || '').trim().toLowerCase() === pri ? 1 : 0;
              return bPri - aPri;
            });
            return list;
          })();
          const allSortTargetsDone = unitRows.length > 0 && unitRows.every((row: any) => {
            const picked = Number(row?.pickedQty ?? 0) || 0;
            const ship = Number(row?.shipQty ?? 0) || 0;
            return picked >= ship;
          });
          const playVerifyAlertBeep = () => {
            try {
              const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
              if (!AC) return;
              const ctx = new AC();
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.type = 'square';
              osc.frequency.value = 880;
              gain.gain.value = 0.06;
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.start();
              osc.stop(ctx.currentTime + 0.12);
              osc.onended = () => { try { ctx.close(); } catch {} };
            } catch {}
          };
          const handleDirectSortVerifySubmit = () => {
            const scanText = String(directOperateSortVerifyInput || '').trim();
            if (!scanText) {
              message.warning('请输入库存单元分拣条码或系统编码');
              refocusDirectSortVerifyInput();
              return;
            }
            const norm = scanText.toLowerCase();
            const matchedRow = unitRowsOrdered.find((row: any) => {
              const iuCode = String(row?.iuCode || '').trim().toLowerCase();
              if (iuCode && iuCode === norm) return true;
              const sortTokens = String(row?.sortBarcode || '')
                .split(/[,\n，\s]+/)
                .map((x: string) => String(x || '').trim().toLowerCase())
                .filter(Boolean);
              return sortTokens.includes(norm);
            });
            if (!matchedRow || !directSortPosition) {
              playVerifyAlertBeep();
              Modal.warning({ title: '匹配失败', content: '未找到匹配的发货库存单元记录' });
              setDirectOperateSortVerifyInput('');
              refocusDirectSortVerifyInput();
              return;
            }
            const prevPicked = Number(matchedRow?.pickedQty ?? 0) || 0;
            const shipQty = Number(matchedRow?.shipQty ?? 0) || 0;
            if (prevPicked >= shipQty) {
              playVerifyAlertBeep();
              Modal.warning({ title: '提示', content: '此库存单元已达到分拣目标' });
              setDirectOperateSortVerifyInput('');
              refocusDirectSortVerifyInput();
              return;
            }
            const iu = String(matchedRow?.iuCode || '').trim().toLowerCase();
            const key = `${directSourceTabName}|${String(directSortPosition).trim()}|${iu}`;
            const nextPicked = Math.min(shipQty, prevPicked + 1);
            setSortedPickedQtyMap((prev) => ({ ...prev, [key]: nextPicked }));
            setDirectOperateUnitPriorityCode(String(matchedRow?.iuCode || '').trim());
            setDirectOperateSortVerifyInput('');
            refocusDirectSortVerifyInput();
            const reached = unitRows.every((row: any) => {
              const rowIu = String(row?.iuCode || '').trim().toLowerCase();
              const picked = rowIu === iu ? nextPicked : (Number(row?.pickedQty ?? 0) || 0);
              const ship = Number(row?.shipQty ?? 0) || 0;
              return picked >= ship;
            });
            if (reached) {
              playVerifyAlertBeep();
              Modal.warning({ title: '提示', content: '所有库存单元都已达到分拣目标' });
            }
          };
          const waybillScanOpen = directOperateWaybillScanTargetKey === '__waybill_scan__';
          const refocusWaybillScanInput = () => {
            setTimeout(() => {
              try { directOperateWaybillScanInputRef.current?.focus?.(); } catch {}
            }, 0);
          };
          const submitWaybillScan = () => {
            const val = String(directOperateWaybillScanInput || '').trim();
            if (!val) {
              message.warning('请输入扫描物流运单');
              refocusWaybillScanInput();
              return;
            }
            const scanNorm = val.toLowerCase();
            const trackingNorm = String(trackingNoRaw || '').trim().toLowerCase();
            const shortLen = Math.min(scanNorm.length, trackingNorm.length);
            const scanCmp = shortLen > 0 ? scanNorm.slice(-shortLen) : '';
            const trackingCmp = shortLen > 0 ? trackingNorm.slice(-shortLen) : '';
            const matched = shortLen > 0 && scanCmp === trackingCmp;
            setDirectOperateWaybillScanResult(val);
            setDirectOperateWaybillScanMatched(matched);
            setDirectOperateWaybillScanInput('');
            refocusWaybillScanInput();
          };
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {showDirectOperateReminder ? (
                <div
                  style={{
                    background: '#fffbe6',
                    border: '1px solid #ffe58f',
                    color: '#cf1322',
                    padding: '8px 12px',
                    borderRadius: 4,
                    fontWeight: 600,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}
                >
                  {tipsRaw ? <div>操作提醒: {tipsRaw}</div> : null}
                  {otherPrintableAtts.length > 0 ? <div>请先打印其他附件再执行操作</div> : null}
                </div>
              ) : null}
              <div style={{ border: '1px solid #91caff', borderRadius: 6, padding: 12 }}>
                <div style={{ marginBottom: 8, color: '#8c8c8c', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                  <span>发货库存单元</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginLeft: 16 }}>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => {
                        setDirectOperateSortVerifyOpen(true);
                        setDirectOperateSortVerifyInput('');
                        setDirectOperateUnitPriorityCode('');
                        setSortedPickedQtyMap((prev) => {
                          const next = { ...prev };
                          unitRows.forEach((row: any) => {
                            const iu = String(row?.iuCode || '').trim().toLowerCase();
                            if (!iu || !directSortPosition) return;
                            const key = `${directSourceTabName}|${String(directSortPosition).trim()}|${iu}`;
                            next[key] = 0;
                          });
                          return next;
                        });
                        refocusDirectSortVerifyInput();
                      }}
                    >
                      分拣验证
                    </Button>
                    {directOperateSortVerifyOpen ? (
                      <>
                        <Input
                          ref={directOperateSortVerifyInputRef}
                          size="small"
                          value={directOperateSortVerifyInput}
                          onChange={(e) => setDirectOperateSortVerifyInput(e.target.value)}
                          onPressEnter={handleDirectSortVerifySubmit}
                          placeholder="扫描库存单元编码"
                          style={{ width: 220 }}
                          disabled={allSortTargetsDone}
                        />
                        <Button size="small" type="primary" onClick={handleDirectSortVerifySubmit} disabled={allSortTargetsDone}>输入</Button>
                      </>
                    ) : null}
                  </span>
                </div>
                <Table
                  size="small"
                  rowKey={(row: any) => row.iuCode}
                  pagination={false}
                  dataSource={unitRowsOrdered}
                  onRow={(row: any) => {
                    const picked = Number(row?.pickedQty ?? 0) || 0;
                    const ship = Number(row?.shipQty ?? 0) || 0;
                    return picked === ship ? { style: { background: '#f6ffed' } } : {};
                  }}
                  columns={[
                    {
                      title: <span style={{ color: '#8c8c8c' }}>库存单元系统编码</span>,
                      dataIndex: 'iuCode',
                      key: 'iuCode',
                      width: 220,
                      align: 'center' as const,
                      render: (v: any) => {
                        const code = String(v || '').trim();
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span>{code || '-'}</span>
                            {code ? (
                              <Tooltip title="复制">
                                <CopyOutlined
                                  style={{ color: '#1677ff', cursor: 'pointer' }}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await navigator.clipboard.writeText(code);
                                      message.success('已复制');
                                    } catch {
                                      message.error('复制失败');
                                    }
                                  }}
                                />
                              </Tooltip>
                            ) : null}
                          </span>
                        );
                      },
                    },
                    { title: <span style={{ color: '#8c8c8c' }}>分拣条码</span>, dataIndex: 'sortBarcode', key: 'sortBarcode', width: 180, align: 'center' as const },
                    {
                      title: <span style={{ color: '#8c8c8c' }}>已分拣/发货数量</span>,
                      key: 'picked_ship',
                      width: 150,
                      align: 'center' as const,
                      render: (_: any, row: any) => {
                        const picked = Number(row?.pickedQty ?? 0) || 0;
                        const ship = Number(row?.shipQty ?? 0) || 0;
                        const done = picked === ship;
                        return (
                          <span
                            style={{
                              display: 'inline-block',
                              minWidth: 72,
                              padding: '1px 8px',
                              borderRadius: 4,
                              border: `1px solid ${done ? '#52c41a' : '#ff4d4f'}`,
                              color: done ? '#389e0d' : '#cf1322',
                              background: done ? '#f6ffed' : '#fff2f0',
                              fontWeight: 600,
                              lineHeight: 1.4,
                            }}
                          >
                            {picked}/{ship}
                          </span>
                        );
                      },
                    },
                  ]}
                  locale={{ emptyText: '暂无库存单元信息' }}
                />
              </div>
              <div style={{ border: '1px solid #ff7a45', borderRadius: 6, padding: 12, display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <span>
                    <span style={{ color: '#8c8c8c', marginRight: 8 }}>包装及推荐包材信息:</span>
                    <span>{packingLabel}</span>
                    <span style={{ marginLeft: 10, color: '#595959' }}>
                      {directOperatePackingEstimateLoading ? (
                        <span style={{ color: '#1677ff' }}>推荐包材计算中...</span>
                      ) : directOperatePackingEstimate?.code || directOperatePackingEstimate?.name ? (
                        <span>
                          <span style={{ color: '#ff4d4f', marginRight: 8 }}>{directOperatePackingEstimate?.code || '-'}</span>
                          {directOperatePackingEstimate?.name || ''}
                        </span>
                      ) : (
                        <span style={{ color: '#8c8c8c' }}>无推荐包材</span>
                      )}
                    </span>
                  </span>
                  {directSourceTabName !== '已有运单' ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Button
                        size="small"
                        onClick={() => {
                          const existing = directPackData;
                          setDirectOperatePackDataLengthCm(existing ? Number(existing.lengthCm) : null);
                          setDirectOperatePackDataWidthCm(existing ? Number(existing.widthCm) : null);
                          setDirectOperatePackDataHeightCm(existing ? Number(existing.heightCm) : null);
                          setDirectOperatePackDataWeightG(existing ? Number(existing.weightG) : null);
                          setDirectOperatePackDataModalOpen(true);
                        }}
                      >
                        输入包装数据
                      </Button>
                      {directSourceTabName === '先申请包装数据' ? (
                        <Button
                          size="small"
                          loading={directCreateIocLoading}
                          disabled={!directPackData || directCreateIocLoading}
                          onClick={() => {
                            void createInstructionOutputContainerForDirectOperate(instr, directPackData || null);
                          }}
                        >
                          创建指令产出容器
                        </Button>
                      ) : null}
                    </span>
                  ) : null}
                </div>
                <div><span style={{ color: '#8c8c8c', marginRight: 8 }}>指令备注:</span><span>{remarks}</span></div>
                <div>
                  <span style={{ color: '#8c8c8c', marginRight: 8 }}>其他需打印附件:</span>
                  {otherPrintableAtts.length === 0 ? (
                    <span>-</span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {otherPrintableAtts.map((att: any, idx: number) => {
                        const name = String(att?.file_name ?? att?.file ?? att?.file_path ?? att?.label ?? '').trim() || `附件${idx + 1}`;
                        const printTip = formatPrintConfigForTitle(att) || '未设置打印';
                        return (
                          <span key={`${name}-${idx}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0, height: 'auto' }}
                              onClick={() => handlePrintableAttachmentClick(att, instr)}
                            >
                              {name}
                            </Button>
                            <Tooltip title={printTip}>
                              <PrinterOutlined style={{ color: '#1890ff', flexShrink: 0, cursor: 'pointer' }} />
                            </Tooltip>
                          </span>
                        );
                      })}
                    </span>
                  )}
                </div>
                {directSourceTabName !== '已有运单' ? (
                  <div>
                    <span style={{ color: '#8c8c8c', marginRight: 8 }}>包装数据:</span>
                    {directPackData ? (
                      <span style={{ whiteSpace: 'pre-wrap' }}>
                        {`尺寸 ${directPackData.lengthCm} × ${directPackData.widthCm} × ${directPackData.heightCm} cm  重量 ${directPackData.weightG} g    |   尺寸 ${(directPackData.lengthCm / 2.54).toFixed(2)} × ${(directPackData.widthCm / 2.54).toFixed(2)} × ${(directPackData.heightCm / 2.54).toFixed(2)} in    重量 ${formatLbOzFromG(directPackData.weightG)}`}
                      </span>
                    ) : (
                      <span>-</span>
                    )}
                  </div>
                ) : null}
                {directSourceTabName === '先申请包装数据' ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span>
                      <span style={{ color: '#8c8c8c', marginRight: 8 }}>已创建IOC:</span>
                      {directIocCode ? (
                        <span>
                          <span style={{ color: '#1677ff', fontWeight: 600, marginRight: 10 }}>{directIocCode}</span>
                          <span>{`包含库存单元总数 ${directIocTotalQty}`}</span>
                        </span>
                      ) : (
                        <span>-</span>
                      )}
                    </span>
                    <Button
                      size="small"
                      loading={directIocLabelLoading}
                      disabled={!directIocCode || directIocLabelLoading}
                      onClick={() => {
                        void previewDirectOperateIocLabel(instr, directIocCode, directPackData || null, directIocTotalQty);
                      }}
                    >
                      打印容器标签
                    </Button>
                  </div>
                ) : null}
              </div>
              <div style={{ border: '1px solid #b7eb8f', borderRadius: 6, padding: 12, display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
                    <span><span style={{ color: '#8c8c8c', marginRight: 8 }}>承运商:</span><span>{carrier}</span></span>
                    <span><span style={{ color: '#8c8c8c', marginRight: 8 }}>跟踪号:</span><span>{trackingNo}</span></span>
                  </div>
                  {directSourceTabName === '仓库提供运单' ? (
                    <Button
                      size="small"
                      onClick={() => {
                        setOperateConfirmBuyWaybillInstr({
                          ...instr,
                          __directPackData: directPackData || null,
                        });
                        setOperateConfirmBuyWaybillModalOpen(true);
                      }}
                    >
                      上传运单信息和附件
                    </Button>
                  ) : null}
                </div>
                <div>
                  <span style={{ color: '#8c8c8c', marginRight: 8 }}>物流运单附件:</span>
                  {waybillAtts.length === 0 ? (
                    <span>-</span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {waybillAtts.map((att: any, idx: number) => {
                        const name = String(att?.file_name ?? att?.file ?? att?.file_path ?? att?.label ?? '').trim() || `运单附件${idx + 1}`;
                        return (
                          <span key={`${name}-${idx}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 16 }}>
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0, height: 'auto' }}
                              onClick={() => handlePrintableAttachmentClick(att, instr)}
                            >
                              {name}
                            </Button>
                            <Button
                              size="small"
                              type="primary"
                              onClick={() => handlePrintableAttachmentDirectPrint(att, instr)}
                            >
                              打印
                            </Button>
                          </span>
                        );
                      })}
                    </span>
                  )}
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    onClick={() => {
                      setDirectOperateWaybillScanTargetKey('__waybill_scan__');
                      setDirectOperateWaybillScanInput('');
                      setDirectOperateWaybillScanResult('');
                      setDirectOperateWaybillScanMatched(null);
                      setTimeout(() => {
                        try { directOperateWaybillScanInputRef.current?.focus?.(); } catch {}
                      }, 0);
                    }}
                  >
                    运单验证
                  </Button>
                  {waybillScanOpen ? (
                    <>
                      <Input
                        ref={directOperateWaybillScanInputRef}
                        size="small"
                        value={directOperateWaybillScanInput}
                        onChange={(e) => setDirectOperateWaybillScanInput(e.target.value)}
                        onPressEnter={submitWaybillScan}
                        placeholder="扫描物流运单"
                        style={{ width: 220 }}
                      />
                      <Button size="small" type="primary" onClick={submitWaybillScan} style={{ marginLeft: 10, marginRight: 10 }}>
                        输入
                      </Button>
                      {directOperateWaybillScanResult ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <span style={{ color: '#1677ff', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {directOperateWaybillScanResult}
                          </span>
                          {directOperateWaybillScanMatched === null ? null : directOperateWaybillScanMatched ? (
                            <span style={{ color: '#52c41a', fontWeight: 600 }}>√ 跟踪号一致</span>
                          ) : (
                            <span style={{ color: '#ff4d4f', fontWeight: 600 }}>× 跟踪号不一致</span>
                          )}
                        </span>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </div>
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button
                  danger
                  onClick={() => {
                    directOperateCloseAfterUnoperatedSubmitRef.current = true;
                    handleSetGlobalWaybillStatus(instr, 'unoperated');
                  }}
                >
                  标注无法操作
                </Button>
                <Button
                  type="primary"
                  disabled={
                    (directSourceTabName === '仓库提供运单' && !hasUploadedWaybillInfoAndAttachment)
                    || (directSourceTabName === '先申请包装数据' && !directIocCode)
                  }
                  onClick={() => {
                    const doComplete = () => {
                      handleSetGlobalWaybillStatus(instr, 'completed');
                      setDirectOperateModalOpen(false);
                      setDirectOperateInstruction(null);
                      setDirectOperatePackingEstimate(null);
                      setDirectOperatePackingEstimateLoading(false);
                      setDirectOperateSortVerifyOpen(false);
                      setDirectOperateSortVerifyInput('');
                      setDirectOperateUnitPriorityCode('');
                      setDirectOperateWaybillScanTargetKey('');
                      setDirectOperateWaybillScanInput('');
                      setDirectOperateWaybillScanResult('');
                      setDirectOperateWaybillScanMatched(null);
                      directOperateCloseAfterUnoperatedSubmitRef.current = false;
                    };
                    const ret = appendDirectOperateWaybillScanRecord(
                      instr,
                      directOperateWaybillScanResult,
                      directOperateWaybillScanMatched,
                    );
                    if (!ret.ok) {
                      showDuplicateScanConfirm(
                        String(directOperateWaybillScanResult || '').trim(),
                        ret.duplicateRecords || [],
                        () => {
                          const retry = appendDirectOperateWaybillScanRecord(
                            instr,
                            directOperateWaybillScanResult,
                            directOperateWaybillScanMatched,
                            true,
                          );
                          if (!retry.ok) return;
                          doComplete();
                        },
                      );
                      return;
                    }
                    doComplete();
                  }}
                >
                  标注已完成
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>
      <Modal
        open={directOperatePackDataModalOpen}
        title="输入包装数据"
        zIndex={1450}
        destroyOnClose
        onCancel={() => setDirectOperatePackDataModalOpen(false)}
        onOk={async () => {
          const l = Number(directOperatePackDataLengthCm ?? 0);
          const w = Number(directOperatePackDataWidthCm ?? 0);
          const h = Number(directOperatePackDataHeightCm ?? 0);
          const g = Number(directOperatePackDataWeightG ?? 0);
          if (!Number.isInteger(l) || !Number.isInteger(w) || !Number.isInteger(h) || !Number.isInteger(g) || l <= 0 || w <= 0 || h <= 0 || g <= 0) {
            message.warning('请填写大于0的整数（长宽高cm、重量g）');
            return;
          }
          const key = getDirectOperatePackDataKey(directOperateInstruction);
          if (!key) {
            message.warning('缺少指令信息');
            return;
          }
          const instructionId = Number(
            directOperateInstruction?.instruction
            ?? directOperateInstruction?.instruction_id
            ?? directOperateInstruction?.id
            ?? 0,
          );
          if (!instructionId) {
            message.warning('缺少指令ID');
            return;
          }
          const payload = {
            actual_pack_length_cm: l,
            actual_pack_width_cm: w,
            actual_pack_height_cm: h,
            actual_gross_weight_g: g,
            pack_result_status: 'provided',
          };
          try {
            await request.patch(`/api/v1/instruction/instructions/${instructionId}/`, payload);
            applyLocalActualPackResultPatch(instructionId, payload);
          } catch (e: any) {
            message.error(e?.response?.data?.detail || e?.message || '保存包装数据失败');
            return;
          }
          setDirectOperatePackDataByInstrKey((prev) => ({
            ...prev,
            [key]: { lengthCm: l, widthCm: w, heightCm: h, weightG: g },
          }));
          message.success('包装数据已保存');
          setDirectOperatePackDataModalOpen(false);
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
          <div>
            <div style={{ marginBottom: 6 }}>长(cm)</div>
            <InputNumber
              precision={0}
              min={1}
              step={1}
              style={{ width: '100%' }}
              value={directOperatePackDataLengthCm ?? undefined}
              onChange={(v) => setDirectOperatePackDataLengthCm((v == null ? null : Number(v)))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>宽(cm)</div>
            <InputNumber
              precision={0}
              min={1}
              step={1}
              style={{ width: '100%' }}
              value={directOperatePackDataWidthCm ?? undefined}
              onChange={(v) => setDirectOperatePackDataWidthCm((v == null ? null : Number(v)))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>高(cm)</div>
            <InputNumber
              precision={0}
              min={1}
              step={1}
              style={{ width: '100%' }}
              value={directOperatePackDataHeightCm ?? undefined}
              onChange={(v) => setDirectOperatePackDataHeightCm((v == null ? null : Number(v)))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>重量(g)</div>
            <InputNumber
              precision={0}
              min={1}
              step={1}
              style={{ width: '100%' }}
              value={directOperatePackDataWeightG ?? undefined}
              onChange={(v) => setDirectOperatePackDataWeightG((v == null ? null : Number(v)))}
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={instrModalOpen}
        title={instrModalOperateContext ? (
          <div style={{ fontSize: 16 }}>
            <p style={{ whiteSpace: 'nowrap', marginBottom: 8 }}>
              {instrModalOperateContext.iuCode}
              {instrModalOperateContext.sortBarcodes.length > 0 ? ` (${instrModalOperateContext.sortBarcodes.join(',')})` : ''}
              <span style={{ marginLeft: 24 }}>
                每单<span style={{ fontWeight: 'bold', color: '#ff4d4f' }}>{instrModalOperateContext.perOrderQty}</span>件 承运商:<span style={{ fontWeight: 'bold', color: '#ff4d4f' }}>{instrModalOperateContext.carrierName}</span>
              </span>
            </p>
            <p style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 0 }}>
              {instrModalOperateContext.isNoPack ? (
                <Tag color="green" style={{ margin: 0 }}>无需打包</Tag>
              ) : (
                <>
                  <Tag color="red" style={{ margin: 0 }}>需打包</Tag>
                  {(() => {
                    const est = packingEstimateByMethod[instrModalOperateContext.packagingKey];
                    const code = est?.code ?? '';
                    const name = est?.name ?? '';
                    if (!code && !name) return <span style={{ color: '#8c8c8c' }}>无推荐包材</span>;
                    return <span><span style={{ color: '#ff4d4f', marginRight: 8 }}>{code}</span>{name}</span>;
                  })()}
                </>
              )}
              <span>本次操作{instrModalOperateContext.operateCount}单</span>
              <Select
                placeholder="完成状态"
                allowClear
                value={instrModalDoneFilter || undefined}
                onChange={(v) => setInstrModalDoneFilter(v ?? '')}
                options={[
                  { label: '已完成', value: '已完成' },
                  { label: '未完成', value: '未完成' },
                ]}
                style={{ width: 100 }}
              />
            </p>
          </div>
        ) : instrModalCarrierContext ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: 40, flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 14 }}>
              {instrModalCarrierContext.iuCode}
              {instrModalCarrierContext.sortBarcodes.length > 0 ? ` (${instrModalCarrierContext.sortBarcodes.join(',')})` : ''}
              {' '}
              每单{instrModalCarrierContext.perOrderQty}件 共{instrModalCarrierContext.totalOrders}单 预计发货{instrModalCarrierContext.perOrderQty * instrModalCarrierContext.totalOrders}件
            </span>
            <span style={{ color: '#1890ff', fontWeight: 500 }}>{instrModalCarrierContext.typeLabel}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Select
                placeholder="处理结果"
                allowClear
                value={instrModalDoneFilter || undefined}
                onChange={(v) => setInstrModalDoneFilter(v ?? '')}
                options={[
                  { label: '已完成', value: 'completed' },
                  { label: '无法操作', value: 'unoperated' },
                  { label: '未标注', value: 'unmarked' },
                ]}
                style={{ width: 110 }}
              />
              <Button
                type="primary"
                size="small"
                disabled={!instrModalList.some((r: any) => !isDoneInOperateConfirm(r) && !isUnoperatedInOperateConfirm(r))}
                onClick={() => {
                  const ctx = instrModalCarrierContext;
                  if (!ctx) return;
                  const n = ctx.totalOrders;
                  const instrs = instrModalList;
                  const processed = (instrs || []).filter((i: any) => isProcessedInOperateConfirm(i)).length;
                  setOperateConfirmContext({
                    row: ctx.row,
                    carrierKey: ctx.carrierKey,
                    carrierName: ctx.carrierName,
                    total: n,
                    done: processed,
                    undone: n - processed,
                  });
                  setOperateConfirmQty(0);
                  setOperateConfirmSelectedPackaging(null);
                  setOperateConfirmPhase('idle');
                  setOperateConfirmBatchFetched(false);
                  setOperateConfirmCurrentBatchInstrs([]);
                  setOperateConfirmMergedMeta({});
                  setOperateConfirmPackDataByPackagingKey({});
                  setOperateConfirmPackDataModalOpen(false);
                  setInstrModalOpen(false);
                  setInstrModalCarrierContext(null);
                  setOperateConfirmOpen(true);
                }}
              >
                开始处理本波次
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: 40 }}>
            <span>{instrModalTitleLeft || '指令列表'}</span>
            <Select
              placeholder="完成状态"
              allowClear
              value={instrModalDoneFilter || undefined}
              onChange={(v) => setInstrModalDoneFilter(v ?? '')}
              options={[
                { label: '已完成', value: '已完成' },
                { label: '未完成', value: '未完成' },
              ]}
              style={{ width: 100 }}
            />
            {instrModalTitleRight ? <span style={{ color: '#1890ff', fontWeight: 500 }}>{instrModalTitleRight}</span> : null}
          </div>
        )}
        footer={null}
        width={instrModalCarrierContext ? 1100 : 900}
        zIndex={1350}
        onCancel={() => { setInstrModalOpen(false); setInstrModalOperateContext(null); setInstrModalCarrierContext(null); }}
      >
        {instrModalList.length > 0 ? (
          <Table
            size="small"
            rowKey={(r: any) => String(r?.instruction ?? r?.id ?? '')}
            dataSource={
              instrModalCarrierContext
                ? (() => {
                    if (!instrModalDoneFilter) return instrModalList;
                    if (instrModalDoneFilter === 'completed') return instrModalList.filter((r: any) => isDoneInOperateConfirm(r));
                    if (instrModalDoneFilter === 'unoperated') return instrModalList.filter((r: any) => isUnoperatedInOperateConfirm(r));
                    if (instrModalDoneFilter === 'unmarked') return instrModalList.filter((r: any) => !isDoneInOperateConfirm(r) && !isUnoperatedInOperateConfirm(r));
                    return instrModalList;
                  })()
                : !instrModalDoneFilter
                  ? instrModalList
                  : instrModalDoneFilter === '已完成'
                    ? instrModalList.filter((r: any) => isDoneInOperateConfirm(r))
                    : instrModalList.filter((r: any) => !isDoneInOperateConfirm(r))
            }
            columns={[
              {
                title: '指令系统编码',
                dataIndex: 'instruction_system_code',
                key: 'instruction_system_code',
                width: 160,
                render: (v: any) => {
                  const code = displayDash(v);
                  if (code === '-') return '-';
                  return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span>{code}</span>
                      <CopyOutlined
                        style={{ cursor: 'pointer', fontSize: 12 }}
                        onClick={async (e) => {
                          e?.stopPropagation?.();
                          try {
                            await navigator.clipboard.writeText(String(code));
                            message.success('已复制');
                          } catch {
                            message.error('复制失败');
                          }
                        }}
                      />
                    </span>
                  );
                },
              },
              { title: '承运商', key: 'waybill_carrier', width: 100, render: (_: any, r: any) => displayDash(r?.waybill_carrier) },
              {
                title: '跟踪号',
                key: 'waybill_tracking_no',
                width: 160,
                render: (_: any, r: any) => {
                  const t = displayDash(r?.waybill_tracking_no);
                  if (t === '-') return '-';
                  return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Tooltip title={t}>
                        <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t}</span>
                      </Tooltip>
                      <CopyOutlined
                        style={{ cursor: 'pointer', fontSize: 12 }}
                        onClick={async (e) => {
                          e?.stopPropagation?.();
                          try {
                            await navigator.clipboard.writeText(String(t));
                            message.success('已复制');
                          } catch {
                            message.error('复制失败');
                          }
                        }}
                      />
                    </span>
                  );
                },
              },
              {
                title: '指令备注',
                key: 'instruction_remarks',
                width: 140,
                render: (_: any, r: any) => {
                  const remarks = String(r?.instruction_remarks ?? '').trim();
                  if (!remarks) return '-';
                  const display = remarks.length > 20 ? `${remarks.slice(0, 20)}…` : remarks;
                  return (
                    <Tooltip title={remarks}>
                      <span style={{ cursor: 'default' }}>{display}</span>
                    </Tooltip>
                  );
                },
              },
              {
                title: '执行提醒',
                key: 'execution_tips',
                width: 140,
                render: (_: any, r: any) => {
                  const tips = String(r?.execution_tips ?? '').trim();
                  if (!tips) return '-';
                  const display = tips.length > 20 ? `${tips.slice(0, 20)}…` : tips;
                  return (
                    <Tooltip title={tips}>
                      <span style={{ cursor: 'default' }}>{display}</span>
                    </Tooltip>
                  );
                },
              },
              ...(instrModalCarrierContext
                ? [{
                    title: '处理结果',
                    key: 'waybillStatus',
                    width: 100,
                    render: (_: any, r: any) => {
                      const done = isDoneInOperateConfirm(r);
                      const unoperated = isUnoperatedInOperateConfirm(r);
                      return (
                        <span
                          style={{
                            display: 'inline-block',
                            minWidth: 70,
                            padding: '2px 8px',
                            borderRadius: 4,
                            background: done ? '#f6ffed' : unoperated ? '#fff2f0' : 'transparent',
                          }}
                        >
                          {done ? <Tag color="green" style={{ margin: 0 }}>已完成</Tag> : unoperated ? (
                            <Tooltip title={operateConfirmUnoperatedReasons[getOperateConfirmInstrKey(r)] || '暂无填写原因'}>
                              <Tag color="red" style={{ margin: 0 }}>无法操作</Tag>
                            </Tooltip>
                          ) : <span style={{ color: '#bfbfbf' }}>未标注</span>}
                        </span>
                      );
                    },
                  }]
                : [{
                    title: '已完成',
                    key: 'done',
                    width: 80,
                    render: (_: any, r: any) => (isDoneInOperateConfirm(r) ? <Tag color="green">是</Tag> : <Tag color="red">否</Tag>),
                  }]
              ),
            ]}
            pagination={{
              current: instrModalPage,
              pageSize: instrModalPageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, [start, end]) => `第${start}-${end}条记录 共${total}条记录`,
              pageSizeOptions: [10, 20, 50],
              onChange: (page, pageSize) => {
                setInstrModalPage(page);
                if (pageSize && pageSize !== instrModalPageSize) {
                  setInstrModalPageSize(pageSize);
                  setInstrModalPage(1);
                }
              },
            }}
          />
        ) : (
          <div style={{ color: '#999' }}>暂无指令</div>
        )}
      </Modal>
      <Modal
        open={scanResultOpen}
        zIndex={1050}
        title={
          scanMatchSource === 'upload_immediate_single' && scanMatchedRows.length > 0 ? (
            <span>
              {(() => {
                const r = scanMatchedRows[0];
                const iu = displayDash(r?.iuCode);
                const sortStr = Array.isArray(r?.sortBarcodes) ? r.sortBarcodes.join(',') : '';
                const suffix = iu !== '-' ? `${iu}${sortStr ? ` (${sortStr})` : ''}` : '';
                return (
                  <>
                    <span>扫描匹配结果 </span>
                    {suffix ? <span style={{ color: '#1677ff' }}>{suffix}</span> : null}
                  </>
                );
              })()}
            </span>
          ) : (
            '扫描匹配结果'
          )
        }
        footer={null}
        width={1100}
        onCancel={() => {
          setScanResultOpen(false);
          setScanMatchedRows([]);
          setScanMatchSource(null);
          setScanRecommendPositionResult(null);
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginTop: 30 }}>
            <span
              style={{
                display: 'inline-block',
                padding: '4px 10px',
                border: '1px solid #91caff',
                background: '#e6f4ff',
                borderRadius: 4,
                fontSize: 13,
                fontWeight: 500,
                color: '#0958d9',
              }}
            >
              已有运单-单品指令
            </span>
            <div style={{ marginTop: 8 }}>
            {scanMatchSource === 'upload_immediate_single' && scanMatchedRows.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <Table
                  size="small"
                  rowKey="key"
                  dataSource={scanMatchedRows}
                  rowClassName={(record: any) => {
                    const total = Number(record?.totalOrders ?? 0);
                    if (total <= 0) return '';
                    const instrs = Array.isArray(record?.allInstructions) ? record.allInstructions : [];
                    const processed = instrs.filter((i: any) => isProcessedInOperateConfirm(i)).length;
                    return processed >= total ? 'single-sku-row-completed' : '';
                  }}
                  columns={[
                    {
                      title: '每单数量',
                      dataIndex: 'perOrderQty',
                      key: 'perOrderQty',
                      width: 90,
                      align: 'center' as const,
                      render: (v: any) => <span style={{ fontWeight: 'bold', color: '#ff4d4f' }}>每单{Number(v ?? 0)}件</span>,
                    },
                    ...(singleSkuMatrixByTab.upload_immediate.dynamicCols || [])
                      .filter((c: any) => c.key?.startsWith('upload_immediate_'))
                      .map((c: any) => {
                        const carrierName = c.carrierDisplayName ?? c.title;
                        return {
                          title: (
                            <div style={{ lineHeight: 1.3, whiteSpace: 'pre-line', textAlign: 'center' }}>
                              {carrierName}
                              {'\n'}
                              已处理/单量
                            </div>
                          ),
                          key: c.key,
                          width: 130,
                          align: 'center' as const,
                          render: (_: any, row: any) => {
                            const n = row.byWaybill?.[c.key];
                            const instrs = row.byWaybillInstructions?.[c.key] || [];
                            if (n == null || n === 0) return '-';
                            const processed = (instrs || []).filter((i: any) => isProcessedInOperateConfirm(i)).length;
                            const undone = n - processed;
                            const allDone = processed >= n;
                            return (
                              <span
                                style={{
                                  cursor: allDone ? 'not-allowed' : 'pointer',
                                  color: allDone ? '#bfbfbf' : '#1890ff',
                                  textDecoration: allDone ? 'none' : 'underline',
                                }}
                                onClick={allDone ? undefined : () => {
                                  const instrs = (row.byWaybillInstructions?.[c.key] || []) as any[];
                                  const undoneInstrs = instrs.filter((i: any) => !isProcessedInOperateConfirm(i));
                                  const needPackInstrs = undoneInstrs.filter((i: any) => i?.is_pack_required === true);
                                  const packGroups = new Map<string, number>();
                                  needPackInstrs.forEach((i: any) => {
                                    const key = String(i?.estimated_packing_method ?? '').trim() || '_other';
                                    packGroups.set(key, (packGroups.get(key) || 0) + 1);
                                  });
                                  setOperateConfirmContext({
                                    row,
                                    carrierKey: c.key,
                                    carrierName,
                                    total: n,
                                    done: processed,
                                    undone,
                                  });
                                  setOperateConfirmQty(0);
                                  setOperateConfirmSelectedPackaging(null);
                                  setOperateConfirmPhase('idle');
                                  setOperateConfirmBatchFetched(false);
                                  setOperateConfirmCurrentBatchInstrs([]);
                                  setOperateConfirmMergedMeta({});
                                  setOperateConfirmPackDataByPackagingKey({});
                                  setOperateConfirmPackDataModalOpen(false);
                                  setOperateConfirmOpen(true);
                                }}
                              >
                                {processed}/{n}单
                              </span>
                            );
                          },
                        };
                      }),
                    {
                      title: '已处理/总单量',
                      dataIndex: 'totalOrders',
                      key: 'totalOrders',
                      width: 120,
                      align: 'center' as const,
                      render: (v: any, row: any) => {
                        const total = Number(v);
                        if (total == null || total === 0) return '-';
                        const instrs = Array.isArray(row?.allInstructions) ? row.allInstructions : [];
                        const processed = instrs.filter((i: any) => isProcessedInOperateConfirm(i)).length;
                        return `${processed}/${total}单`;
                      },
                    },
                    {
                      title: '预计发货总数量',
                      dataIndex: 'totalShipQty',
                      key: 'totalShipQty',
                      width: 120,
                      align: 'center' as const,
                      render: (_: any, row: any) => {
                        const perOrderQty = Number(row?.perOrderQty ?? 0) || 0;
                        const totalOrders = Number(row?.totalOrders ?? 0) || 0;
                        const expected = perOrderQty * totalOrders;
                        return expected > 0 ? expected : '-';
                      },
                    },
                  ]}
                  pagination={{
                    current: scanResultPage,
                    pageSize: scanResultPageSize,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, [start, end]) => `第${start}-${end}条记录 共${total}条记录`,
                    pageSizeOptions: [10, 20, 50],
                    onChange: (page, pageSize) => {
                      setScanResultPage(page);
                      if (pageSize && pageSize !== scanResultPageSize) {
                        setScanResultPageSize(pageSize);
                        setScanResultPage(1);
                      }
                    },
                  }}
                  scroll={{ x: 'max-content' }}
                />
              </div>
            ) : (
              <div style={{ color: '#999' }}>暂无匹配记录</div>
            )}
            </div>
          </div>
          <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 30, paddingTop: 30 }}>
            <div
              style={{
                marginTop: 0,
                minHeight: 84,
                border: '1px dashed #d9d9d9',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: '#595959',
                fontSize: 13,
                padding: '10px 16px',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
              {scanRecommendPositionResult ? (
                <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', minHeight: 64 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', minHeight: 64 }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        border: '1px solid #b7eb8f',
                        background: '#f6ffed',
                        borderRadius: 4,
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#237804',
                      }}
                    >
                      分拣推荐位
                    </span>
                    <span style={{ color: '#1677ff', fontWeight: 800, fontSize: 30, lineHeight: 1 }}>{scanRecommendPositionResult.sortingPosition}</span>
                    <span style={{ color: '#595959', fontWeight: 700, fontSize: 26, lineHeight: 1 }}>x</span>
                    <span style={{ color: '#ff4d4f', fontWeight: 800, fontSize: 30, lineHeight: 1 }}>{scanRecommendPositionResult.gapQty}</span>
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', flex: 1, justifyContent: 'center' }}>
                    <span>来源:{scanRecommendPositionResult.sourceTabName}</span>
                    <span>发货数量:{scanRecommendPositionResult.shippedQty}</span>
                    <span>已分拣数量:{scanRecommendPositionResult.pickedQty}</span>
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span>数量</span>
                    <InputNumber
                      min={1}
                      max={Math.max(1, Number(scanRecommendPositionResult.gapQty ?? 0) || 1)}
                      value={scanRecommendPutQty}
                      onChange={(v) => {
                        const gap = Math.max(1, Number(scanRecommendPositionResult.gapQty ?? 0) || 1);
                        const next = Number(v);
                        if (!Number.isFinite(next)) {
                          setScanRecommendPutQty(1);
                          return;
                        }
                        setScanRecommendPutQty(Math.min(gap, Math.max(1, Math.floor(next))));
                      }}
                      style={{ width: 88 }}
                      size="small"
                    />
                    <Button
                      type="primary"
                      size="small"
                      onClick={handlePutIntoRecommendPosition}
                    >
                      放入分拣位
                    </Button>
                  </span>
                </div>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: '#8c8c8c', fontSize: 12 }}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '4px 10px',
                      border: '1px solid #b7eb8f',
                      background: '#f6ffed',
                      borderRadius: 4,
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#237804',
                    }}
                  >
                    分拣推荐位
                  </span>
                  <span>暂无可推荐的分拣位</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        open={mergedWaybillPdfOpen}
        title={mergedPreviewTitle || '合并运单预览'}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              disabled={mergedWaybillPdfLoading || !mergedWaybillPdfUrl}
              onClick={() => {
                if (!mergedWaybillPdfUrl) return;
                const iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                iframe.src = mergedWaybillPdfUrl;
                document.body.appendChild(iframe);
                iframe.onload = () => {
                  try { iframe.contentWindow?.print(); } catch {}
                  setTimeout(() => document.body.removeChild(iframe), 1000);
                };
              }}
            >
              打印
            </Button>
            <Button onClick={handleMergedWaybillClose}>
              关闭
            </Button>
          </div>
        )}
        width={900}
        zIndex={1300}
        onCancel={handleMergedWaybillClose}
      >
        {mergedWaybillPdfLoading ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>正在合并运单附件...</p>
          </div>
        ) : mergedWaybillPdfUrl ? (
          <iframe title="合并运单" src={mergedWaybillPdfUrl} style={{ width: '100%', height: 600, border: 'none' }} />
        ) : null}
      </Modal>
      <Modal
        open={operateConfirmOpen}
        title={operateConfirmContext ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', fontSize: 14 }}>
            <span style={{ fontWeight: 600 }}>波次操作</span>
            <span>
              {displayDash(operateConfirmContext.row?.iuCode)}
              {Array.isArray(operateConfirmContext.row?.sortBarcodes) && operateConfirmContext.row.sortBarcodes.length > 0
                ? ` (${operateConfirmContext.row.sortBarcodes.join(',')})`
                : ''}
            </span>
            <span>
              每单<span style={{ fontWeight: 'bold', color: '#ff4d4f' }}>{Number(operateConfirmContext.row?.perOrderQty ?? 0)}</span>件
            </span>
            <span>
              承运商:<span style={{ fontWeight: 'bold', color: '#ff4d4f' }}>{operateConfirmContext.carrierName}</span>
            </span>
            <span style={{ marginLeft: 100 }}>共{operateConfirmContext.total}单 | 已处理{operateConfirmContext.done}单(已完成+无法操作) | 未标注{operateConfirmContext.undone}单</span>
          </div>
        ) : '波次操作'}
        closable
        width={1400}
        zIndex={1200}
        footer={null}
        styles={{ body: { height: '78vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
        onCancel={resetOperateConfirmState}
      >
        {operateConfirmContext ? (() => {
          const ctx = operateConfirmContext;
          const instrs = (ctx.row?.byWaybillInstructions?.[ctx.carrierKey] || []) as any[];
          const unmarkedInModal = instrs.filter((i: any) => !isDoneInOperateConfirmDraft(i) && !isUnoperatedInOperateConfirmDraft(i)).length;
          const unmarkedInstrs = instrs.filter((i: any) => !isDoneInOperateConfirmDraft(i) && !isUnoperatedInOperateConfirmDraft(i));
          const needPackInstrs = unmarkedInstrs.filter((i: any) => i?.is_pack_required === true);
          const packGroups = new Map<string, number>();
          needPackInstrs.forEach((i: any) => {
            const key = String(i?.estimated_packing_method ?? '').trim() || '_other';
            packGroups.set(key, (packGroups.get(key) || 0) + 1);
          });
          const isNoPack = needPackInstrs.length === 0;
          const packEntries = Array.from(packGroups.entries())
            .filter(([k]) => k !== '_other')
            .sort((a, b) => b[1] - a[1]);
          const otherCount = packGroups.get('_other') || 0;
          if (otherCount > 0) packEntries.push(['_other', otherCount]);
          const isWarehouseOrAfterPackFlow = ctx.carrierKey === 'warehouse' || ctx.carrierKey === 'after_pack';
          const isWarehouseFlow = ctx.carrierKey === 'warehouse';
          return (
            <div style={{ padding: '8px 0', fontSize: 16, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <div style={{ marginBottom: 8 }}>
                {isNoPack ? (
                  <Tag color="green" style={{ margin: 0, cursor: 'default' }}>无需打包</Tag>
                ) : (
                  <Tag color="red" style={{ margin: 0, cursor: 'default' }}>需打包</Tag>
                )}
              </div>
              <div style={{ borderTop: '1px solid #f0f0f0', margin: '12px 0' }} />
              <div style={{ display: 'flex', gap: 16, marginTop: 12, minHeight: 0, flex: 1 }}>
                <div style={{ flex: '0 0 50%', minWidth: 0, minHeight: 0, borderRight: '1px solid #f0f0f0', paddingRight: 16, maxHeight: '100%', overflowY: 'auto', paddingBottom: 8 }}>
                  <div style={{ marginBottom: 8 }}>包装方式及本次操作</div>
                  {!isNoPack && packingEstimateLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1677ff', marginBottom: 12 }}>
                      <Spin size="small" />
                      <span>包装预估计算中，请稍候...</span>
                    </div>
                  ) : null}
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={
                      operateConfirmPackSnapshot
                        ? (operateConfirmPackSnapshot.isNoPack
                            ? [{ key: '_no_pack', count: operateConfirmPackSnapshot.noPackCount }]
                            : operateConfirmPackSnapshot.entries)
                        : (isNoPack ? [{ key: '_no_pack', count: unmarkedInModal }] : packEntries.map(([key, count]) => ({ key, count })))
                    }
                    rowKey="key"
                    onRow={(record) => {
                      const isSelected = operateConfirmSelectedPackaging === record.key;
                      const inScanMode = operateConfirmScanAreaVisible;
                      return {
                        onClick: () => {
                          if (operateConfirmPhase === 'batch_active') return;
                          setOperateConfirmSelectedPackaging(record.key);
                          setOperateConfirmQty(record.count);
                        },
                        style: {
                          cursor: operateConfirmPhase === 'batch_active' ? 'not-allowed' : 'pointer',
                          backgroundColor: isSelected ? (inScanMode ? '#bae7ff' : '#e6f7ff') : undefined,
                          opacity: inScanMode && !isSelected ? 0.5 : undefined,
                        },
                      };
                    }}
                    columns={[
                      {
                        title: '选择',
                        dataIndex: 'key',
                        key: 'select',
                        width: 48,
                      align: 'center' as const,
                      render: (key: string, record: { key: string; count: number }) => (
                        <Radio
                          checked={operateConfirmSelectedPackaging === key}
                          disabled={operateConfirmPhase === 'batch_active'}
                          onChange={() => {
                            if (operateConfirmPhase === 'batch_active') return;
                            setOperateConfirmSelectedPackaging(key);
                            setOperateConfirmQty(record.count);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ),
                    },
                    {
                      title: '包装方式',
                      dataIndex: 'key',
                      key: 'method',
                      width: 100,
                      render: (key: string, record: { key: string; count: number }) => {
                        const isSelected = operateConfirmSelectedPackaging === key;
                        return (
                          <span style={!isSelected ? { color: '#bfbfbf' } : undefined}>
                            {key === '_no_pack' ? '无需打包' : key === '_other' ? '其他' : getPackagingMethodLabel(key)}
                          </span>
                        );
                      },
                    },
                    {
                      title: '包材推荐',
                      dataIndex: 'key',
                      key: 'pkg',
                      width: 110,
                      render: (key: string, record: { key: string; count: number }) => {
                        const isSelected = operateConfirmSelectedPackaging === key;
                        if (key === '_no_pack') return <span style={!isSelected ? { color: '#bfbfbf' } : { color: '#8c8c8c' }} />;
                        const est = packingEstimateByMethod[key];
                        const code = est?.code ?? '';
                        const name = est?.name ?? '';
                        const gray = !isSelected ? { color: '#bfbfbf' } : undefined;
                        if (code || name) return (
                          <div style={{ ...gray, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {code ? <div style={isSelected ? { color: '#ff4d4f' } : { color: '#bfbfbf' }}>{code}</div> : null}
                            {name ? <div>{name}</div> : null}
                          </div>
                        );
                        return <span style={!isSelected ? { color: '#bfbfbf' } : { color: '#8c8c8c' }}>无推荐包材</span>;
                      },
                    },
                    ...(isWarehouseOrAfterPackFlow
                      ? [{
                          title: '包装数据',
                          dataIndex: 'key',
                          key: 'pack_data',
                          width: 150,
                          render: (key: string, record: { key: string; count: number }) => {
                            const isSelected = operateConfirmSelectedPackaging === key;
                            const data = operateConfirmPackDataByPackagingKey[key];
                            if (!data) return <span style={!isSelected ? { color: '#bfbfbf' } : undefined}>-</span>;
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.3 }}>
                                <span style={!isSelected ? { color: '#bfbfbf' } : undefined}>
                                  {`${data.lengthIn} × ${data.widthIn} × ${data.heightIn} cm`}
                                </span>
                                <span style={!isSelected ? { color: '#bfbfbf' } : undefined}>
                                  {`${data.weightLb} g`}
                                </span>
                              </div>
                            );
                          },
                        }]
                      : []),
                    {
                      title: '未标注',
                      dataIndex: 'count',
                      key: 'count',
                      width: 70,
                      align: 'center' as const,
                      render: (count: number, record: { key: string; count: number }) => {
                        const isSelected = operateConfirmSelectedPackaging === record.key;
                        return <span style={!isSelected ? { color: '#bfbfbf' } : undefined}>{count}</span>;
                      },
                    },
                    {
                      title: '本次操作',
                      dataIndex: 'key',
                      key: 'operateQty',
                      width: 80,
                      align: 'center' as const,
                      render: (key: string, record: { key: string; count: number }) => {
                        const isSelected = operateConfirmSelectedPackaging === key;
                        return (
                          <span
                            className={isSelected ? 'operate-qty-input-red' : 'operate-qty-input-gray'}
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                          >
                            <InputNumber
                              min={1}
                              max={record.count}
                              value={isSelected ? operateConfirmQty : 0}
                              onChange={(v) => isSelected && operateConfirmPhase !== 'batch_active' && setOperateConfirmQty(Number(v) ?? 0)}
                              disabled={!isSelected || operateConfirmPhase === 'batch_active'}
                              style={{ width: 56 }}
                            />
                          </span>
                        );
                      },
                    },
                    {
                      title: '操作',
                      key: 'actions',
                      width: 165,
                      align: 'center' as const,
                      render: (_: any, record: { key: string; count: number }) => {
                        const isSelected = operateConfirmSelectedPackaging === record.key;
                        const meta = operateConfirmMergedMeta[record.key];
                        const hasMerged = !!meta;
                        const qty = isSelected ? operateConfirmQty : record.count;
                        const pool = record.key === '_no_pack'
                          ? unmarkedInstrs
                          : needPackInstrs.filter((i: any) => (String(i?.estimated_packing_method ?? '').trim() || '_other') === record.key);
                        const sortedAll = [...pool].sort((a: any, b: any) => getInstructionSequence(a) - getInstructionSequence(b));
                        const selectedBatchInstrs = sortedAll.slice(0, Math.max(1, qty));
                        const withWaybill = pool.filter((i: any) => getWaybillAttachments(i).length > 0);
                        const sorted = [...withWaybill].sort((a: any, b: any) => getInstructionSequence(a) - getInstructionSequence(b));
                        const selectedInstrs = sorted.slice(0, Math.max(1, qty));
                        const batchReady = operateConfirmBatchFetched && (operateConfirmCurrentBatchInstrs || []).length > 0;
                        const countInstrs = meta
                          ? meta.selectedInstrs
                          : (isWarehouseOrAfterPackFlow
                            ? (batchReady ? operateConfirmCurrentBatchInstrs : selectedBatchInstrs)
                            : selectedInstrs);
                        const totalMatched = (() => {
                          if (!Array.isArray(countInstrs) || countInstrs.length === 0) return 0;
                          const codeSet = new Set(
                            countInstrs.map((instr: any) => getInstructionSystemCode(instr)).filter((code: string) => !!code && code !== '-'),
                          );
                          if (codeSet.size === 0) return 0;
                          const matchedCodeSet = new Set(
                            operateConfirmDraftScanMatchRecords
                              .map((rec) => String(rec?.matchedInstruction || '').trim())
                              .filter((code) => codeSet.has(code)),
                          );
                          return matchedCodeSet.size;
                        })();
                        const M = countInstrs.length;
                        const runFetchBatchInstructions = () => {
                          if (selectedBatchInstrs.length === 0) {
                            message.warning('未找到可获取的指令');
                            return;
                          }
                          setOperateConfirmCurrentBatchInstrs(selectedBatchInstrs);
                          setOperateConfirmBatchFetched(true);
                          setOperateConfirmPhase('batch_active');
                          setOperateConfirmInstrPage(1);
                          setOperateConfirmMergedMeta((prev) => {
                            const next = { ...prev };
                            delete next[record.key];
                            return next;
                          });
                          const hasPrintable = selectedBatchInstrs.some((i: any) => hasPrintableAttachments(i));
                          if (hasPrintable) {
                            setTimeout(() => {
                              Modal.warning({ title: '打印提醒', content: '本波次部分指令需打印其他附件，请先打印附件再执行后续操作' });
                            }, 100);
                          }
                        };
                        const runMerge = async (autoPrint = false) => {
                          if (selectedInstrs.length === 0) { message.warning('未找到带运单附件的指令'); return; }
                          const toMerge: { att: any; instr: any }[] = [];
                          selectedInstrs.forEach((instr: any) => {
                            getWaybillAttachments(instr).forEach((att: any) => toMerge.push({ att, instr }));
                          });
                          if (toMerge.length === 0) { message.warning('未找到可合并的运单附件'); return; }
                          if (!autoPrint) mergedWaybillBatchInstrsRef.current = [...selectedInstrs];
                          else mergedWaybillBatchInstrsRef.current = [];
                          setMergedWaybillAutoPrint(autoPrint);
                          setMergedPreviewTitle('合并运单预览');
                          setMergedPreviewSource('waybill');
                          setMergedWaybillPdfLoading(true);
                          setMergedWaybillPdfOpen(true);
                          try {
                            const result = await mergeWaybillAttachmentsToPdf(toMerge);
                            if (!result || !result.blob || result.blob.size === 0) { message.error('合并 PDF 失败'); mergedWaybillBatchInstrsRef.current = []; setMergedWaybillPdfOpen(false); return; }
                            if (mergedWaybillPdfUrlRef.current) { try { URL.revokeObjectURL(mergedWaybillPdfUrlRef.current); } catch {} mergedWaybillPdfUrlRef.current = null; }
                            const url = URL.createObjectURL(result.blob);
                            mergedWaybillPdfUrlRef.current = url;
                            setMergedWaybillPdfUrl(url);
                            setOperateConfirmMergedMeta((prev) => ({ ...prev, [record.key]: { pageCount: result.pageCount, selectedInstrs } }));
                            const badInstrs = (selectedInstrs || [])
                              .map((instr: any) => {
                                const code = getInstructionSystemCode(instr) || '-';
                                const pages = Number(result?.instructionPageCounts?.[code] ?? 0);
                                return { code, pages };
                              })
                              .filter((x: { code: string; pages: number }) => x.pages !== 1);
                            if (badInstrs.length > 0) {
                              setTimeout(() => {
                                Modal.warning({
                                  title: '运单页数提醒',
                                  content: (
                                    <div>
                                      <div style={{ marginBottom: 8 }}>以下指令的运单附件页数不是 1 页：</div>
                                      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                                        {badInstrs.map((x: { code: string; pages: number }, idx: number) => (
                                          <div key={`${x.code}-${idx}`}>{x.code}（{x.pages}页）</div>
                                        ))}
                                      </div>
                                    </div>
                                  ),
                                });
                              }, 80);
                            }
                          } catch (e: any) {
                            message.error(e?.message || '合并运单 PDF 失败');
                            mergedWaybillBatchInstrsRef.current = [];
                            setMergedWaybillPdfOpen(false);
                          } finally {
                            setMergedWaybillPdfLoading(false);
                          }
                        };
                        const mergeEnabled = isSelected && operateConfirmPhase !== 'batch_active';
                        const othersEnabled = isSelected && (hasMerged || batchReady);
                        const hasPackData = !!operateConfirmPackDataByPackagingKey[record.key];
                        const inputPackDataEnabled = isSelected;
                        const fetchEnabled = isWarehouseOrAfterPackFlow
                          ? (isSelected && hasPackData && operateConfirmPhase !== 'batch_active')
                          : mergeEnabled;
                        const otherBtnsEnabled = isWarehouseOrAfterPackFlow
                          ? (isSelected && batchReady)
                          : othersEnabled;
                        const createIocLoading = !!operateConfirmCreateIocLoadingByKey[record.key];
                        const batchInstrsForIoc = batchReady ? (operateConfirmCurrentBatchInstrs || []) : [];
                        const allMarkedForIoc = batchInstrsForIoc.length > 0
                          && batchInstrsForIoc.every((i: any) => isDoneInOperateConfirmDraft(i) || isUnoperatedInOperateConfirmDraft(i));
                        const doneInstrsForIoc = batchInstrsForIoc.filter((i: any) => isDoneInOperateConfirmDraft(i));
                        const createIocEnabled = ctx.carrierKey === 'after_pack'
                          && isSelected
                          && hasPackData
                          && batchReady
                          && allMarkedForIoc
                          && doneInstrsForIoc.length > 0
                          && !createIocLoading;
                        const createIocTip = !hasPackData
                          ? '请先输入包装数据'
                          : (!batchReady
                            ? '请先获取本波次指令'
                            : (!allMarkedForIoc
                              ? '请先完成本波次全部指令标注（已完成/无法操作）'
                              : (doneInstrsForIoc.length <= 0 ? '当前无“已完成”指令，无需创建产出容器' : '')));
                        const pageCountStr = hasMerged && meta ? `(${meta.pageCount})` : '';
                        return (
                          <div
                            style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', width: 150 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isWarehouseOrAfterPackFlow ? (
                              <Button
                                size="small"
                                disabled={!inputPackDataEnabled}
                                style={{ width: '100%', whiteSpace: 'normal', height: 'auto', minHeight: 28 }}
                                onClick={() => {
                                  const methodKey = String(record?.key || '').trim();
                                  if (!isSelected) {
                                    setOperateConfirmSelectedPackaging(methodKey);
                                    setOperateConfirmQty(record.count);
                                  }
                                  const methodLabel = methodKey === '_no_pack' ? '无需打包' : methodKey === '_other' ? '其他' : getPackagingMethodLabel(methodKey);
                                  const est = packingEstimateByMethod[methodKey];
                                  const recommendationCode = String(est?.code || '').trim();
                                  const recommendationName = String(est?.name || '').trim();
                                  const recommendation = (est?.code || est?.name)
                                    ? `${recommendationCode} ${recommendationName}`.trim()
                                    : '无推荐包材';
                                  const existing = operateConfirmPackDataByPackagingKey[methodKey];
                                  const defaultFromInstr = (() => {
                                    const sourceInstrs = Array.isArray(pool) && pool.length > 0 ? pool : (Array.isArray(instrs) ? instrs : []);
                                    for (const ins of sourceInstrs) {
                                      const targets = Array.isArray(ins?.targets_detail) ? ins.targets_detail : [];
                                      for (const t of targets) {
                                        if (String(t?.target_type || '').trim() !== 'inventory_unit') continue;
                                        const snap = t?.snapshot || {};
                                        const actualLength = Number(t?.actual_length ?? snap?.actual_length ?? 0) || 0;
                                        const actualWidth = Number(t?.actual_width ?? snap?.actual_width ?? 0) || 0;
                                        const actualHeight = Number(t?.actual_height ?? snap?.actual_height ?? 0) || 0;
                                        const actualWeight = Number(t?.actual_weight ?? snap?.actual_weight ?? 0) || 0;
                                        if (actualLength > 0 && actualWidth > 0 && actualHeight > 0 && actualWeight > 0) {
                                          return { lengthIn: actualLength, widthIn: actualWidth, heightIn: actualHeight, weightLb: actualWeight };
                                        }
                                        const declaredLength = Number(t?.length ?? snap?.length ?? 0) || 0;
                                        const declaredWidth = Number(t?.width ?? snap?.width ?? 0) || 0;
                                        const declaredHeight = Number(t?.height ?? snap?.height ?? 0) || 0;
                                        const declaredWeight = Number(t?.weight ?? snap?.weight ?? 0) || 0;
                                        if (declaredLength > 0 && declaredWidth > 0 && declaredHeight > 0 && declaredWeight > 0) {
                                          return { lengthIn: declaredLength, widthIn: declaredWidth, heightIn: declaredHeight, weightLb: declaredWeight };
                                        }
                                      }
                                    }
                                    return null;
                                  })();
                                  setOperateConfirmPackDataEditingKey(methodKey);
                                  setOperateConfirmPackDataModalInfo({
                                    iuCode: displayDash(ctx.row?.iuCode),
                                    sortBarcodes: Array.isArray(ctx.row?.sortBarcodes) ? ctx.row.sortBarcodes : [],
                                    perOrderQty: Number(ctx.row?.perOrderQty ?? 0) || 0,
                                    operateQty: Number(isSelected ? operateConfirmQty : record.count) || 0,
                                    packagingLabel: methodLabel,
                                    packageRecommendation: recommendation,
                                    packageRecommendationCode: recommendationCode || undefined,
                                    packageRecommendationName: recommendationName || undefined,
                                  });
                                  const preset = existing || (methodKey === '_no_pack' ? defaultFromInstr : null);
                                  setOperateConfirmPackDataLengthIn(preset ? Number(preset.lengthIn) : null);
                                  setOperateConfirmPackDataWidthIn(preset ? Number(preset.widthIn) : null);
                                  setOperateConfirmPackDataHeightIn(preset ? Number(preset.heightIn) : null);
                                  setOperateConfirmPackDataWeightLb(preset ? Number(preset.weightLb) : null);
                                  setOperateConfirmPackDataModalOpen(true);
                                }}
                              >
                                输入包装数据
                              </Button>
                            ) : null}
                            <Tooltip
                              title={isWarehouseOrAfterPackFlow && !hasPackData ? '请先输入包装数据再获取指令.' : ''}
                            >
                              <span style={{ width: '100%' }}>
                                <Button
                                  size="small"
                                  disabled={!fetchEnabled}
                                  style={{ width: '100%', whiteSpace: 'normal', height: 'auto', minHeight: 28 }}
                                  onClick={() => {
                                    if (isWarehouseOrAfterPackFlow) {
                                      runFetchBatchInstructions();
                                    } else {
                                      runMerge(false);
                                    }
                                  }}
                                >
                                  {isWarehouseOrAfterPackFlow ? '获取指令' : '获取指令与合并运单'}
                                </Button>
                              </span>
                            </Tooltip>
                            {ctx.carrierKey === 'after_pack' ? (
                              <Tooltip title={createIocTip}>
                                <span style={{ width: '100%' }}>
                                  <Button
                                    size="small"
                                    loading={createIocLoading}
                                    disabled={!createIocEnabled}
                                    style={{ width: '100%', whiteSpace: 'normal', height: 'auto', minHeight: 28 }}
                                    onClick={() => {
                                      const targetInstrs = doneInstrsForIoc;
                                      void createInstructionOutputContainersForBatchWave({
                                        packagingKey: String(record.key || ''),
                                        packData: hasPackData ? (operateConfirmPackDataByPackagingKey[record.key] || null) : null,
                                        instructions: targetInstrs,
                                        rowContext: ctx?.row,
                                      });
                                    }}
                                  >
                                    创建指令产出容器
                                  </Button>
                                </span>
                              </Tooltip>
                            ) : null}
                            {ctx.carrierKey !== 'after_pack' ? (
                              <Button size="small" disabled={!otherBtnsEnabled} style={{ width: '100%', whiteSpace: 'normal', height: 'auto', minHeight: 28 }} onClick={() => runMerge(true)}>
                                预览/打印合并运单{pageCountStr}
                              </Button>
                            ) : null}
                            {ctx.carrierKey !== 'after_pack' ? (
                              <Button
                                size="small"
                                disabled={!otherBtnsEnabled}
                                style={{ width: '100%', whiteSpace: 'normal', height: 'auto', minHeight: 28 }}
                                onClick={() => {
                                  setOperateConfirmScanAreaVisible(true);
                                  setOperateConfirmScanMatchedCode('');
                                  setTimeout(() => operateConfirmScanInputRef.current?.focus(), 80);
                                }}
                              >
                                扫描运单与标记完成({totalMatched}/{M})
                              </Button>
                            ) : null}
                          </div>
                        );
                      },
                    },
                  ]}
                />
                {/* 运单扫描区：默认隐藏，点击「扫描运单与标记完成」后展开 */}
                {operateConfirmScanAreaVisible && (
                  <div style={{ marginTop: 12, padding: 12, border: '1px solid #d9d9d9', borderRadius: 4, background: '#fafafa' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>运单扫描</span>
                      <Button type="link" size="small" style={{ padding: 0, height: 'auto', fontSize: 12 }} onClick={() => setOperateConfirmScanAreaVisible(false)}>
                        收起
                      </Button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Input
                        ref={operateConfirmScanInputRef}
                        placeholder="请扫描已完成处理的包裹上的物流运单"
                        size="small"
                        style={{ width: 280 }}
                        value={operateConfirmScanInputValue}
                        onChange={(e) => setOperateConfirmScanInputValue(e.target.value)}
                        onPressEnter={() => submitOperateConfirmScan(false)}
                        allowClear
                      />
                      <Button size="small" type="primary" onClick={() => submitOperateConfirmScan(false)}>输入</Button>
                      {operateConfirmScanMatchedCode ? <span style={{ fontSize: 12, color: '#52c41a' }}>已匹配: {operateConfirmScanMatchedCode}</span> : null}
                    </div>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>扫描包裹上的物流单号，自动匹配指令并标记完成</div>
                  </div>
                )}
                </div>
                <div style={{ flex: '0 0 50%', minWidth: 0, minHeight: 0, paddingRight: 24, maxHeight: '100%', overflowY: 'auto', paddingBottom: 8 }}>
                  {/* 标题行：左-标题 中-统计 右-提交按钮 */}
                  <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ fontWeight: 600 }}>波次操作指令列表</span>
                    {operateConfirmBatchFetched && (
                      <span style={{ fontSize: 12, color: '#8c8c8c', flex: 1, textAlign: 'center', minWidth: 180 }}>
                        {(() => {
                          const list = operateConfirmCurrentBatchInstrs || [];
                          const totalM = list.length;
                          const doneN = list.filter((i: any) => isDoneInOperateConfirmDraft(i)).length;
                          const unoperatedK = list.filter((i: any) => isUnoperatedInOperateConfirmDraft(i)).length;
                          const unmarkedX = list.filter((i: any) => !isDoneInOperateConfirmDraft(i) && !isUnoperatedInOperateConfirmDraft(i)).length;
                          return <>共{totalM}单 已完成{doneN}单 无法操作{unoperatedK}单 未标注{unmarkedX}单</>;
                        })()}
                      </span>
                    )}
                    {operateConfirmBatchFetched && (
                      <Tooltip title="本次操作指令必须全部标注「已完成」或「无法操作」；先申请包装数据波次下，已完成指令还需先创建产出容器">
                        <span style={{ display: 'inline-block' }}>
                          <Button
                            type="primary"
                            size="small"
                            disabled={
                              (operateConfirmCurrentBatchInstrs || []).some((i: any) => !isDoneInOperateConfirmDraft(i) && !isUnoperatedInOperateConfirmDraft(i))
                              || (
                                String(operateConfirmContext?.carrierKey || '').trim() === 'after_pack'
                                && (operateConfirmCurrentBatchInstrs || []).some((i: any) => isDoneInOperateConfirmDraft(i) && !hasCreatedIocForInstr(i))
                              )
                            }
                            onClick={() => setOperateConfirmSubmitModalOpen(true)}
                          >
                            提交本次数据
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  {/* 筛选行 */}
                  {operateConfirmBatchFetched && (
                    <>
                      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Select
                          placeholder="处理结果"
                          value={operateConfirmWaybillStatusFilter || undefined}
                          onChange={(v) => setOperateConfirmWaybillStatusFilter(v ?? '')}
                          style={{ width: 120 }}
                          size="small"
                          allowClear
                          options={[
                            { label: '已完成', value: 'completed' },
                            { label: '无法操作', value: 'unoperated' },
                            { label: '未标注', value: 'unmarked' },
                          ]}
                        />
                        <Input
                          placeholder="按物流号查找"
                          value={operateConfirmTrackingNoFilter}
                          onChange={(e) => setOperateConfirmTrackingNoFilter(e.target.value)}
                          style={{ width: 140 }}
                          size="small"
                          allowClear
                        />
                        <Input
                          placeholder="按系统编码查找"
                          value={operateConfirmSystemCodeFilter}
                          onChange={(e) => setOperateConfirmSystemCodeFilter(e.target.value)}
                          style={{ width: 140 }}
                          size="small"
                          allowClear
                        />
                        <Input
                          placeholder="按运单解析内容查找"
                          value={operateConfirmOcrTextFilter}
                          onChange={(e) => setOperateConfirmOcrTextFilter(e.target.value)}
                          style={{ width: 140 }}
                          size="small"
                          allowClear
                        />
                      </div>
                      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Button
                            size="small"
                            type="primary"
                            style={{ background: '#52c41a', borderColor: '#52c41a' }}
                            onClick={handleMarkAllDone}
                          >
                            全部标注完成
                          </Button>
                          <Button
                            size="small"
                            type="primary"
                            style={{ background: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' }}
                            onClick={handleMarkAllUnoperated}
                          >
                            全部标注无法操作
                          </Button>
                          <Button
                            size="small"
                            onClick={handleClearAllOperateConfirmMarks}
                          >
                            清空所有标注
                          </Button>
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                            <Switch size="small" checked={operateConfirmTipsOnly} onChange={setOperateConfirmTipsOnly} />
                            <span>仅显示带操作提醒</span>
                          </label>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                            <Switch size="small" checked={operateConfirmPrintOnly} onChange={setOperateConfirmPrintOnly} />
                            <span>仅显示带打印附件</span>
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0 }}>
                    {(() => {
                      if (!operateConfirmBatchFetched) {
                        const fetchBtnText = isWarehouseOrAfterPackFlow ? '获取指令' : '获取指令与合并运单';
                        const hint = operateConfirmSelectedPackaging
                          ? `请点击「${fetchBtnText}」获取本波次指令`
                          : `请选择包装方式并点击「${fetchBtnText}」`;
                        return <div style={{ color: '#8c8c8c', padding: '8px 0', fontSize: 12 }}>{hint}</div>;
                      }
                      const list = operateConfirmCurrentBatchInstrs || [];
                      const waybillStatusFilter = operateConfirmWaybillStatusFilter;
                      const getPrintableCount = (instr: any): number => {
                        const attList = Array.isArray(instr?.attachments_detail) ? instr.attachments_detail : [];
                        let cnt = 0;
                        for (const a of attList) {
                          const m = a?.meta;
                          const parsed = typeof m === 'string' ? (() => { try { return JSON.parse(m); } catch { return {}; } })() : (m || {});
                          const cfg = parsed?.print_config;
                          if (Number(cfg?.copies ?? 0) > 0) cnt++;
                        }
                        return cnt;
                      };
                      const trimTracking = (s: string) => String(s || '').trim().toLowerCase();
                      const trimCode = (s: string) => String(s || '').trim().toLowerCase();
                      const isUnmarked = (i: any) => !isDoneInOperateConfirmDraft(i) && !isUnoperatedInOperateConfirmDraft(i);
                      let filtered = list;
                      if (waybillStatusFilter === 'completed') filtered = filtered.filter((i: any) => isDoneInOperateConfirmDraft(i));
                      if (waybillStatusFilter === 'unoperated') filtered = filtered.filter((i: any) => isUnoperatedInOperateConfirmDraft(i));
                      if (waybillStatusFilter === 'unmarked') filtered = filtered.filter((i: any) => isUnmarked(i));
                      if (operateConfirmTipsOnly) filtered = filtered.filter((i: any) => String(i?.execution_tips ?? '').trim().length > 0);
                      if (operateConfirmPrintOnly) filtered = filtered.filter((i: any) => getPrintableCount(i) > 0);
                      if (operateConfirmTrackingNoFilter) {
                        const k = trimTracking(operateConfirmTrackingNoFilter);
                        filtered = filtered.filter((i: any) => trimTracking(i?.waybill_tracking_no || '').includes(k));
                      }
                      if (operateConfirmSystemCodeFilter) {
                        const k = trimCode(operateConfirmSystemCodeFilter);
                        filtered = filtered.filter((i: any) => trimCode(i?.instruction_system_code ?? i?.instruction?.instruction_system_code ?? i?.system_code ?? '').includes(k));
                      }
                      if (operateConfirmOcrTextFilter) {
                        const k = String(operateConfirmOcrTextFilter || '').trim().toLowerCase();
                        filtered = filtered.filter((i: any) => {
                          const waybillAttsForOcr = (Array.isArray(i?.attachments_detail) ? i.attachments_detail : []).filter((a: any) => {
                            const label = String(a?.label || '').trim();
                            return label === '物流运单';
                          });
                          const ocrTexts = waybillAttsForOcr.map((a: any) => String(a?.parse_text ?? '').trim()).filter((t: string) => t.length > 0);
                          const combined = ocrTexts.join(' ').toLowerCase();
                          return combined.includes(k);
                        });
                      }
                      const totalM = filtered.length;
                      const start = (operateConfirmInstrPage - 1) * operateConfirmInstrPageSize;
                      const showList = filtered.slice(start, start + operateConfirmInstrPageSize);
                      const PAPER_LABELS: Record<string, string> = { '4x6_thermal': '4×6英寸热敏纸打印', 'A4': 'A4激光打印' };
                      const PLACEMENT_LABELS: Record<string, string> = { 'inside': '放入包裹内', 'outside': '贴在包裹外' };
                      const formatPrintConfigTooltip = (cfg: { copies?: number; paper_size?: string; placement?: string }): string => {
                        if (!cfg) return '未设置打印';
                        const parts: string[] = [];
                        if (cfg.copies != null) parts.push(`${cfg.copies}份`);
                        if (cfg.paper_size) parts.push(`纸张: ${PAPER_LABELS[cfg.paper_size] || cfg.paper_size}`);
                        if (cfg.placement) parts.push(`放置: ${PLACEMENT_LABELS[cfg.placement] || cfg.placement}`);
                        return parts.length > 0 ? parts.join(' · ') : '点击设置打印';
                      };
                      const getPrintableAttachments = (instr: any): Array<{ name: string; printConfig: any; att: any }> => {
                        const list = Array.isArray(instr?.attachments_detail) ? instr.attachments_detail : [];
                        const result: Array<{ name: string; printConfig: any; att: any }> = [];
                        for (const a of list) {
                          const m = a?.meta;
                          const parsed = typeof m === 'string' ? (() => { try { return JSON.parse(m); } catch { return {}; } })() : (m || {});
                          const cfg = parsed?.print_config;
                          const copies = Number(cfg?.copies ?? 0);
                          if (copies <= 0) continue;
                          const name = String(a?.file_name ?? '').trim();
                          result.push({ name, printConfig: cfg, att: a });
                        }
                        return result;
                      };
                      return showList.length === 0 ? (
                        <div style={{ color: '#8c8c8c', padding: '8px 0', fontSize: 12 }}>暂无待操作指令</div>
                      ) : (
                        <>
                          <div style={{ flex: 1, minHeight: 0, maxHeight: 280, overflow: 'auto', fontSize: 12 }}>
                            <table style={{ width: '100%', minWidth: 1100, borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
                                {isWarehouseFlow ? (
                                  <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, minWidth: 110 }}>操作</th>
                                ) : null}
                                <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600, minWidth: 70 }}>处理结果</th>
                                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, minWidth: 120 }}>指令编码</th>
                                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, minWidth: 90 }}>操作提醒</th>
                                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, minWidth: 120 }}>需打印附件</th>
                                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, minWidth: 90 }}>指令备注</th>
                                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, minWidth: 120 }}>物流跟踪号</th>
                                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, minWidth: 100 }}>运单附件</th>
                                <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, minWidth: 140 }}>运单OCR解析结果</th>
                              </tr>
                            </thead>
                            <tbody>
                              {showList.map((instr: any, idx: number) => {
                                const code = String(instr?.instruction_system_code ?? instr?.instruction?.instruction_system_code ?? instr?.system_code ?? '').trim() || `指令${idx + 1}`;
                                const done = isDoneInOperateConfirmDraft(instr);
                                const unoperated = isUnoperatedInOperateConfirmDraft(instr);
                                const tips = String(instr?.execution_tips ?? '').trim() || '-';
                                const printableList = getPrintableAttachments(instr);
                                const remarks = String(instr?.instruction_remarks ?? instr?.remarks ?? '').trim() || '-';
                                const trackingNo = String(instr?.waybill_tracking_no ?? instr?.instruction?.waybill_tracking_no ?? '').trim() || '-';
                                const waybillAtts = getWaybillAttachments(instr);
                                const waybillOcrTexts = waybillAtts.map((a: any) => String(a?.parse_text ?? '').trim()).filter((t: string) => t.length > 0);
                                const waybillOcrDisplay = waybillOcrTexts.length > 0 ? waybillOcrTexts.join(' | ') : '-';
                                return (
                                  <tr key={code + idx} style={{ borderBottom: '1px solid #f0f0f0', background: (done || unoperated) ? '#f6ffed' : undefined }}>
                                    {isWarehouseFlow ? (
                                      <td style={{ padding: '6px 8px' }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                          {(() => {
                                            const rowTrackingNo = String(instr?.waybill_tracking_no ?? instr?.instruction?.waybill_tracking_no ?? '').trim();
                                            const rowWaybillAtts = getWaybillAttachments(instr);
                                            const uploaded = !!rowTrackingNo && rowWaybillAtts.length > 0;
                                            return (
                                              <Button
                                                size="small"
                                                type="default"
                                                style={uploaded ? { background: '#e6f4ff', borderColor: '#91caff', color: '#1677ff' } : undefined}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setOperateConfirmBuyWaybillInstr(instr);
                                                  setOperateConfirmBuyWaybillModalOpen(true);
                                                }}
                                              >
                                                {uploaded ? '已上传运单' : '购买并上传运单'}
                                              </Button>
                                            );
                                          })()}
                                        </span>
                                      </td>
                                    ) : null}
                                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                      <Tooltip title="点击修改">
                                        <Dropdown
                                          menu={{
                                            items: [
                                              { key: 'completed', label: '已完成' },
                                              { key: 'unoperated', label: '无法操作' },
                                              { key: 'unmarked', label: '未标注' },
                                            ],
                                            onClick: ({ key }) => handleSetOperateConfirmWaybillStatus(instr, key as 'completed' | 'unoperated' | 'unmarked'),
                                          }}
                                          trigger={['click']}
                                        >
                                        <span
                                          style={{
                                            display: 'inline-block',
                                            minWidth: 70,
                                            padding: '2px 8px',
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                            background: done ? '#f6ffed' : unoperated ? '#fff2f0' : 'transparent',
                                            border: '1px solid transparent',
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = '#d9d9d9';
                                            e.currentTarget.style.background = done ? '#f6ffed' : unoperated ? '#fff2f0' : '#fafafa';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = 'transparent';
                                            e.currentTarget.style.background = done ? '#f6ffed' : unoperated ? '#fff2f0' : 'transparent';
                                          }}
                                        >
                                          {done ? <Tag color="green" style={{ margin: 0 }}>已完成</Tag> : unoperated ? (
                                            <Tooltip title={operateConfirmDraftUnoperatedReasons[getOperateConfirmInstrKey(instr)] || '无法操作'}>
                                              <Tag color="red" style={{ margin: 0 }}>无法操作</Tag>
                                            </Tooltip>
                                          ) : <span style={{ color: '#bfbfbf' }}>未标注</span>}
                                        </span>
                                        </Dropdown>
                                      </Tooltip>
                                    </td>
                                    <td style={{ padding: '6px 8px', color: done ? '#8c8c8c' : '#262626', wordBreak: 'break-all', whiteSpace: 'normal' }}>
                                      <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 4 }}>
                                        <span style={{ flex: 1, minWidth: 0 }}>{code}</span>
                                        <Tooltip title="复制指令编码">
                                          <CopyOutlined
                                            style={{ flexShrink: 0, cursor: 'pointer', color: '#1890ff' }}
                                            onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(code); message.success('已复制'); }}
                                          />
                                        </Tooltip>
                                      </span>
                                    </td>
                                    <td style={{ padding: '6px 8px', color: done ? '#8c8c8c' : '#595959', maxWidth: 100 }}>
                                      <Tooltip title={tips}>
                                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tips}</span>
                                      </Tooltip>
                                    </td>
                                    <td style={{ padding: '6px 8px', color: done ? '#8c8c8c' : '#595959', maxWidth: 120 }}>
                                      {printableList.length === 0 ? '-' : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                          {printableList.map((item, i) => (
                                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                              <Tooltip title={item.name}>
                                                <span
                                                  style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePrintableAttachmentClick(item.att, instr);
                                                  }}
                                                >
                                                  {item.name}
                                                </span>
                                              </Tooltip>
                                              <Tooltip title={formatPrintConfigTooltip(item.printConfig)}>
                                                <PrinterOutlined style={{ color: '#1890ff', flexShrink: 0, cursor: 'help' }} />
                                              </Tooltip>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </td>
                                    <td style={{ padding: '6px 8px', color: done ? '#8c8c8c' : '#595959', maxWidth: 100 }}>
                                      <Tooltip title={remarks}>
                                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{remarks}</span>
                                      </Tooltip>
                                    </td>
                                    <td style={{ padding: '6px 8px', color: done ? '#8c8c8c' : '#595959' }}>
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 130 }}>
                                        <Tooltip title={trackingNo}>
                                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{trackingNo}</span>
                                        </Tooltip>
                                        {trackingNo !== '-' && (
                                          <Tooltip title="复制物流跟踪号">
                                            <CopyOutlined
                                              style={{ flexShrink: 0, cursor: 'pointer', color: '#1890ff' }}
                                              onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(trackingNo); message.success('已复制'); }}
                                            />
                                          </Tooltip>
                                        )}
                                      </span>
                                    </td>
                                    <td style={{ padding: '6px 8px', color: done ? '#8c8c8c' : '#595959' }}>
                                      {waybillAtts.length === 0 ? '-' : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                          {waybillAtts.map((att: any, ai: number) => {
                                            const fullName = String(att?.file_name ?? att?.file ?? att?.file_path ?? att?.label ?? '').trim() || `附件${ai + 1}`;
                                            return (
                                              <Tooltip key={ai} title={fullName}>
                                                <span
                                                  style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 90, cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
                                                  onClick={(e) => { e.stopPropagation(); handlePrintableAttachmentClick(att, instr); }}
                                                >
                                                  {att?.file_name ?? att?.label ?? `附件${ai + 1}`}
                                                </span>
                                              </Tooltip>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </td>
                                    <td style={{ padding: '6px 8px', color: done ? '#8c8c8c' : '#595959' }}>
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 130 }}>
                                        <Tooltip title={waybillOcrDisplay}>
                                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{waybillOcrDisplay}</span>
                                        </Tooltip>
                                        {waybillOcrTexts.length > 0 && (
                                          <Tooltip title="复制解析文本">
                                            <CopyOutlined
                                              style={{ flexShrink: 0, cursor: 'pointer', color: '#1890ff' }}
                                              onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(waybillOcrDisplay); message.success('已复制'); }}
                                            />
                                          </Tooltip>
                                        )}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          </div>
                          {totalM > 0 && (
                            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
                              <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                                第{start + 1}-{Math.min(start + operateConfirmInstrPageSize, totalM)}条 共{totalM}条
                              </span>
                              <Pagination
                                size="small"
                                current={operateConfirmInstrPage}
                                pageSize={operateConfirmInstrPageSize}
                                total={totalM}
                                showSizeChanger
                                showQuickJumper
                                pageSizeOptions={['5', '10', '20', '50']}
                                onChange={(page, size) => {
                                  setOperateConfirmInstrPage(page);
                                  setOperateConfirmInstrPageSize(size || 10);
                                }}
                              />
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  {operateConfirmScanAreaVisible && (
                    <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
                      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ fontWeight: 600 }}>运单扫描与匹配记录</span>
                        <Button
                          size="small"
                          danger
                          disabled={operateConfirmDraftScanMatchRecords.length === 0}
                          onClick={() => {
                            const deleted = [...operateConfirmDraftScanMatchRecords];
                            setOperateConfirmDraftScanMatchRecords([]);
                            rollbackDraftStatusByDeletedScanRecords(deleted, []);
                            setOperateConfirmScanRecordPage(1);
                          }}
                        >
                          清空记录
                        </Button>
                      </div>
                      {(() => {
                        const total = operateConfirmDraftScanMatchRecords.length;
                        const start = (operateConfirmScanRecordPage - 1) * operateConfirmScanRecordPageSize;
                        const pagedRecords = operateConfirmDraftScanMatchRecords.slice(start, start + operateConfirmScanRecordPageSize);
                        return (
                          <>
                      <div style={{ maxHeight: 150, overflow: 'auto' }}>
                        <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
                              <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 150 }}>时间</th>
                              <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 150 }}>运单扫描值</th>
                              <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, minWidth: 120 }}>匹配指令</th>
                              <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, width: 120 }}>匹配方式</th>
                              <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 600, width: 80 }}>操作</th>
                            </tr>
                          </thead>
                          <tbody>
                            {total === 0 ? (
                              <tr>
                                <td colSpan={5} style={{ padding: '10px 8px', color: '#8c8c8c', textAlign: 'center' }}>暂无扫描与匹配记录</td>
                              </tr>
                            ) : (
                              pagedRecords.map((rec, idx) => {
                                const actualIdx = start + idx;
                                return (
                                <tr key={`${rec.time}-${rec.scanValue}-${idx}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                  <td style={{ padding: '6px 8px', color: '#8c8c8c', whiteSpace: 'nowrap' }}>{rec.time}</td>
                                  <td style={{ padding: '6px 8px', wordBreak: 'break-all' }}>{rec.scanValue}</td>
                                  <td style={{ padding: '6px 8px', wordBreak: 'break-all' }}>{rec.matchedInstruction}</td>
                                  <td style={{ padding: '6px 8px' }}>{rec.matchMethod}</td>
                                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                    <Button
                                      size="small"
                                      danger
                                      onClick={() => {
                                        const deleted = operateConfirmDraftScanMatchRecords[actualIdx];
                                        const next = operateConfirmDraftScanMatchRecords.filter((_, i) => i !== actualIdx);
                                        setOperateConfirmDraftScanMatchRecords(next);
                                        if (deleted) rollbackDraftStatusByDeletedScanRecords([deleted], next);
                                      }}
                                    >
                                      删除
                                    </Button>
                                  </td>
                                </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                      {total > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
                          <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                            第{start + 1}-{Math.min(start + operateConfirmScanRecordPageSize, total)}条 共{total}条
                          </span>
                          <Pagination
                            size="small"
                            current={operateConfirmScanRecordPage}
                            pageSize={operateConfirmScanRecordPageSize}
                            total={total}
                            showSizeChanger
                            showQuickJumper
                            pageSizeOptions={['5', '10', '20', '50']}
                            onChange={(page, size) => {
                              setOperateConfirmScanRecordPage(page);
                              setOperateConfirmScanRecordPageSize(size || 10);
                            }}
                          />
                        </div>
                      )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })() : null}
      </Modal>
      <Modal
        open={operateConfirmManualMatchModalOpen}
        title="手动选择匹配指令"
        width={980}
        zIndex={1300}
        onCancel={() => {
          setOperateConfirmManualMatchModalOpen(false);
          setOperateConfirmManualMatchCandidates([]);
          setOperateConfirmManualMatchSelectedKey('');
          setOperateConfirmManualCodeFilter('');
          setOperateConfirmManualOcrFilter('');
          setOperateConfirmPendingScanValue('');
          setOperateConfirmScanInputValue('');
          setTimeout(() => operateConfirmScanInputRef.current?.focus(), 0);
        }}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              onClick={() => {
                setOperateConfirmManualMatchModalOpen(false);
                setOperateConfirmManualMatchCandidates([]);
                setOperateConfirmManualMatchSelectedKey('');
                setOperateConfirmManualCodeFilter('');
                setOperateConfirmManualOcrFilter('');
                setOperateConfirmPendingScanValue('');
                setOperateConfirmScanInputValue('');
                setTimeout(() => operateConfirmScanInputRef.current?.focus(), 0);
              }}
            >
              取消
            </Button>
            <Button
              type="primary"
              disabled={!operateConfirmManualMatchSelectedKey || !operateConfirmManualMatchCandidates.some((i: any) => getOperateConfirmInstrKey(i) === operateConfirmManualMatchSelectedKey)}
              onClick={() => {
                const selected = operateConfirmManualMatchCandidates.find((i: any) => getOperateConfirmInstrKey(i) === operateConfirmManualMatchSelectedKey);
                if (!selected) {
                  message.warning('请选择要匹配的指令');
                  return;
                }
                const raw = String(operateConfirmPendingScanValue || '').trim();
                if (!raw) {
                  message.warning('扫描值为空，无法提交手动匹配');
                  return;
                }
                commitOperateConfirmScanMatch(raw, selected, '手动匹配');
              }}
            >
              确认匹配
            </Button>
          </div>
        )}
      >
        {(() => {
          const getWaybillOcrText = (instr: any): string => {
            const waybillAtts = (Array.isArray(instr?.attachments_detail) ? instr.attachments_detail : []).filter((att: any) => {
              const label = String(att?.label || '').trim();
              return label === '物流运单';
            });
            return waybillAtts.map((att: any) => String(att?.parse_text ?? '').trim()).filter((t: string) => t.length > 0).join(' | ');
          };
          const codeKeyword = String(operateConfirmManualCodeFilter || '').trim().toLowerCase();
          const ocrKeyword = String(operateConfirmManualOcrFilter || '').trim().toLowerCase();
          const filteredCandidates = (operateConfirmManualMatchCandidates || []).filter((instr: any) => {
            const code = getInstructionSystemCode(instr).toLowerCase();
            const ocr = getWaybillOcrText(instr).toLowerCase();
            if (codeKeyword && !code.includes(codeKeyword)) return false;
            if (ocrKeyword && !ocr.includes(ocrKeyword)) return false;
            return true;
          });
          return (
            <>
              <div style={{ fontSize: 12, color: '#595959', marginBottom: 8 }}>
                未找到自动匹配结果，请从未标注指令中手动选择（扫描值：
                <span style={{ color: '#1677ff', fontWeight: 500 }}>{operateConfirmPendingScanValue || '-'}</span>
                ）
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <Input
                  size="small"
                  allowClear
                  style={{ width: 180 }}
                  placeholder="按指令编码模糊查找"
                  value={operateConfirmManualCodeFilter}
                  onChange={(e) => setOperateConfirmManualCodeFilter(e.target.value)}
                />
                <Input
                  size="small"
                  allowClear
                  style={{ width: 230 }}
                  placeholder="按运单OCR识别结果模糊查找"
                  value={operateConfirmManualOcrFilter}
                  onChange={(e) => setOperateConfirmManualOcrFilter(e.target.value)}
                />
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>候选 {filteredCandidates.length} 条</span>
              </div>
              {filteredCandidates.length === 0 ? (
                <div style={{ color: '#8c8c8c', padding: '8px 0' }}>当前筛选条件下无可匹配指令</div>
              ) : (
                <Radio.Group
                  style={{ width: '100%' }}
                  value={operateConfirmManualMatchSelectedKey}
                  onChange={(e) => setOperateConfirmManualMatchSelectedKey(e.target.value)}
                >
                  <div style={{ maxHeight: 340, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 4 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
                          <th style={{ padding: '6px 8px', textAlign: 'center', width: 60 }}>选择</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', width: 110, minWidth: 110 }}>指令编码</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', width: 280, minWidth: 280, whiteSpace: 'nowrap' }}>物流跟踪号</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', width: 160, minWidth: 160 }}>运单OCR识别结果</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCandidates.map((instr: any, idx: number) => {
                          const key = getOperateConfirmInstrKey(instr);
                          const code = getInstructionSystemCode(instr);
                          const tracking = String(instr?.waybill_tracking_no ?? instr?.instruction?.waybill_tracking_no ?? '').trim() || '-';
                          const ocrText = getWaybillOcrText(instr) || '-';
                          return (
                            <tr
                              key={`${key}-${idx}`}
                              style={{ borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}
                              onClick={() => setOperateConfirmManualMatchSelectedKey(key)}
                            >
                              <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                <Radio value={key} />
                              </td>
                              <td style={{ padding: '6px 8px', wordBreak: 'break-all' }}>{code}</td>
                              <td style={{ padding: '6px 8px', wordBreak: 'break-all' }}>{tracking}</td>
                              <td style={{ padding: '6px 8px', color: '#595959' }}>
                                <Tooltip title={ocrText}>
                                  <span style={{ display: 'inline-block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {ocrText}
                                  </span>
                                </Tooltip>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Radio.Group>
              )}
            </>
          );
        })()}
      </Modal>
      <Modal
        open={operateConfirmPackDataModalOpen}
        title="输入包装数据"
        zIndex={1450}
        onCancel={() => {
          setOperateConfirmPackDataModalOpen(false);
          setOperateConfirmPackDataEditingKey('');
          setOperateConfirmPackDataModalInfo(null);
          setOperateConfirmPackDataLengthIn(null);
          setOperateConfirmPackDataWidthIn(null);
          setOperateConfirmPackDataHeightIn(null);
          setOperateConfirmPackDataWeightLb(null);
        }}
        onOk={async () => {
          const key = String(operateConfirmPackDataEditingKey || '').trim();
          if (!key) {
            message.warning('缺少包装方式标识');
            return;
          }
          const l = Number(operateConfirmPackDataLengthIn ?? 0);
          const w = Number(operateConfirmPackDataWidthIn ?? 0);
          const h = Number(operateConfirmPackDataHeightIn ?? 0);
          const weight = Number(operateConfirmPackDataWeightLb ?? 0);
          if (!(l > 0) || !(w > 0) || !(h > 0) || !(weight > 0)) {
            message.warning('请完整输入长宽高和重量');
            return;
          }
          setOperateConfirmPackDataByPackagingKey((prev) => ({
            ...prev,
            [key]: {
              lengthIn: Math.round(l),
              widthIn: Math.round(w),
              heightIn: Math.round(h),
              weightLb: Math.round(weight),
            },
          }));
          const payload = {
            actual_pack_length_cm: Math.round(l),
            actual_pack_width_cm: Math.round(w),
            actual_pack_height_cm: Math.round(h),
            actual_gross_weight_g: Math.round(weight),
            pack_result_status: 'provided',
          };
          const ctx = operateConfirmContext;
          const allInstrs = (ctx?.row?.byWaybillInstructions?.[ctx?.carrierKey || ''] || []) as any[];
          const unmarkedInstrs = allInstrs.filter((i: any) => !isDoneInOperateConfirmDraft(i) && !isUnoperatedInOperateConfirmDraft(i));
          const needPackInstrs = unmarkedInstrs.filter((i: any) => i?.is_pack_required === true);
          const pool = key === '_no_pack'
            ? unmarkedInstrs
            : needPackInstrs.filter((i: any) => (String(i?.estimated_packing_method ?? '').trim() || '_other') === key);
          const sortedAll = [...pool].sort((a: any, b: any) => getInstructionSequence(a) - getInstructionSequence(b));
          const qty = Math.max(1, Number(operateConfirmQty) || 1);
          const targetInstrs = sortedAll.slice(0, qty);
          const instructionIds = Array.from(new Set(
            targetInstrs
              .map((i: any) => Number(i?.instruction ?? i?.instruction_id ?? i?.id ?? 0))
              .filter((id: number) => Number.isFinite(id) && id > 0),
          ));
          if (instructionIds.length === 0) {
            message.warning('当前包装方式下无可更新的指令');
            return;
          }
          const results = await Promise.allSettled(
            instructionIds.map((instructionId) => request.patch(`/api/v1/instruction/instructions/${instructionId}/`, payload)),
          );
          let success = 0;
          let failed = 0;
          results.forEach((r, idx) => {
            if (r.status === 'fulfilled') {
              success += 1;
              applyLocalActualPackResultPatch(instructionIds[idx], payload);
            } else {
              failed += 1;
            }
          });
          if (success === 0) {
            message.error('保存包装数据失败');
            return;
          }
          if (failed > 0) {
            message.warning(`包装数据已保存${success}条，失败${failed}条`);
          } else {
            message.success(`包装数据已保存${success}条`);
          }
          setOperateConfirmPackDataModalOpen(false);
          setOperateConfirmPackDataEditingKey('');
          setOperateConfirmPackDataModalInfo(null);
          setOperateConfirmPackDataLengthIn(null);
          setOperateConfirmPackDataWidthIn(null);
          setOperateConfirmPackDataHeightIn(null);
          setOperateConfirmPackDataWeightLb(null);
        }}
      >
        {(() => {
          const info = operateConfirmPackDataModalInfo;
          if (!info) return <div style={{ color: '#8c8c8c' }}>暂无包装数据上下文</div>;
          const sortText = Array.isArray(info.sortBarcodes) && info.sortBarcodes.length > 0 ? info.sortBarcodes.join(',') : '-';
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 13, lineHeight: 1.5, color: '#595959' }}>
                <div>库存单元：<span style={{ color: '#1677ff', fontWeight: 600 }}>{info.iuCode}</span>{sortText !== '-' ? ` (${sortText})` : ''}</div>
                <div>数量信息：每单{info.perOrderQty}件</div>
                <div>包装方式：{info.packagingLabel}</div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span>推荐包材：</span>
                  {info.packageRecommendationCode || info.packageRecommendationName ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      {info.packageRecommendationCode ? <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{info.packageRecommendationCode}</span> : null}
                      {info.packageRecommendationName ? <span style={{ marginLeft: info.packageRecommendationCode ? 14 : 0 }}>{info.packageRecommendationName}</span> : null}
                    </span>
                  ) : (
                    <span>{info.packageRecommendation}</span>
                  )}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(110px, 1fr))', gap: 8 }}>
                <div>
                  <div style={{ marginBottom: 6 }}>长(cm)</div>
                  <InputNumber min={0} precision={0} value={operateConfirmPackDataLengthIn as any} onChange={(v) => setOperateConfirmPackDataLengthIn(Number.isFinite(Number(v)) ? Math.max(0, Math.round(Number(v))) : null)} style={{ width: '100%' }} />
                </div>
                <div>
                  <div style={{ marginBottom: 6 }}>宽(cm)</div>
                  <InputNumber min={0} precision={0} value={operateConfirmPackDataWidthIn as any} onChange={(v) => setOperateConfirmPackDataWidthIn(Number.isFinite(Number(v)) ? Math.max(0, Math.round(Number(v))) : null)} style={{ width: '100%' }} />
                </div>
                <div>
                  <div style={{ marginBottom: 6 }}>高(cm)</div>
                  <InputNumber min={0} precision={0} value={operateConfirmPackDataHeightIn as any} onChange={(v) => setOperateConfirmPackDataHeightIn(Number.isFinite(Number(v)) ? Math.max(0, Math.round(Number(v))) : null)} style={{ width: '100%' }} />
                </div>
                <div>
                  <div style={{ marginBottom: 6 }}>重量(g)</div>
                  <InputNumber min={0} precision={0} value={operateConfirmPackDataWeightLb as any} onChange={(v) => setOperateConfirmPackDataWeightLb(Number.isFinite(Number(v)) ? Math.max(0, Math.round(Number(v))) : null)} style={{ width: '100%' }} />
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>
      <Modal
        open={operateConfirmBuyWaybillModalOpen}
        title={(() => {
          const instr = operateConfirmBuyWaybillInstr || {};
          const info = instr?.buy_waybill_info || {};
          const code = String(
            instr?.instruction_system_code
            ?? instr?.instruction?.instruction_system_code
            ?? instr?.system_code
            ?? '-',
          ).trim() || '-';
          const preferredCarrierRaw = String(info?.preferred_carrier ?? instr?.waybill_carrier ?? '').trim();
          const preferredCarrier = ['none', 'null', 'undefined', '-'].includes(preferredCarrierRaw.toLowerCase()) ? '' : preferredCarrierRaw;
          return (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span>购买并上传运单信息</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#1677ff', fontWeight: 600 }}>
                <span>{code}</span>
                {code && code !== '-' ? (
                  <CopyOutlined
                    style={{ cursor: 'pointer', color: '#1677ff' }}
                    onClick={async (e) => {
                      e?.stopPropagation?.();
                      try {
                        await navigator.clipboard.writeText(code);
                        message.success('已复制');
                      } catch {
                        message.error('复制失败');
                      }
                    }}
                  />
                ) : null}
              </span>
              <span style={{ marginLeft: 30, color: '#000' }}>
                偏好承运商:{' '}
                <span style={{ color: '#ff4d4f', fontWeight: 600 }}>
                  {preferredCarrier || '无'}
                </span>
              </span>
            </div>
          );
        })()}
        width={760}
        zIndex={1260}
        onCancel={() => {
          setOperateConfirmBuyWaybillModalOpen(false);
          setOperateConfirmBuyWaybillInstr(null);
          setOperateConfirmBuyWaybillCarrier('');
          setOperateConfirmBuyWaybillTrackingNo('');
          setOperateConfirmBuyWaybillChargeAmount(null);
          setOperateConfirmBuyWaybillChargeCurrency('USD');
          setOperateConfirmBuyWaybillChargeNote('');
          setOperateConfirmBuyWaybillFileList([]);
        }}
        footer={null}
      >
        {(() => {
          const instr = operateConfirmBuyWaybillInstr || {};
          const info = instr?.buy_waybill_info || {};
          const recipient = info?.recipient || {};
          const normText = (v: any): string => {
            const s = String(v ?? '').trim();
            if (!s) return '';
            const low = s.toLowerCase();
            if (low === 'none' || low === 'null' || low === 'undefined' || s === '-') return '';
            return s;
          };
          const pickText = (v: any, fallback: any = '') => {
            const s = normText(v);
            if (s) return s;
            return normText(fallback);
          };
          const joinNonEmpty = (arr: any[], sep: string, fallback: string = '') => {
            const vals = arr.map((v) => normText(v)).filter(Boolean);
            return vals.length > 0 ? vals.join(sep) : fallback;
          };
          const insuranceRequired = info?.insurance_signature?.insurance_required;
          const signatureRequired = info?.insurance_signature?.signature_required;
          const insuredAmount = info?.insurance_signature?.insured_amount;
          const insuranceInfo = insuranceRequired === true
            ? `需要${insuredAmount != null ? `（$${insuredAmount}）` : ''}`
            : insuranceRequired === false
            ? '不需要'
            : '';
          const signatureInfo = signatureRequired === true ? '需要' : signatureRequired === false ? '不需要' : '';
          const line1 = pickText(recipient?.name);
          const line2 = joinNonEmpty([recipient?.address_line_1, recipient?.address_line_2], ', ');
          const line3 = joinNonEmpty([recipient?.city, recipient?.state, recipient?.postal_code], ' ');
          const line4 = joinNonEmpty([recipient?.company_name, recipient?.phone, recipient?.email, recipient?.country], ' ');
          const copyText = [line1, line2, line3].filter(Boolean).join('\n');
          const getPackagingKeyForInstr = (it: any): string => {
            if (it?.is_pack_required !== true) return '_no_pack';
            const k = String(it?.estimated_packing_method ?? '').trim();
            return k || '_other';
          };
          const packagingKey = getPackagingKeyForInstr(instr);
          const instrPackData = instr?.__directPackData || null;
          const packData = instrPackData || operateConfirmPackDataByPackagingKey[packagingKey];
          const packLengthCm = Number(packData?.lengthCm ?? packData?.lengthIn ?? 0) || 0;
          const packWidthCm = Number(packData?.widthCm ?? packData?.widthIn ?? 0) || 0;
          const packHeightCm = Number(packData?.heightCm ?? packData?.heightIn ?? 0) || 0;
          const packWeightG = Number(packData?.weightG ?? packData?.weightLb ?? 0) || 0;
          const cmToIn = (cm: number) => cm / 2.54;
          const formatLbOz = (g: number): string => {
            const grams = Number(g) || 0;
            if (grams <= 0) return '0磅0盎司';
            const totalOz = grams / 28.349523125;
            let lbs = Math.floor(totalOz / 16);
            let oz = totalOz - (lbs * 16);
            oz = Math.round(oz * 10) / 10;
            if (oz >= 16) {
              lbs += 1;
              oz = 0;
            }
            return `${lbs}磅${oz}盎司`;
          };
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13, lineHeight: 1.6 }}>
              {packData ? (
                <div style={{ border: '1px solid #fa8c16', borderRadius: 6, padding: 12 }}>
                  <div style={{ fontWeight: 600, color: '#d46b08', marginBottom: 6 }}>包装数据</div>
                  <div>公制尺寸：{`${packLengthCm} × ${packWidthCm} × ${packHeightCm} cm`}</div>
                  <div>公制重量：{`${packWeightG} g`}</div>
                  <div>英制尺寸：{`${cmToIn(packLengthCm).toFixed(2)} × ${cmToIn(packWidthCm).toFixed(2)} × ${cmToIn(packHeightCm).toFixed(2)} in`}</div>
                  <div>英制重量：{formatLbOz(packWeightG)}</div>
                </div>
              ) : null}
              <div style={{ border: '1px solid #52c41a', borderRadius: 6, padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ fontWeight: 600, color: '#389e0d' }}>收件人完整信息</div>
                  <Button
                    size="small"
                    onClick={async () => {
                      if (!copyText) {
                        message.warning('无可复制信息');
                        return;
                      }
                      try {
                        await navigator.clipboard.writeText(copyText);
                        message.success('已复制');
                      } catch {
                        message.error('复制失败');
                      }
                    }}
                  >
                    复制信息
                  </Button>
                </div>
                {line1 ? <div>{line1}</div> : null}
                {line2 ? <div>{line2}</div> : null}
                {line3 ? <div>{line3}</div> : null}
                {line4 ? <div>{line4}</div> : null}
              </div>
              <div style={{ border: '1px solid #1677ff', borderRadius: 6, padding: 12 }}>
                <div style={{ fontWeight: 600, color: '#1677ff', marginBottom: 6 }}>辅助信息</div>
                {pickText(info?.preferred_carrier, pickText(instr?.waybill_carrier)) ? (
                  <div>偏好承运商：{pickText(info?.preferred_carrier, pickText(instr?.waybill_carrier))}</div>
                ) : null}
                {insuranceInfo ? (
                  <div>
                    保险信息：
                    {insuranceRequired === true ? (
                      <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                        {insuranceInfo}
                      </span>
                    ) : (
                      <span>{insuranceInfo}</span>
                    )}
                  </div>
                ) : null}
                {signatureInfo ? (
                  <div>
                    签名需求：
                    {signatureRequired === true ? (
                      <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 4, background: '#ff4d4f', color: '#fff', fontWeight: 600 }}>
                        {signatureInfo}
                      </span>
                    ) : (
                      <span>{signatureInfo}</span>
                    )}
                  </div>
                ) : null}
                {pickText(info?.buyer_remarks) ? (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span>买家备注：{pickText(info?.buyer_remarks)}</span>
                    <CopyOutlined
                      style={{ cursor: 'pointer', color: '#1890ff' }}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(pickText(info?.buyer_remarks));
                          message.success('已复制');
                        } catch {
                          message.error('复制失败');
                        }
                      }}
                    />
                  </div>
                ) : null}
                {pickText(info?.instruction_remarks, pickText(instr?.instruction_remarks)) ? (
                  <div>指令备注：{pickText(info?.instruction_remarks, pickText(instr?.instruction_remarks))}</div>
                ) : null}
                {pickText(info?.execution_tips, pickText(instr?.execution_tips)) ? (
                  <div>操作提醒：{pickText(info?.execution_tips, pickText(instr?.execution_tips))}</div>
                ) : null}
              </div>
              <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 10 }}>上传运单信息和附件</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <div style={{ marginBottom: 6 }}>运单所属承运商</div>
                    <Select
                      value={operateConfirmBuyWaybillCarrier}
                      onChange={(v) => setOperateConfirmBuyWaybillCarrier(String(v || ''))}
                      placeholder="请选择承运商"
                      options={operateConfirmLogisticsProviderOptions}
                      style={{ width: '100%' }}
                      showSearch
                      optionFilterProp="label"
                      allowClear
                    />
                  </div>
                  <div>
                    <div style={{ marginBottom: 6 }}>物流跟踪号</div>
                    <Input
                      value={operateConfirmBuyWaybillTrackingNo}
                      onChange={(e) => setOperateConfirmBuyWaybillTrackingNo(e.target.value)}
                      placeholder="请输入物流跟踪号"
                      allowClear
                    />
                  </div>
                  <div>
                    <div style={{ marginBottom: 6 }}>运单费用金额</div>
                    <InputNumber
                      value={operateConfirmBuyWaybillChargeAmount ?? undefined}
                      onChange={(v) => setOperateConfirmBuyWaybillChargeAmount(v == null ? null : Number(v))}
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                      placeholder="请输入费用金额"
                    />
                  </div>
                  <div>
                    <div style={{ marginBottom: 6 }}>币种</div>
                    <Select
                      value={operateConfirmBuyWaybillChargeCurrency}
                      onChange={(v) => setOperateConfirmBuyWaybillChargeCurrency((String(v || 'USD').toUpperCase() || 'USD') as 'CNY' | 'USD' | 'EUR')}
                      options={[
                        { label: 'CNY', value: 'CNY' },
                        { label: 'USD', value: 'USD' },
                        { label: 'EUR', value: 'EUR' },
                      ]}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <div style={{ marginBottom: 6 }}>费用备注（可选）</div>
                  <Input
                    value={operateConfirmBuyWaybillChargeNote}
                    onChange={(e) => setOperateConfirmBuyWaybillChargeNote(e.target.value)}
                    placeholder="可填写采购备注、渠道说明等"
                    allowClear
                  />
                </div>
                <div style={{ marginTop: 10 }}>
                  <div style={{ marginBottom: 6 }}>运单附件</div>
                  {(() => {
                    const waybillAtts = getWaybillAttachments(instr);
                    if (!waybillAtts.length) return null;
                    return (
                      <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <span style={{ color: '#666' }}>当前运单文件:</span>
                        {waybillAtts.map((att: any, idx: number) => {
                          const fname = String(att?.name || att?.file_name || `运单附件${idx + 1}`).trim() || `运单附件${idx + 1}`;
                          return (
                            <Button
                              key={`waybill-file-${idx}`}
                              type="link"
                              size="small"
                              style={{ padding: 0, height: 'auto' }}
                              onClick={() => handlePrintableAttachmentClick(att, instr)}
                            >
                              {fname}
                            </Button>
                          );
                        })}
                      </div>
                    );
                  })()}
                  <Upload
                    beforeUpload={() => false}
                    fileList={operateConfirmBuyWaybillFileList}
                    onChange={(info) => setOperateConfirmBuyWaybillFileList((info?.fileList || []).slice(-1))}
                    onPreview={(file) => previewBuyWaybillUploadFile(file)}
                    itemRender={(originNode, file, _fileList, actions) => (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{ color: '#1677ff', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            actions.preview?.();
                          }}
                        >
                          {String((file as any)?.name || '运单附件')}
                        </span>
                        {actions.remove ? (
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, height: 'auto' }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              actions.remove?.();
                            }}
                          >
                            移除
                          </Button>
                        ) : null}
                      </div>
                    )}
                    maxCount={1}
                    accept=".pdf,.png,.jpg,.jpeg"
                  >
                    <Button>选择文件</Button>
                  </Upload>
                </div>
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button type="primary" loading={operateConfirmBuyWaybillSubmitting} onClick={submitBuyWaybillUpload}>
                    提交并更新指令
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>
      <Modal
        open={operateConfirmMarkAllUnoperatedModalOpen}
        title={(
          <div>
            <div>{`全部标注无法操作（共${operateConfirmCurrentBatchInstrs?.length || 0}条）`}</div>
            {operateConfirmCurrentBatchInstrs.length > 0 ? (
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {operateConfirmCurrentBatchInstrs.map((instr: any, idx: number) => {
                  const code = getInstructionSystemCode(instr);
                  return (
                    <span key={`${code}-${idx}`} style={{ color: '#1677ff', fontWeight: 500 }}>
                      {code}
                    </span>
                  );
                })}
              </div>
            ) : null}
          </div>
        )}
        zIndex={1250}
        onCancel={() => {
          setOperateConfirmMarkAllUnoperatedModalOpen(false);
          setOperateConfirmMarkAllUnoperatedReason('');
        }}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => { setOperateConfirmMarkAllUnoperatedModalOpen(false); setOperateConfirmMarkAllUnoperatedReason(''); }}>取消</Button>
            <Button
              type="primary"
              danger
              onClick={handleConfirmMarkAllUnoperated}
            >
              确认标注
            </Button>
          </div>
        )}
      >
        <div style={{ padding: '16px 0' }}>
          <div style={{ marginBottom: 12 }}>
            <span style={{ marginRight: 8 }}>常用：</span>
            <Button size="small" type="default" onClick={() => setOperateConfirmMarkAllUnoperatedReason((prev) => (prev ? `${prev}、` : '') + '库存不足')}>
              库存不足
            </Button>
            <Button size="small" type="default" style={{ marginLeft: 8 }} onClick={() => setOperateConfirmMarkAllUnoperatedReason((prev) => (prev ? `${prev}、` : '') + '指令错误')}>
              指令错误
            </Button>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>无法操作原因：</label>
            <Input.TextArea
              value={operateConfirmMarkAllUnoperatedReason}
              onChange={(e) => setOperateConfirmMarkAllUnoperatedReason(e.target.value)}
              placeholder="请输入原因（可点击上方快捷选项添加）"
              rows={3}
              allowClear
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={operateConfirmUnoperatedReasonModalOpen}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>请输入无法操作的原因</span>
            {operateConfirmUnoperatedReasonInstr ? (
              <span style={{ color: '#1677ff', fontWeight: 500 }}>
                {getInstructionSystemCode(operateConfirmUnoperatedReasonInstr)}
              </span>
            ) : null}
          </div>
        )}
        zIndex={1450}
        onCancel={() => {
          setOperateConfirmUnoperatedReasonModalOpen(false);
          setOperateConfirmUnoperatedReasonInstr(null);
          setOperateConfirmUnoperatedReasonInput('');
          setOperateConfirmUnoperatedReasonMode('draft');
        }}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => { setOperateConfirmUnoperatedReasonModalOpen(false); setOperateConfirmUnoperatedReasonInstr(null); setOperateConfirmUnoperatedReasonInput(''); setOperateConfirmUnoperatedReasonMode('draft'); }}>取消</Button>
            <Button
              type="primary"
              onClick={() => {
                const reason = String(operateConfirmUnoperatedReasonInput || '').trim();
                if (!reason) {
                  message.warning('请输入无法操作的原因');
                  return;
                }
                handleConfirmUnoperatedWithReason(reason);
              }}
            >
              确认
            </Button>
          </div>
        )}
      >
        <div style={{ padding: '16px 0' }}>
          <div style={{ marginBottom: 12 }}>
            <span style={{ marginRight: 8 }}>快捷选项：</span>
            <Button
              size="small"
              type="default"
              onClick={() => setOperateConfirmUnoperatedReasonInput((prev) => (prev ? `${prev} ` : '') + '库存不足')}
            >
              库存不足
            </Button>
            <Button
              size="small"
              type="default"
              style={{ marginLeft: 8 }}
              onClick={() => setOperateConfirmUnoperatedReasonInput((prev) => (prev ? `${prev} ` : '') + '指令错误')}
            >
              指令错误
            </Button>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>原因：</label>
            <Input
              value={operateConfirmUnoperatedReasonInput}
              onChange={(e) => setOperateConfirmUnoperatedReasonInput(e.target.value)}
              placeholder="请输入或选择无法操作的原因"
              allowClear
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={operateConfirmSubmitModalOpen}
        title="确认提交"
        zIndex={1250}
        onCancel={() => setOperateConfirmSubmitModalOpen(false)}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setOperateConfirmSubmitModalOpen(false)} disabled={operateConfirmSubmitLoading}>取消</Button>
            <Button
              type="primary"
              loading={operateConfirmSubmitLoading}
              disabled={
                (operateConfirmCurrentBatchInstrs || []).some((i: any) => !isDoneInOperateConfirmDraft(i) && !isUnoperatedInOperateConfirmDraft(i))
                || (
                  String(operateConfirmContext?.carrierKey || '').trim() === 'after_pack'
                  && (operateConfirmCurrentBatchInstrs || []).some((i: any) => isDoneInOperateConfirmDraft(i) && !hasCreatedIocForInstr(i))
                )
              }
              onClick={() => handleConfirmSubmit()}
            >
              确认提交
            </Button>
          </div>
        )}
      >
        {(() => {
          const list = operateConfirmCurrentBatchInstrs || [];
          const totalM = list.length;
          const doneN = list.filter((i: any) => isDoneInOperateConfirmDraft(i)).length;
          const unoperatedK = list.filter((i: any) => isUnoperatedInOperateConfirmDraft(i)).length;
          const unmarkedX = list.filter((i: any) => !isDoneInOperateConfirmDraft(i) && !isUnoperatedInOperateConfirmDraft(i)).length;
          return (
            <div style={{ padding: '16px 0' }}>
              <p style={{ marginBottom: 12, fontWeight: 500 }}>当前波次汇总：</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div>总单数：<span style={{ fontWeight: 600 }}>{totalM}</span> 单</div>
                <div>已完成单数：<span style={{ fontWeight: 600, color: '#52c41a' }}>{doneN}</span> 单</div>
                <div>无法操作单数：<span style={{ fontWeight: 600, color: '#ff4d4f' }}>{unoperatedK}</span> 单</div>
              </div>
            </div>
          );
        })()}
      </Modal>
      <Modal
        open={batchMarkModalOpen}
        title={(
          <div>
            <div>{`批量标注（已选${progressSelectedKeys.length}条）`}</div>
            {batchMarkStatus === 'unoperated' && batchMarkSelectedCodes.length > 1 ? (
              <div style={{ marginTop: 6, padding: '0 8px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {batchMarkSelectedCodes.map((code, idx) => (
                  <span key={`${code}-${idx}`} style={{ color: '#1677ff', fontWeight: 500 }}>
                    {code}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        )}
        onCancel={() => {
          setBatchMarkModalOpen(false);
          setBatchMarkStatus('completed');
          setBatchMarkUnoperatedReason('');
        }}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              onClick={() => {
                setBatchMarkModalOpen(false);
                setBatchMarkStatus('completed');
                setBatchMarkUnoperatedReason('');
              }}
            >
              取消
            </Button>
            <Button type="primary" onClick={handleBatchMarkConfirm}>
              确认标注
            </Button>
          </div>
        )}
      >
        <div style={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>处理结果：</div>
            <Radio.Group
              value={batchMarkStatus}
              onChange={(e) => setBatchMarkStatus(e.target.value)}
              options={[
                { label: '已完成', value: 'completed' },
                { label: '无法操作', value: 'unoperated' },
                { label: '未标注', value: 'unmarked' },
              ]}
            />
          </div>
          {batchMarkStatus === 'unoperated' && (
            <div>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>无法操作原因：</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                <span style={{ color: '#666', fontSize: 13 }}>常用：</span>
                <Button
                  type="default"
                  size="small"
                  onClick={() => setBatchMarkUnoperatedReason((prev) => (prev ? `${prev}、库存不足` : '库存不足'))}
                >
                  库存不足
                </Button>
                <Button
                  type="default"
                  size="small"
                  onClick={() => setBatchMarkUnoperatedReason((prev) => (prev ? `${prev}、指令错误` : '指令错误'))}
                >
                  指令错误
                </Button>
              </div>
              <Input.TextArea
                placeholder="请输入原因（如：库存不足、指令错误等），可点击上方快捷选项添加"
                value={batchMarkUnoperatedReason}
                onChange={(e) => setBatchMarkUnoperatedReason(e.target.value)}
                rows={3}
                allowClear
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DropshipPackRenderer;
